/* Design Studio behavior. Extracted from sections/make-yours.liquid on 2026-09-12.

   ⚠️ IT LIVES HERE BECAUSE THE SECTION HIT SHOPIFY'S 256 KB TEMPLATE LIMIT. The push
   failed with "Template content exceeds 256 KB limit" and the CLI still printed its
   success banner underneath the error, so three commits' worth of work sat in git
   believing it was on the theme. Assets are not bound by that limit.

   ⚠️ THERE IS NO LIQUID IN THIS FILE AND THERE MUST NEVER BE. That is what made the
   move safe: every value the flow needs already arrives through `data-` attributes on
   [data-mky] or through a JSON <script> (fabric-data, garment shapes). Putting a
   {{ }} in here would silently emit the literal braces, because an asset is served as
   a static file and never rendered. New settings go on the root element in the
   section, exactly as the existing ones do.

   Loaded with `defer`, so it still runs after the markup it reads.
*/
(function () {
  var root = document.querySelector('[data-mky]');
  if (!root) return;

  function readJSON(id) {
    var el = document.getElementById(id);
    if (!el) { console.warn('[make-yours] missing data block: ' + id); return []; }
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      // Swallowing this left the flow showing no fabrics at all, with nothing
      // anywhere saying why. The empty return is still right; the silence was not.
      console.warn('[make-yours] could not parse ' + id, e);
      return [];
    }
  }
  var FABRICS = readJSON('fabric-data');
  /* ⚠️ A SEPARATE BLOCK, READ ONLY BY SEARCH. See the comment beside its render in
     sections/make-yours.liquid: the classification, the recommender's scoring and its
     seeded paging live in `fabric-data` and nothing here touches them.
     ⚠️ AND IT IS AN OBJECT KEYED BY CODE, NOT AN ARRAY, so `readJSON`'s `[]` fallback is
     the wrong empty for it. An absent block means search matches nothing rather than
     throwing, which is the correct degradation: the pickers all work without it. */
  var FABRIC_SEARCH = (function () {
    var v = readJSON('fabric-search');
    return (v && !Array.isArray(v)) ? v : {};
  })();

  /* ── SOLID COLOUR REQUESTS ────────────────────────────────────────────────────────
     ⚠️ A REQUEST IS NOT A FABRIC, AND THIS WHOLE BLOCK EXISTS TO KEEP THEM APART.
     Nunez hold solids and never post them as swatches, so there is no photograph, no
     measurement and no supplier code for any of them. Evelin's process is: the customer
     names a colour direction, the workroom photographs what is actually on the shelf,
     and the customer approves a real fabric before anything is cut. So a solid choice is
     a REQUEST WITH A PENDING CONFIRMATION, and the flow must never dress one up as
     confirmed stock.

     Three rules, and every one of them was a way to get this wrong:
      - **Never invent a code.** A slot holds `solid:<family>[:<refinement>]`, which
        cannot be mistaken for a Nunez number and cannot collide with one. What must not
        happen is a made-up four-digit code reaching Evelin's cutting line.
      - **Never claim the screen colour.** `hex` here is an APPROXIMATION for the preview
        and every surface that shows it says so. It is not a swatch.
      - **Never let it vanish.** It occupies a fabric assignment inside the same
        two-fabric limit, travels through the URL, the review, the maker line and the
        POST, and forces "Solid fabric confirmation needed" onto the review and the
        maker instructions. A frontend that silently loses a colour request is the one
        outcome Codex's brief names outright.

     ⚠️ CODES 9001 AND 9002 ARE THE SAME THING WEARING A CATALOGUE ROW. `register.csv`
     records both as `source=assumed` with the note "solid assumed in stock at Nunez, who
     do not post solids as swatches. NOT confirmed by a swatch post. Confirm with Evelin
     before promising it." They are placeholders, and the flow has been offering them as
     ordinary fabrics. They stay IN the catalogue -- removing them would move the
     recommender's pool, its seeded paging and the "Browse all fabrics (N)" count, which
     Codex's brief forbids -- and they are presented as the black and white solid
     REQUESTS they always were. Old links carrying `fabric=9001` keep working and now
     mean what they always meant. */
  var SOLID_PREFIX = 'solid:';
  /* Twelve families, which is a palette a person can hold in their head. Deliberately
     NOT a colour wheel or a hex field: a continuous picker invites a precision we cannot
     honour, because what she gets is whatever Nunez actually have in that direction. */
  var SOLID_FAMILIES = [
    { key: 'black',  name: 'Black',  hex: '#111111', legacy: '9001' },
    { key: 'white',  name: 'White',  hex: '#F7F7F5', legacy: '9002' },
    { key: 'cream',  name: 'Cream',  hex: '#EFE6D2' },
    { key: 'brown',  name: 'Brown',  hex: '#6B4B33',
      refine: [['chocolate', 'Dark chocolate'], ['tan', 'Tan'], ['camel', 'Camel']] },
    /* ⚠️ THE KEY STAYS `grey` AND THE LABEL IS `Gray`, AND THAT IS NOT AN INCONSISTENCY.
       The key is URL vocabulary -- it travels in `solid:grey` on every saved link and is
       mirrored by `_fabric_ref()` in the backend, which drops a family it does not
       recognise. Renaming it would silently void every link already sent and needs both
       sides changed together. The LABEL is the only half a customer ever reads, and
       ILLIXIS copy is US English. Same rule for the refinement below: `pale` is the key. */
    { key: 'grey',   name: 'Gray',   hex: '#8C8C8C',
      refine: [['pale', 'Pale gray'], ['charcoal', 'Charcoal']] },
    { key: 'blue',   name: 'Blue',   hex: '#2F5FA8',
      refine: [['sky', 'Sky blue'], ['royal', 'Royal blue'], ['navy', 'Navy']] },
    { key: 'green',  name: 'Green',  hex: '#3B7A4B',
      refine: [['mint', 'Mint'], ['emerald', 'Emerald'], ['olive', 'Olive']] },
    { key: 'yellow', name: 'Yellow', hex: '#E8C33A',
      refine: [['pale', 'Pale yellow'], ['bright', 'Bright yellow'],
               ['golden', 'Golden/mustard']] },
    { key: 'orange', name: 'Orange', hex: '#D9762B',
      refine: [['peach', 'Peach'], ['bright', 'Bright orange'], ['rust', 'Rust']] },
    { key: 'red',    name: 'Red',    hex: '#B72A2A',
      refine: [['bright', 'Bright red'], ['deep', 'Deep red'], ['coral', 'Coral']] },
    { key: 'pink',   name: 'Pink',   hex: '#D96A93',
      refine: [['pale', 'Pale pink'], ['bright', 'Bright pink'], ['fuchsia', 'Fuchsia']] },
    { key: 'purple', name: 'Purple', hex: '#6E4B9E',
      refine: [['lilac', 'Lilac'], ['deep', 'Deep purple'], ['plum', 'Plum']] }
  ];
  var SOLID_BY_KEY = (function () {
    var m = {};
    SOLID_FAMILIES.forEach(function (f) { m[f.key] = f; });
    return m;
  })();
  /* The legacy placeholder codes, read back to the family they stand for. */
  var SOLID_BY_LEGACY = (function () {
    var m = {};
    SOLID_FAMILIES.forEach(function (f) { if (f.legacy) m[f.legacy] = f; });
    return m;
  })();
  /** Is this code a solid REQUEST -- either the new sentinel or a legacy placeholder? */
  function isSolidRequest(code) {
    if (!code) return false;
    return code.indexOf(SOLID_PREFIX) === 0 || !!SOLID_BY_LEGACY[code];
  }
  /** `solid:yellow:bright` (or `9001`) -> `{ fam, refine }`, or null if it is neither.
   *  ⚠️ VALIDATED, NOT PARSED. A link can carry anything and this value reaches Evelin's
   *  cutting line; an unrecognised family is rejected here so it can never look answered. */
  function parseSolid(code) {
    if (!code) return null;
    if (SOLID_BY_LEGACY[code]) return { fam: SOLID_BY_LEGACY[code], refine: null };
    if (code.indexOf(SOLID_PREFIX) !== 0) return null;
    var bits = code.slice(SOLID_PREFIX.length).split(':');
    var fam = SOLID_BY_KEY[bits[0]];
    if (!fam) return null;
    var refine = null;
    if (bits[1]) {
      (fam.refine || []).forEach(function (r) { if (r[0] === bits[1]) refine = r; });
      if (!refine) return null;     // a refinement this family does not offer
    }
    return { fam: fam, refine: refine };
  }
  function solidCode(famKey, refineKey) {
    return SOLID_PREFIX + famKey + (refineKey ? ':' + refineKey : '');
  }
  /** What a solid request is CALLED. The refinement when she gave one, else the family. */
  function solidName(code) {
    var p = parseSolid(code);
    if (!p) return '';
    return p.refine ? p.refine[1] : p.fam.name;
  }
  /* slot -> URL param. Flat and explicit, like `bustLabel`, which is the other free-text
     answer this flow carries. */
  /* Which piece a slot is, in words, for the workroom's line and the review. */
  var SLOT_LABEL = { fabric: 'main fabric', fabricBottom: 'bottom',
                     fabricInside: 'reverse face', fabricCrop: 'crop' };
  var SOLID_NOTE_PARAM = [['fabric', 'solidNote'], ['fabricBottom', 'solidNoteBottom'],
                          ['fabricInside', 'solidNoteInside'],
                          ['fabricCrop', 'solidNoteCrop']];
  /* ⚠️ THE WORKROOM'S INSTRUCTION, AND IT IS NOW THE ONLY PLACE THE PENDING STATE IS
     STATED IN WORDS. Nuno removed every customer-facing caveat on 2026-09-17: "I don't
     want any caveats about the solid colors needing to be confirmed. If we, for some
     unlikely reason, can't source a blue fabric, we would notify the customer at that
     point. They don't need to be warned."
     ⚠️ THIS HALF MAY NOT FOLLOW IT. Evelin has to source and photograph a solid before
     anything is cut, because Nunez do not post solids as swatches -- so the cutting line
     has to say so, in capitals, and `solid_pending` has to stay in the POST. Removing the
     customer's warning was a copy decision. Removing this would be a manufacturing
     defect: a cutting line reading "Blue" with nothing marking it unsourced. */
  var SOLID_MAKER_NOTE = 'SOLID FABRIC CONFIRMATION NEEDED';

  /* Every entry has a Shop By > Top collection behind it. `tankini` is the store's
     "Triangle & Long Sleeve Crop"; the value keeps its old name so saved design links
     from before 2026-09-08 still resolve. */
  /* ⚠️ DO NOT PUT 'tankini' (the long-sleeve crop) BACK IN THIS LIST. It is the THIRD
     PIECE of a three piece, never a two-piece top: the store's triangle-long-sleeve
     collection holds four products and every one of them is a 3-Piece. Checked against
     the live store 2026-09-12, the same way open back and mid back were. The value still
     exists -- `topsFor()` fixes it as the crop on a three piece and the "Worn over it"
     tile draws its symbol directly -- it is just not something she picks as a top. */
  var TOPS_TWO = [['triangle','Triangle'],['halter','Halter'],
                  ['asymmetrical','Asymmetrical'],['classic','Bandeau'],
                  ['high-neck-crop','High-neck crop'],['sports-bra-cut','Sports bra cut']];
  /* ⚠️ DO NOT RE-ADD 'mid-back'. Evelin, 2026-09-11, the same call that retired
     open back: it is not a shape she offers. The store's mid-back-top collection holds
     ONE product and that product is the "Denim Floral Low-Back One-Piece" -- a single
     garment whose own name says LOW back, filed under a collection called Mid-Back Top.
     The collection was the error, not the garment.
     This one had reached further than open back did, so three surfaces came out
     together: this list, the studio deep link on /collections/mid-back-top, and the
     picker option behind it. `shape-one-mid-back` stays in the sprite; see the note
     there, and do not read that file as a menu. */
  var TOPS_ONE = [['classic','Classic'],['asymmetrical','Asymmetrical']];
  /* A monokini IS the shape, so picking it as the style is the whole choice. The one
     tile exists so the preview has a symbol to draw and the record has a name. */
  var TOPS_MONO = [['monokini','Monokini']];

  /* The four Shop By > Style entries.
     ⚠️ ONE PIECE LEADS, AND IT USED TO BE THE STORE'S ORDER OF POPULARITY. This list now
     opens the garment chapter on its own screen, and a one piece is the simplest thing
     to picture -- every other entry is described against it ("a single garment", "one
     piece cut away at the sides"). Nuno, 2026-09-12.
     ⚠️ `state.style` STILL DEFAULTS TO 'two'. Reordering this list does not reorder that,
     so the screen opens with the SECOND option ticked. Deliberate for now; changing the
     default is its own decision. */
  var STYLES = [
    ['one',      'One piece',   'A single garment, still cut in two sizes.'],
    ['two',      'Two piece',   'Top and bottom sized and shaped separately.'],
    ['three',    'Three piece', 'A two piece with a long-sleeve crop to wear over it.'],
    ['monokini', 'Monokini',    'One piece, cut away at the sides.']
  ];

  /* ⚠️ EVERY STYLE BRANCH GOES THROUGH THESE. There were 35 comparisons against the
     literals 'one' and 'two' when the flow had exactly two styles; editing each one to
     add two more is how a silent break happens. `isSingle` means one garment, so no
     separate bottom; `isSplit` means separate pieces. */
  /* ⚠️ COMPARE THE LITERALS HERE, and nowhere else in the file. These two bodies were
     destroyed by the very find-and-replace that created them: rewriting every
     `state.style === 'one'` into `isSingle()` rewrote this line too, so isSingle called
     itself, blew the stack on first render, and the page came up empty with a dead
     Continue button. Verified on the live QR URL 2026-09-08. */
  function isSingle() { return state.style === 'one' || state.style === 'monokini'; }
  function isSplit()  { return state.style === 'two' || state.style === 'three'; }

  /** The top shapes this style can take. */
  function topsFor() {
    if (state.style === 'monokini') return TOPS_MONO;
    if (isSingle()) return TOPS_ONE;
    /* ⚠️ A THREE PIECE PICKS ITS TOP LIKE A TWO PIECE. It was locked to Triangle,
       because every 3-Piece product in the store is tagged "Triangle & Long Sleeve
       Crop". That is a fact about the four three-pieces Evelin has photographed, not a
       limit on what she can cut: a three piece IS a two piece with a crop over it, so
       the top and the bottom underneath are both hers to choose. Nuno, 2026-09-12.
       The CROP is the fixed piece -- it is drawn on the stage and shown as the
       "Worn over it" tile, and it is not selectable. That is what "three piece" means. */
    return TOPS_TWO;
  }

  function styleLabel() {
    var o = STYLES.filter(function (x) { return x[0] === state.style; })[0];
    return o ? o[1] : 'Two piece';
  }
  /* ⚠️ THE SHOP BY MENU IS THE AUTHORITY. Nuno, 2026-09-08: "if the product doesn't
     exist on the site, assume Evelin does not cut it."

     `hipster` and `skirted` were removed here. Neither has a product, a tag or a
     collection anywhere in the catalog: they were inherited verbatim from the shape
     list published on /pages/custom-orders before this project began (snapshot
     hub-before-20260831-134051.json), copied into the request form on 2026-09-01 as
     its defaults, and into this flow after that. Nobody invented them and nobody
     checked them either. Do not restore either without a product to point at. */
  /* ⚠️ THESE TILES ARE CONSTRUCTION ONLY. How much is covered is a SEPARATE question,
     because the two are independent and the store's single `Bottom Style:` tag slot
     cannot hold both: Zigzag Zest exists as a fixed-side brief (tagged Modest Coverage)
     AND as a side-tie (tagged Tie Side) -- same print, same top, two constructions.
     Filing forces one property to be recorded and the other lost.

     Keeping them apart is the point of a made-to-order tool: it lets someone ask for a
     modest-coverage side tie, a combination the catalog cannot express because Evelin
     only ever cut one of each, and which she can obviously make.

     Do NOT put Full / Modest / Mid-Rise / Low Coverage in this list. They are the
     coverage question's answers. */
  /* ⚠️ DO NOT RE-ADD 'open-back' HERE, AND THE SPRITE IS NOT EVIDENCE THAT YOU SHOULD.
     `shape-bottom-open-back` still exists in snippets/garment-shapes.liquid and will
     keep tempting whoever reads the sprite as a menu. Evelin confirmed 2026-09-11 that
     open back is not a bottom. It never was: the store's open-back-bottom collection
     holds ONE product and that product is a Monokini, tagged Bottom Style: Open Back --
     a tag on a one piece, read as if it were a cut of bikini bottom. The homepage grid
     dropped it in cc7f8c2; this is the same correction on the studio side.
     Old shared links carrying bottomShape=open-back are handled: fromUrl() membership
     tests this list and clears anything it does not contain, so such a link behaves as
     if it never named a bottom and coverage guesses one. */
  var BOTTOMS  = [['brief','Classic brief'],
                  ['side-tie','Side tie'],
                  ['high-waisted-full-back-coverage','High-waisted, full back'],
                  ['high-thigh-cut','High-thigh cut']];
  /* ⚠️ THESE MUST MATCH /pages/size-chart EXACTLY. They did not: the flow offered
     S, SM, M, L, XL while the chart listed X-SM, SM, MED, XL, so S, M and L had no row
     to look up and X-SM could not be chosen. Both are now XS, S, M, L, XL. Researched
     2026-09-04 against Roxy, Nike and Indyeva: the row the chart called XL grades exactly
     one step above MED and matches their L, so it was renamed and a real XL added. */
  var SIZES = ['XS','S','M','L','XL'];

  /* ⚠️ THE ONE NEW QUESTION THAT CHANGES THE GARMENT BY ITSELF. `topSize` and
     `bottomSize` have always been independent, and until now the split between them was
     set by hand on a screen that defaults both to M -- so the commonest real shape, a
     woman who is not one letter all over, had to notice the two chip rows were separate
     and act on it. `volume` sets that split from an answer she has already given.

     ONE STEP, NEVER TWO. /pages/size-chart grades about two inches of chest per letter,
     so a two-letter split is a different garment rather than a different size.

     Recomputed on every paint rather than once on answering, because it splits from
     effectiveTopSize() and THAT changes later, when she gives a band and cup on the
     sizes screen. `sizesTouched` retires it the moment she moves a chip herself: her
     hand beats our inference, always. */
  var SIZE_SPLIT = { upper: -1, balanced: 0, lower: 1 };
  var sizesTouched = false;
  function applySizeSplit() {
    if (sizesTouched || !state.volume || !(state.volume in SIZE_SPLIT)) return;
    var i = SIZES.indexOf(effectiveTopSize());
    if (i < 0) return;
    state.bottomSize = SIZES[Math.min(SIZES.length - 1, Math.max(0, i + SIZE_SPLIT[state.volume]))];
  }

  /* Height at either extreme, on a woman who already says one-pieces ride up, is the
     same class of problem as a chest past the chart: outside what a standard grade holds
     without re-drafting. It gets the same answer the bust gets -- talk it through before
     anything is cut -- rather than a silent standard-length suit.
     ⚠️ SHOWN ON THE RECORD, NOT ON THE TORSO SCREEN. Single-choice questions advance on
     the tap, so a note that depends on the torso answer can never be read there. */
  function heightBeyondGrade() {
    /* ⚠️ GUARDED ON APPLICABILITY SINCE 2026-09-15. The warning is "a long torso
       adjustment on a woman at either end of the height range is outside a standard
       grade". A split suit gets no torso adjustment at all, so a stale `torso: 'long'`
       left in state from a one piece she has since switched away from would raise a
       cutting warning about an allowance that is not being made. */
    return torsoApplies() &&
           (state.height === 'u52' || state.height === '511p') && state.torso === 'long';
  }

  /** Her frame and shape in her own words, for the record and for Evelin. */
  function bodyLine() {
    var bits = [];
    if (state.height) bits.push(label(Q.height.opts, state.height));
    if (state.volume) bits.push(label(Q.volume.opts, state.volume));
    if (state.waist)  bits.push(label(Q.waist.opts, state.waist) + ' waist');
    if (state.tummy)  bits.push(label(Q.tummy.opts, state.tummy) + ' stomach');
    return bits.join(' \u00b7 ');
  }

  /* ⚠️ THE BUST IS THE ONE THING XS-XL CANNOT SAY, and Evelin reports it is her
     customers' biggest problem. /pages/size-chart gives ONE chest figure per size
     (M = 37-38"), so 32DD, 34D, 36B and 38A are all "M" and all different garments.
     A top has to satisfy two independent dimensions -- the band holds it on, the cup
     contains -- and one letter collapses them.

     Bra vocabulary, because it is the only sizing language women already have for this.
     A bra size is a size she SELECTS, like taking an M, not a measurement we take of
     her: /pages/privacy-policy says "we collect the fabric, shapes and sizes you select.
     We do not collect body measurements", and that stays true. NEVER ask for an
     underbust in inches. */
  /* ⚠️ SET FROM EVIDENCE, NOT FROM AMBITION, and deliberately narrow to start.
     Measured on the live catalog 2026-09-09: every suit this store has ever made is
     M(42) SM(15) S(13) L(13) XL(5). Nothing above XL, and no bra-sized product at all,
     so there is NO track record on cups.

     Band stops at 40 and cup at DD because that is roughly the edge of what standard
     swim construction holds without components a solo maker may not stock: powernet
     lining, wire channels, a wider engineered underband. A pattern grades two or three
     sizes from its base and then needs re-drafting rather than scaling, which is hours
     per customer on a $295 garment that is FINAL SALE -- a fit miss there lands on
     someone already failed by every other brand, holding a suit she cannot return.

     This excludes real customers, and that is a known cost: a 32G is a 37" chest, well
     inside the chart, and exactly who this feature exists for. She goes down the
     talk-first path instead of being quietly mis-sold. WIDEN THIS once Evelin has made
     a few and knows her own limit -- the number to raise is here, in one place. */
  var BANDS = ['30','32','34','36','38','40'];
  /* US grading, one inch of bust-minus-band per letter. */
  var CUPS = [['A',1],['B',2],['C',3],['D',4],['DD',5]];

  /* The chart tops out at a 42" chest, which is the largest thing the store has made.
     A combination past it is not refused -- it opens a conversation before anything is
     cut, which is the only honest answer when there is no pattern to point at. */
  var BUST_CEILING_IN = 42;
  function bustBeyondGrade() {
    var inches = 0;
    for (var i = 0; i < CUPS.length; i++) if (CUPS[i][0] === state.cup) inches = CUPS[i][1];
    if (!state.band || !inches) return false;
    return parseInt(state.band, 10) + inches > BUST_CEILING_IN;
  }

  /** The XS-XL the chart would call this bust, or '' if it is not known.
   *  Derivable in this direction ONLY: 34D is always M, but M is 34D or 32DD or 36B. */
  function bustToStandard(band, cup) {
    var inches = 0;
    for (var i = 0; i < CUPS.length; i++) if (CUPS[i][0] === cup) inches = CUPS[i][1];
    if (!band || !inches) return '';
    var bust = parseInt(band, 10) + inches;
    /* ⚠️ CLAMPS AT XL, and the chart is why. /pages/size-chart stops at a 42" chest,
       so anything above that -- a 40C is 43" -- has no row to land on and reads XL.
       Evelin cuts to order so the garment is fine; the CHART is what is short. The
       band and cup travel with the design regardless, so she always has the real
       figure rather than a clamped letter. */
    return bust <= 34 ? 'XS' : bust <= 36 ? 'S' : bust <= 38 ? 'M' : bust <= 40 ? 'L' : 'XL';
  }

  /** The size Evelin cuts the top to, however she got there. */
  function effectiveTopSize() {
    return bustToStandard(state.band, state.cup) || state.topSize;
  }

  /* Every option carries the construction change it causes. Nothing is asked that does
     not appear in `builds`, and nothing appears in `builds` that Evelin cannot sew. */
  var Q = {
    /* ⚠️ HEIGHT IS A SIZE SHE STATES, NOT A MEASUREMENT WE TAKE, which is the same
       footing as the bra band and cup below and keeps /pages/privacy-policy's "we
       collect the fabric, shapes and sizes you select. We do not collect body
       measurements" true. Bands, not exact inches, for the same reason: a figure to
       the inch would be false precision on a garment that is FINAL SALE.

       ⚠️ IT CARRIES NO `builds` STRING YET, AND THAT IS DELIBERATE. Height's job is to
       GRADE the torso answer below -- a 5'11" and a 5'2" woman both answering "yes,
       they ride up" do not need the same suit, and today they both get the identical
       'Body cut longer through the torso'. Only Evelin can say what that grade is.
       Until she does, height travels to her on the spec line, where she can use it at
       the cutting table, and promises the customer nothing.

       WHEN SHE ANSWERS: the number goes in Q.torso.builds, and nothing else changes. */
    height: {
      q: 'How tall are you?',
      hint: 'It changes the length the body is cut to, and how long the straps are.',
      multi: false,
      opts: [
        ['u52',    'Under 5\'2"', ''],
        ['52-54',  '5\'2"-5\'4"', ''],
        ['55-57',  '5\'5"-5\'7"', ''],
        ['58-510', '5\'8"-5\'10"', ''],
        ['511p',   '5\'11"+', '']
      ],
      builds: {}
    },
    /* ⚠️ THESE THREE CARRY NO `builds` EITHER, AND FOR A DIFFERENT REASON THAN HEIGHT.
       Nuno and Evelin, 2026-09-11: both find the SmartSize flow useful and want the
       questions asked now, with the answers discarded later if they earn nothing. They
       are asked, and they reach Evelin's spec line, but NOT ONE OF THEM CLAIMS A
       CONSTRUCTION CHANGE. Inventing a note here is precisely the failure this file has
       already reverted twice -- v1's taste quiz and v2's two freeform questions both
       died because the answer visibly changed nothing.

       So the governing rule still holds, read properly: `builds` is what the CUSTOMER is
       told, the payload is what EVELIN is sent, and a question impacts the suit when it
       changes what she cuts. `volume` already does more than that (see SIZE_SPLIT).

       KEPT AS ONE CONTIGUOUS BLOCK on purpose, and listed together in FIT_KEYS, so
       dropping them is deleting a block rather than unpicking a flow. */
    volume: {
      q: 'Where do you carry more?',
      hint: 'Tap the form closest to yours. It sets where the suit starts wider.',
      multi: false,
      tiles: 'volume',
      opts: [
        ['upper',    'Upper body', 'Fuller through the bust than the hips'],
        ['balanced', 'Balanced',   'Bust and hips about even'],
        ['lower',    'Lower body', 'Fuller through the hips than the bust']
      ],
      builds: {}
    },
    waist: {
      q: 'How defined is your waist?',
      hint: 'Where the side seam curves in, and how much.',
      multi: false,
      tiles: 'waist',
      opts: [
        ['straight', 'Straight',      'Runs almost straight from ribs to hip'],
        ['gentle',   'Gently curved', 'Curves in a little'],
        ['defined',  'Defined',       'Clearly narrower than ribs and hips']
      ],
      builds: {}
    },
    tummy: {
      q: 'How does your stomach sit?',
      hint: 'From the side, which is closest?',
      multi: false,
      tiles: 'tummy',
      opts: [
        ['flat',  'Flat',    'Sits flat against your frame'],
        ['soft',  'Soft',    'A little forward below the bust'],
        ['round', 'Rounded', 'Rounded forward below the bust']
      ],
      builds: {}
    },
    /* ⚠️ ASKED OF CONNECTED GARMENTS ONLY SINCE 2026-09-15. See the ROUTE row: a
       one piece and a monokini run from shoulder to leg in one piece and therefore have a
       length between the two that can be wrong; a bikini top and a bikini bottom are not
       joined to each other, and a three piece's crop is worn OVER them rather than joining
       them. It used to be asked of everyone, so a two-piece customer was asked whether
       one pieces ride up and her answer then cut a torso allowance into a garment that has
       no torso.
       ⚠️ THE QUESTION ASKS ABOUT ONE PIECES SHE HAS WORN, NOT ABOUT THE ONE SHE IS
       DESIGNING, and the hint says "including monokinis" for exactly that reason: a
       monokini customer must not have to have worn a monokini specifically before she can
       answer. Codex, 2026-09-15. */
    torso: {
      q: 'Do one-piece suits ride up on you, or pull at the shoulders?',
      /* ⚠️ "This includes monokinis" IS ITS OWN SENTENCE, SHORT AND EARLY. It was buried
         mid-clause in a long hint, which is not the same as being told. Codex, 2026-09-15:
         a short hint can say it outright, so that a monokini customer does not have to
         work out whether the question is for her. */
      hint: 'About one-piece suits you have worn. This includes monokinis. The most common complaint there is, and the easiest thing to fix \u2014 we are not asking for a measurement, just whether it happens.',
      multi: false,
      /* ⚠️ "No" FIRST, DELIBERATELY, AND IT USED TO BE LAST. Nuno, 2026-09-11. The list
         now runs from no adjustment to the most of it, so reading down it is reading the
         allowance growing -- which is exactly what the picture beside it does as she
         moves down. Answer KEYS are untouched, so saved links and `builds` are unaffected;
         only the reading order changed. */
      opts: [
        ['standard', 'No, they sit fine',      'Standard length.'],
        ['slight',   'A bit, on a long day',   'A little extra length, not the full adjustment.'],
        ['long',     'Yes, most of them do',   'We cut the body longer between bust and leg.'],
        /* ⚠️ LAST, BECAUSE IT IS NOT ON THE SCALE. The three answers above run from no
           adjustment to the most of it, and reading down them is reading the allowance
           grow -- which is what the picture beside them does. "I haven't worn one" is not
           a smaller or larger allowance; it is a different kind of answer, so it sits
           below the scale rather than inside it.
           ⚠️ IT CARRIES NO `builds` STRING AND IT NEVER MAY. Codex, 2026-09-15:
           unknown creates no automatic fit adjustment. It is not `standard` -- "no
           adjustment because she says they sit fine" and "no adjustment because nobody
           knows yet" are different things to the person cutting, and collapsing them is
           what the answer exists to prevent. */
        /* ⚠️ IT PROMISES NOTHING AND CONFIRMS NOTHING. It read "Evelin will talk it
           through with you", which invents a follow-up nobody committed to, and any
           wording along the lines of "stays standard" would turn "I don't know" into a
           confirmation that a standard length is right for her. Codex, 2026-09-15:
           neutral wording only. The answer records an absence of information; it does not
           record a decision. */
        ['unknown',  'I haven\'t worn one enough to know', 'No adjustment selected from this answer.']
      ],
      builds: { long: 'Body cut longer through the torso',
                slight: 'Slightly longer through the torso' }
    },
    top: {
      q: 'What does a swimsuit top usually get wrong for you?',
      hint: 'Each one changes how the top is built.',
      multi: true,
      opts: [
        ['spill',   'I spill over the top of it', 'Deeper cups and a higher side.'],
        ['dig',     'The straps dig into my shoulders', 'Wider, adjustable straps.'],
        /* ⚠️ "HIDDEN INSIDE THE TOP" IS DOING WORK, not decorating. The shelf is the one
           answer here whose pink marks something you cannot see on the garment, and the
           caption that used to say so was removed when Q6's caption went static
           (2026-09-13). This line is now the only place it is said, and it is read before
           she hovers rather than after. Do not trim it back to "A shelf bra built in." */
        ['nosupport','It gives no real support',  'A shelf bra built in, hidden inside the top.'],
        ['rideup',  'It rides up over my bust',   'A wider, firmer underband.'],
        ['none',    'Nothing. Tops fit me fine', ''],
        /* ⚠️ AN UNCERTAIN CUSTOMER MUST NOT HAVE TO CLAIM HER TOPS FIT HER WELL. Until
           2026-09-15 the only way past this question without ticking a problem was
           "Nothing. Tops fit me fine", which is a positive claim about her wardrobe --
           and it reaches Evelin as one. Codex: add the truthful answer directly rather
           than gating the question behind "have you worn one?".
           ⚠️ EXCLUSIVE AGAINST BOTH THE ISSUES AND `none`, which is why it is handled
           beside `none` in every selection path rather than as a fifth issue. No
           `builds` string: it changes nothing about the top. */
        ['unknown', 'I\'m not sure yet', '']
      ],
      builds: { spill: 'Deeper cups, higher at the side', dig: 'Wider adjustable straps',
                nosupport: 'Shelf bra built in', rideup: 'Wider, firmer underband' }
    },
    wire: {
      q: 'Underwire?',
      hint: 'Genuinely divisive, so we ask rather than assume.',
      multi: false,
      opts: [
        ['yes',  'Yes, I want the structure', 'Underwired cups.'],
        ['no',   'No, I find it digs',        'No wire. Structure comes from the seams.'],
        ['any',  'No strong feeling',         'We will pick for the shape you choose.']
      ],
      builds: { yes: 'Underwired', no: 'Wire-free, structured seams' }
    },
    padding: {
      q: 'Padding?',
      hint: 'Asked because the answer changes how the top is cut, not just what is in it.',
      multi: false,
      opts: [
        ['removable', 'Pads I can take out',   'Pockets and a removable pad set.'],
        ['sewn',      'Sewn in, please',       'Cups sewn into the lining.'],
        ['no',        'None at all',           'Unlined, no cups.']
      ],
      builds: { removable: 'Removable pads in pockets', sewn: 'Sewn-in cups',
                no: 'Unlined, no cups' }
    },
    /* ⚠️ "SWIMWEAR", NOT "BIKINI BOTTOMS", SINCE 2026-09-15. Every suit this flow
       makes has a seat -- a one piece and a monokini included -- and the question is asked
       of all of them, so naming only bikini bottoms told a one-piece customer the question
       was not about her and threw away real information on the garment where a riding seat
       is hardest to live with. Codex, 2026-09-15. The answer VALUES are untouched, so
       saved links and `builds` are unaffected. */
    seat: {
      q: 'Does your swimwear ride up at the back?',
      hint: 'A seat cut and a leg elastic problem, not a size problem.',
      multi: false,
      opts: [
        ['yes', 'Yes, constantly',  'Fuller seat and a softer leg elastic.'],
        ['no',  'No, they stay put', 'Standard seat.'],
        /* ⚠️ NOT THE SAME AS "No", AND THAT IS THE WHOLE REASON IT EXISTS. "No, they
           stay put" tells Evelin the standard seat is right for this customer. "I'm not
           sure" tells her nobody has checked. Both leave the seat standard; only one of
           them is a claim. No `builds` string. */
        /* Same rule as the torso answer's: no follow-up promise, no claim that a standard
           seat is right for her. See the note there. */
        ['unknown', 'I\'m not sure', 'No adjustment selected from this answer.']
      ],
      builds: { yes: 'Fuller seat, softer leg elastic' }
    },
    coverage: {
      pairTiles: true,
      q: 'How much do you want covered?',
      hint: 'You know this better than any quiz does. It sets a starting point; you change the shapes next.',
      multi: false,
      opts: [
        ['full',   'As covered as it comes',  'High-waisted, full seat, more chest and shoulder.'],
        ['modest', 'Covered, not sporty',     'Generous seat, a top that stays put.'],
        ['middle', 'Somewhere in the middle', 'Classic bikini proportions.'],
        ['low',    'Less rather than more',   'A triangle top and a side-tie bottom.']
      ],
      builds: {}
    }
  };
  /* ⚠️ ONE FLAT LIST, TWO CHAPTERS. Every step index in this file is derived from the
     length of the question list, so splitting the list itself would mean re-deriving all
     of it. BODY_KEYS is a PREFIX of QKEYS instead: the indices stay exactly as they were
     and only chapterOf/firstStepOf learn where the boundary is.
     Nuno, 2026-09-11, on the four body questions sitting inside Fit: "we just tried to
     fit a square peg into a round hole". They are their own chapter because they are
     about HER, not about the suit -- and the stage draws her, not the garment, while
     they are being asked. */
  var BODY_KEYS = ['height','volume','waist','tummy'];
  var FIT_KEYS  = ['torso','top','wire','padding','seat'];
  var QKEYS     = BODY_KEYS.concat(FIT_KEYS);

  /* CLOTH. Four questions, three answers each, narrowing ~100 fabrics to a handful.

     ⚠️ THESE ARE BANDS NOW, NOT SCORES. Until 2026-09-12 each answer was a target position
     on a normalized numeric axis and `suggested()` summed absolute distance. That ranked a
     fabric perfect on three axes and wrong on the fourth above one decent on all four, and
     then called all six "the closest to what you said": 228 of 486 recommendations across
     the 81 answer combinations missed two or more answers under that heading. Worse, the
     axes measured something other than what the words asked -- edge density ranked 5216,
     the flattest fabric in the drop, as the busiest of 102.
     Each fabric now carries a reviewed BAND per question, emitted from the register by
     build_swatch_picker.py, and matching is band equality. See iralsol-ops
     fabrics/review/2026-09-12-fabric-labels/DEFINITIONS.md.

     ⚠️ THE NUMERIC AXES ARE STILL EMITTED AND ARE NO LONGER USED FOR MATCHING. They are
     drop-normalized min-max, so one sell-out moved 13 unchanged fabrics into a different
     lightness band; a stored band cannot do that. Do not reintroduce them as a tiebreak.

     ⚠️ A CLOTH WITH NO BAND ON A QUESTION MAKES NO CLAIM THERE. Blank (nobody looked) and
     `uncertain` (someone looked and could not decide) both withhold the claim; neither is
     defaulted to a middle band. Defaulting is what served unreviewed fabrics under
     "matches everything you said". */
  var FABRIC_Q = [
    { key: 'color', q: 'How much color do you want?',
      hint: 'Not which color. How many.',
      opts: [['limited', 'One color, or close to it'],
             ['moderate', 'A few that work together'],
             ['multicolor', 'The more the better']] },
    /* ⚠️ THIS QUESTION HAS BEEN RE-WORDED TWICE AND BOTH TIMES FOR THE SAME REASON.
       "Loud or quiet?" and then "Soft or vivid?" both described IMPACT, while the answer
       describes SATURATION. A black-and-white newspaper print has no color at all and is
       the loudest thing in the drop across a pool; under either old wording its band read
       as a lie. "Muted, or barely any color" is true of it and implies nothing about how
       much it stands out -- which is question three. */
    { key: 'intensity', q: 'How strong is the color?',
      hint: 'How saturated the colors are &mdash; not how much the print stands out.',
      opts: [['soft', 'Muted, or barely any color'],
             ['mid', 'Somewhere in between'],
             ['vivid', 'Strong and saturated']] },
    /* ⚠️ THE MIDDLE ANSWER IS DELIBERATELY BROAD, AND THAT IS WHAT AVOIDED A FOURTH ONE.
       It used to be "spaced motifs", which demanded countable things on a visible ground,
       so a gingham check had nothing to count and no ground to be open and could not be
       classified at all -- 5263 became the one fabric in the drop that could never match
       anything. "Some pattern" asks only how much detail is visible and how much space is
       around it. Checks, wide stripes and simple dots all land here; dense ones still go
       to packed, so a stripe is not automatically the middle band. */
    { key: 'density', q: 'Busy or calm?',
      hint: 'How much is going on across the surface.',
      opts: [['open', 'Calm, with large simple areas'],
             ['spaced', 'Some pattern'],
             ['packed', 'Busy, packed, detailed']] },
    /* ⚠️ "Either" IS null, AND IT IS NOT THE SAME AS UNANSWERED. null means ANSWERED WITH
       NO PREFERENCE: this question stops constraining, and a fabric whose ground nobody
       could decide becomes matchable again. `undefined` means she has not answered yet and
       Continue stays locked. The two must stay distinguishable through the URL as well --
       see prefsToUrl/prefsFromUrl, which is where they were being lost entirely. */
    { key: 'ground', q: 'Light or dark ground?',
      hint: 'What the pattern sits on.',
      opts: [['dark', 'Dark'],
             [null, 'Either'],
             ['light', 'Light']] }
  ];
  var FABRIC_DIMS = ['color', 'intensity', 'density', 'ground'];
  /* The legal bands, in order. Order is load-bearing: ADJACENCY is computed from it, and
     adjacency is what separates "close" from "the opposite of what you asked for". */
  var FABRIC_BANDS = {
    color:    ['limited', 'moderate', 'multicolor'],
    intensity: ['soft', 'mid', 'vivid'],
    density:   ['open', 'spaced', 'packed'],
    ground:    ['dark', 'light']
  };
  /* ⚠️ WHICH NEAR-MISS IS MOST FORGIVABLE IS A PRODUCT ASSUMPTION, NOT A MEASURED
     CUSTOMER PREFERENCE, and nothing here inferred it. It is the working policy until
     someone at Ir Al Sol says otherwise. Measured across all 24 orderings it moves the
     top-20 share of displayed slots by about a point, so it is not load-bearing for
     exposure -- it is a claim about taste, and it is labeled as one. */
  var FABRIC_MISS_ORDER = ['ground', 'intensity', 'density', 'color'];

  /* ⚠️ EVERY COVERAGE ANSWER MUST LAND ON A DIFFERENT SUIT. "middle" and "low" both
     mapped to triangle + hipster, so two of the four answers changed nothing on screen
     and the option read as broken. Triangle is already the most minimal top we cut, so
     the difference between them is the bottom: hipster against a side tie. */
  /* ⚠️ `full` USED TO BE 'tankini' AND THAT WAS THE CROP. It made "As covered as it
     comes" draw a long-sleeve crop on a two piece, which the store does not sell, and on
     a three piece it showed the crop as the thing that VARIES when the crop is the piece
     that is included. High-neck crop is the most covered top that is actually a two-piece
     top. */
  var COVER_TOP = { full:'high-neck-crop', modest:'halter', middle:'triangle', low:'triangle' };
  /* A coverage answer no longer PICKS a coverage; it suggests the construction that
     usually carries it, and she changes it on the next screen. All four land on a
     different tile, which is the check §3q added after two answers collapsed onto one. */
  var COVER_BOT = { full:'high-waisted-full-back-coverage', modest:'brief',
                    middle:'high-thigh-cut', low:'side-tie' };

  /* ⚠️ THE POSSESSIVES ARE LOAD-BEARING. `You`, `Your Size` and `Yours` are one thread
     read in order, and they are deliberately NOT adjacent: band and cup is the most
     personal question in the flow, so it is asked AFTER she has chosen a coverage level
     and is looking at her own suit, when she can see what the number is for. Named
     `Body` and `Sizes` the split read as two unrelated body chapters somebody forgot to
     merge -- Nuno, 2026-09-11: "that might just be mental gymnastics". It is the naming
     that was wrong, not the order. Do not rename these back to bare nouns. */
  /* ⚠️ CHAPTER 2 IS 'Style' AND IT HOLDS TWO STEPS: what kind of suit, then how much it
     covers. The kind used to be a block at the top of the Shapes screen, which put it
     AFTER coverage -- so she was shown a bikini top-and-bottom pair and asked how much
     she wanted covered before she had said she wanted two pieces. Nuno, 2026-09-12.
     It moved ahead of coverage rather than the whole Shapes screen moving, because
     coverage SEEDS the shape tiles (see the click handler: it writes topShape and
     bottomShape). Shapes before coverage would have had coverage silently overwrite
     the tiles she had just chosen. */
  /* ⚠️ FOUR CHAPTERS SINCE 2026-09-15, AND THE SEVEN ABOVE ARE THE ORDER THEY REPLACED.
     The old rail named a chapter per SCREEN GROUP -- You, Fit, Style, Shapes, Your Size,
     Fabric, Yours -- which is a table of contents for the file rather than for the
     customer, and it read as seven separate errands. Codex, 2026-09-15: four chapters, in
     her language, each answering "what am I deciding now". Your look is the whole garment
     decision (kind, cloth, cut, arrangement); Your comfort is what a top gets wrong and
     what holds it up; Your fit is her own frame and sizes; Your design is the record.
     The possessives survive the rename: they were load-bearing before and they still are.
     ⚠️ THE MEMBERSHIP IS ON THE ROUTE, NOT HERE. Each row carries its chapter, so a
     screen cannot belong to a chapter the rail does not draw and moving a screen between
     chapters is editing one field. */
  var CHAPTERS = ['Your look','Your comfort','Your fit','Your design'];
  var CHAPTER_LEDE = (function () {
    /* ⚠️ A PIPE INSIDE A LEDE SHIFTS EVERY LATER CHAPTER'S. The seven ride in one data
       attribute joined by `|`, so one stray character in a theme setting silently puts
       the Fabric lede on Your Size and so on -- wrong copy, no error. The schema warns an
       editor; this warns whoever is debugging it. */
    var parts = (root.dataset.ledes || '').split('|');
    if (parts.length !== CHAPTERS.length && window.console) {
      console.warn('Design Studio: expected ' + CHAPTERS.length + ' ledes, got ' +
                   parts.length + '. A lede probably contains a "|".');
    }
    return parts;
  })();

  var state = {
    intent: '',
    /* Empty band/cup means she did not know it, which is not a gap: the flow falls back
       to `topSize`, exactly what it cut to before this existed. `bustSkipped` records
       that she was ASKED and said so, so Continue can tell "not answered yet" from
       "answered, and the answer is I don't know". */
    band: '', cup: '', bustSkipped: false,
    /* ⚠️ "MY SIZE ISN'T LISTED" IS NOT "I'M NOT SURE", AND CONFLATING THEM LOSES REAL
       INFORMATION. A woman who wears a 32G knows her size exactly; the chart stops at DD.
       Routed through bustSkipped she was asked for a top size instead, which quietly
       became an M on a record that said nothing about the G -- so Evelin never learned
       the one number that mattered. These carry the label she gave and the system she
       gave it in, and the record says it needs review rather than claiming a grade. */
    bustUnlisted: false, bustLabel: '',
    height: '', volume: '', waist: '', tummy: '',
    torso: '', top: [], wire: '', padding: '', seat: '', coverage: '',
    /* ⚠️ NO DEFAULT KIND. It was 'two', which ticked the second option the moment the
       screen opened -- the one question in the flow that answered itself. Nuno,
       2026-09-12. Everything downstream already copes: isSingle() and isSplit() are both
       false for '', so the ghost draws the two-piece pair it always drew, and
       canContinue gates on !!state.style so Continue stays dead until she chooses. */
    style: '', topShape: '', topSize: 'M', bottomShape: '', bottomSize: 'M', fabric: '',
    /* ⚠️ `fabric` HOLDS BANDS, AND undefined != null. An absent key means she has not
       answered that question; an explicit null means she answered "Either" and the
       question stops constraining. Both have to survive a round trip through the URL,
       which is what prefsToUrl/prefsFromUrl exist for. */
    /* ⚠️ `prefs`, NOT `fabric`, AND THE NAME IS LOAD-BEARING. This key holds the four
       ANSWERS; `fabric` twelve lines up holds the code she CHOSE. A blanket cloth->fabric
       rename made both of them `fabric:` in this one object literal -- the second silently
       won, `state.prefs` became undefined, and the very first screen threw
       "Cannot read properties of undefined (reading 'color')" before it drew anything. */
    prefs: {}, showAll: false,
    /* ⚠️ THE SEED IS PART OF THE ANSWER, NOT A RENDER DETAIL. It decides which of several
       equally-good fabrics she is shown, so a link that drops it shows her a different six
       than the one she saved. Minted once per flow and carried in the URL beside the
       answers. */
    seed: '',
    /* Which page of suggestions she is on. Stored, not rebuilt: "Show more options" has to
       survive Back, refresh and a saved link or it silently restarts. */
    fabricPage: 0,
    /* The catalogue-label version the answers were last resolved against. A seed cannot
       hold results still when the eligible pool itself changes; this is how we notice. */
    fabricVer: '',
    /* Set when a resumed link's suggestions had to be recomputed against a changed
       catalogue, so the screen can say so instead of quietly showing different fabrics. */
    prefsRefreshed: false,
    /* Set when a resumed link named a fabric that is no longer in the drop. Her choice is
       never silently swapped; the screen explains. */
    fabricGone: '',
    /* Per-slot record of fabrics a link named that we cannot honor, so each picker can name
       its own. `{fabric|fabricBottom|fabricInside: code}`. */
    fabricGoneSlots: {},
    /* ⚠️ EMPTY, AND IT USED TO BE 'same'. A default ticked the first answer the moment
       the screen opened, so the question arrived already answered and Continue was live
       before she had read it -- the same defect Nuno removed from `style` on 2026-09-12,
       and the reason that one now starts empty too. Codex, 2026-09-14: a new design must
       reach this question unanswered, and only a deliberate selection may record it.
       ⚠️ WHAT THIS CHANGED DOWNSTREAM, AUDITED RATHER THAN ASSUMED. `extraPicks()`,
       `normalizeVariation()` and every `=== 'reversible'` test read empty as "not that
       one" and are correct unchanged. `stepAnswered()` reads `!!state.variation`, which is
       what now gates Continue. The two that needed work are the resume ladder (see
       fromUrl: a link carrying a fabric but no variation lands ON this question instead of
       skipping to the record) and the price, which is read through priceNow(). */
    variation: '', fabricBottom: '', fabricInside: '', fabricCrop: '',
    /* ⚠️ ONE NOTE PER SLOT, BECAUSE A DESIGN CAN HOLD TWO PENDING COLOURS. Keyed by the
       slot's own state field, so nothing has to map between two vocabularies. Her words
       about a colour she wants, bounded here and again on the way into the URL: it is the
       only free text on these screens and the POST behind them is unauthenticated. */
    solidNotes: {},
    /* ⚠️ HER NOTES, AND THE ID THAT FINDS THEM AGAIN. `notes` is the truth for this page;
       storage is a mirror of it (notesSave). `designId` IS serialized -- it is the opaque
       key, not the content -- and `noteDraft` is NOT: it is text she has typed and not
       yet added, so it must never reach a shareable address. It survives the panel being
       closed and reopened, which is Codex's "do not discard typed text when the panel is
       temporarily closed", and it dies with the page, which is correct for something she
       never pressed Add on. */
    notes: [],
    noteDraft: '',
    noteEditing: '',
    designId: '',
    /* Which browsing control is on: '' (everything), 'prints' or 'solids'. Not an answer
       and never serialized -- it is where she is LOOKING, not what she has chosen, and a
       saved link that reopened on the solids palette would misreport her design. */
    browse: '',
    /* What she has typed. Not serialized, for the same reason `browse` is not: it is
       where she is LOOKING, not what she has chosen, and a saved link that reopened on a
       search would misreport her design. */
    query: '',
    /* The colour family whose refinements are open, while she is choosing one. Also not
       an answer: nothing is recorded until she presses a refinement or "Use this colour
       direction". */
    solidOpen: '',
    /* ⚠️ A LINK SAVED BEFORE 2026-09-15 CAN NAME THREE FABRICS, AND IT IS NOT DISCARDED.
       Reversible used to ask for a separate bottom fabric on top of the reverse face (see
       extraPicks()), so a two piece could be specified in three cloths. Those links are
       out there, in inboxes and bookmarks. Silently dropping one of the three would change
       her design without telling her, and silently KEEPING all three would submit a suit
       the workroom cannot cut -- so the link resumes on a correction screen that names all
       three and makes her choose which two to keep. `{ top, bottom, inside }` while it is
       unresolved, null once she has chosen. Nothing already SUBMITTED is touched: those
       are rows in ILLIXIS, not designs in this flow. */
    fabricExcess: null,
    /* ⚠️ A ROLE, NEVER A CODE, AND THAT IS WHAT SURVIVES A SWAP. `'primary'` is the
       fabric she chose first -- the outside on a reversible suit, the top on a mixed one
       -- and `'second'` is the other. The two swap buttons in this flow exchange
       `state.fabric` and `state.fabricBottom`, so a crop answer stored as a CODE would
       silently follow the cloth onto the other piece and the crop would change fabric
       because she reordered two garments it is not part of. Empty until she chooses:
       nothing in this flow answers a question on her behalf. */
    cropFace: ''
  };

  var PRICE = { same: 295, mixed: 295, samecrop: 295, reversible: 395 };
  /** The price of the design as specified.
   *  ⚠️ THE FALLBACK IS A FLOOR, NOT A DEFAULT, AND IT IS NOT PRESENTED AS HER CHOICE.
   *  `state.variation` starts empty, so `PRICE[state.variation]` is `undefined` until she
   *  answers -- which would print "$undefined" in the record and send it to Evelin. The
   *  flow no longer lets an unanswered variation reach the record (Continue is gated, and a
   *  legacy link carrying a fabric but no variation resumes ON the question), so this
   *  should be unreachable. It exists because "should be unreachable" is not a guarantee,
   *  and the base price is the honest thing to show if it ever is.
   *  ⚠️ DO NOT USE THIS TO DECIDE WHAT SHE CHOSE. It answers "what does this cost", and
   *  nothing else may read a variation out of it. */
  function priceNow() { return PRICE[state.variation] || PRICE.same; }

  /* ⚠️ REVERSIBLE CANNOT CARRY INTERNAL STRUCTURE. A shelf bra, molded cups or an
     underwire need an inside to hide in, and a reversible garment has none - they would
     show or bulge on the reverse face. Three of the four `top` answers and the underwire
     question lead there, and the research says bust support is the top concern for this
     customer, so the women most likely to want support are the least able to take
     reversible. Surface it in the flow; do not let it arrive in Evelin's reply email. */
  /* Only INTERNAL structure conflicts. A shelf bra and an underwire need an inside to
     live in; deeper cups and a firmer underband are pattern and seam changes, which a
     reversible garment takes fine. Being precise here matters - over-warning would put
     people off reversible for no reason. */
  /* `sewn` for the same reason as `nosupport`: a reversible garment has no wrong
   side to hide a cup or a bra band on, so promising either contradicts the
   variation screen's own warning. */
  /* ⚠️ `NO_REVERSIBLE` IS GONE, AND REVERSIBLE_BLOCKS BELOW IS WHERE IT WENT. It held
     two of the three blocked answers and buildGroups() had to special-case the third
     (`wire: 'yes'`) beside it, which is the split that let the warning and the record
     disagree about sewn-in padding. One table, keyed by question AND answer, read by the
     warning, the record and the conflict screen. Do not reintroduce a value-only map: the
     conflict screen needs to know WHICH question an answer came from. */
  /** Every selection reversible construction would have to drop, named.
   *  ⚠️ IT USED TO MISS SEWN-IN PADDING, AND THE SUMMARY DID NOT. `sewn` is a PADDING
   *  answer; this function only looked in `state.top` and `state.wire`, while
   *  buildGroups() drops anything in NO_REVERSIBLE across every fit key -- padding
   *  included. So a woman who asked for sewn-in pads and nothing else got no warning at
   *  all and then found the pads quietly gone from her record. The warning and the record
   *  now read the same list, because they read the same function.
   *  ⚠️ NAMES, NOT A BOOLEAN. "Some of your choices conflict" is not something she can
   *  act on; "an underwire and sewn-in pads" is. */
  /* ⚠️ ONE TABLE, AND IT REPLACED THREE HAND-WRITTEN TESTS IN TWO PLACES. This list
     was spelled out once as `if` statements here and once as `NO_REVERSIBLE[one] ||
     (k === 'wire' && one === 'yes')` in buildGroups(), which is the pair whose drift this
     file has already had to fix: the warning missed sewn-in padding while the record
     dropped it, so a customer got no warning and then found her pads quietly gone.
     ⚠️ AND SINCE 2026-09-15 A THIRD PLACE READS IT. The support questions now come
     AFTER the arrangement, so the conflict can arrive from either direction and the
     screen that asks about it has to name the ONE answer she just chose, not the whole
     list. That needs the key and the value, not a sentence. */
  var REVERSIBLE_BLOCKS = [
    { key: 'wire',    value: 'yes',       name: 'an underwire' },
    { key: 'top',     value: 'nosupport', name: 'a built-in shelf for support' },
    { key: 'padding', value: 'sewn',      name: 'sewn-in pads' }
  ];
  /** Is `v` currently among the answers to `key`? Multi-select holds an array. */
  function holdsAnswer(key, v) {
    return (Q[key] && Q[key].multi) ? state[key].indexOf(v) > -1 : state[key] === v;
  }
  /** The block this answer runs into, or null. */
  function reversibleBlockFor(key, v) {
    for (var i = 0; i < REVERSIBLE_BLOCKS.length; i++) {
      if (REVERSIBLE_BLOCKS[i].key === key && REVERSIBLE_BLOCKS[i].value === v) {
        return REVERSIBLE_BLOCKS[i];
      }
    }
    return null;
  }
  function reversibleConflicts() {
    var out = [];
    REVERSIBLE_BLOCKS.forEach(function (b) {
      if (holdsAnswer(b.key, b.value)) out.push(b.name);
    });
    return out;
  }
  /** Everything reversible construction cannot hold, named, whether or not she has asked
   *  for any of it. Said at the moment she chooses reversible -- which since 2026-09-15 is
   *  usually BEFORE she has answered a single support question, so a warning built from her
   *  answers would be silent exactly when it is the only warning she will get. */
  function reversibleRestriction() {
    return 'It cannot carry ' +
           andList(REVERSIBLE_BLOCKS.map(function (b) { return b.name; })) +
           ': there is no inside to hide them in.';
  }
  function wantsStructure() { return reversibleConflicts().length > 0; }
  /* ⚠️ EVELIN WORKS IN US SIZING ONLY -- Nuno, 2026-09-12 -- so there is no system to
     pick and the picker is gone. That does NOT make the system irrelevant: UK, EU and AU
     diverge from US above D, which is exactly the range these unlisted sizes live in, so
     "32E" is a different garment depending on which one she meant.
     ⚠️ SO IT IS STATED, NOT ASSUMED SILENTLY. The record tells her we are reading her
     number as US sizing, which is what gives a UK customer the chance to say otherwise
     before anything is cut. Removing the picker without saying so would have buried the
     assumption rather than removed it. */
  var BUST_SYSTEM = 'US';
  /** Escape for an HTML attribute. Everything else on this screen is built from fixed
   *  tables; this is the only value a customer types, so it is the only one that can
   *  carry a quote and break out of the markup. */
  function escAttr(v) {
    return String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /** "a, b and c" -- the conflict list is read aloud in a sentence, not bulleted. */
  /** The conflict names with their articles stripped, for a BUTTON. The sentence needs
   *  "an underwire"; a button reading "Remove an underwire" does not. Nuno, 2026-09-14. */
  function bareList(a) {
    return andList(a.map(function (x) {
      return x.replace(/^an? /, '').replace(/^a built-in shelf for support$/, 'the shelf');
    }));
  }
  function andList(a) {
    if (a.length < 2) return a[0] || '';
    return a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1];
  }
  /* True only while she has pressed Reversible and not yet answered the conflict. Not in
     `state`: it is a question on screen, not part of the design, and it must never reach
     the URL or the saved record. */
  var pendingReversible = false;

  /* ── THE CONFLICT FROM THE OTHER DIRECTION ────────────────────────────────────────
     ⚠️ THE ORDER REVERSED ON 2026-09-15 AND THE OLD HANDLING ONLY WORKED ONE WAY.
     Until then the support questions came BEFORE the arrangement, so the only way to
     meet this conflict was to press Reversible with an underwire already asked for --
     `pendingReversible` and its two buttons. Codex's progressive-discovery order puts the
     arrangement in Your look and the support questions in Your comfort, which means the
     ordinary path is now the OPPOSITE one: reversible is chosen first, and the conflict
     arrives when she later asks for an underwire, sewn-in cups or a built-in shelf.

     Left alone, that path had exactly the failure the first direction was built to fix:
     buildGroups() would drop her request silently, the card would go on showing it as
     selected, and she would find it missing from the record with nothing having said so.
     Codex, 2026-09-15: choosing one of those "must not silently remove that preference or
     leave it shown as active on an incompatible reversible design. Present the existing
     explicit tradeoff at that point, with the actual affected preferences named."

     ⚠️ IT NAMES THE ONE ANSWER SHE JUST CHOSE, NOT THE WHOLE LIST. The variation
     screen's panel names every conflict because she is deciding about all of them at once;
     here she has pressed one thing and the question is about that thing.

     ⚠️ SCREEN STATE, LIKE pendingReversible. It never reaches `state`, the URL or the
     record, and render() clears it on a step change -- a question she walked away from
     must not be sitting there when she comes back. */
  var pendingComfortConflict = null;
  /** Ask, if this answer cannot be built on the arrangement she has already chosen.
   *  Returns true when the flow must stop and put the question to her.
   *  `prev` is what the answer was before, so dismissing can put it back. */
  function openComfortConflict(key, value, prev) {
    if (state.variation !== 'reversible') return false;
    if (!reversibleBlockFor(key, value)) return false;
    pendingComfortConflict = { key: key, value: value, prev: prev };
    return true;
  }
  /** Dismissed. Put the answer back exactly as it was and stay put.
   *  ⚠️ THE OTHER SELECTED ISSUES SURVIVE, because `prev` is the whole previous answer
   *  -- the entire array on a multi-select -- rather than a flag saying which one to
   *  untick. Codex, 2026-09-15: multi-select conflict handling must preserve other
   *  selected issues. */
  function dismissComfortConflict() {
    var c = pendingComfortConflict;
    pendingComfortConflict = null;
    if (!c) return;
    state[c.key] = c.prev;
    render();
  }
  /** She keeps the reversible suit. The preference is KEPT TOO -- recorded, and shown as
   *  not built, in the record and in what reaches Evelin. See buildGroups()'s
   *  "Recorded, not built" group: this is the branch that makes that group appear. */
  function acceptInactivePreference() {
    var c = pendingComfortConflict;
    pendingComfortConflict = null;
    if (!c) return;
    /* Multi-select never advances on a tick; single-choice does, and this is the advance
       that was held back while the question was open. */
    if (Q[c.key] && Q[c.key].multi) render();
    else goNext();
  }
  /** She keeps the preference, so the suit is not reversible any more.
   *  ⚠️ THE ARRANGEMENT IS UNANSWERED, NOT SWITCHED TO 'same'. Picking one for her is
   *  the defect this file removed from `style` and from `variation` on 2026-09-12 and
   *  2026-09-14: an answer that appears without being chosen. She is taken back to the
   *  question with a line saying why.
   *  ⚠️ `fabricInside` GOES WITH IT AND `fabricBottom` DOES NOT. The inside face exists
   *  only on a reversible suit; a second fabric for the bottom is also what `mixed` uses,
   *  so keeping it means her choice is still there if she picks that instead.
   *  ⚠️ maxStep IS CLAMPED, WHICH IS THE SAME RULE forfeitAfter USES. The screens after
   *  the arrangement were reached on the strength of an answer that no longer stands. */
  function dropReversibleForPreference() {
    var c = pendingComfortConflict;
    pendingComfortConflict = null;
    if (!c) return;
    state.variation = '';
    state.fabricInside = '';
    var i = indexOfScreen('variation');
    if (i < 0) { render(); return; }
    step = i;
    maxStep = i;
    render();
    /* After render(), which clears this line at the top of every screen. */
    needs('Your suit is no longer reversible, so it can carry ' +
          bareList([reversibleBlockFor(c.key, c.value).name]) +
          '. Choose how you want the fabric arranged.');
  }
  /** Draw the question, in whichever layout is on screen. Called from finishStep(), which
   *  is the one place every step renderer converges on -- the deck has `extra`/`wire`
   *  hooks and the option list has none, and a second copy of this markup is how the two
   *  layouts came to say different things the last two times. */
  function paintComfortConflict() {
    if (!pendingComfortConflict || !el.step) return;
    var c = pendingComfortConflict;
    var blk = reversibleBlockFor(c.key, c.value);
    if (!blk) { pendingComfortConflict = null; return; }
    var bare = bareList([blk.name]);
    var box = document.createElement('div');
    box.className = 'mky__modal';
    box.id = 'mky-comfortconflict';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-labelledby', 'mky-comfortconflict-t');
    /* ⚠️ EACH BUTTON SAYS WHAT IT COSTS, AND THEY CARRY THE SAME VISUAL WEIGHT. The
       comment used to claim neither was favoured while the markup made one of them the
       primary pink and the other a secondary outline -- Codex, 2026-09-15, reading the
       screenshot rather than the comment. These are two live options between which the
       flow has no opinion, so both wear `button--secondary` and neither is the pink one.
       ⚠️ THE CONSEQUENCE LEADS THE SUB-LINE. Codex's wording: "Keep it reversible —
       Without underwire. Your request remains noted." What she gives up is said first,
       because that is the part of the choice she cannot see from the label. */
    box.innerHTML =
      '<div class="mky__modalbox">' +
        '<h3 class="mky__modalt" id="mky-comfortconflict-t" tabindex="-1">' +
          'Your suit is reversible, and it cannot carry ' + blk.name + '.</h3>' +
        '<button class="button button--secondary mky__modalbtn" type="button" data-cc="keep-rev">' +
          'Keep it reversible' +
          /* ⚠️ THE BARE NAME HERE, THE FULL ONE IN A SENTENCE. `bareList` strips the
             article so a BUTTON can read "Keep underwire"; this sub-line is a fragment
             rather than prose, so it takes the bare form too. */
          '<span>Without ' + bare + '. Your request remains noted, and the record says it ' +
          'is not being built. Support comes from the cut instead.</span>' +
        '</button>' +
        '<button class="button button--secondary mky__modalbtn" type="button" data-cc="keep-pref">' +
          'Keep ' + bare +
          '<span>Without a reversible suit. You choose how the fabric is arranged ' +
          'again.</span>' +
        '</button>' +
        '<button class="mky__modalback" type="button" data-cc="cancel">' +
          'Cancel, leave my answer as it was</button>' +
      '</div>';
    el.step.appendChild(box);
    Array.prototype.forEach.call(box.querySelectorAll('[data-cc]'), function (b) {
      b.addEventListener('click', function () {
        if (b.dataset.cc === 'keep-rev') acceptInactivePreference();
        else if (b.dataset.cc === 'keep-pref') dropReversibleForPreference();
        else dismissComfortConflict();
      });
    });
    /* Escape and a press on the backdrop are the same as Cancel: a consistent prior
       state, with the answer as she left it. */
    box.addEventListener('click', function (e) {
      if (e.target === box) dismissComfortConflict();
    });
    box.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.keyCode === 27) { e.stopPropagation(); dismissComfortConflict(); }
    });
    var t = box.querySelector('.mky__modalt');
    if (t) { try { t.focus({ preventScroll: true }); } catch (e) { t.focus(); } }
  }

  function setVariation(v) {
    /* ⚠️ A NEW ARRANGEMENT UNASKS THE CROP QUESTION, BECAUSE IT CHANGES WHAT THE ANSWER
       MEANT. `cropFace` is a RELATIONSHIP -- 'primary' or 'second' -- and cropPair()
       resolves the second cloth differently on each arrangement: on `mixed` it is the
       BOTTOM's fabric, on `reversible` it is the REVERSE face. So "second", answered on a
       mixed suit and carried into a reversible one, quietly reassigns the crop to a cloth
       she has not chosen yet, and `screenAnswered('cropFabric')` is `!!state.cropFace`, so
       the screen would have shown that reassignment already selected and let Continue
       through. Codex, 2026-09-17: "Do not ... silently reinterpret jacket choices, or
       present an unanswered choice as complete."
       ⚠️ ONLY WHEN IT ACTUALLY MOVES. Re-pressing the arrangement she is already on is an
       ordinary thing to do on a card deck, and throwing her crop answer away for it would
       be the same silent loss from the other direction.
       ⚠️ A FABRIC REPLACEMENT DOES *NOT* CLEAR IT, AND MUST NOT. "Match my bottom" still
       means the bottom when she swaps which cloth is on the bottom; that is the whole
       reason this field stores a relationship rather than a code. See swapTopAndBottom(),
       which flips the relationship precisely so the CLOTH survives. */
    if (state.variation !== v) state.cropFace = '';
    state.variation = v;
    /* ⚠️ CHOOSING AN ARRANGEMENT IS ITSELF AN EXPLICIT CORRECTION. A legacy three-fabric
       link can also be resolved by going back and answering this question again: whichever
       arrangement she picks, the slots it does not have stop being part of the
       specification (see bottomFabricCode()), so the design is inside the limit and she
       decided it. What must never happen is the excess being cleared by anything that is
       not a press. */
    state.fabricExcess = null;
    if (v === 'same') { state.fabricBottom = ''; state.fabricInside = ''; }
    if (v === 'mixed') state.fabricInside = '';
  }

  /* ⚠️ `samecrop` IS A THREE-PIECE ARRANGEMENT AND NOTHING ELSE CAN TAKE IT. Nuno,
     2026-09-15: a suit in one fabric with the long-sleeve crop cut from another is a
     construction Evelin offers, and the flow could not express it -- the crop only ever
     got to pick between two cloths the TOP and BOTTOM had already claimed. It is still two
     fabrics, so it sits inside Evelin's limit, and it cannot be combined with `mixed` or
     `reversible` because both of those have already spent the second one.
     ⚠️ $295, THE SAME AS `mixed`. Two fabrics, no reversible construction. Nuno, 2026-09-15
     -- read from the decision, never inferred from the table. */
  /* ⚠️ THE VALUES, IN ORDER. THE WORDS ARE variationCopy()'s AND BRANCH ON THE GARMENT.
     This array used to carry the label and the description too, and both were written for
     a two piece: "One fabric throughout / The fabric you chose, inside and out" says
     nothing about the third piece on a three piece, and "Reversible, inside and out" does
     not say WHICH pieces turn -- which is the whole question once there is a crop that
     does not. Codex, 2026-09-17, named the four arrangements in customer words and asked
     that the relationship between the pieces be visible in them.
     ⚠️ ONE SOURCE, TWO LAYOUTS. The pointer list and the phone deck both read
     variations() and both print `v[1]` and `v[2]`; this file has twice shipped copy
     changed in one layout and not the other (the reversible warning, the deck legend), so
     the words live in ONE function and neither layout may restate them. */
  var VARIATIONS = ['same', 'mixed', 'samecrop', 'reversible'];
  /* ⚠️ "LONG-SLEEVE CROP", NEVER "JACKET". Codex's 2026-09-17 brief writes the third
     piece as "jacket" throughout; Nuno's ruling the same day is that the store has one
     name for that garment and it is the long-sleeve crop. Every string below, every crop
     question, and the record and the cutting line all say the same thing. Do not
     reintroduce a second name for one garment. */
  var CROP_WORD = 'long-sleeve crop';
  /** The name and the meaning of one arrangement, in the words that fit THIS garment.
   *
   *  ⚠️ THREE BRANCHES, NOT TWO, AND THE THIRD IS THE ONE THAT WAS MISSING. `isSingle()`
   *  separates a one piece from separates, which is what `variations()` filters on -- but
   *  a two piece and a three piece are both separates and they do not describe the same
   *  suit. Only a three piece has a crop to place, so only a three piece names it.
   *  ⚠️ `reversible`'s DESCRIPTION IS OVERWRITTEN BY BOTH LAYOUTS, which append the
   *  restriction sentence and any comfort conflict. It is still written correctly here:
   *  the day one of those layouts stops overriding it, the fallback must not be wrong. */
  function variationCopy(v) {
    var three = state.style === 'three';
    var one = isSingle();
    if (v === 'same') {
      return { label: 'One fabric throughout',
               desc: three
                 ? 'The same fabric for your top, bottom, and ' + CROP_WORD + '.'
                 : one ? 'The fabric you chose, inside and out.'
                       : 'The same fabric for your top and bottom.' };
    }
    if (v === 'mixed') {
      return { label: 'Different top and bottom',
               desc: three
                 ? 'Two fabrics. Pick the second next, then say which one your ' +
                   CROP_WORD + ' matches.'
                 : 'Two fabrics. Pick the second next.' };
    }
    if (v === 'samecrop') {
      return { label: 'Matching top and bottom, different crop',
               desc: 'Your top and bottom in the fabric you chose, your ' + CROP_WORD +
                     ' in another. Two fabrics, not reversible.' };
    }
    return { label: one ? 'Reversible, inside and out' : 'Reversible top and bottom',
             desc: three
               ? 'A second face on your top and bottom. Four ways to wear them. Your ' +
                 CROP_WORD + ' takes one of the two fabrics and does not turn.'
               : one ? 'A second face. Two ways to wear it.'
                     : 'A second face on both pieces. Four ways to wear it.' };
  }
  var step = 0;
  /* ⚠️ THE SHAPES A LINK CARRIED, KEPT APART FROM THE STATE. A collection page sends
     her here with `topShape=halter` and nothing else, so the flow starts at the body
     questions -- and the coverage answer used to overwrite both shapes from its
     table, which threw the halter away before she ever reached the Shapes step. The
     coverage table is a guess at a shape; a link is a choice of one, and a choice
     outranks a guess. Only the KEYS the link carried are recorded, never the values,
     because the state holds those and holding them twice is how they drift. */
  var linked = { topShape: false, bottomShape: false };
  /* ⚠️ A `picked` FLAG LIVED HERE AND IS GONE, AND ITS DELETION IS THE POINT. It was
     added earlier on 2026-09-15 to mark a shape she had pressed, beside `linked` for one
     a collection link carried, because `state.topShape` then held EITHER her choice or the
     coverage guess and could not tell them apart. The later correction made that field
     hold only an explicit choice, so `shapeIsExplicit` became `!!state[attr]` -- and
     nothing read `picked` again. It was written in three places and read in none: a
     mechanism installed whose output nothing consumes, which is the shape that reads as a
     live safeguard to the next person and is not one. Found by the defensive-coding pass
     at the end of that session, not by a test, because dead state fails nothing.
     `linked` STAYS: the shapes hint reads it to say "The shape you were browsing" rather
     than "A starting point from your answers", which is a real distinction between a
     collection link's choice and her own. */
  /** Did she choose this shape herself, by link or by tile?
   *  ⚠️ `state[attr]` HOLDS ONLY AN EXPLICIT CHOICE SINCE 2026-09-15, so this is simply
   *  "is there one". It used to be a pair of flags beside a state field that ALSO held the
   *  coverage guess, and that is precisely what Codex caught in the mobile screenshot:
   *  arriving at the shapes screen showed a pink card, a checkmark and "Shape: Classic"
   *  for a shape nobody had chosen, while the desktop layout said "An example, until you
   *  choose below". A field that holds two different kinds of thing cannot be rendered
   *  correctly from itself. The guess is DERIVED now -- see suggestedShape() -- and the
   *  field means what its name says. The flags are kept because they still record WHICH
   *  explicit route the shape arrived by, which `keepExplicitShape` and the shapes hint
   *  both read. */
  function shapeIsExplicit(attr) { return !!state[attr]; }
  /** The shape this garment starts on when she has not chosen one.
   *  ⚠️ DERIVED ON EVERY READ, NEVER STORED. Coverage used to WRITE this into
   *  `state.topShape`, which is how a guess came to be indistinguishable from a decision.
   *  Reading it live also means changing the coverage answer moves the suggestion without
   *  touching anything she has chosen -- which is the rule Codex's brief already asked for
   *  ("coverage can seed an unchosen shape, but cannot silently overwrite an explicit
   *  one"), now true by construction rather than by a guard. */
  function suggestedShape(attr) {
    return attr === 'bottomShape'
      ? snapShape(BOTTOMS, COVER_BOT[state.coverage] || 'brief')
      : snapShape(topsFor(), COVER_TOP[state.coverage] || 'triangle');
  }
  /** The shape to DRAW: hers if she has chosen one, the suggestion otherwise.
   *  Every preview, tile, photograph, record row and payload field goes through this, so
   *  none of them can disagree about which garment is on screen. */
  function effectiveShape(attr) {
    return state[attr] || suggestedShape(attr);
  }
  /** Accept the suggestion for any component she has not chosen herself.
   *  ⚠️ CONTINUE IS AN EXPLICIT ACCEPTANCE, AND THAT IS WHAT MAKES THE SAVED LINK HONEST.
   *  Codex, 2026-09-15: "Tapping it may accept and advance; Continue may explicitly accept
   *  the suggestion." Pressing Continue on the shapes screen is a decision, so it writes
   *  the shape -- which means any design that has got PAST this screen carries chosen
   *  shapes in its URL, and a link written while still standing on it carries none. The
   *  suggested/selected distinction therefore survives a round trip instead of being an
   *  in-session nicety. */
  function acceptSuggestedShapes() {
    shapeParts().forEach(function (part) {
      if (!state[part.attr]) state[part.attr] = suggestedShape(part.attr);
    });
  }
  /* A shape her style can no longer draw, named so the shapes screen can say what happened
     rather than showing a replacement as though she had picked it. Screen state: it never
     reaches `state`, the URL or the record. */
  var shapeDropped = { topShape: '', bottomShape: '' };

  /* ══════════════════════════════════════════════════════════════════════════════════
     THE ROUTE. ONE ORDERED LIST OF NAMED SCREENS, AND IT IS THE ONLY ORDER IN THE FILE.

     ⚠️ THIS REPLACED FIVE NUMERIC OFFSETS, AND THE OFFSETS ARE THE REASON IT HAD TO.
     Until 2026-09-15 the order lived in `BODY_STEPS`, `Q_STEPS`, `FABRIC_START`,
     `FABRIC_PICK` and `VARIATION`, each derived from the LENGTH of a question list, and
     roughly fifty sites did arithmetic on them -- `step - Q_STEPS === 3` was the sizes
     screen, `step > VARIATION` was an extra fabric, `chapterOf` returned `q2 + 1`. Moving
     one question therefore meant re-deriving all of it, and nothing in the file said out
     loud what screen 12 was. Codex's progressive-discovery brief (2026-09-15) reorders
     nineteen screens; doing that on offsets is not a reorder, it is a rewrite with no way
     to check itself.

     Every screen now has a STABLE IDENTITY (`id`) that does not change when the order
     does, an APPLICABILITY predicate (`applies`) saying which garments ask it, and a
     chapter. `step` is still an index -- into this array -- so `maxStep`, the dots, the
     rail and the backward/forward jumps keep working exactly as they did.

     ⚠️ THE ORDER IS THE PRODUCT DECISION AND IT IS NOT ARBITRARY. Codex, 2026-09-15:
     "Let the customer develop a suit she wants before asking about her body." The suit,
     its cloth and its cut come first; what goes wrong with tops comes second; her own
     proportions and her sizes come last. The body questions used to be screens one to
     four, so the first thing the flow ever asked a stranger was how tall she is.

     ⚠️ ADDING A SCREEN IS ADDING A ROW. Nothing else in this file may hard-code a
     position; ask `screenId()`, `at()`, `isAtOrAfter()` or `indexOfScreen()`. */
  var LOOK = 0, COMFORT = 1, FIT = 2, DESIGN = 3;
  var ROUTE = [
    /* ── Your look: the suit, its cloth, its cut, its arrangement ─────────────────── */
    { id: 'style',        ch: LOOK },
    { id: 'color',        ch: LOOK, pref: true },
    { id: 'intensity',    ch: LOOK, pref: true },
    { id: 'density',      ch: LOOK, pref: true },
    { id: 'ground',       ch: LOOK, pref: true },
    { id: 'fabric',       ch: LOOK },
    { id: 'coverage',     ch: LOOK, applies: function () { return coverageApplies(); } },
    { id: 'shapes',       ch: LOOK, applies: function () { return shapesApply(); } },
    { id: 'variation',    ch: LOOK },
    /* Only ever applicable to a resumed legacy link; a design built in this flow can no
       longer reach three fabrics, so this screen never appears for a new customer. */
    { id: 'fabricFix',    ch: LOOK, applies: function () { return !!state.fabricExcess; } },
    { id: 'fabricBottom', ch: LOOK, applies: function () { return needsSlot('bottom'); } },
    { id: 'fabricInside', ch: LOOK, applies: function () { return needsSlot('inside'); } },
    /* Last of the cloth decisions, because it is a choice BETWEEN the two fabrics above
       and cannot be asked before both exist. Skipped whenever there is only one. */
    { id: 'fabricCrop',   ch: LOOK, applies: function () { return needsSlot('crop'); } },
    { id: 'cropFabric',   ch: LOOK, applies: function () { return cropChoiceApplies(); } },
    /* ── Your comfort: what a top gets wrong, and what holds it up ─────────────────── */
    { id: 'top',          ch: COMFORT },
    { id: 'wire',         ch: COMFORT },
    { id: 'padding',      ch: COMFORT },
    { id: 'seat',         ch: COMFORT },
    /* ── Your fit: her frame, her proportions, her sizes ───────────────────────────── */
    { id: 'height',       ch: FIT },
    /* ⚠️ ONE PIECES AND MONOKINIS ONLY, AND THAT IS A CONSTRUCTION FACT, NOT A GUESS.
       The question is about a garment that runs from shoulder to leg in one piece and
       therefore has a length between the two that can be wrong. A bikini top and a bikini
       bottom are not joined to each other, so there is nothing between them to be short;
       a three piece's long-sleeve crop is a separate garment worn over them and does not
       create the connection either. Codex, 2026-09-15.
       ⚠️ SKIPPED IS NOT ANSWERED, AND IT IS NOT `standard`. See torsoApplies()'s callers:
       the build, the record and the payload all have to say "not applicable to this
       garment" rather than quietly cutting a standard length. */
    { id: 'torso',        ch: FIT, applies: function () { return isSingle(); } },
    { id: 'volume',       ch: FIT },
    { id: 'waist',        ch: FIT },
    { id: 'tummy',        ch: FIT },
    { id: 'sizes',        ch: FIT },
    /* ── Your design ──────────────────────────────────────────────────────────────── */
    { id: 'review',       ch: DESIGN }
  ];
  /* Built once. `id -> index`, so nothing has to scan the route to ask where a screen is. */
  var ROUTE_IX = (function () {
    var m = {};
    ROUTE.forEach(function (r, i) { m[r.id] = i; });
    return m;
  })();
  /** The id of the screen at `s`, or '' past the end of the route. */
  function screenAt(s) { return (ROUTE[s] || {}).id || ''; }
  /** The id of the screen she is on. */
  function screenId() { return screenAt(step); }
  /** Where `id` sits in the route, or -1 if it is not a screen. */
  function indexOfScreen(id) { return id in ROUTE_IX ? ROUTE_IX[id] : -1; }
  /** Is she on `id` right now? */
  function at(id) { return screenId() === id; }
  /** Is she at or past `id`? For the handful of rules that are about how FAR she has got
   *  rather than which screen she is on -- the photograph window and the still-open
   *  count. Everything else asks `at()`. */
  function isAtOrAfter(id) { var i = indexOfScreen(id); return i > -1 && step >= i; }
  function isAfter(id) { var i = indexOfScreen(id); return i > -1 && step > i; }
  /** Does the chosen arrangement need the `bottom` / `inside` fabric slot? */
  function needsSlot(which) { return extraPicks().indexOf(which) > -1; }
  /** Is the torso question asked of this garment at all? */
  function torsoApplies() { return isSingle(); }
  /** The cloth-preference question for a screen id, or null. */
  var FABRIC_Q_BY_KEY = (function () {
    var m = {};
    FABRIC_Q.forEach(function (q) { m[q.key] = q; });
    return m;
  })();
  function prefQFor(id) { return FABRIC_Q_BY_KEY[id] || null; }

  /** Which extra fabrics this variation needs. Empty for a plain suit, so the flow does
   *  not charge everyone the steps a minority want. */
  /* ⚠️ A ONE PIECE HAS NO SEPARATE BOTTOM. Asking for a second fabric "for the bottom"
     on a one piece produced a screen headed "Fabric for the bottom" over a one-piece
     preview, a swap offer between two halves that do not exist, and a record naming two
     fabrics on a single garment. Reversible on a one piece needs the inside and nothing
     else; mixed is not offered at all (see VARIATIONS below). */
  /* ⚠️ TWO FABRICS IS THE CONSTRUCTION LIMIT, AND REVERSIBLE USED TO BREAK IT. Evelin,
     2026-09-15: a suit may be cut from at most TWO fabrics. Reversible on a two piece
     asked for `['bottom', 'inside']` on top of the primary, so a customer could specify
     THREE -- a top, a different bottom, and a reverse face -- and every one of them
     reached the record, the saved link and Evelin's cutting line. The limit is about what
     the workroom buys and cuts; it is not about how many ways the finished suit can be
     worn. A reversible two piece still gives FOUR wearing combinations (A/A, A/B, B/A,
     B/B) out of exactly two fabrics, because each piece can be turned independently.
     ⚠️ `mixed` KEEPS ITS SECOND BOTTOM FABRIC. That arrangement IS "two fabrics, one per
     piece", which is inside the limit; it is only reversible that added a third. */
  function extraPicks() {
    var one = isSingle();
    if (state.variation === 'reversible') return ['inside'];
    if (state.variation === 'mixed') return one ? [] : ['bottom'];
    /* The crop IS the second fabric here, rather than one of two the suit already has. */
    if (state.variation === 'samecrop') return state.style === 'three' ? ['crop'] : [];
    return [];
  }

  /** The second fabric this design ACTUALLY carries on the bottom, or '' when the
   *  arrangement has no such slot.
   *
   *  ⚠️ READ THIS, NEVER `state.fabricBottom`, AND THAT IS WHAT ENFORCES THE TWO-FABRIC
   *  LIMIT. Hiding the picker would have been cosmetic: `state.fabricBottom` survives a
   *  switch from "different top and bottom" to "reversible" on purpose, so that going back
   *  restores her choice -- and every reader that touched the field directly would then
   *  have drawn, printed, serialized and SUBMITTED a third fabric on a reversible suit,
   *  with no picker anywhere in sight. Same rule, and the same reason, as `torsoApplies()`
   *  gating the torso answer: the answer is kept, the SPECIFICATION drops it.
   *  ⚠️ SO THE CHECKLIST FOR ANY NEW READER IS: the preview, the review line, the saved
   *  link, the maker payload and the looks panel all go through here. */
  function bottomFabricCode() {
    return needsSlot('bottom') ? state.fabricBottom : '';
  }
  /** The reverse face this design ACTUALLY carries, or '' when the arrangement has none.
   *  ⚠️ SAME RULE AS bottomFabricCode(), AND IT CLOSES THE SAME HOLE ON THE OTHER SLOT.
   *  `setVariation` clears `fabricInside` when she picks `same` or `mixed`, so this was
   *  invisible through the UI -- but a saved link reaches `state` without passing
   *  `setVariation`, and `variation=same&fabricInside=5234` would have printed a reverse
   *  face on the record of a suit that has none. */
  function insideFabricCode() {
    return needsSlot('inside') ? state.fabricInside : '';
  }
  /** The crop's OWN fabric, on the one arrangement where the crop has one. */
  function cropSlotCode() {
    return needsSlot('crop') ? state.fabricCrop : '';
  }
  /** Every fabric this design commits the workroom to, de-duplicated, in A/B order.
   *  Never more than two once a design is valid; a legacy link can hold three, which is
   *  what `fabricExcess()` exists to catch. */
  /** The three-piece crop's cloth, in words, or '' when this garment has no crop.
   *
   *  ⚠️ PRESERVED, NOT INVENTED, AND IT WAS ALREADY TRUE ON SCREEN. The stage draws the
   *  crop with `<use href="#shape-top-tankini">` INSIDE `el.top` -- the group filled with
   *  fabric A -- on every arrangement, and the photographic render composes the same
   *  printed crop beside the pair. So "the crop is cut from fabric A" is the assignment
   *  this flow has always shown; what it never did was SAY so. A three piece therefore
   *  needs no third fabric and never did.
   *  ⚠️ WHAT IS *NOT* ESTABLISHED, AND IS NOT GUESSED HERE: whether the crop on a
   *  REVERSIBLE three piece is itself cut reversible (A outside, B reverse) or is a single
   *  faced garment in A. Nothing in the flow, the register or the four photographed
   *  three pieces answers it, so this line states the fabric -- which IS established --
   *  and claims nothing about a second face on the crop. Raised with Evelin 2026-09-15.
   *  Do not resolve it by writing a rule here. */
  /** The two fabrics a three piece's crop can be cut from, or null when there is only
   *  one to choose between.
   *
   *  ⚠️ THE SECOND FABRIC IS A DIFFERENT PIECE ON EACH ARRANGEMENT, and that is why this
   *  is a function rather than a field. On `mixed` the two cloths are the TOP's and the
   *  BOTTOM's; on `reversible` they are the OUTSIDE and the REVERSE of both pieces. Same
   *  two fabrics, two entirely different sentences, and the question is unanswerable
   *  without the right one. */
  function cropPair() {
    /* ⚠️ NULL ON `samecrop`, WHICH IS WHAT SKIPS THE CHOICE SCREEN. There is nothing to
       choose between: the crop has a fabric of its own and the suit has the other. */
    if (state.style !== 'three' || needsSlot('crop')) return null;
    var a = state.fabric;
    var b = state.variation === 'reversible' ? state.fabricInside : bottomFabricCode();
    if (!a || !b || a === b) return null;
    return { a: a, b: b, reversible: state.variation === 'reversible' };
  }
  /** Does this garment have a crop-fabric choice to make?
   *  ⚠️ FALSE ON A ONE-FABRIC SUIT, AND THE SCREEN IS SKIPPED RATHER THAN DEFAULTED.
   *  With one cloth there is nothing to choose and asking would be a confirmation dressed
   *  as a question -- the same reason coverage is skipped on a one piece. */
  function cropChoiceApplies() { return !!cropPair(); }
  /** The fabric the crop is actually cut from. The primary when she has not chosen yet,
   *  which is only ever reachable before the question (Continue gates it) -- never in a
   *  record. */
  function cropFabricCode() {
    if (state.style !== 'three') return '';
    if (needsSlot('crop')) return state.fabricCrop;
    var pair = cropPair();
    if (!pair) return state.fabric;
    return state.cropFace === 'second' ? pair.b : pair.a;
  }
  /** The crop question, once, for the option list and for the phone deck.
   *  ⚠️ ONE DESCRIPTION, TWO LAYOUTS. The variation screen's warning and the record drifted
   *  apart when they were written twice; this file has fixed that twice. The deck spec and
   *  the pointer branch both read this.
   *  ⚠️ IT NEVER OFFERS "BOTH". The crop does not turn, so there is no arrangement in
   *  which it carries two fabrics, and an option saying otherwise would promise
   *  construction Evelin does not cut. */
  function cropOptions() {
    var pair = cropPair();
    if (!pair) return [];
    return [
      { v: 'primary', code: pair.a, face: cropFaceLabel('primary') },
      { v: 'second',  code: pair.b, face: cropFaceLabel('second') }
    ].map(function (o) {
      var x = slotFace(o.code) || { name: 'Not chosen' };
      /* ⚠️ TWO ARRANGEMENTS, TWO QUESTIONS, AND THE BUTTON HAS TO ANSWER THE ONE ASKED.
         Codex, 2026-09-17. On `mixed` the two cloths ARE the top's and the bottom's, so
         the choice is a RELATIONSHIP -- "Match my top" -- and the fabric name is the
         supporting detail. On `reversible` both pieces carry both cloths, so there is no
         piece to match and the choice is a FABRIC: the name leads and the face it belongs
         to is the detail. Printing the fabric name as the button on `mixed` was the old
         behaviour and it made her pick a cloth when the decision in front of her was
         which garment the crop goes with. */
      return { v: o.v, code: o.code, face: o.face, name: x.name,
               label: pair.reversible
                 ? x.name
                 : (o.v === 'second' ? 'Match my bottom' : 'Match my top'),
               sub: pair.reversible ? o.face : x.name,
               pending: !!x.pending, art: x };
    });
  }
  /** The crop question, in the words the arrangement makes answerable.
   *  ⚠️ A FUNCTION, NOT A CONSTANT, AND BOTH LAYOUTS READ IT. It was one fixed string for
   *  both arrangements; see the branch note in cropOptions(). */
  function cropQ() {
    var pair = cropPair();
    return pair && pair.reversible
      ? 'Which fabric for your ' + CROP_WORD + '?'
      : 'Which should your ' + CROP_WORD + ' match?';
  }
  function cropHint() {
    var pair = cropPair();
    /* ⚠️ THE REVERSIBLE LINE IS CODEX'S, VERBATIM IN MEANING: the customer has just been
       told her suit turns, and the next screen asks her to fix one piece in one cloth.
       Saying which pieces turn and which does not is the whole reason the question is
       answerable. */
    return pair && pair.reversible
      ? 'Your top and bottom are reversible. Your ' + CROP_WORD + ' is not.'
      : 'Your top and bottom are in different fabrics. Your ' + CROP_WORD +
        ' is worn over them and is cut from one of the two.';
  }
  /** Which piece the crop's cloth is shared with, in her words.
   *  ⚠️ THE ARRANGEMENT DECIDES THE WORDS, NOT THE SLOT. On `mixed` the two cloths are the
   *  TOP's and the BOTTOM's; on `reversible` they are the OUTSIDE and the REVERSE of both
   *  pieces. Same two fabrics, two entirely different sentences, and the question is
   *  unanswerable with the wrong one. */
  function cropFaceLabel(which) {
    var pair = cropPair();
    if (!pair) return '';
    if (pair.reversible) {
      return which === 'second' ? 'the reverse fabric' : 'the outside fabric';
    }
    return which === 'second' ? "the bottom's fabric" : "the top's fabric";
  }
  /** The crop's cloth, in words, or '' when this garment has no crop.
   *
   *  ⚠️ THE CROP IS NOT REVERSIBLE, AND EVELIN SAID SO ON 2026-09-15. Only the top and
   *  the bottom turn. So a reversible three piece is cut from two fabrics, the crop takes
   *  ONE of them, and which one is the customer's decision rather than ours -- which is
   *  what `cropFace` records and what this sentence reports. The earlier version of this
   *  function asserted "the same fabric as the top", which was the renderer's behaviour
   *  described as a rule; it was true only because the crop had been drawn inside the
   *  top's fill group since the day the three piece shipped.
   *  ⚠️ IT STILL INTRODUCES NO THIRD FABRIC. The choice is between the two she already
   *  has; `fabricSlotCodes()` is unaffected. */
  function cropAssignment() {
    if (state.style !== 'three') return '';
    var f = slotFace(cropFabricCode());
    if (!f) return '';
    var pair = cropPair();
    return 'Long-sleeve crop: ' + f.name +
           (needsSlot('crop')
             ? ', cut in its own fabric'
             : pair
               ? ', ' + cropFaceLabel(state.cropFace === 'second' ? 'second' : 'primary')
               : ', the same fabric as the rest of the suit') +
           '. Not reversible';
  }
  function fabricSlotCodes() {
    var out = [];
    [state.fabric, bottomFabricCode(), insideFabricCode(),
     cropSlotCode()].forEach(function (c) {
      if (c && out.indexOf(c) < 0) out.push(c);
    });
    return out;
  }

  /** The variations this garment can actually take. */
  function variations() {
    return VARIATIONS.filter(function (v) {
      if (v === 'mixed' && isSingle()) return false;
      /* No crop, no crop arrangement. It is the only option in this list that depends on
         the garment having a third PIECE rather than on how many faces it has. */
      if (v === 'samecrop' && state.style !== 'three') return false;
      return true;
    /* ⚠️ THE ROW SHAPE IS UNCHANGED -- [value, label, description] -- BECAUSE BOTH
       LAYOUTS AND THE DECK SPEC INDEX INTO IT. What changed is where the words come
       from; see variationCopy(). Returning objects here would have been tidier and would
       have touched five call sites for no gain. */
    }).map(function (v) {
      var c = variationCopy(v);
      return [v, c.label, c.desc];
    });
  }

  /* Switching to a one piece after choosing "different top and bottom" left the state on
     a variation the garment cannot have. Coerce rather than trust the screen to hide it. */
  function normalizeVariation() {
    if (isSingle() && state.variation === 'mixed') {
      state.variation = 'same';
      state.fabricBottom = '';
    }
    /* Same coercion, same reason: switching away from a three piece leaves the state on an
       arrangement the garment cannot have, and the screen hiding the option is not enough
       -- a saved link reaches `state` without passing the screen. */
    if (state.style !== 'three' && state.variation === 'samecrop') {
      state.variation = 'same';
      state.fabricCrop = '';
    }
    /* ⚠️ AND THE CROP'S RELATIONSHIP GOES WITH THE CROP. A garment with no third piece
       has no crop question; `cropFabricCode()` and `cropAssignment()` both return '' on
       one, so this was invisible through the UI -- but the field travels in the saved
       link, and switching back to a three piece would have restored an answer to a
       question this design has not been asked, already selected. Same rule, same reason,
       as the `samecrop` coercion above it. */
    if (state.style !== 'three') state.cropFace = '';
    if (isSingle()) state.fabricBottom = '';
  }
  /** The coverage answer in her words. It is now HALF the bottom spec -- the tiles
   *  carry construction and this carries how much is covered -- so it has to travel
   *  with the design rather than only nudging a default. */
  function coverageLabel() {
    /* ⚠️ A GARMENT THAT IS NEVER ASKED THIS QUESTION HAS NO ANSWER TO IT, and the same
       rule the torso answer follows applies here for the same reason. A one piece and a
       monokini skip the coverage screen entirely; a customer who answered it as a two
       piece and then switched still has the answer in `state` -- deliberately, so
       switching back restores it -- and printing it would put "Coverage: As covered as it
       comes" on the record and in Evelin's line for a question that customer was never
       shown. Found 2026-09-15 while probing the reorder: `style` is now one dot away from
       `coverage`, so this went from a corner to an ordinary move.
       ⚠️ THE SEEDED SHAPE IS NOT UNDONE BY THIS AND MUST NOT BE. `COVER_TOP`/`COVER_BOT`
       read `state.coverage` directly and already wrote a shape; that shape is now the
       design's, hers to change on the shapes screen. What is dropped is the CLAIM that she
       chose a coverage level. */
    if (!coverageApplies()) return '';
    var o = (Q.coverage.opts || []).filter(function (x) { return x[0] === state.coverage; })[0];
    return o ? o[1] : '';
  }

  /** The record screen. Fixed now: the extra fabric screens are ROUTE rows with
   *  applicability predicates rather than a tail appended to a number, so the route
   *  length no longer moves when she picks reversible. What still moves is the COUNT of
   *  screens she will see, which is stepCount() -- see the note there. */
  function lastStep() { return ROUTE.length - 1; }

  /** Has the CURRENT step been answered? Steps with a real default (shapes, sizes,
   *  which are pre-set from her coverage answer and always show a selection) are
   *  answered by construction; the ones that start empty are not. */
  /** Show or clear the blocked-step message. */
  function needs(msg) {
    var n = document.getElementById('mky-needs');
    if (!n) return;
    n.textContent = msg || '';
    n.classList.toggle('mky__needs--on', !!msg);
  }

  /** Why this step is not finished, in her words. Empty string means it is. */
  function whyBlocked() {
    if (stepAnswered()) return '';
    var id = screenId();
    if (Q[id] && Q[id].multi) {
      /* ⚠️ IT NAMES BOTH WAYS OUT, AND SINCE 2026-09-15 THERE ARE TWO. "I'm not sure yet"
         is an answer, not a blank: a customer who has never worked out what her tops get
         wrong must not have to claim they fit her fine to get past this screen. */
      return 'Choose at least one, or "Nothing. Tops fit me fine", or "I\'m not sure yet".';
    }
    if (id === 'sizes') {
      return (state.bustUnlisted
      /* She has opened the unlisted route, so the block is the empty box, not the chips. */
      ? 'Tell us the band and cup you wear, so Evelin can review it.'
      : 'Choose a band and a cup, tell us yours is not listed, or tell us you are not sure.');
    }
    if (id === 'fabric') return 'Choose a fabric to carry on.';
    if (id === 'fabricFix') return 'This saved design names three fabrics. Choose which ' +
      'two to keep before carrying on.';
    if (id === 'fabricBottom') return 'Choose the fabric for the bottom to carry on.';
    if (id === 'fabricInside') return 'Choose your reverse fabric to carry on.';
    if (id === 'fabricCrop') return 'Choose the fabric for the long-sleeve crop to ' +
      'carry on.';
    /* ⚠️ IT ASKS THE SAME QUESTION THE SCREEN DOES. Two arrangements put two different
       questions here (see cropQ()), and a blocked message describing the other one is
       how a customer is told to answer something that is not on screen. */
    if (id === 'cropFabric') {
      var cpair = cropPair();
      return cpair && cpair.reversible
        ? 'Choose the fabric for your ' + CROP_WORD + ' to carry on.'
        : 'Choose which piece your ' + CROP_WORD + ' matches to carry on.';
    }
    return 'Choose an answer to carry on.';
  }

  /** Throw the whole design away and go back to question one.
   *  ⚠️ IT CLEARS EVERY QUESTION KEY, NOT A HAND-WRITTEN LIST. The old version named
   *  torso, top, wire, seat and coverage -- and silently left height, volume, waist,
   *  tummy and padding behind, so "Start again" walked her back to question one with her
   *  old body answers still ticked. A list of fields is a list that goes stale every time
   *  a question is added; QKEYS cannot.
   *  ⚠️ `style` RESETS TO EMPTY, NOT TO 'two'. The kind screen has no default any more. */
  function startAgain() {
    step = 0;
    maxStep = 0;
    QKEYS.forEach(function (k) { state[k] = Q[k] && Q[k].multi ? [] : ''; });
    state.coverage = ''; state.style = '';
    state.topShape = ''; state.bottomShape = ''; state.fabric = '';
    state.topSize = 'M'; state.bottomSize = 'M';
    state.band = ''; state.cup = ''; state.bustSkipped = false;
    state.bustUnlisted = false; state.bustLabel = '';
    sizesTouched = false;
    /* Start again forgets where she came in from too, or the collection's shape would
       keep outranking her answers on a suit she has already thrown away. */
    linked.topShape = false; linked.bottomShape = false;
    shapeDropped.topShape = ''; shapeDropped.bottomShape = '';
    /* A new design gets the transitions again: they introduce passages she is about to
       walk for the first time. */
    shownTransition = {};
    state.prefs = {}; state.showAll = false;
    state.solidNotes = {}; state.browse = ''; state.solidOpen = '';
    state.prefsPage = 0; state.prefsRefreshed = false;
    state.fabricGone = ''; state.fabricGoneSlots = {};
    /* A new suit is a new draw: keep the seed for the SESSION, not for the design. */

    state.variation = ''; state.fabricBottom = ''; state.fabricInside = '';
    state.fabricExcess = null; state.cropFace = ''; state.fabricCrop = '';
    /* Resets the CHOICE too, and collapses the disclosure with it. Leaving it selected
       would carry a decision about a suit that no longer exists onto the next one, with
       the email box already open under it. */
    state.intent = '';
    /* ⚠️ THE NOTES GO WITH THE DESIGN THEY WERE WRITTEN ABOUT. See notesReset(): this
       also mints a fresh `did`, so the next design cannot read the last one's notes even
       if the old row survives in storage. */
    notesReset();
    closeAsk();
    var sendBox0 = document.getElementById('mky-send');
    if (sendBox0) sendBox0.classList.remove('mky__send--on');
    var email0 = document.getElementById('mky-save-email');
    if (email0) email0.removeAttribute('required');
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-mky-intent]'),
      function (b) { b.setAttribute('aria-pressed', 'false'); }
    );
  }

  var FABRIC_PAGE = 20;
  var fabricShown = FABRIC_PAGE;
  /* ⚠️ THE TWO-TAP ARMING IS GONE, AND SO IS THE BUTTON IT PROTECTED. "Start again"
     used to occupy the primary slot on the record screen -- the same bottom-right slot
     Continue occupies on all nineteen screens before it, in the same pink -- next to a
     Save It that was ALSO pink. Nuno, 2026-09-12: "the user will very easily hit the
     wrong button... to add insult to injury the START AGAIN button is in the EXACT SAME
     PLACE the proceed button usually is." Arming was a guard bolted onto a button that
     should never have been there. It is now the quiet `.mky__reset` link every other
     screen carries, which has always confirmed before destroying anything. */

  /** Has the screen at `s` been answered? One switch on the screen's NAME, which is what
   *  replaced eight offset comparisons that had to be re-derived whenever a question
   *  moved. */
  function screenAnswered(s) {
    var id = screenAt(s);
    if (!id || id === 'review') return true;        // "Start again" is always live
    if (Q[id]) return Q[id].multi ? state[id].length > 0 : !!state[id];
    if (id === 'style') return !!state.style;
    if (id === 'shapes') return true;               // shapes carry defaults
    /* Sizes: the BOTTOM carries a default, the bust does not. `topSize` also defaults
       to M, so a state test would always pass and the question would be skippable in
       silence -- which is the whole failure this replaces. She must either give a band
       and a cup, or say she does not know. */
    /* ⚠️ AN UNLISTED SIZE ONLY COUNTS ONCE SHE HAS WRITTEN IT DOWN. Pressing the button
       and leaving the box empty tells Evelin nothing at all -- less than "I'm not sure",
       which at least says which conversation to have. */
    if (id === 'sizes') return Boolean((state.band && state.cup) || state.bustSkipped ||
                                       (state.bustUnlisted && state.bustLabel.trim()));
    if (prefQFor(id)) {
      /* ⚠️ `!== undefined`, NOT TRUTHINESS. "Either" is null and is a real answer; a
         truthiness test would leave Continue dead on the one option that says
         "I don't mind". */
      return state.prefs[id] !== undefined;
    }
    if (id === 'fabric') return !!state.fabric;     // no fabric, no suit
    if (id === 'variation') return !!state.variation;
    /* Answered exactly when the excess is gone, which only a deliberate press does. */
    if (id === 'fabricFix') return !state.fabricExcess;
    if (id === 'fabricBottom') return !!state.fabricBottom;
    if (id === 'fabricInside') return !!state.fabricInside;
    if (id === 'fabricCrop') return !!state.fabricCrop;
    if (id === 'cropFabric') return !!state.cropFace;
    return true;
  }
  function stepAnswered() { return screenAnswered(step); }

  /** What a SAVED LINK has to carry for a screen to count as done.
   *  ⚠️ IT DIFFERS FROM screenAnswered ON EXACTLY ONE SCREEN, AND THE DIFFERENCE IS OLD.
   *  Shapes are answered by construction -- Continue accepts the suggestion, so the screen
   *  can always be left -- which is right for Continue and wrong for a link: a link that
   *  never named a shape has not chosen one, and resuming past the question would present
   *  our guess as her decision. Since 2026-09-15 `state.topShape` holds ONLY an explicit
   *  choice, so this test says exactly what it means. */
  function screenResumed(s) {
    var id = screenAt(s);
    if (id === 'shapes') {
      return !!state.topShape && (!isSplit() || !!state.bottomShape);
    }
    return screenAnswered(s);
  }

  function chapterOf(s) { return (ROUTE[s] || ROUTE[ROUTE.length - 1]).ch; }
  /* ⚠️ BY SCREEN NAME, NOT BY INDEX. The four body questions are no longer contiguous --
     the torso question sits between height and volume, and only on a connected garment --
     so "the stage is drawing HER" cannot be a range any more. It never should have been:
     an index silently points at a neighbour the next time a question moves. */
  /** True while the stage should be drawing HER rather than the suit. */
  function inBody() { return BODY_KEYS.indexOf(screenId()) > -1; }
  function inTorso() { return at('torso'); }
  /* ⚠️ COVERAGE IS SKIPPED ON A ONE PIECE AND A MONOKINI, BECAUSE IT DOES NOTHING THERE.
     Measured on all four answers for all four styles, 2026-09-12: on a two piece and a
     three piece each answer lands on a different pair of shapes, and the four tiles draw
     four different garments. On a one piece all four tiles draw the SAME picture
     (shape-one-classic) and all four answers leave topShape='classic'; on a monokini the
     tiles draw a plain one piece -- not even the garment she chose -- and every answer
     leaves topShape='monokini'. Four identical pictures and four identical outcomes is
     not a question.
     ⚠️ SKIPPED, NOT DELETED. The step still exists and keeps its index, so nothing else
     in this file has to be re-derived, and a two piece still gets it. Navigation steps
     over it via goNext/goBack; do not add a third place that moves `step` by hand. */
  function coverageApplies() { return isSplit(); }
  /* ⚠️ A MONOKINI HAS NOTHING TO PICK ON THE SHAPES SCREEN EITHER. TOPS_MONO holds one
     entry and isSplit() is false, so the screen is a single tile, already selected, with
     no bottom section -- a confirmation dressed as a question. Nuno, 2026-09-12.
     ⚠️ THIS DOES NOT CONTRADICT THE NOTE ON THE SHAPES RENDERER. That one says never to
     render an EMPTY shape section INSIDE the screen, which still holds: if the screen
     shows, it shows its one tile. This skips the screen entirely, which is a different
     decision about a different thing. */
  function shapesApply() { return state.style !== 'monokini'; }
  /** Does the flow step over the screen at `s` for the garment she has chosen?
   *  ⚠️ ONE ANSWER, READ OFF THE ROUTE. It used to be two hard-coded offsets, so every
   *  new skippable screen meant another line here AND another index to keep in step with
   *  the ones around it. A screen declares its own applicability now; this only asks. */
  function skipsStep(s) {
    var r = ROUTE[s];
    return !!(r && r.applies && !r.applies());
  }
  /* ⚠️ WHILE, NOT IF. A monokini skips BOTH coverage and shapes, and a single hop landed
     straight on the second skipped screen -- the shapes question appeared anyway. Any
     number of consecutive screens may be skippable; step over all of them. */
  function goNext() { step++; while (skipsStep(step)) step++; render(); }
  /** Where the in-flow Back button wants to go.
   *  ⚠️ THE SHAPE CHOOSER'S COMPONENTS ARE WALKED BY BACK BEFORE IT LEAVES THE SCREEN.
   *  They are substeps of one question, so Back has to undo the last thing that happened
   *  -- which, standing on the bottom shape, is the top shape having been committed. It
   *  leaves the step only from the first component. Nothing is un-answered on the way:
   *  the top's choice stays, and its deck reopens on it. */
  function backTarget() {
    var id = ((deckOn(step) || desktopOn()) ? deckIdFor(step) : '') || '';
    if (id.indexOf('shape:') === 0 && shapePartIx() > 0) {
      return { step: step, part: shapePartIx() - 1, within: true };
    }
    var to = step;
    if (to > 0) { to--; while (to > 0 && skipsStep(to)) to--; }
    return { step: to, part: 0, within: false };
  }

  function goBack() {
    var want = backTarget();
    /* ⚠️ IT WALKS THE STUDIO'S OWN HISTORY RATHER THAN PILING ANOTHER ENTRY ON TOP OF
       IT, AND THAT REVERSES THE RULE THIS FILE SHIPPED WITH. An in-flow Back used to
       PUSH, on the reasoning that it is itself a visit. The consequence, which Codex
       reported 2026-09-16: from A to B to C, embedded Back reached B and the browser's
       Back then went FORWARD to C, because the entry behind her was the one her own Back
       had just written. Two controls that both mean "back" took her in opposite
       directions.
       So when the entry directly behind her is the screen the in-flow Back wants, this
       CONSUMES it -- one entry per screen, and the browser's Back carries on backwards
       past it. The push is kept only as the fallback for when there is nothing behind
       her to consume: a saved link opened deep in the flow, or a progress-dot jump whose
       previous entry names some other screen entirely.
       ⚠️ MATCHED THROUGH histLandable(), NOT ON THE RAW NUMBER. An entry recorded
       before the garment changed can name a screen this suit no longer asks; it is where
       that entry would LAND that has to be the screen Back wants, or Back would consume
       an entry and arrive somewhere else. */
    var prev = histDepth > 0 ? histTrail[histDepth - 1] : null;
    if (prev && !histPopping && typeof prev.step === 'number') {
      var lands = histLandable(prev.step);
      var same = want.within
        ? (lands === want.step && (prev.part || 0) === want.part)
        : (lands === want.step);
      if (same) { history.back(); return; }
    }
    if (want.within) {
      shapePart = want.part;
      stepJustChanged = true;
      render();
      return;
    }
    step = want.step;
    render();
  }
  /* The rest of "does this question draw a photograph" lives in FIG / inFig(). */

  /* ------------------------------------------------------------------------------
     BROWSER BACK AND FORWARD.

     ⚠️ THE ADDRESS BAR WAS ALWAYS CORRECT AND THE BROWSER BUTTONS WERE ALWAYS WRONG.
     `syncUrl()` has written the design into the URL since the cloth questions shipped,
     but it wrote it with `replaceState` on every render -- so twenty screens of a flow
     the page itself calls "your design" occupied ONE history entry. Pressing Back
     anywhere in the flow left the studio entirely, on a phone, where Back is the
     system gesture and not a button somebody has to find. Every answer survived,
     because the URL carried them; she just was not on the page any more.

     ⚠️ THE IN-FLOW BACK BUTTON AND THE BROWSER'S BACK ARE TWO DIFFERENT CONTROLS AND
     BOTH OF THEM ARE RIGHT. In-flow Back means THE PREVIOUS QUESTION -- `step - 1` over
     the route, stepping over screens this garment does not ask. The browser's Back means
     THE PREVIOUS SCREEN SHE WAS ON. They differ the moment she jumps: from question ten,
     reached by pressing a progress dot from question three, in-flow Back goes to nine and
     the browser goes to three. Conflating them would have meant picking one meaning and
     breaking the other, so each keeps its own -- and an in-flow Back is itself a visit,
     which is why it PUSHES an entry rather than consuming one.

     ⚠️ THE ENTRY CARRIES THE SCREEN, BECAUSE THE URL CANNOT. The resume ladder in
     fromUrl() places her at the first UNANSWERED applicable screen, which is the right
     answer for a link arriving cold and the wrong one for Back: every entry in a
     completed flow would resolve to the record. `{ step, part }` is written into the
     history entry itself, so a pop knows exactly which screen that entry WAS.

     ⚠️ AND A POP MAY NOT REACH ANYWHERE A PROGRESS DOT COULD NOT. `maxStep` is how far
     she has actually got, and it is clamped back when an answer forfeits ground (see
     forfeitAfter and dropReversibleForPreference). A Forward press after changing the
     kind of suit must not walk back onto screens built from the answer she just replaced,
     so the same two rules the dots use -- inside `maxStep`, never onto a skipped screen --
     are the rules here. Nothing in this block invents a third way to move.

     ⚠️ `browse`, `query` AND `solidOpen` ARE NOT RESTORED, AND THAT IS NOT AN OMISSION.
     They are where she is LOOKING, not what she has chosen; they are deliberately absent
     from the URL for that reason (see the state object), and a history entry that put a
     search back on screen as though it were an answer would be the same mistake with a
     different mechanism. A pop moves the SCREEN and touches no answer at all.
     ------------------------------------------------------------------------------ */

  /** Which screen an entry is for. `part` is the shape chooser's component, which is a
   *  substep of one question and not a step of its own -- see shapeParts(). */
  function histMark() { return { step: step, part: shapePartIx() }; }
  /* The mark of the entry currently on screen, or null before the first render has
     written one. */
  var histAt = null;
  /* True while a pop is being applied, so the render it triggers replaces that entry
     rather than pushing a new one on top of it. */
  var histPopping = false;
  /* ⚠️ THE JOURNAL IS WHAT LETS THE IN-FLOW BACK CONSUME AN ENTRY INSTEAD OF ADDING
     ONE. `history` will not tell you what is BEHIND you -- only `popstate` will, and only
     after the move has already happened -- so the marks we push are kept here, indexed by
     depth, and `mkyd` on each entry is what re-syncs the index after the browser moves
     us. A pushed entry truncates the forward half of the trail exactly as the browser
     truncates its own forward stack.
     ⚠️ IT IS PER-DOCUMENT AND THAT IS ALL IT NEEDS TO BE. Leave the studio and come
     back and the trail is empty again -- along with every other piece of JS state -- so
     goBack() finds nothing behind it and falls back to pushing, which is the correct
     behaviour for a screen that has no studio history in this document. */
  var histTrail = [];
  var histDepth = 0;
  /* Set while Start again is unwinding the studio's own entries; see the reset button. */
  var histRewind = null;
  var histRewindTimer = 0;

  /** Write the design into the address bar, pushing a new entry when the SCREEN moved.
   *  ⚠️ NAMESPACED UNDER `mky` AND NOT WRITTEN AS THE WHOLE STATE. This is a Shopify page
   *  template and other scripts on it may keep their own history state; an entry we
   *  replace is one we did not necessarily create, so its other keys are carried over.
   *  A pushed entry is ours and starts clean. */
  function histWrite(url) {
    var mark = histMark();
    var moved = !!histAt && (histAt.step !== mark.step || histAt.part !== mark.part);
    if (moved && !histPopping) {
      histDepth += 1;
      histTrail.length = histDepth;          // a push throws the forward half away
      histTrail[histDepth] = mark;
      history.pushState({ mky: mark, mkyd: histDepth }, '', url);
    } else {
      var keep = {};
      try {
        var cur = history.state;
        if (cur && typeof cur === 'object') {
          Object.keys(cur).forEach(function (k) { keep[k] = cur[k]; });
        }
      } catch (e) { /* some browsers throw on a cross-origin or opaque state; start clean */ }
      keep.mky = mark;
      keep.mkyd = histDepth;
      histTrail[histDepth] = mark;
      history.replaceState(keep, '', url);
    }
    histAt = mark;
  }

  /** Where a popped entry is actually allowed to land.
   *  ⚠️ CLAMPED IN BOTH DIRECTIONS AND THEN WALKED OFF A SKIPPED SCREEN IN THE DIRECTION
   *  OF TRAVEL. A screen that WAS asked can stop being asked -- a monokini skips coverage
   *  and shapes -- so an entry recorded before the garment changed can name a screen this
   *  suit no longer has. Walking in the direction she pressed keeps Back going back. */
  function histLandable(want) {
    var to = want;
    if (to < 0) to = 0;
    if (to > lastStep()) to = lastStep();
    /* Never past where she has actually been. Same rule as a progress dot. */
    if (to > maxStep) to = maxStep;
    var dir = to < step ? -1 : 1;
    while (to > 0 && to < lastStep() && skipsStep(to)) to += dir;
    /* The walk can run off the end into a skipped screen at either edge; come back. */
    while (to > 0 && skipsStep(to)) to--;
    while (to < lastStep() && skipsStep(to)) to++;
    return to;
  }

  /** Walk back to the studio's first entry, then run `done` on it.
   *  ⚠️ BECAUSE START AGAIN LEAVES OBSOLETE ENTRIES IN FRONT OF HER BACK BUTTON. Codex,
   *  2026-09-16: Start again must work "without making the customer press Back repeatedly
   *  through ineffective obsolete entries." Every entry behind her names a screen of the
   *  design she has just thrown away; landing on one restores a question about a garment
   *  that no longer exists. `history` cannot delete entries, but it can be walked back to
   *  the one the studio opened on, and the render that follows REPLACES that entry -- so
   *  the forward half goes the moment she answers anything, and one more Back leaves the
   *  studio rather than touring the wreckage.
   *  ⚠️ AND IT ALWAYS FINISHES. A `popstate` that never arrives -- an entry that is not
   *  ours, a browser that coalesces the hop -- would otherwise leave Start again having
   *  destroyed the design and rendered nothing. The timer is the floor, not the plan. */
  function histUnwind(done) {
    if (histDepth <= 0 || histRewind) { done(); return; }
    histRewind = done;
    histRewindTimer = setTimeout(function () {
      histRewindTimer = 0;
      if (!histRewind) return;
      var go = histRewind;
      histRewind = null;
      histDepth = 0;
      go();
    }, 500);
    history.go(-histDepth);
  }

  window.addEventListener('popstate', function (e) {
    var mark = e.state && e.state.mky;
    /* ⚠️ AN ENTRY THAT IS NOT OURS IS NOT OURS TO HANDLE. The first studio entry is a
       REPLACE, so the entry behind it belongs to whatever page she came from and Back
       from the opening screen leaves the studio -- which is correct, and is also what
       happens on a saved link that resumes deep in the flow: she has not been on the
       screens before it in this document, so there is nothing to go back to. */
    if (!mark || typeof mark.step !== 'number') return;
    /* Re-sync the journal index with wherever the browser has actually put us. */
    if (typeof e.state.mkyd === 'number') histDepth = e.state.mkyd;
    /* ⚠️ A REWIND IS ABSORBED, NOT RENDERED. Start again walks back to the studio's
       first entry so the screens belonging to the design she just threw away are not
       sitting in front of her Back button; the render that matters is the one the
       callback runs once we have arrived. */
    if (histRewind) {
      if (histDepth <= 0) {
        var go = histRewind;
        histRewind = null;
        if (histRewindTimer) { clearTimeout(histRewindTimer); histRewindTimer = 0; }
        go();
      }
      return;
    }
    var to = histLandable(mark.step);
    var part = typeof mark.part === 'number' ? mark.part : 0;
    histPopping = true;
    try {
      step = to;
      render();
      /* ⚠️ THE COMPONENT IS RESTORED AFTER THE RENDER, NOT BEFORE IT. A step change runs
         resetCards(), which zeroes `shapePart` -- correct for every other arrival and
         wrong for this one. The second render only happens when the chooser on screen is
         actually showing a different component from the one the entry names, which is the
         shapes deck and nothing else.
         ⚠️ AND `part: 0` IS A COMPONENT LIKE ANY OTHER. An earlier draft restored only
         `part > 0`, so walking back from the bottom shape to the top shape -- the one hop
         where the step does NOT move, and therefore the one hop resetCards() does not
         make for us -- did nothing at all, and the entry was then rewritten to say it was
         the bottom component after all. Compare what is on screen with what the entry
         says; never treat one of the values as "no value". */
      if (String(deckIdFor(step) || '').indexOf('shape:') === 0 && shapePartIx() !== part) {
        shapePart = part;
        /* Same as the shape walk in goBack(): a new component is a new question arriving,
           even though the step index did not move, so its heading takes focus. */
        stepJustChanged = true;
        render();
      }
    } finally {
      histPopping = false;
    }
    /* The entry we landed on now carries the screen we actually reached, which may have
       been clamped. Written by the render above through histWrite(). */
  });

  /* ------------------------------------------------------------------------------
     CLOTH STATE THAT HAS TO SURVIVE THE URL.

     ⚠️ THIS IS THE DEFECT THAT WAS LIVE FROM THE DAY THE CLOTH QUESTIONS SHIPPED.
     `syncUrl()` serialized twenty flat keys and `state.prefs` was not one of them -- it is
     an object, so the flat loop could not have carried it anyway -- and `fromUrl()` never
     restored it. A saved design link therefore resumed at the variation screen, one tap of
     Back reached the fabric picker with `state.prefs = {}`, and `suggested()` short-circuited
     on "no answers" and returned the catalogue in FILE ORDER: 5175, 5177, 5178, 5179, 5180,
     5182, under a heading reading "The six closest to what you said" when she had said
     nothing on that visit. The same six for every customer who got there.
     ------------------------------------------------------------------------------ */

  /** Four dot-separated tokens in FABRIC_DIMS order, carried as `prefs=`.
   *
   *  ⚠️ `prefs` AND `fabric` ARE DIFFERENT PARAMS AND MUST STAY THAT WAY. `prefs` is the four
   *  ANSWERS; `fabric` is the one she CHOSE. A blanket fabric->fabric rename briefly collapsed
   *  them onto one key, which made a saved link overwrite her chosen fabric with a preference
   *  string. That is why the preference side is `prefs` and not `fabric`. `-` unanswered, `either` = null. */
  function prefsToUrl() {
    var any = false;
    var s = FABRIC_DIMS.map(function (d) {
      var v = state.prefs[d];
      if (v === undefined) return '-';
      any = true;
      return v === null ? 'either' : v;
    }).join('.');
    return any ? s : '';
  }
  /** ⚠️ READ BOTH SPELLINGS, WRITE ONLY THE NEW ONE. Links made before the 2026-09-12 rename
   *  carry `cloth=` and the token `multicolour`; links made after carry `prefs=` and
   *  `multicolor`. Nothing has been published, so this protects testing links rather than
   *  customers -- but a test link that silently restores NOTHING is worse than one that fails,
   *  because it reads as "the answers were never set" and sends you hunting in the wrong file.
   *  ⚠️ AN EXPLICIT `prefs=` ALWAYS WINS. A link carrying both is a link written after the
   *  rename that happens to have an old key riding along; the new one is the intended state. */
  function prefsParam(q) {
    var now = q.get('prefs');
    if (now) return now;
    return q.get('cloth') || '';
  }
  /** Legacy band tokens -> canonical. Only ever applied on the way IN. */
  var PREFS_LEGACY = { multicolour: 'multicolor', colour: 'color' };

  function prefsFromUrl(s) {
    /* A link can carry anything. An unrecognized band is dropped and treated as never
       sent -- the same rule topShape, band and cup already follow -- so a stale or hand-
       edited link asks the question again instead of matching on a value we do not have. */
    var parts = String(s || '').split('.');
    if (parts.length !== FABRIC_DIMS.length) return;
    FABRIC_DIMS.forEach(function (d, i) {
      var v = parts[i];
      if (v === '-' || v === '') return;
      v = PREFS_LEGACY[v] || v;                 // multicolour -> multicolor, on input only
      if (v === 'either') {
        /* Only the ground question has an Either answer. Anywhere else it is a bad link. */
        if (d === 'ground') state.prefs[d] = null;
        return;
      }
      if (FABRIC_BANDS[d].indexOf(v) > -1) state.prefs[d] = v;
    });
  }

  /** A short fingerprint of what is actually matchable right now.
   *
   *  ⚠️ A SEED CANNOT HOLD RESULTS STILL WHEN THE POOL CHANGES. Re-label a fabric or sell
   *  one out and the same seed and the same answers legitimately produce a different six.
   *  Storing this alongside them is how a resumed link can tell that happened and say so,
   *  rather than showing her different fabrics under the same link and hoping.
   */
  /* ⚠️ BUMP THIS WHENEVER THE RANKING OR THE MISS-AXIS RULES CHANGE. The fingerprint below
     covers the DATA; this covers the POLICY. Re-order FABRIC_MISS_ORDER, change what counts as
     an adjacent miss, move confidence above or below family -- any of those changes which
     fabrics a saved link comes back to, and none of them touch a single fabric row. Without
     this, a deploy silently hands a returning customer a different six under the same link. */
  var FABRIC_POLICY_VERSION = '2026-09-13.2';

  function fabricVersion() {
    /* ⚠️ EVERY INPUT THAT CAN MOVE A CLOTH IN OR UP THE LIST, NOT JUST THE BANDS. The first
       version hashed code + bands + availability and stopped there, so re-labeling a
       confidence or re-assigning a visual family left the fingerprint UNCHANGED while
       genuinely changing both membership and order -- confidence sorts the tier and family
       decides which sibling leads a page. Codex caught it, 2026-09-12.
       ⚠️ DETERMINISTIC REPRESENTATION. FABRICS is emitted in code order by the generator, and
       it is sorted here anyway so a reordered file alone cannot look like a changed drop.
       Fixed field order, explicit separators, `-` for absent so a missing band cannot collide
       with an empty one. */
    var rows = FABRICS.map(function (f) {
      var parts = [f.code, (f.cuts_left > 0 ? '1' : '0')];
      FABRIC_DIMS.forEach(function (d) {
        parts.push(fabricBand(f, d) || '-');
        parts.push(fabricConf(f, d) || '-');
      });
      parts.push(fabricFamily(f));
      return parts.join(':');
    }).sort();
    return fabricHash(FABRIC_POLICY_VERSION + '|' + FABRIC_MISS_ORDER.join('>') + '|' +
                     rows.join(',')).toString(36);
  }

  /** ⚠️ AVAILABILITY IS "IN THE DROP AND NOT SOLD OUT", and a resumed link must test both.
   *  A code can vanish between drops, and a fabric still listed can have had its last cut
   *  taken -- `cuts_left` is real scarcity from the register, not a countdown invented for
   *  urgency. Every 102 rows currently sit at 3, so the sold-out branch is exercised by
   *  simulation in tests/design-studio/fabric_resume.py rather than by the live data. */
  /** ⚠️ A COLOUR REQUEST IS ALWAYS "AVAILABLE", BECAUSE IT IS NOT A CLAIM ABOUT STOCK.
   *  This function answers "can we cut this", and the honest answer for a `solid:` request
   *  is "we will find out and show you photographs" -- which is the whole point of the
   *  pending state. Treating one as unavailable would make every saved link carrying a
   *  colour direction resume with its fabric silently emptied under a message saying the
   *  fabric had sold out, which is the opposite of true.
   *
   *  ⚠️ AND 9001/9002 ARE *NOT* COVERED BY THAT, WHICH IS A BUG THIS FUNCTION ALREADY HAD
   *  FOR ONE AFTERNOON. The first version exempted every solid request INCLUDING the two
   *  legacy placeholder codes -- and those are ordinary catalogue rows: they carry
   *  `cuts_left`, they can leave the drop, and `fabric_resume.py` caught it within the
   *  hour because its walked design happens to pick 9001. PENDING and AVAILABLE are two
   *  different axes. 9001 is *pending* (assumed stock, never confirmed by a swatch post)
   *  and it is also *removable*; a `solid:` request is pending and has no stock to remove.
   *  Ask `slotPending()` for the first question and this for the second. */
  function fabricAvailable(code) {
    if (code && code.indexOf(SOLID_PREFIX) === 0) return !!parseSolid(code);
    var f = code && fabricBy(code);
    return !!(f && f.cuts_left > 0);
  }

  /** ⚠️ EVERY LIST A CUSTOMER CAN CHOOSE FROM COMES THROUGH HERE. Rejecting a sold-out
   *  fabric on resume and then offering her the same fabric two lines later is worse than
   *  either behavior alone -- she is told to replace it and handed it back. Reproduced by
   *  Codex on 2026-09-12: `9001` with `cuts_left: 0` was still returned as an exact match.
   *  ⚠️ `FABRICS` ITSELF STAYS WHOLE. `fabricBy()` must still find an unavailable row so a
   *  resumed link can NAME the fabric she had, and `fabricVersion()` must still see it so the
   *  fingerprint notices the day it sells out. Filter at the point of OFFER, never at the
   *  point of lookup. */
  function offerable() {
    return FABRICS.filter(function (f) { return f.cuts_left > 0; });
  }

  /** ⚠️ STABLE, NEVER FRESH PER RENDER. A link with no seed must show the same fabrics
   *  every time it is opened, so an old link's seed is DERIVED from the link itself
   *  rather than minted. Only a genuinely new flow gets a random one. */
  function fabricSeedFor(q) {
    if (q && q.get('seed')) return q.get('seed').slice(0, 16);
    /* ⚠️ THE SEED IS DERIVED FROM THE NORMALIZED ANSWERS, NOT THE RAW KEY. A legacy link
       and its renamed equivalent must derive the SAME seed, or the same design shows a
       different six depending on which spelling it was saved under. */
    var carried = q ? (prefsParam(q) || '') + '|' + (q.get('fabric') || '') +
                      '|' + (q.get('style') || '') : '';
    if (carried.replace(/[|]/g, '')) return 'l' + fabricHash(carried).toString(36);
    return 's' + Math.random().toString(36).slice(2, 10);
  }

  /* ------------------------------------------------------------------------------
     CLOTH MATCHING. Ported from iralsol-ops fabrics/review/2026-09-12-fabric-labels/
     candidate_match.py, which is where it was measured. Keep the two in step.
     ------------------------------------------------------------------------------ */

  function fabricBand(f, dim) {
    var c = f.fabric;
    if (!c) return '';
    var v = c[dim];
    /* Blank and `uncertain` are both "no claim". They differ upstream -- nobody looked
       versus someone looked and could not decide -- and neither may become a match. */
    return (v && v !== 'uncertain') ? v : '';
  }
  function fabricConf(f, dim) { return (f.fabric && f.fabric[dim + '_conf']) || ''; }
  function fabricFamily(f) {
    /* ⚠️ THE DESCRIPTION IS NOT AN IDENTITY. "Snake print" names six visibly different
       fabrics and "Abstract design" three, while 5175 and 5252 are both dot prints under
       different descriptions. Identity is the CODE; the family is a reviewed field and
       exists only so one screen does not show four zebras. Falling back to the code means
       an unlabeled fabric is its own family and is never grouped with anything. */
    return (f.fabric && f.fabric.family) || ('code:' + f.code);
  }

  /** 'exact' | 'near' | '' , plus which single dimension is missed. */
  function fabricVerdict(f, ans) {
    var missed = null;
    for (var i = 0; i < FABRIC_DIMS.length; i++) {
      var d = FABRIC_DIMS[i], want = ans[d];
      if (want === undefined || want === null) continue;   // unanswered, or Either
      var have = fabricBand(f, d);
      /* ⚠️ NO BAND ON A CONSTRAINED QUESTION MEANS NO CLAIM, NOT A NEAR MISS. An attribute
         nobody decided can be wrong in any direction, so "close, but a darker ground"
         would be the same unsupported claim in a quieter voice. Browse-all only. */
      if (!have) return { kind: '', miss: null };
      if (have !== want) {
        if (missed) return { kind: '', miss: null };       // two or more: never suggested
        missed = d;
      }
    }
    if (!missed) return { kind: 'exact', miss: null };
    /* ⚠️ ONE DIMENSION OFF IS NOT AUTOMATICALLY CLOSE. `limited` against `multicolor`,
       `soft` against `vivid`, `open` against `packed` are opposite ends of a three-band
       scale, and calling them close is what offered a solid BLACK to a woman who asked for
       as much color as possible. Ground has two bands, so its only possible miss is the
       adjacent one and always qualifies. */
    var order = FABRIC_BANDS[missed];
    if (order.length > 2 &&
        Math.abs(order.indexOf(fabricBand(f, missed)) - order.indexOf(ans[missed])) > 1) {
      return { kind: '', miss: null };
    }
    return { kind: 'near', miss: missed };
  }

  /** How many CONSTRAINED dimensions of this match rest on a low-confidence call. */
  function fabricLowConf(f, ans) {
    var n = 0;
    FABRIC_DIMS.forEach(function (d) {
      if (ans[d] === undefined || ans[d] === null) return;
      if (fabricConf(f, d) === 'low') n++;
    });
    return n;
  }

  /* xmur3 + a 32-bit mix: a stable, tiny string hash. The only requirement is that the
     same (seed, answers, code) always produces the same number and different seeds do not
     correlate. Not cryptographic and does not need to be. */
  function fabricHash(str) {
    var h = 1779033703 ^ str.length;
    for (var i = 0; i < str.length; i++) {
      h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
      h = (h << 13) | (h >>> 19);
    }
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^ (h >>> 16)) >>> 0;
  }
  function fabricAnswerKey(ans) {
    return FABRIC_DIMS.map(function (d) {
      return d + '=' + (ans[d] === undefined ? '-' : (ans[d] === null ? 'either' : ans[d]));
    }).join('|');
  }

  /** The two queues, ordered. Exported shape: {exact:[], near:[], miss:{code:dim}}. */
  function fabricQueues(ans) {
    var ex = [], nr = [], miss = {};
    offerable().forEach(function (f) {
      var v = fabricVerdict(f, ans);
      if (v.kind === 'exact') ex.push(f);
      else if (v.kind === 'near') { nr.push(f); miss[f.code] = v.miss; }
    });
    var key = state.seed + '|' + fabricAnswerKey(ans);
    function seeded(salt) {
      return function (f) { return fabricHash(key + '|' + salt + '|' + f.code); };
    }
    var sx = seeded(''), sn = seeded('near');
    /* ⚠️ THE PRECEDENCE IS: TIER, then CONFIDENCE, then SEED -- and family preference is
       applied later, at page-fill time, among candidates that are already comparable.
       Ordering by family here would let a family decide which fabric is a better match,
       which it must never do. */
    ex.sort(function (x, y) {
      var d = fabricLowConf(x, ans) - fabricLowConf(y, ans);
      return d || (sx(x) - sx(y));
    });
    nr.sort(function (x, y) {
      var d = FABRIC_MISS_ORDER.indexOf(miss[x.code]) - FABRIC_MISS_ORDER.indexOf(miss[y.code]);
      if (d) return d;
      d = fabricLowConf(x, ans) - fabricLowConf(y, ans);
      return d || (sn(x) - sn(y));
    });
    return { exact: ex, near: nr, miss: miss };
  }

  /** Up to n from `queue`, skipping anything already displayed, preferring fresh families
   *  and then fresh descriptions.
   *
   *  ⚠️ THE PRECEDENCE IS EXPLICIT AND IT MATTERS: TIER, then CONFIDENCE, then FAMILY, then
   *  DESCRIPTION, then SEED. Family diversity is applied only among candidates that are
   *  ALREADY COMPARABLE --
   *  the queue is walked in confidence groups, and a fresh family can only jump a sibling
   *  inside the same group, never a better-supported match in a stronger one. Letting the
   *  family preference run over the whole queue put a low-confidence fabric ahead of a
   *  high-confidence one 35 times across the 81 answers; a family must never decide which
   *  fabric is the better match.
   *
   *  ⚠️ FOUR PASSES PER GROUP, AND THE LAST ONE IS WHY NOTHING IS EVER WITHHELD. Pass one
   *  prefers a fabric whose family AND description are both new to this screen; pass two
   *  relaxes the description; pass three relaxes the family; pass four takes whatever is
   *  left in the group. Every pass stays INSIDE one rank group, so no preference can promote
   *  a weaker match, and pass four guarantees a slot is never left to a weaker tier. An
   *  absolute one-per-family cap withheld an exact match (9002) while three near-misses took
   *  the slots; diversity must never cost a better match its place.
   *
   *  ⚠️ FAMILY OUTRANKS DESCRIPTION, DELIBERATELY. Passes one and two together carry exactly
   *  the family constraint the previous two-pass version applied, so description can only
   *  break ties INSIDE a family stratum and family diversity cannot be traded away for it.
   *
   *  ⚠️ AND THE DESCRIPTION PASS BUYS LESS THAN IT LOOKS LIKE IT SHOULD. Measured over 81
   *  answers x 25 seeds, paged to exhaustion (5,025 screens): repeated descriptions
   *  31.7% -> 30.4%, worst case 4 either way, family repeats 1.269 -> 1.275 per screen,
   *  concentration and exposure unchanged (page 1 40.8%, cumulative 25.0%, 102/102 reached).
   *  12.6% of repeats are FORCED -- the eligible pool holds fewer distinct descriptions than
   *  the six slots, as with `limited/vivid/packed/light`, where 8 eligible fabrics carry 5
   *  names. The rest are reachable only by crossing a confidence or miss-order boundary,
   *  which would promote a weaker match and is exactly what must not happen. The real cause
   *  is that 12 descriptions are shared by more than one fabric -- six fabrics are "Snake
   *  print" -- and that is a catalogue fact, not an ordering one.
   */
  function fabricTake(queue, shown, n, fams, rank, names) {
    names = names || {};
    /* The customer-visible description, normalised. Identity is still the CODE; this is
       only what she reads under the tile, which is what makes two of them side by side
       look like the same fabric shown twice. */
    function fabricName(f) { return ((f && f.name) || '').trim().toLowerCase(); }
    var out = [], groups = [], seenRank = {};
    queue.forEach(function (f) {
      if (shown[f.code]) return;
      var r = rank ? rank(f) : 0;
      if (seenRank[r] === undefined) { seenRank[r] = groups.length; groups.push([r, []]); }
      groups[seenRank[r]][1].push(f);
    });
    groups.sort(function (x, y) { return x[0] - y[0]; });
    var want = [
      function (f) { return !fams[fabricFamily(f)] && !names[fabricName(f)]; },
      function (f) { return !fams[fabricFamily(f)]; },
      function (f) { return !names[fabricName(f)]; },
      function () { return true; }
    ];
    for (var g = 0; g < groups.length && out.length < n; g++) {
      var band = groups[g][1];
      for (var w = 0; w < want.length && out.length < n; w++) {
        for (var i = 0; i < band.length && out.length < n; i++) {
          if (out.indexOf(band[i]) > -1) continue;
          if (!want[w](band[i])) continue;
          out.push(band[i]);
          fams[fabricFamily(band[i])] = 1;
          names[fabricName(band[i])] = 1;
        }
      }
    }
    return out;
  }

  /** The suggestions screen for page `page`. Replays from page 0 so `shown` is exact.
   *
   *  ⚠️ THERE IS NO CURSOR, AND THAT IS THE FIX. A cursor that rewound to the first
   *  deferred sibling re-served four fabrics from page one on page two. Eligibility is
   *  "not displayed yet", so a deferred sibling is simply still available next time and a
   *  displayed fabric never is.
   */
  function fabricScreen(ans, page, n) {
    n = n || 6;
    var q = fabricQueues(ans), shown = {}, ex = [], nr = [], alt = [], p;
    /* ⚠️ THE FALLBACK POOL IS BUILT ONCE, OUTSIDE THE REPLAY LOOP. fabricScreen replays
       every page from zero so `shown` is exact; rebuilding a sorted 102-row queue on each
       replayed page would be the same work N times for the same answer. */
    var altQ = (q.exact.length || q.near.length) ? [] : fabricFallbackQueue(ans);
    for (p = 0; p <= page; p++) {
      var fams = {}, nms = {};
      var rank = function (f) { return fabricLowConf(f, ans); };
      ex = fabricTake(q.exact, shown, n, fams, rank, nms);
      nr = [];
      /* ⚠️ THE EXACT TIER MUST BE SPENT BEFORE THE NEAR TIER IS TOUCHED. */
      var exactLeft = q.exact.some(function (f) {
        return !shown[f.code] && ex.indexOf(f) < 0;
      });
      /* The near tier ranks by which dimension is missed FIRST -- that is its tier
         structure -- and by confidence inside that. */
      if (ex.length < n && !exactLeft) {
        nr = fabricTake(q.near, shown, n - ex.length, fams, function (f) {
          return FABRIC_MISS_ORDER.indexOf(q.miss[f.code]) * 8 + fabricLowConf(f, ans);
        }, nms);
      }
      /* ⚠️ NOTHING TO SUGGEST IS NOT A REASON TO SHOW HER NOTHING. When both tiers are
         empty she used to get a sentence and a button, and had to ask again to see any
         fabric at all. The fallback fills the same six slots from everything we can
         actually cut, ordered by how far each one sits from what she said, and it pages
         and de-duplicates through the SAME `shown` map as the other two tiers -- so a
         fabric cannot repeat within a page or across pages, and paging still reaches the
         whole pool. Codex, 2026-09-13. */
      alt = altQ.length ? fabricTake(altQ, shown, n, fams, function (f) {
        return fabricFallbackRank(f, ans);
      }, nms) : [];
      if (p < page) {
        ex.concat(nr).concat(alt).forEach(function (f) { shown[f.code] = 1; });
      }
    }
    var seen = {};
    ex.concat(nr).concat(alt).forEach(function (f) { seen[f.code] = 1; });
    var more = q.exact.concat(q.near).concat(altQ).some(function (f) {
      return !shown[f.code] && !seen[f.code];
    });
    return { exact: ex, near: nr, alt: alt, miss: q.miss, more: more,
             exactTotal: q.exact.length, nearTotal: q.near.length,
             altTotal: altQ.length, offerable: offerable().length,
             page: page, broaden: q.exact.length ? [] : fabricBroaden(ans) };
  }

  /** ⚠️ WHEN NOTHING MATCHES, NAME THE WAY OUT. A dead end plus "browse 102" is not an
   *  answer. Which single answer change opens the most fabrics, and how many? Offered as a
   *  button; nothing is applied unless she presses it. */
  function fabricBroaden(ans) {
    var out = [];
    FABRIC_DIMS.forEach(function (d) {
      var opts = FABRIC_Q[FABRIC_DIMS.indexOf(d)].opts;
      opts.forEach(function (o) {
        if (o[0] === ans[d]) return;
        var trial = {};
        FABRIC_DIMS.forEach(function (k) { trial[k] = ans[k]; });
        trial[d] = o[0];
        var n = 0;
        /* The count on the button has to be the number she can actually have. */
        offerable().forEach(function (f) { if (fabricVerdict(f, trial).kind === 'exact') n++; });
        if (n) out.push({ dim: d, value: o[0], label: o[1], count: n });
      });
    });
    out.sort(function (x, y) { return y.count - x.count; });
    return out.slice(0, 2);
  }

  /** Three illustrative swatches for one answer.
   *
   *  ⚠️ EXAMPLES ARE DRAWN FROM THE SAME LABELS THE RECOMMENDER MATCHES ON. That is the
   *  whole defect this was built to close: the old examples were chosen by hand against
   *  scores that measured something else, so the pictures and the results disagreed.
   *  ⚠️ AND THEY ARE ONLY EXAMPLES. The recommendation considers every eligible fabric in
   *  the drop, not these three.
   *  Higher confidence first, then one per family so the row shows a range rather than
   *  three near-identical fabrics, then code order so the row is STABLE -- the examples are
   *  not part of the seeded variation and must not move under her between renders.
   */
  function fabricExamples(dim, band, n) {
    n = n || 3;
    if (band === null) return [];          // "Either" is not a look; nothing to illustrate
    /* An example is an illustration, not a choice -- but illustrating an answer with a
       fabric we cannot cut sets an expectation the picker then takes away. */
    var pool = offerable().filter(function (f) { return fabricBand(f, dim) === band; });
    var rank = { high: 0, medium: 1, low: 2 };
    pool.sort(function (x, y) {
      var d = (rank[fabricConf(x, dim)] === undefined ? 3 : rank[fabricConf(x, dim)]) -
              (rank[fabricConf(y, dim)] === undefined ? 3 : rank[fabricConf(y, dim)]);
      return d || (parseInt(x.code, 10) - parseInt(y.code, 10));
    });
    var out = [], fams = {}, i;
    for (i = 0; i < pool.length && out.length < n; i++) {
      var fam = fabricFamily(pool[i]);
      if (fams[fam]) continue;
      fams[fam] = 1; out.push(pool[i]);
    }
    for (i = 0; i < pool.length && out.length < n; i++) {
      if (out.indexOf(pool[i]) < 0) out.push(pool[i]);
    }
    return out;
  }
  /** The one option that is the ABSENCE of a preference, drawn rather than left blank.
   *  ⚠️ IT WAS AN EMPTY CARD UNTIL 2026-09-16, and an intentional blank is indistinguishable
   *  from a picture that failed to load. Nuno, on a handset: "the 'Either' answer is
   *  intentionally blank... it looks like something is missing."
   *  ⚠️ REAL CLOTH, NOT TWO FLAT COLOURS. Every other card on this screen shows fabrics we
   *  actually stock, and answering "either" with two swatches of invented paint would be
   *  the only abstraction on the screen. One light-ground fabric and one dark-ground
   *  fabric, split diagonally, says "either of these" in the same vocabulary as its
   *  neighbours. Diagonal rather than vertical: a vertical split reads as TWO THINGS side
   *  by side, which is what the other cards are, and this is one answer.
   *  ⚠️ IT FALLS BACK TO FLAT TONES IF EITHER SIDE HAS NO EXAMPLE, because a half-drawn
   *  tile is the blank card again. */
  function eitherSwatch(dim) {
    var order = FABRIC_BANDS[dim] || [];
    if (order.length < 2) return '';
    var lo = fabricExamples(dim, order[0], 1)[0];
    var hi = fabricExamples(dim, order[order.length - 1], 1)[0];
    var face = function (f, fallback) {
      if (f && f.image) {
        return '<img class="mky__eitherhalf" src="' + f.image + '&width=120" alt="" ' +
               'loading="lazy" width="22" height="44">';
      }
      return '<span class="mky__eitherhalf" style="background:' +
             ((f && f.hex) || fallback) + '"></span>';
    };
    /* ⚠️ DARK GROUND ON THE LEFT, LIGHT GROUND ON THE RIGHT, and the order of these two
       calls is the only thing that decides it -- first child is the left half. `hi` is the
       LAST band in FABRIC_BANDS, which for this dimension is the light one, so the pair is
       written lo-then-hi rather than hi-then-lo. Nuno, 2026-09-17: "the full left side
       should be the [dark] swatch and the full right side should be the polka dot
       swatch." Do not "tidy" this back into band order; band order is not screen order. */
    return '<span class="mky__ex" aria-hidden="true">' +
           '<span class="mky__exsw mky__exsw--either">' +
           face(lo, '#2A2A2A') + face(hi, '#F2EFE9') +
           '</span></span>';
  }

  function fabricExampleRow(dim, band) {
    /* `null` is ANSWERED WITH NO PREFERENCE, not unanswered -- see the note on the ground
       question -- so it has no band to pull examples for and needs its own picture. */
    if (band === null) return eitherSwatch(dim);
    var ex = fabricExamples(dim, band, 3);
    if (!ex.length) return '';
    /* ⚠️ aria-hidden AND NO alt TEXT. A screen reader must hear the answer, not three
       fabric names it cannot choose: the whole row is one control and the examples are
       decoration on it. The button's own <b> carries the label. */
    return '<span class="mky__ex" aria-hidden="true">' + ex.map(function (f) {
      return f.hex
        ? '<span class="mky__exsw mky__exsw--solid" style="background:' + f.hex + '"></span>'
        : '<img class="mky__exsw" src="' + f.image + '&width=120" alt="" loading="lazy" ' +
          'width="44" height="44">';
    }).join('') + '</span>';
  }

  /** The direction of a near-miss, in her words. Never a fixed phrase: "more color" on a
   *  fabric with FEWER colors than she asked for is simply false. */
  var FABRIC_DIRECTION = {
    color:    ['fewer colors', 'more color'],
    intensity: ['softer', 'stronger color'],
    density:   ['calmer', 'busier'],
    ground:    ['the other ground', 'the other ground']
  };
  function fabricMissText(f, ans, dim) {
    var order = FABRIC_BANDS[dim];
    var mine = order.indexOf(fabricBand(f, dim)), want = order.indexOf(ans[dim]);
    return FABRIC_DIRECTION[dim][mine < want ? 0 : 1];
  }

  /* ⚠️ WHAT WE SAY WHEN WE DO NOT KNOW, and it is never a direction. A fabric with no
     band on a question she answered is not "softer" or "busier" -- we have nothing to
     compare. Saying so plainly is the only honest option, and inventing a direction here
     would be the same unsupported claim the whole fabric chapter was rebuilt to stop.
     No process vocabulary: "not known", never "uncertain" or "low confidence". */
  var FABRIC_UNKNOWN = {
    color:     'number of colors not known',
    intensity: 'color strength not known',
    density:   'how busy it is not known',
    ground:    'light or dark not known'
  };

  /** How far one fabric sits from her answers, per constrained question.
   *
   *  Returns {parts, diffs, dist, unknowns} where `parts` is one entry per CONSTRAINED
   *  dimension, in FABRIC_DIMS order, each `{dim, kind, text}` with kind 'match' | 'off'
   *  | 'unknown'.
   *
   *  ⚠️ AN UNKNOWN BAND COSTS AS MUCH AS THE WORST KNOWN DIFFERENCE. That is the whole
   *  point of scoring it rather than skipping it: a fabric we never classified must not
   *  float to the top of the fallback by having nothing to disagree with. Skipping
   *  unknowns would have made an unlabeled fabric a perfect match by construction.
   */
  function fabricDiffs(f, ans) {
    var parts = [], diffs = 0, dist = 0, unknowns = 0;
    FABRIC_DIMS.forEach(function (d) {
      if (ans[d] === undefined || ans[d] === null) return;   // unanswered, or Either
      var band = fabricBand(f, d);
      if (!band) {
        parts.push({ dim: d, kind: 'unknown', text: FABRIC_UNKNOWN[d] });
        unknowns++; diffs++; dist += FABRIC_OPPOSITE_COST;
        return;
      }
      var order = FABRIC_BANDS[d];
      var delta = Math.abs(order.indexOf(band) - order.indexOf(ans[d]));
      if (!delta) { parts.push({ dim: d, kind: 'match', text: '' }); return; }
      parts.push({ dim: d, kind: 'off', text: fabricMissText(f, ans, d) });
      diffs++; dist += delta;
    });
    return { parts: parts, diffs: diffs, dist: dist, unknowns: unknowns };
  }

  /* An opposite-end miss on a three-band axis. An unknown is charged the same: never
     better than a confirmed adjacent difference, never treated as a match. */
  var FABRIC_OPPOSITE_COST = 2;

  /** Plain language for every way a fallback fabric differs from her answers.
   *
   *  ⚠️ EVERY DIFFERENCE, NOT THE FIRST ONE. The near tier shows fabrics that miss on
   *  exactly one axis, so one phrase was always enough there. A fallback fabric can miss
   *  on three at once, and naming only one would understate what she is looking at.
   */
  function fabricDiffText(f, ans) {
    var d = fabricDiffs(f, ans), said = [];
    d.parts.forEach(function (p) { if (p.kind !== 'match') said.push(p.text); });
    return said.join(' &middot; ');
  }

  /** Deterministic fallback ordering. Lower is nearer to what she asked for.
   *
   *  ⚠️ THIS IS THE ONLY PLACE AN OPPOSITE-END OR MULTI-AXIS DIFFERENCE MAY APPEAR, and
   *  it is a deliberate revision of the rule that keeps them OUT of the close tier.
   *  `fabricVerdict` is untouched: nothing here can promote a fabric into exact or near.
   *
   *  THE FACTORS, AND HOW THEY INTERACT -- each one only breaks ties left by the one
   *  above it:
   *    1. FEWER differing questions            (diffs, unknowns counted as differing)
   *    2. SMALLER total difference             (dist; adjacent 1, opposite 2, unknown 2)
   *    3. FEWER unknowns                       (a confirmed difference beats an
   *                                             unconfirmed one when 1 and 2 are level)
   *    4. FEWER low-confidence constrained     (the same confidence handling the exact
   *       calls                                 and near tiers already use)
   *  and below that, family and description variety plus seeded rotation, applied by
   *  `fabricTake` exactly as they are for the other two tiers -- which is what stops the
   *  same handful of fabrics being served to everyone regardless of their answers.
   */
  function fabricFallbackRank(f, ans) {
    var d = fabricDiffs(f, ans);
    return d.diffs * 1000 + d.dist * 100 + d.unknowns * 10 + fabricLowConf(f, ans);
  }

  /** Every available fabric valid for this design, ordered for the fallback section. */
  function fabricFallbackQueue(ans) {
    var key = state.seed + '|' + fabricAnswerKey(ans) + '|alt';
    return offerable().slice().sort(function (x, y) {
      var d = fabricFallbackRank(x, ans) - fabricFallbackRank(y, ans);
      if (d) return d;
      return fabricHash(key + '|' + x.code) - fabricHash(key + '|' + y.code);
    });
  }

  /* A bare list of construction changes does not say WHICH answer caused each one, so
     the panel read as a list of assertions about the customer rather than a receipt for
     what they told us. Each group is headed by the concern it came from. */
  var BUILD_LABEL = { torso: 'Torso', top: 'Top', wire: 'Support',
                      padding: 'Padding', seat: 'Seat' };

  /** Every construction decision the answers have caused, grouped by the question. */
  function buildGroups() {
    var rev = state.variation === 'reversible';
    var out = [], inactive = [];
    FIT_KEYS.forEach(function (k) {
      /* ⚠️ A QUESTION THE GARMENT NEVER ASKED CANNOT BUILD ANYTHING. Since 2026-09-15 the
         torso question is asked of connected garments only, and a customer who answered it
         on a one piece and then switched to a two piece still has that answer in `state` --
         deliberately, so switching back restores it. Reading it here would cut a torso
         allowance into a garment that has no torso, which is exactly what Codex's brief
         forbids: omit it from the effective design and the maker build.
         ⚠️ SKIPPED IS NOT `standard`. Nothing here writes a standard-length note in its
         place; the question simply produced no construction for this suit, and
         fitNotes() says so to Evelin in as many words. */
      if (k === 'torso' && !torsoApplies()) return;
      var def = Q[k], v = state[k];
      if (!v || !v.length) return;
      var items = [];
      (def.multi ? v : [v]).forEach(function (one) {
        if (!def.builds[one]) return;
        /* ⚠️ Never promise structure a reversible garment cannot hold. The variation
           screen warns about this; the record has to agree with the warning, or the
           customer is told two different things about the same suit. */
        /* The same three selections reversibleConflicts() names, dropped here. Keep the
           two in step: the record contradicting the warning is the bug this pair fixes. */
        if (rev && reversibleBlockFor(k, one)) {
          /* ⚠️ NAMED, NOT COUNTED, AND THAT IS NEW ON 2026-09-15. It used to set a boolean
             and print one generic sentence, which was enough while the conflict could only
             be met BEFORE the arrangement was chosen -- the warning on that screen had
             already named the selections. The comfort questions now come AFTER it, so the
             record is where a customer can first see which of her requests is not being
             built, and "some of your choices" is not something she can check. Codex,
             2026-09-15: any incompatible preference accepted as inactive must be described
             that way in review and maker data. */
          inactive.push(def.builds[one]);
          return;
        }
        items.push(def.builds[one]);
      });
      if (items.length) out.push({ label: BUILD_LABEL[k] || k, items: items });
    });
    if (rev && inactive.length) {
      out.push({ label: 'Support',
                 items: ['Support from the cut rather than a built-in bra, so it can be reversible'] });
      out.push({ label: 'Recorded, not built',
                 items: inactive.map(function (t) {
                   return t + ' (asked for, and not built: a reversible suit has no inside to hide it in)';
                 }) });
    }
    return out;
  }

  /** What EVELIN has to be told that no construction line can say: a question this
   *  garment never asked, and an answer that is honestly "I don't know".
   *
   *  ⚠️ THESE ARE NOT `builds` AND MUST NEVER BECOME THEM. `builds` is what the CUSTOMER
   *  is told we will cut. Nothing here changes a single cut -- Codex, 2026-09-15: unknown
   *  creates no automatic fit adjustment -- so promoting any of it into the Cut-for-you
   *  panel would put a promise on screen that nobody made.
   *
   *  ⚠️ AND THEY ARE THE REASON UNANSWERED, UNKNOWN AND NOT-APPLICABLE ARE THREE STATES.
   *  Collapsed into one they all read as "standard", and Evelin loses the difference
   *  between a woman whose one pieces sit fine, a woman who has never worn one, and a
   *  bikini that was never asked. The maker wording is the brief's: "not sure /
   *  insufficient experience", never "standard fit". */
  function fitNotes() {
    var out = [];
    if (!torsoApplies()) {
      out.push('Torso length: not applicable \u2014 separate pieces, no connected body');
    } else if (state.torso === 'unknown') {
      out.push('Torso length: not sure / insufficient experience; no torso adjustment selected');
    }
    /* ⚠️ IT NAMES THE TOP *ISSUES*, NOT THE TOP, AND SAYS WHERE THE REST LIVES. It read
       "no top changes made", which is false the moment she has also asked for an underwire
       or sewn-in cups -- those are separate questions with their own builds, and a note
       that sweeps them up reads to the person cutting as though her support choices had
       been withdrawn. Codex, 2026-09-15. */
    if (state.top.indexOf('unknown') > -1) {
      out.push('Top issues: not sure / insufficient experience; no top-issue adjustment ' +
               'selected. Underwire and padding are answered separately, below');
    }
    /* ⚠️ "STANDARD SEAT, UNCONFIRMED" IS GONE. Unknown is not a confirmation of standard
       fit, and describing it as one is what Codex asked to be removed. */
    if (state.seat === 'unknown') {
      out.push('Seat fit: not sure / insufficient experience; no seat adjustment selected');
    }
    return out;
  }

  /** The same decisions, flat, for the record. */
  function builds() {
    var out = [];
    buildGroups().forEach(function (g) { out = out.concat(g.items); });
    return out;
  }

  /** Flat color for a solid, the tiled photograph for a print, a ghost for neither. */
  function fillFor(f, patternId) {
    if (!f) return 'rgba(0,0,0,.14)';
    return f.hex ? f.hex : 'url(#' + patternId + ')';
  }
  /* ⚠️ A WHITE GARMENT ON A WHITE PAGE HAS NO EDGE. White is the solid most people will
     want for a reversible inside, and it rendered as an invisible hole in the tile. Any
     pale solid gets a hairline so the shape survives. Prints never need it: they carry
     their own edge. */
  function edgeFor(f) {
    return (f && f.hex && f.lightness > 0.7) ? 'rgba(0,0,0,.22)' : 'none';
  }
  /** What a swatch tile shows. A solid is a block of color, not a missing image. */
  function swatchFace(f) {
    return f.hex
      ? '<span class="mky__solid" style="background:' + f.hex + '"></span>'
      : '<img src="' + f.image + '&width=260" alt="' + f.name + '" loading="lazy">';
  }

  /** One swatch tile. `note` is the near-miss reason, or '' for an exact match.
   *
   *  ⚠️ NO CODE AND NO REVIEW METADATA ON THE CUSTOMER'S SCREEN. The register carries a
   *  supplier code, a confidence and a provenance string per fabric; none of it belongs
   *  here. A visible supplier code invites shopping around, and "medium confidence" is a
   *  statement about our process, not about her suit.
   */
  /* ── THE SOLIDS PALETTE ──────────────────────────────────────────────────────────
     ⚠️ ONE RENDERER AND ONE BINDER, READ BY BOTH SCREENS. The main fabric picker and the
     partner pickers are two separate branches with two separate markup blocks, and this
     file has twice had to fix copy that was changed in one of them and not the other --
     the reversible warning, and the deck legend. A palette duplicated into both would be
     the third. `slot` is the state field being filled ('fabric', 'fabricBottom',
     'fabricInside', 'fabricCrop'), which is all either screen needs to differ by.

     ⚠️ IT IS A REQUEST, AND EVERY SURFACE SAYS SO. There is no photograph behind any of
     these: Nunez hold solids and never post them as swatches, so the block of colour is an
     APPROXIMATION and the honest sentence about it is on the panel, on the selected row,
     on the preview caption, in the review and on the maker line. Removing it from any one
     of those turns a colour direction into a claim about stock. */
  /** All fabrics / Prints / Solids, for whichever picker is on screen.
   *  ⚠️ IT FILTERS WHAT IS SHOWN AND NEVER TOUCHES WHAT IS CHOSEN. Codex, 2026-09-15:
   *  searching or filtering must not select a fabric, advance the question, clear an
   *  existing selection or rewrite preference answers. `state.browse` is not serialized
   *  for the same reason -- it is where she is LOOKING, not what she has decided, and a
   *  saved link that reopened on the solids palette would misreport her design. */
  var BROWSE_MODES = [['', 'All fabrics'], ['prints', 'Prints'], ['solids', 'Solids']];
  /* ⚠️ `aria-current`, NEVER `aria-pressed`, AND THE REASON IS NOT COSMETIC.
     `aria-pressed="true"` inside `#mky-step` MEANS "this is her answer" throughout this
     flow: `_preview.answer_current`, `record.py`, `resume.py` and `zoom.py` all read it to
     decide whether a screen has been answered, and so does anything assistive that scans
     for the chosen option. The first version of this control rendered "All fabrics" as
     pressed by default -- it is the active filter, which is true -- and three suites
     immediately concluded the fabric screen was already answered, never picked a fabric,
     and stalled on a gated Continue. That is not a harness quirk: a filter that announces
     itself as a selection is telling a screen-reader user the same wrong thing.
     `aria-current` is the standard for "the one in this set that is active" when the set
     is not a group of answers, which is exactly what this is. */
  function browseControlsHtml() {
    return '<p class="mky__actions mky__actions--quiet mky__browse" role="group" ' +
      'aria-label="Which fabrics to show">' +
      BROWSE_MODES.map(function (m) {
        var on = state.browse === m[0];
        return '<button class="mky__reset" type="button" data-browse="' + m[0] + '"' +
               (on ? ' aria-current="true"' : '') + '>' + m[1] + '</button>';
      }).join('') + '</p>';
  }
  /** The search field. One markup block, both pickers, like the palette. */
  function searchFieldHtml() {
    return '<label class="mky__searchwrap" for="mky-search">' +
           '<span class="mky__searchlabel">Search prints or colors</span>' +
           '<input type="search" id="mky-search" class="mky__search" autocomplete="off" ' +
           'placeholder="Try pineapple, blue or floral" value="' + escAttr(state.query) +
           '"></label>';
  }
  /** Bind it. `after` runs on every keystroke, once state.query is current. */
  /* ⚠️ THE FIRST KEYSTROKE IS A FULL RENDER, AND IT USED TO EAT THE KEYBOARD. Set by the
     input handler, read and cleared by bindSearchField() after the field is back on screen.

     `paintSearchResults` replaces ONLY the results region and leaves the field alone -- but
     that region does not exist until a search is running, so the transition from "not
     searching" to "searching" falls through to a full render, `#mky-step.innerHTML` is
     replaced, and the input she is typing into is destroyed with focus on it. Measured on
     the dev theme at 390x659: focus went to BODY on the first character, so a customer
     typed "w", the on-screen keyboard closed, and "hite" went nowhere. The feature reads as
     completely broken and the console says nothing.
     ⚠️ IT IS NOT ENOUGH TO RESTORE FOCUS; THE CARET HAS TO GO BACK TO THE END. Focusing an
     input puts the caret at position 0 in some browsers, so the next character would be
     typed in front of what she has already written. */
  var searchRefocus = false;

  function bindSearchField() {
    var box = document.getElementById('mky-search');
    if (!box) return;
    /* ⚠️ RE-RENDER WOULD DESTROY THE FIELD SHE IS TYPING INTO and take the caret with it,
       which is the same trap the unlisted-bust field and the colour note carry a note
       about. The difference here is that the RESULTS have to follow every keystroke, so
       the results region is replaced by hand and the field is left alone -- and where it
       cannot be (see searchRefocus), the field is put back under her finger. */
    box.addEventListener('input', function () {
      state.query = box.value.slice(0, 60);
      /* Read after the render this may trigger; see searchRefocus. */
      searchRefocus = true;
      paintSearchResults();
    });
    box.addEventListener('keydown', function (e) {
      /* Enter must not submit anything: this field sits inside a flow whose only form is
         the contact form on the record screen, and a stray submit there would mail Evelin
         a half-finished design. */
      if (e.key === 'Enter' || e.keyCode === 13) e.preventDefault();
    });
    /* ⚠️ ONLY WHEN A KEYSTROKE CAUSED THIS RENDER. Arriving at the picker normally must
       leave focus on the question heading, where finishStep() puts it -- stealing it into
       a search box would open the keyboard on a screen she came to look at. */
    if (searchRefocus) {
      searchRefocus = false;
      try {
        box.focus();
        var n = box.value.length;
        if (box.setSelectionRange) box.setSelectionRange(n, n);
      } catch (e) { /* a detached or disabled field: nothing to restore */ }
    }
  }
  /* ⚠️ THE ONE PLACE THAT REDRAWS RESULTS WITHOUT REDRAWING THE SCREEN. Set by whichever
     picker is on screen; cleared by render() so a stale closure cannot outlive its step. */
  var paintSearchResults = function () {};
  /** Bind every catalogue swatch on screen to fill `slot`.
   *  ⚠️ ONE BINDER, THREE CALLERS, AND IT WAS TWO NEAR-IDENTICAL COPIES. The main picker
   *  and the partner pickers each had their own, differing only in which field they wrote;
   *  the incremental search repaint would have made a third. The two things every copy
   *  had to remember are kept here: a tile can OUTLIVE the render that made it (a stale
   *  page while stock moved, a re-render racing a tap), so availability is re-checked at
   *  the click and not merely at the render; and choosing clears this slot's gone-marker
   *  so the "no longer available" line does not survive the replacement. */
  function bindFabricTiles(slot) {
    Array.prototype.forEach.call(el.step.querySelectorAll('.mky__swatch'), function (b) {
      if (b.disabled || b.dataset.solid !== undefined ||
          b.dataset.solidpick !== undefined || b.dataset.crop !== undefined) return;
      if (b.dataset.tileBound) return;
      b.dataset.tileBound = '1';
      b.addEventListener('click', function () {
        /* A fabric that has gone since the render is the one case that must rebuild
           from scratch: the tile she pressed is about to stop existing. */
        if (!fabricAvailable(b.dataset.code)) { render(); return; }
        state[slot] = b.dataset.code;
        if (slot === 'fabric') state.fabricGone = '';
        if (state.fabricGoneSlots) delete state.fabricGoneSlots[slot];
        renderKeepingPlace();
      });
    });
  }
  /** Bind the colour tiles a results region or a palette carries.
   *  ⚠️ THE SAME INTERACTION AS A SWATCH, DOWN TO KEEPING HER PLACE IN THE RAIL. The
   *  only thing that differs is what gets written: a `solid:` request rather than a
   *  supplier code. `state.solidOpen` follows the tap so the refinements for the family
   *  she chose are the ones on screen -- a narrowing offered AFTER a valid answer, never
   *  a second picker standing in front of one. */
  function bindSolidTiles(slot) {
    Array.prototype.forEach.call(el.step.querySelectorAll('[data-solidpick]'), function (b) {
      if (b.dataset.tileBound) return;
      b.dataset.tileBound = '1';
      b.addEventListener('click', function () {
        var bits = String(b.dataset.solidpick).split(':');
        setSolid(slot, bits[0], bits[1] || '');
        state.solidOpen = bits[0];
        renderKeepingPlace();
      });
    });
  }

  /** The results region for a query, for whichever slot is being filled.
   *
   *  ⚠️ SOLIDS AND PRINTS ARE ONE LIST NOW, AND THE REQUEST LINK IS GONE. Codex,
   *  2026-09-16: "Remove the separate 'Request a blue solid' link and equivalent request
   *  links. Under All fabrics, searching 'blue' should show a visible Blue · Solid color
   *  tile first, followed by matching photographed fabrics." A link that opened a
   *  different picker made choosing a colour a different KIND of act from choosing a
   *  print, which is the distinction the brief says belongs in the supporting information
   *  and in the saved data -- not in the flow.
   *
   *  ⚠️ IT NEVER SELECTS, ADVANCES OR CLEARS ANYTHING. Codex, 2026-09-15, and it still
   *  holds for the solid tiles: they are ordinary swatch buttons, bound by the picker
   *  that owns them, and pressing one records a choice exactly as pressing a print does.
   *  Typing does not.
   *
   *  ⚠️ THE BROWSE FILTER APPLIES TO A SEARCH TOO, OR IT IS NOT A FILTER. Until
   *  2026-09-16 the search ignored the row entirely, so pressing "Prints" and then typing
   *  changed nothing on screen. Prints excludes the colour directions AND the two assumed
   *  solids that wear a catalogue row; Solids shows the directions and nothing else. */
  function searchResultsHtml(slot, pool) {
    var mode = state.browse;
    var sols = mode === 'prints' ? [] : solidQuery(state.query);
    /* Saying "solid" is asking for one, so it narrows the list the same way the Solids
       filter does -- and the filter row still wins when she has pressed Prints. */
    var onlySolids = mode === 'solids' || (mode !== 'prints' && saysSolid(state.query));
    var r = onlySolids
      ? { exact: [], related: [], terms: [] }
      : fabricSearch(pool.filter(function (f) {
          return mode !== 'prints' || isPrint(f);
        }), fabricQuery(state.query));
    var out = '';
    /* ⚠️ "options", NOT "fabrics", AND THE WORD CHANGED BECAUSE THE LIST DID. Codex:
       "Mixed result counts should use an accurate term such as 'options' or 'matches' and
       include the solid tiles." A count saying "3 fabrics" over a row of four tiles, one
       of which is a colour we have not photographed, is wrong twice over. */
    var total = sols.length + r.exact.length;
    if (total) {
      out += '<p class="mky__matchhead">' + total + ' option' + (total === 1 ? '' : 's') +
             ' for &ldquo;' + escAttr(state.query) + '&rdquo;</p>' +
             '<div class="mky__swatches mky__swatches--row" data-rail="hits">' +
             /* The colour tiles lead: she typed a colour word, and the exact thing she
                named is the one result nobody has to interpret. */
             sols.map(function (x) {
               return solidTileHtml(slot, x.fam, x.refine);
             }).join('') +
             r.exact.map(function (f) { return fabricTile(f, '', slot); }).join('') +
             '</div>';
    }
    if (r.related.length) {
      /* ⚠️ RELATED IS NOT A MATCH AND MUST NOT READ AS ONE. These are fabrics answering
         SOME of what she typed, or answering it only through a tag nobody has checked.
         Naming the difference is what the reviewed/unreviewed split in the metadata is
         for; collapsing them would spend that whole audit. */
      out += '<p class="mky__nearhead text-sm text-subdued">Related</p>' +
             '<div class="mky__swatches mky__swatches--row" data-rail="related">' +
             r.related.slice(0, 12).map(function (f) {
               return fabricTile(f, '', slot);
             }).join('') + '</div>';
    }
    if (!total && !r.related.length) {
      /* ⚠️ AN EMPTY RESULT IS EXPLAINED AND HAS A WAY OUT. "No results" on its own is a
         dead end, and this catalogue is a hundred fabrics she can still browse. */
      out += '<p class="mky__matchhead mky__matchhead--near">Nothing in this month\u2019s ' +
             'drop matches &ldquo;' + escAttr(state.query) + '&rdquo;.</p>' +
             '<p class="mky__hint text-sm text-subdued">Try a single word, a color, or a ' +
             'print you have in mind. Your answers are kept either way.</p>';
    }
    out += '<p class="mky__actions mky__actions--quiet">' +
           '<button class="mky__reset" type="button" data-searchclear>Clear search</button>' +
           '</p>';
    return out;
  }
  /** Bind the controls a results region carries that the picker does not already own. */
  function bindSearchResults() {
    var clear = el.step.querySelector('[data-searchclear]');
    if (clear) {
      clear.addEventListener('click', function () {
        state.query = '';
        render();
      });
    }
  }

  function bindBrowseControls() {
    Array.prototype.forEach.call(el.step.querySelectorAll('[data-browse]'), function (b) {
      b.addEventListener('click', function () {
        state.browse = b.dataset.browse;
        state.solidOpen = '';
        fabricShown = FABRIC_PAGE;
        render();
      });
    });
  }
  /** A photographed print, as opposed to an assumed solid with no swatch behind it. */
  function isPrint(f) { return !slotPending(f.code); }

  /* ── SEARCH ──────────────────────────────────────────────────────────────────────
     ⚠️ IT SEARCHES THE WHOLE ELIGIBLE POOL, NOT THE SHORTLIST. Codex, 2026-09-15:
     "Search the whole eligible catalogue for the current fabric slot, not only the
     shortlist from the four preference questions. Preserve those answers without silently
     using them to exclude explicit search results." Typing a word is a more specific
     instruction than four taste answers given several screens ago, so it wins -- and her
     answers are untouched, which is what lets her clear the search and get them back.

     ⚠️ EXACT AND RELATED ARE KEPT APART, AND THE DATA IS WHY. `c` colours were measured
     off the swatch photograph and then LOOKED AT on a contact sheet; `cu` colours are
     measured and unreviewed. Motif tags carry their own provenance (`rm`). A result whose
     only match is unreviewed is offered as RELATED, never as a match -- the same rule the
     recommender follows for low-confidence bands, for the same reason. */
  var SEARCH_SYNONYM = {
    /* Colour words a shopper types that are not one of the twelve families. Kept small and
       obvious; this is not a thesaurus and must not become one. */
    gold: 'yellow', golden: 'yellow', mustard: 'yellow', lemon: 'yellow',
    navy: 'blue', teal: 'blue', turquoise: 'blue', aqua: 'blue', sky: 'blue',
    cobalt: 'blue', indigo: 'blue',
    olive: 'green', mint: 'green', emerald: 'green', lime: 'green', sage: 'green',
    burgundy: 'red', maroon: 'red', crimson: 'red', wine: 'red', scarlet: 'red',
    fuchsia: 'pink', fuschia: 'pink', magenta: 'pink', rose: 'pink', blush: 'pink',
    lilac: 'purple', violet: 'purple', lavender: 'purple', plum: 'purple',
    /* ⚠️ `coral` IS RED'S, AND THE TWO VOCABULARIES DISAGREED ABOUT IT. This map sends a
       typed colour word to a FAMILY, and `SOLID_FAMILIES` lists Coral as a refinement of
       Red -- so searching "coral" offered "Request an orange solid" and the palette it
       sent her to had no Coral in it. Every other refinement agrees across the two lists
       (navy/sky->blue, mint/emerald/olive->green, charcoal->grey, fuchsia->pink,
       lilac/plum->purple, tan/camel/chocolate->brown); this was the only one that did not.
       Changed HERE rather than in SOLID_FAMILIES because that list is the customer-facing
       vocabulary and is mirrored by `_fabric_ref()` in the backend -- moving a refinement
       between families needs both sides changed together, and nothing about coral being
       red's is in question. */
    peach: 'orange', rust: 'orange', coral: 'red', apricot: 'orange', tangerine: 'orange',
    tan: 'brown', camel: 'brown', chocolate: 'brown', beige: 'brown', khaki: 'brown',
    ivory: 'cream', charcoal: 'grey', gray: 'grey', silver: 'grey',
    /* Motif and pattern words with an obvious other spelling. */
    spot: 'dots', spots: 'dots', dot: 'dots', polkadot: 'dots', polkadots: 'dots',
    stripe: 'stripes', striped: 'stripes', flower: 'floral', flowers: 'floral',
    floral: 'floral', leaf: 'leaves', check: 'checks', checked: 'checks',
    plaid: 'checks', animal: 'animal', camo: 'camouflage'
  };
  /* ⚠️ GENERIC MODIFIERS NAME NO FAMILY AND MUST NOT BE MADE TO. `bright`, `deep` and
     `pale` each sit under three or four families, so the ambiguity filter below drops them
     on its own. `dark` appears exactly once -- "Dark chocolate" -- and would otherwise be
     indexed as brown, which is far more work than that one label has earned it. */
  var SOLID_SHADE_STOP = { dark: 1, light: 1, bright: 1, deep: 1, pale: 1, soft: 1 };
  /** One typed word -> one colour direction. BUILT FROM `SOLID_FAMILIES`, never written
   *  out, so the palette and the search vocabulary cannot drift the way `coral` did.
   *
   *  ⚠️ A WORD TWO FAMILIES BOTH CLAIM NAMES NEITHER. "Pale" is a direction under gray,
   *  yellow and pink; indexing it would pick whichever family happened to be listed first
   *  and put a colour on screen she never asked for.
   *  ⚠️ AND A FAMILY ALWAYS BEATS A REFINEMENT SPELLED THE SAME. "Blue" is the family,
   *  even though `Sky blue` and `Royal blue` are labels containing the word -- which is
   *  why the family pass runs LAST and overwrites.
   *  ⚠️ BUILT LAZILY BECAUSE `SEARCH_SYNONYM` IS DECLARED BELOW IT. Hoisting gives us
   *  the binding, not the value; asking for the index before the first search is what
   *  would read `undefined`. */
  var solidShadeIx = null;
  function solidShades() {
    if (solidShadeIx) return solidShadeIx;
    var fams = {}, refs = {}, claims = {};
    var addRef = function (w, famKey, refKey) {
      if (!w || w.length < 3 || SOLID_SHADE_STOP[w]) return;
      if (!claims[w]) claims[w] = {};
      claims[w][famKey] = 1;
      refs[w] = { fam: famKey, refine: refKey };
    };
    SOLID_FAMILIES.forEach(function (f) {
      fams[f.key] = { fam: f.key, refine: '' };
      fams[f.name.toLowerCase()] = { fam: f.key, refine: '' };
      (f.refine || []).forEach(function (r) {
        addRef(r[0], f.key, r[0]);
        String(r[1]).toLowerCase().replace(/[^a-z ]+/g, ' ').split(/\s+/)
          .forEach(function (w) { addRef(w, f.key, r[0]); });
      });
    });
    var ix = {};
    Object.keys(refs).forEach(function (w) {
      if (Object.keys(claims[w]).length > 1) return;   // two families claim it: drop it
      ix[w] = refs[w];
    });
    /* The colour words that are not in the palette's own vocabulary at all -- teal,
       burgundy, ivory. Family level only: a synonym names a direction, not a shade. */
    Object.keys(SEARCH_SYNONYM).forEach(function (w) {
      var fam = SEARCH_SYNONYM[w];
      if (!SOLID_BY_KEY[fam] || ix[w] || fams[w]) return;
      ix[w] = { fam: fam, refine: '' };
    });
    Object.keys(fams).forEach(function (w) { ix[w] = fams[w]; });
    solidShadeIx = ix;
    return ix;
  }
  /* Words that ask for a solid without naming one. */
  var SOLID_WORD = { solid: 1, solids: 1, plain: 1, color: 1, colors: 1,
                     colour: 1, colours: 1 };
  /** Did she say the word? "solid yellow" and "plain navy" EXCLUDE the prints; bare
   *  "yellow" offers the colour beside them. Both are one list either way -- this only
   *  decides what is in it. */
  function saysSolid(q) {
    return String(q || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').split(/\s+/)
      .some(function (w) { return !!SOLID_WORD[w]; });
  }
  /** The query with the filter words taken out, for the FABRIC half of the search.
   *  ⚠️ OR "solid" IS SEARCHED FOR AS THOUGH IT WERE A PRINT. No fabric name or tag
   *  contains it, so under the AND rule every photographed yellow answered only one of
   *  two terms and the whole catalogue fell into Related under a heading saying these
   *  were not matches. It is a filter word; it belongs to the solid half. */
  function fabricQuery(q) {
    return String(q || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').split(/\s+/)
      .filter(function (w) { return w && !SOLID_WORD[w]; }).join(' ');
  }
  /** The colour directions a query names, in the order she typed them.
   *
   *  ⚠️ "blue floral" IS NOT A COLOUR REQUEST, AND THIS IS THE FUNCTION THAT SAYS SO.
   *  Codex, 2026-09-16: a query naming a print must prioritise the prints rather than
   *  insert an unrelated plain block beside them. So EVERY word has to be a colour word
   *  or this offers nothing at all -- one non-colour term and the whole query is about
   *  fabrics.
   *  ⚠️ AND THE SAME CHOICE IS NEVER OFFERED TWICE. "navy blue" names one thing: the
   *  more specific word wins and the family it belongs to is not repeated beside it. */
  function solidQuery(q) {
    var raw = String(q || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').split(/\s+/);
    var ix = solidShades();
    var hits = [], wantAny = false, ok = true;
    raw.forEach(function (w) {
      if (!w) return;
      if (SOLID_WORD[w]) { wantAny = true; return; }
      var s = ix[w] ||
              (w.length > 3 && w.slice(-1) === 's' ? ix[w.slice(0, -1)] : null);
      if (!s) { ok = false; return; }
      hits.push(s);
    });
    if (!ok) return [];
    if (!hits.length) {
      /* "solid" on its own is a request to see the palette, so it IS the palette. */
      return wantAny
        ? SOLID_FAMILIES.map(function (f) { return { fam: f.key, refine: '' }; })
        : [];
    }
    var byFam = {}, order = [];
    hits.forEach(function (h) {
      if (!(h.fam in byFam)) { byFam[h.fam] = h; order.push(h.fam); }
      else if (h.refine && !byFam[h.fam].refine) byFam[h.fam] = h;
    });
    return order.map(function (k) { return byFam[k]; });
  }
  /** Words a query is actually made of. Lower-cased, de-punctuated, de-pluralised. */
  function searchTerms(q) {
    var raw = String(q || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').split(/\s+/);
    var out = [];
    raw.forEach(function (w) {
      if (!w) return;
      /* ⚠️ A NAIVE PLURAL STRIP IS ENOUGH HERE AND A STEMMER WOULD BE WORSE. The
         vocabulary is a hundred fabric names and a few dozen tags; "dots" must find
         `dots` and "flowers" must find `floral`, and both go through SEARCH_SYNONYM
         first. Stemming "leaves" to "leav" would break the one it is needed for. */
      var syn = SEARCH_SYNONYM[w];
      if (!syn && w.length > 3 && w.slice(-1) === 's') {
        syn = SEARCH_SYNONYM[w.slice(0, -1)];
      }
      out.push(syn || w);
    });
    return out;
  }
  /** How one fabric answers one term. 2 = its own name or code, 1 = a reviewed tag,
   *  0.5 = a tag nobody has checked, 0 = no. */
  function searchHit(f, term) {
    var meta = FABRIC_SEARCH[f.code] || {};
    if (f.code === term) return 3;
    var name = String(f.name || '').toLowerCase();
    /* ⚠️ BEING THE THING SHE TYPED BEATS MERELY CONTAINING IT, AND THIS TIER IS WHY.
       Every hit on a name scored a flat 2, so "White" the solid tied with fifteen prints
       whose names contain the word, and the tiebreak is the supplier code -- which put
       the one fabric actually called White at position SIXTEEN of nineteen, in a row that
       scrolls sideways. Nuno, on a handset 2026-09-16: "searching for white returns
       'Request a white solid'; why not just return the white option?" It did return it;
       nobody could reach it. A whole-name match is the least ambiguous signal this
       function has, and it was being thrown away. */
    if (name === term) return 3;
    if (name.indexOf(term) > -1) return 2;
    var inList = function (a) {
      return !!(a && a.some(function (x) { return x === term || x.indexOf(term) > -1; }));
    };
    if (inList(meta.c)) return 1;
    if (inList(meta.m) || inList(meta.p)) return meta.rm ? 1 : 0.75;
    if (inList(meta.cu)) return 0.5;
    return 0;
  }
  /** Search `pool`. Returns { exact, related, terms } -- every term matched, or not all.
   *  ⚠️ "blue floral" MEANS BOTH, WHICH IS WHY THIS IS AN AND AND NOT AN OR. A fabric
   *  answering only one of two terms is a RELATED suggestion, never a match; an OR would
   *  make every two-word query broader than each of its words, which is the opposite of
   *  what typing a second word means. */
  function fabricSearch(pool, q) {
    var terms = searchTerms(q);
    var exact = [], related = [];
    if (!terms.length) return { exact: [], related: [], terms: terms };
    pool.forEach(function (f) {
      var scores = terms.map(function (t) { return searchHit(f, t); });
      var worst = Math.min.apply(null, scores);
      var total = scores.reduce(function (a, b) { return a + b; }, 0);
      /* ⚠️ THREE OUTCOMES, NOT TWO, AND THE THIRD IS WHAT STOPS A DEAD END. Answering
         EVERY term through a reviewed tag is a match. Answering every term but through a
         tag nobody has checked, OR answering only some of them, is RELATED -- shown, and
         labelled as not a match. A two-word query that returns nothing while each word
         alone returns plenty is the same dead end this file already removed from the
         recommender, and for the same reason: "nothing" is indistinguishable from "we do
         not stock it". */
      if (worst >= 1) exact.push({ f: f, score: total });
      else if (total > 0) related.push({ f: f, score: total });
    });
    var byScore = function (a, b) {
      return (b.score - a.score) || (parseInt(a.f.code, 10) - parseInt(b.f.code, 10));
    };
    exact.sort(byScore);
    related.sort(byScore);
    return { exact: exact.map(function (x) { return x.f; }),
             related: related.map(function (x) { return x.f; }), terms: terms };
  }

  function solidSelected(slot) {
    return parseSolid(state[slot]);
  }
  /** The palette, the refinements for whichever family is open, and the note field. */
  function solidsPanelHtml(slot) {
    var sel = solidSelected(slot);
    var openKey = state.solidOpen || (sel ? sel.fam.key : '');
    var open = SOLID_BY_KEY[openKey];
    /* ⚠️ THE EXPLAINER SITS BELOW THE PALETTE, AND IT IS NOT A STYLE CHOICE. It is three
       lines -- 79px on a 390px handset -- and it used to sit between the search field and
       the colours. `fitFabricPreview()` sizes the suit preview from whatever is LEFT after
       the chrome above the first choice, and with that block in the way the answer fell
       below FABPREV_FLOOR (76), got floored, and the surplus pushed the first row of
       colours 32px BEHIND the sticky action bar. Measured on the Ir Al Sol dev theme at
       390x659, 2026-09-16: the same picker without the palette open clears by +20, which
       is FABPREV_SLACK exactly, so the fit was never wrong -- it had run out of room.
       Below the palette it costs the preview nothing, and it is better placed: it explains
       what happens AFTER she picks a direction, which is what the match line under it says
       too. Moving it back above the swatches reintroduces the defect. */
    var out =
      '<div class="mky__swatches mky__swatches--row mky__solids" data-rail="solids">' +
      SOLID_FAMILIES.map(function (f) {
        /* ⚠️ THE ROW IS PRESSED FOR THE FAMILY SHE HAS CHOSEN, NOT FOR THE ONE SHE IS
           READING. Opening a family's refinements must never read as an answer; only a
           refinement or "Use this colour direction" records one. */
        var on = !!(sel && sel.fam.key === f.key);
        return '<button class="mky__swatch mky__solid--fam" type="button" data-solid="' +
               f.key + '" aria-pressed="' + on + '">' +
               '<span class="mky__solid" style="background:' + f.hex + '"></span>' +
               '<b>' + f.name + '</b></button>';
      }).join('') + '</div>';
    if (open) {
      /* ⚠️ A FEW NAMED REFINEMENTS, NEVER A COLOUR WHEEL OR A HEX FIELD. A continuous
         picker invites a precision we cannot honour: what she gets is whatever Nunez
         actually have in that direction, photographed and sent to her to approve. And
         "Help me choose" is a real answer -- the family on its own, with no refinement --
         rather than a way out of the question. */
      out += '<div class="mky__refine" data-family="' + open.key + '">' +
             '<p class="mky__nearhead text-sm text-subdued">' + open.name +
             ' &middot; which direction?</p>' +
             '<p class="mky__actions mky__actions--quiet">' +
             (open.refine || []).map(function (r) {
               var on = !!(sel && sel.refine && sel.refine[0] === r[0]);
               return '<button class="mky__reset" type="button" data-refine="' + r[0] +
                      '" aria-pressed="' + on + '">' + r[1] + '</button>';
             }).join('') +
             '<button class="mky__reset" type="button" data-refine="" aria-pressed="' +
             (!!(sel && sel.fam.key === open.key && !sel.refine)) + '">' +
             (open.refine && open.refine.length ? 'Help me choose' : 'Use ' +
              open.name.toLowerCase()) + '</button>' +
             '</p></div>';
    }
    if (sel) {
      /* ⚠️ THIS LINE NAMES THE CHOICE; IT DOES NOT CARRY A CAVEAT. The picker had a
         "not a fabric yet" sentence for one build and Nuno removed it (2026-09-17): it
         could not be made readable at 390x659 with a store header above the studio --
         the preview was already at its 76px floor and the sentence's second line still
         sat behind the action bar -- and the RECORD screen already says "STILL TO
         CONFIRM / Solid fabric confirmation needed" at the moment she saves or reserves,
         which is the moment that decides anything.
         ⚠️ THIS DEVIATES FROM CODEX'S BRIEF ITEM 1 AND THEIR 2026-09-17 RULING, both of
         which asked for that note here. It is Nuno's call and is recorded as one. If it
         comes back it needs a position that clears the bar; see the handoff. */
      out += '<p class="mky__matchhead mky__matchhead--near mky__pending">Your color: ' +
             solidName(state[slot]) + '</p>' +
             '<label class="mky__savelabel mky__solidnotefield" for="mky-solidnote">' +
             'Anything else about the color? (optional)' +
             '<input type="text" id="mky-solidnote" maxlength="120" value="' +
             escAttr(state.solidNotes[slot] || '') +
             '" placeholder="Softer than the block above, say"></label>';
    }
    return out;
  }
  /** Bind the palette. `slot` is the state field being filled. */
  function bindSolidsPanel(slot) {
    Array.prototype.forEach.call(el.step.querySelectorAll('[data-solid]'), function (b) {
      b.addEventListener('click', function () {
        var key = b.dataset.solid;
        state.solidOpen = key;
        /* ⚠️ TAPPING A COLOUR RECORDS IT. THE REFINEMENTS NARROW AN ANSWER THAT ALREADY
           EXISTS; THEY NO LONGER GATE ONE.
           Until 2026-09-16 only black, white and cream -- the only three families with no
           named directions -- recorded on tap, and every other family opened its row and
           wrote NOTHING. Nuno, on a handset and then on a desktop: "I can only select the
           first 3 color... the selection doesn't seem to stick."
           ⚠️ AND WHAT MADE IT READ AS BROKEN RATHER THAN UNFINISHED: the PREVIOUS answer
           stayed on screen. Tap cream, then blue, and the palette still pressed cream, the
           match line still said "Cream - illustrative color", and the URL -- which this
           page tells her IS her design -- still said `solid:cream`, while she was looking
           at blue's directions. The screen contradicted the tap instead of ignoring it.
           ⚠️ THE STANDING RULE IS "OPENING A FAMILY MUST NEVER READ AS AN ANSWER", and
           this does not break it: that rule is about BROWSING not committing, and browsing
           this screen is SCROLLING THE PALETTE, not tapping a colour. A tap is deliberate.
           Nothing is fabricated either -- a family with no refinement is already a valid
           answer, the one "Help me choose" writes. Nuno approved the override 2026-09-16.
           ⚠️ RE-TAPPING THE FAMILY SHE IS ALREADY IN KEEPS HER DIRECTION. Without this,
           tapping Blue to re-read the directions after choosing Navy would silently demote
           `solid:blue:navy` back to `solid:blue`. */
        var cur = solidSelected(slot);
        var keep = (cur && cur.fam.key === key && cur.refine) ? cur.refine[0] : '';
        setSolid(slot, key, keep);
        renderKeepingPlace();
      });
    });
    Array.prototype.forEach.call(el.step.querySelectorAll('[data-refine]'), function (b) {
      b.addEventListener('click', function () {
        setSolid(slot, state.solidOpen, b.dataset.refine);
        renderKeepingPlace();
      });
    });
    var note = document.getElementById('mky-solidnote');
    if (note) {
      /* Written to state as she types, like the unlisted-bust field: re-rendering would
         destroy the field she is typing into and take the caret with it. */
      note.addEventListener('input', function () {
        state.solidNotes[slot] = note.value.slice(0, 120);
        syncUrl();
      });
    }
  }
  /** Record a colour direction in `slot`.
   *  ⚠️ IT OCCUPIES A FABRIC ASSIGNMENT INSIDE THE SAME TWO-FABRIC LIMIT. A pending colour
   *  is not a placeholder for a fabric; it IS the fabric assignment, unconfirmed. Codex,
   *  2026-09-15: it must not become a fabricated code and must not disappear from the
   *  submitted specification. */
  function setSolid(slot, famKey, refineKey) {
    var fam = SOLID_BY_KEY[famKey];
    if (!fam) return;
    /* The two placeholder codes keep working, so a link saved before this existed still
       means what it meant. See SOLID_BY_LEGACY. */
    state[slot] = (fam.legacy && !refineKey) ? fam.legacy : solidCode(famKey, refineKey);
    if (slot === 'fabric') state.fabricGone = '';
    if (state.fabricGoneSlots) delete state.fabricGoneSlots[slot];
  }

  /** One photographed swatch.
   *  ⚠️ `slot` IS NOT OPTIONAL DECORATION, AND LEAVING IT OUT WAS A REAL DEFECT. The
   *  pressed state was compared against `state.fabric` unconditionally, so a fabric
   *  chosen for the BOTTOM, the reverse face or the crop -- through search, where these
   *  tiles are the result rows -- rendered with nothing pressed. She tapped, the suit
   *  redrew, and the tile she had just chosen looked exactly like the eleven she had not.
   *  ⚠️ AND IT IS NEVER PASSED TO `map` BARE. `pool.map(fabricTile)` hands the ARRAY
   *  INDEX in as `note`, which printed a running number under every swatch from the
   *  second onward on "Browse all fabrics" -- 1, 2, 3, ... down a hundred-tile grid, live
   *  on the published theme until 2026-09-16. Always call it through a wrapper. */
  function fabricTile(f, note, slot) {
    return '<button class="mky__swatch" type="button" data-code="' + f.code +
           '" aria-pressed="' + (f.code === state[slot || 'fabric']) + '">' + swatchFace(f) +
           '<b>' + f.name + '</b>' +
           (note ? '<em class="mky__miss">' + note + '</em>' : '') + '</button>';
  }
  /** A colour direction drawn as a swatch: the same box, the same pressed state, the same
   *  confirmation button. Codex, 2026-09-16: "Give solid tiles the same dimensions,
   *  selection treatment, preview behavior, and confirmation interaction as photographed
   *  swatches." A customer browses and selects a solid exactly as she browses and selects
   *  a print; what separates a confirmed fabric from a colour awaiting confirmation lives
   *  in the note under the preview and in what gets saved, not in a second flow.
   *  ⚠️ IT IS STILL NOT ALLOWED TO LOOK LIKE A PHOTOGRAPH. `.mky__solid` is a flat
   *  block with a hairline, and the word `Solid` is on the label so the tile says what
   *  kind of thing it is without anyone having to infer it from the absence of texture. */
  function solidTileHtml(slot, famKey, refineKey) {
    var fam = SOLID_BY_KEY[famKey];
    if (!fam) return '';
    var code = solidCode(famKey, refineKey);
    var p = parseSolid(state[slot]);
    var on = !!(p && p.fam.key === famKey &&
                (p.refine ? p.refine[0] : '') === (refineKey || ''));
    return '<button class="mky__swatch mky__swatch--solid" type="button" ' +
           'data-solidpick="' + escAttr(famKey + (refineKey ? ':' + refineKey : '')) +
           '" aria-pressed="' + on + '">' +
           '<span class="mky__solid" style="background:' + fam.hex + '"></span>' +
           '<b>' + escAttr(solidName(code) || fam.name) + ' &middot; Solid</b></button>';
  }

  /** A CATALOGUE row, and only a catalogue row. A `solid:` request is not one; ask
   *  `slotFace()` when you need something to draw or to name. */
  function fabricBy(code) {
    for (var i = 0; i < FABRICS.length; i++) if (FABRICS[i].code === code) return FABRICS[i];
    return null;
  }
  /** What a fabric ASSIGNMENT looks like on screen: a photographed swatch, or a colour
   *  direction we have not confirmed yet. Null when the slot is empty.
   *
   *  ⚠️ EVERY RENDERER AND EVERY LABEL GOES THROUGH HERE, WHICH IS WHY IT RETURNS THE
   *  SAME SHAPE FOR BOTH. `fillFor`, `edgeFor`, `swatchFace` and `lookSvg` already branch
   *  on `hex` vs `image` -- that is how the two assumed solids have always drawn -- so a
   *  colour request drops into the existing artwork with no new drawing code and no
   *  invented photograph.
   *  ⚠️ `pending` IS THE ONE FIELD NOTHING MAY DROP. It is what separates "Butterfly
   *  print, code 5283" from "Bright yellow, which we still have to find and photograph
   *  for you". A surface that shows the name without the pending note is claiming stock
   *  we have not got.
   *  ⚠️ AND THE LEGACY PLACEHOLDERS COME BACK PENDING TOO. 9001/9002 are `source=assumed`
   *  in the register, never confirmed by a swatch post, so a design naming one is in
   *  exactly the same position as a new colour request and is told so. */
  function slotFace(code) {
    if (!code) return null;
    var p = parseSolid(code);
    if (p) {
      return { code: code, name: solidName(code), hex: p.fam.hex, pending: true,
               solidFamily: p.fam.key, solidRefine: p.refine ? p.refine[0] : '',
               /* The three measured axes a renderer reads. A flat colour has no pattern,
                  so two of them are zero by definition; lightness is estimated from the
                  hex exactly the way build_swatch_picker.py estimates it for 9001/9002,
                  and is used for nothing but deciding whether a pale block needs a
                  hairline edge. */
               boldness: 0, variety: 0, busyness: 0,
               lightness: parseInt(p.fam.hex.slice(1, 3), 16) / 255 };
    }
    var f = fabricBy(code);
    if (!f) return null;
    /* ⚠️ A CATALOGUE ROW WITH A `hex` AND NO PHOTOGRAPH IS AN ASSUMED SOLID. Handled
       above via SOLID_BY_LEGACY for the two we ship; this guard catches any future one
       the generator adds from a `source=assumed` register row, so a new placeholder
       cannot quietly present itself as confirmed stock. */
    if (f.hex && !f.image) {
      var clone = {};
      Object.keys(f).forEach(function (k) { clone[k] = f[k]; });
      clone.pending = true;
      return clone;
    }
    return f;
  }
  /** Does this assignment still need a real fabric found and approved? */
  function slotPending(code) {
    var x = slotFace(code);
    return !!(x && x.pending);
  }
  /** Every slot in this design that is still a colour direction rather than a fabric. */
  function pendingSlots() {
    var out = [];
    [['fabric', state.fabric], ['fabricBottom', bottomFabricCode()],
     ['fabricInside', insideFabricCode()],
     ['fabricCrop', cropSlotCode()]].forEach(function (pair) {
      if (pair[1] && slotPending(pair[1])) out.push(pair[0]);
    });
    return out;
  }
  /** A shape this list actually contains: the wanted one, else the list's first. */
  function snapShape(pairs, want) {
    for (var i = 0; i < pairs.length; i++) if (pairs[i][0] === want) return want;
    return pairs[0][0];
  }
  function label(pairs, v) {
    for (var i = 0; i < pairs.length; i++) if (pairs[i][0] === v) return pairs[i][1];
    return v;
  }

  /** Real arithmetic over the real option counts. It is the claim, so never inflate it. */
  function stillOpen() {
    var tops = topsFor().length;
    var n = 1;
    n *= state.topShape ? 1 : tops;
    n *= (isSingle() || state.bottomShape) ? 1 : BOTTOMS.length;   // unchosen: still open
    n *= isAfter('sizes') ? 1 : SIZES.length * SIZES.length;
    // fabric is only ever set by choosing one, so the value itself is the signal.
    // Before that, the fabric answers narrow the pool rather than eliminating it.
    /* ⚠️ COUNT WHAT SHE CAN HAVE. This number is a CLAIM about how many suits are still
       possible, so a sold-out fabric in it makes the claim false. */
    var offers = offerable().length;
    n *= state.fabric ? 1
       : (isAtOrAfter('fabric') ? Math.min(6, offers) : Math.max(1, offers));
    return n;
  }

  var el = {};
  ['rail','stage','step','back','next','summary','open','prog','progn','progfill'].forEach(function (k) {
    el[k] = document.getElementById('mky-' + k);
  });
  el.body = document.querySelector('.mky__body');
  el.builtSlot = document.getElementById('mky-built-slot');
  el.save = document.getElementById('mky-save');
  el.imgTop = document.getElementById('img-top');
  el.imgCrop = document.getElementById('img-crop');
  el.fillCrop = document.getElementById('fill-crop');
  el.imgBottom = document.getElementById('img-bottom');
  el.fillTop = document.getElementById('fill-top');
  el.fillBottom = document.getElementById('fill-bottom');
  el.caveat = document.getElementById('mky-caveat');
  el.lede = document.getElementById('mky-lede');
  el.bodyPane = document.getElementById('mky-bodypane');
  el.reset = document.getElementById('mky-reset');
  el.nav = document.querySelector('.mky__nav');
  el.actions = document.getElementById('mky-actions');
  el.actionSel = document.getElementById('mky-actionsel');
  el.pilotHead = document.getElementById('mky-pilothead');
  el.pilotChapter = document.getElementById('mky-pilotchapter');
  el.pilotCount = document.getElementById('mky-pilotcount');
  el.pilotMenu = document.getElementById('mky-pilotmenu');
  el.pilotMenuBody = document.querySelector('#mky-pilotmenu .mky__pilotmenubody');
  el.pilotRail = document.getElementById('mky-pilotrail');
  el.pilotDesign = document.getElementById('mky-pilotdesign');
  el.pilotDesignBody = document.getElementById('mky-pilotdesignbody');
  el.reference = document.getElementById('mky-reference');
  el.referenceHome = el.reference ? el.reference.parentNode : null;
  el.referenceNext = el.reference ? el.reference.nextSibling : null;
  el.railHome = document.querySelector('.mky__rail');
  /* ⚠️ THE HOME AND THE ANCHOR ARE CAPTURED ONCE, BEFORE ANYTHING HAS MOVED, which is the
     lesson `referenceHome`/`referenceNext` above already record: `appendChild` on the way
     back would leave the dock after whatever followed it, and the desktop order would
     drift one step per trip to a deck screen and back. */
  el.helpDock = document.getElementById('mky-helpdock');
  el.helpHome = el.helpDock ? el.helpDock.parentNode : null;
  el.helpNext = el.helpDock ? el.helpDock.nextSibling : null;
  el.pilotMenu = document.getElementById('mky-pilotmenu');
  (function () {
    /* Forwards to the one real Start again, so there is still one handler and one
       confirm. See the comment beside the button in the section. */
    var b = document.getElementById('mky-pilotreset');
    if (b) b.addEventListener('click', function () {
      if (el.pilotMenu) el.pilotMenu.open = false;
      if (el.reset) el.reset.click();
    });
  })();
  /* ⚠️ SCOPED TO `#mky-save`, AND IT WAS PAGE-WIDE UNTIL 2026-09-17. `.mky__submitrow`
     is a STYLE, and round 20 added a second form that legitimately wants the same one --
     the unfinished-design inquiry. A bare `querySelector` takes the FIRST match in the
     document, and that form sits above the save section, so `el.submitRow` silently
     became the INQUIRY's row: `placeBottomRow()` then moved Back and Start again into a
     `display:none` container the moment she pressed Save, and the record screen lost both
     escape routes. Exactly the failure the note above placeBottomRow() records from the
     first time -- "the controls were in the right parent and 0x0" -- and `record.py`
     caught it the same way, by measuring rather than by looking.
     ⚠️ SO THE RULE IS: ANY `el.*` LOOKUP THAT NAMES A CLASS RATHER THAN AN ID MUST BE
     SCOPED TO ITS SECTION. There are two contact forms on this page now, and they share
     a stylesheet on purpose. */
  el.submitRow = document.querySelector('#mky-save .mky__submitrow');

  /* ⚠️ SKIP TO THE QUESTION, NOT TO THE PAGE. The theme's own `skip-to-content` link
     clears the site header in one stop and lands on `#main` -- which on this template is
     still above the chapter rail and the progress dots, so on a 19-step flow she tabs
     past 19 dots to reach what she is being asked. Measured 2026-09-13.

     ⚠️ THE TARGET IS LOOKED UP ON EVERY CLICK, and that is the whole reason this is not a
     bare anchor. render() replaces `#mky-step` wholesale, so the heading that exists now
     is not the one that will exist after Continue, Back, or a resumed link; a reference
     captured at load would go stale on the first screen change.

     ⚠️ `tabindex="-1"` IS SET HERE RATHER THAN IN THE TEMPLATE for the same reason -- the
     heading is built by render() -- and it is -1 so the heading can take focus
     programmatically without ever becoming a Tab stop of its own.

     preventDefault, because letting the anchor navigate would put `#mky-step` in the URL
     and the URL IS the saved design. */
  (function bindSkipToQuestion() {
    var skip = document.getElementById('mky-skip');
    if (!skip) return;
    skip.addEventListener('click', function (e) {
      var step = document.getElementById('mky-step');
      if (!step) return;                       // nothing to skip to; let the href try
      e.preventDefault();
      var target = step.querySelector('.mky__q') || step;
      target.setAttribute('tabindex', '-1');
      try { target.focus({ preventScroll: false }); } catch (err) { target.focus(); }
      /* Scrolled as well as focused: focus alone can leave the heading under a sticky
         header on a phone, which is the same dead end from the other direction. */
      if (target.scrollIntoView) target.scrollIntoView({ block: 'center' });
    });
  })();
  el.progdots = document.getElementById('mky-progdots');
  el.looksPane = document.getElementById('mky-lookspane');
  el.looksRail = document.getElementById('mky-looksrail');
  el.looksPos = document.getElementById('mky-lookspos');
  el.figPane = document.getElementById('mky-figpane');
  el.figImg = document.getElementById('mky-figimg');
  el.figMarks = document.getElementById('mky-figmarks');
  el.figNote = document.getElementById('mky-fignote');
  el.suitPane = document.getElementById('mky-suitpane');
  el.suitFrame = el.suitPane ? el.suitPane.querySelector('.mky__suitframe') : null;
  el.suitImgA = document.getElementById('mky-suitimg-a');
  el.suitImgB = document.getElementById('mky-suitimg-b');
  el.suitImgC = document.getElementById('mky-suitimg-c');
  el.suitNote = document.getElementById('mky-suitnote');
  el.bodyImg = document.getElementById('mky-bodyimg');
  el.svg = document.getElementById('preview-stage');
  /* THE PANES' HOME POSITIONS. The fabric screens borrow `#preview-stage` and
     `#mky-suitpane` into the question column on a phone (see placeFabricPreview), and
     "put it back" has to mean the exact position it came from -- an appendChild on the
     way out would walk them one place down the stage's children on every trip.

     ⚠️ A MARKER, NOT A CAPTURED `nextSibling`, AND THE DIFFERENCE BROKE THE WHOLE SCREEN
     ON A REAL THEME. The first version stored `el.suitPane.nextSibling` and re-homed with
     `insertBefore(pane, thatNode)`. The node after the suit pane is the reference block --
     and `paintPilotChrome()` RELOCATES the reference block into the "Your design so far"
     disclosure on a phone. So the anchor stopped being a child of the stage, `insertBefore`
     threw `NotFoundError`, and because this runs on the FIRST LINE of render() every
     subsequent render died: tapping a swatch did nothing, the search box did nothing, the
     browse controls did nothing. Found on the dev theme 2026-09-16; invisible to 2086 local
     assertions because the harness never moves that block and the desktop layout never
     moves it at all.

     ⚠️ SO THE ANCHOR IS A NODE NOTHING ELSE OWNS. An empty comment inserted right after
     each pane, at wiring time. Nothing reads it, nothing moves it, and its parent is the
     stage for the life of the page. Do not "simplify" this back to a sibling reference:
     any sibling here is a node some other feature is entitled to relocate. */
  function homeMarker(node) {
    if (!node || !node.parentNode) return null;
    var m = document.createComment('mky-home');
    node.parentNode.insertBefore(m, node.nextSibling);
    return m;
  }
  el.svgHomeAt = homeMarker(el.svg);
  el.suitHomeAt = homeMarker(el.suitPane);
  el.top = document.getElementById('piece-top');
  el.bottom = document.getElementById('piece-bottom');
  el.crop = document.getElementById('piece-crop');

  /* ⚠️ HOVER PREVIEW IS FOR A POINTER, AND A FINGER IS NOT ONE.
     iOS Safari synthesises `mouseenter` on the FIRST tap, and also focuses the button. If
     that handler changes the page, Safari WITHHOLDS the click and waits for a second tap.
     "What kind of suit?" needed two taps for exactly this reason: showStyle() repaints the
     stage AND rebuilds the progress row through paint() -> paintProgress() -> paintDots(),
     because changing the kind changes how many screens the flow has. The body and fit
     previews only swap the stage image and its marks, which is why they were the only ones
     that still took one tap. Nuno, 2026-09-12, on a phone.
     ⚠️ THE GATE COVERS `focus` TOO. A tap focuses the button, so leaving focus ungated
     would fire the same preview by the other door and suppress the same click.
     ⚠️ EVALUATED ONCE, AT WIRING TIME, NOT PER EVENT. It is a device capability, not a
     state: a phone does not grow a mouse mid-flow, and re-asking matchMedia on every
     pointer entering every option is work for an answer that cannot change. */
  var CAN_HOVER = !window.matchMedia ||
                  window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ── THE BODY, ILLUSTRATED ─────────────────────────────────────────────────────
     Seven generated PNG pairs (converted to WebP for delivery), front on the left and a
     right-facing side on the right, mapped to fourteen answers.

     ⚠️ THIS REPLACED TWO EARLIER ATTEMPTS AND THE REASON IS THE SAME BOTH TIMES: neither
     had a real reference for human anatomy. A generate-then-trace pipeline produced lumpy
     outlines; a parametric SVG built from landmarks I chose produced, in Nuno's words,
     proportions where "she'd be extraterrestrial". Artwork made by something that can
     draw a body is the fix, and no amount of tuning replaces it. Do not reinstate the
     procedural renderer.

     ⚠️ NOTHING ACCUMULATES, AND THAT IS A DELIBERATE TRADE. Seven fixed images cannot
     compose the 135 combinations a live silhouette could, so each question shows an
     EXAMPLE of its own answer with every other axis neutral. Confirmed 2026-09-11. The
     caption says so out loud rather than implying the picture is her. */
  var BODY_ASSETS = (function () {
    /* ⚠️ DO NOT SWALLOW THIS SILENTLY. On a parse failure every body image renders blank
       and the You chapter looks broken with nothing saying why -- the caller sees a valid
       empty object and treats it as "no artwork configured". The likeliest cause is a
       theme setting that introduced a quote into the attribute, which is invisible in the
       editor. */
    try {
      return JSON.parse(root.dataset.bodyAssets || '{}');
    } catch (e) {
      if (window.console) console.warn('Design Studio: body artwork map failed to parse; the You chapter will show no illustrations.', e);
      return {};
    }
  })();

  /* ⚠️ HEIGHT NOW HAS FIVE DRAWN IMAGES, AND NOTHING IS SCALED ANY MORE.
     It used to be ONE image at five zoom levels, which is a picture of a smaller woman,
     not a shorter one -- a zoom shrinks the bust and the shoulders along with the legs.
     v4 of the pack draws the five separately: the torso and upper-thigh lengths differ
     and the horizontal widths deliberately do not. Every `scale`/`scaleY` is gone.

     `shift` is the ONLY transform, and it is a POSITION offset, never a size one. The
     five sources were generated with different amounts of whitespace under the hem, so
     each is pushed down by its own measured amount to put every thigh hem on ONE line at
     96.68% of the frame. Without it the
     figures float at five different heights and the comparison says nothing.

     Values come from the pack's manifest.json and were re-measured here off the artwork:
     hem rows 888 / 913 / 897 / 944 / 979 of 1024, each carried to 990. Re-measure if the
     artwork is ever regenerated; do NOT auto-fit or trim per image, which would undo
     exactly the proportion differences these five exist to show.

     ⚠️ IT IS AN ILLUSTRATIVE STATURE, NOT A PREDICTION OF HER PROPORTIONS. Standing
     height does not determine torso length; the caption says so and must keep saying so. */
  var BODY_MAP = {
    height: {
      'u52':     { asset: 'h1', shift: 10.06 },
      '52-54':   { asset: 'h2', shift: 7.52 },
      '55-57':   { asset: 'h3', shift: 9.08 },
      '58-510':  { asset: 'h4', shift: 4.49 },
      '511p':    { asset: 'h5', shift: 1.07 }
    },
    volume: { upper: { asset: 'upper' }, balanced: { asset: 'neutral' },
              lower: { asset: 'lower' } },
    waist:  { straight: { asset: 'straight' }, gentle: { asset: 'neutral' },
              defined: { asset: 'defined' } },
    /* ⚠️ `soft` HAS ITS OWN DRAWING NOW -- it used to share `neutral` with "gently
       curved", so two different answers to two different questions showed one picture.
       `rounded` is v4's redraw: its lower-abdominal fullness is far more restrained than
       the version it replaces (the traced curve peaks at 71.2% of the tile, where the old
       one peaked at 76.5%). BELLY below is measured off these files and must be
       re-measured with them. */
    tummy:  { flat: { asset: 'flat' }, soft: { asset: 'soft' },
              round: { asset: 'rounded' } }
  };
  var Q5_DETAIL_SRC = root.dataset.q5Detail || '';
  /* ⚠️ ONE PHOTOGRAPH, AND THE BAND IS DRAWN ON TOP OF IT. It was three generated
     images differing only in the band, which cost two extra downloads to say something
     two numbers say better, and carried the generator's shading drift between them --
     the reason the brief had to forbid a crossfade. One image cannot drift, so the swap
     is now a CSS change with no image load at all.

     ⚠️ THE KEYS ARE Q.torso's ANSWER KEYS AND MUST STAY THAT WAY. The saved answer, the
     `builds` string and this table are three readings of one value; renaming here
     silently drops the band rather than erroring.

     `top` and `height` are percentages of the frame, MEASURED off the three generated
     details before they were retired, so the band sits exactly where Nuno approved it:
     both started at row 207 of 1024, and ran 91 and 140 rows deep. Re-measure against
     the artwork if the photograph is ever re-cropped; do not nudge them by eye.

     ⚠️ `standard` HAS NO BAND, AND THAT IS NOT THE SAME AS BEING SELECTED. paintFig is
     passed the saved value or null; null draws this same bandless state without writing
     anything to state, so the flow cannot advance off a picture. */
  var Q5_DETAIL = {
    'standard': { marks: [],
                  alt: 'Close view of the swimsuit at the hip, with no length allowance marked.' },
    'slight':   { marks: [{ top: 20.2, height: 8.9, left: 25.5, width: 49, kind: 'band' }],
                  note: 'Pink shows where extra length is allowed.',
                  alt: 'Close view of the swimsuit at the hip, with a narrow pink band marking a small length allowance.' },
    'long':     { marks: [{ top: 20.2, height: 13.7, left: 25.5, width: 49, kind: 'band' }],
                  note: 'Pink shows where extra length is allowed.',
                  alt: 'Close view of the swimsuit at the hip, with a wide pink band marking a larger length allowance.' },
    /* ⚠️ THE SAME PHOTOGRAPH WITH NOTHING MARKED, AND NO NEW ARTWORK WAS GENERATED
       FOR IT. Codex's brief is explicit that an unknown answer creates no adjustment, so
       there is no allowance to draw -- and drawing one would be inventing a claim the
       answer refuses to make. The caption says what the empty frame means, so the card is
       not read as a missing image.
       ⚠️ IT IS NOT `standard`'s STATE REUSED. They look the same and they say different
       things; sharing the entry is how the two would come to share a caption. */
    'unknown':  { marks: [], note: 'Nothing is marked: no length adjustment selected.',
                  alt: 'Close view of the swimsuit at the hip, with no length allowance marked.' }
  };

  /* ⚠️ ONE IMAGE FOR ALL FIVE ANSWERS. `view` is kept on every state because the next
     pack may legitimately need a second view, and a one-key map is cheaper to widen than
     a hard-coded src is to re-generalise. */
  var Q6_ASSETS = { exterior: root.dataset.q6Exterior || '' };
  /* ⚠️ NO RECTANGLES HERE ANY MORE. Every answer on this question draws a CURVE over the
     whole photograph, from Codex's 2026-09-13 overlay pack (revision 2). The boxes that
     used to sit here said "somewhere in this area"; a spill that follows the neckline and
     an arrow that points up say what the answer actually means. The old geometry, the
     traced SHELF_PATH and the retired interior photograph are all gone -- do not
     reinstate any of them from git history without re-reading that pack.

     ⚠️ THE PATHS ARE IN THE IMAGE'S OWN PIXELS, NOT IN PERCENTAGES. viewBox is
     `0 0 1536 1024`, which is the exact size of `mky-q6-exterior-transparent-v1.webp`, and every mark is
     laid over the FULL frame (top/left 0, 100% x 100%). That is the pack's coordinate
     system verbatim, so a path can be checked against the artwork by opening both at
     1536x1024. Fitting them into a tight box would require renormalising every number and
     is exactly what this rewrite removed.

     ⚠️ THE PATHS CARRY NO STROKE WIDTH, AND THE PACK'S 6 IS NOT WHAT SHIPS. `--q6` pins
     the stroke at 2.5px with `vector-effect`, which is question 7's `--line` exactly. The
     first version took the pack's `stroke-width: 6` literally in image pixels and let it
     scale -- 1.64px on desktop, 1.17px on a phone -- and Nuno read it as too thin against
     Q7 two screens later. See the CSS for why "it scales with the picture" lost.

     ⚠️ THE SHELF IS A HIDDEN LAYER, NOT A PINK PANEL. Its wash is 3.5% and it is dashed
     for the same reason question 7's wire is unfilled: it is inside the garment, so the
     drawing is a note about construction and must not read as fabric she is being
     offered. The meaning now lives in the option's own helper copy ("hidden inside the
     top"), which is where she reads it BEFORE hovering, rather than in a caption that
     only appears once she does.

     ⚠️ `none` IS EXCLUSIVE AND HAS NO MARK. "Tops fit me fine" cannot coexist with a
     complaint, and highlighting nothing is the honest picture of it. */
  /** One mark covering the whole photograph, drawn in the image's own 1536x1024 pixels.
   *  ⚠️ SHARED BY QUESTION 6 AND QUESTION 8, AND `kind` IS THE ONLY DIFFERENCE. Q8 arrived
   *  in the same full-frame form a day later and its helper was character-for-character
   *  this one; two copies would have been two places to get the viewBox wrong. The KINDS
   *  stay separate on purpose -- `--q6` and `--q8` have their own CSS blocks so a change
   *  to one question's treatment cannot reach the other. Shared geometry, isolated
   *  styling; do not merge the CSS to match.
   *
   *  ⚠️ AND DO NOT CONVERT QUESTIONS 5, 7 OR 9 TO THIS SCHEME FOR CONSISTENCY'S SAKE. Two
   *  coordinate systems coexist in this file on purpose: Q6 and Q8 draw in the photograph's
   *  own 1536x1024 pixels; Q5, Q7 and Q9 draw 0-100 paths inside local percentage boxes.
   *  That asymmetry looks like drift and is not. Asked directly, Codex ruled on 2026-09-13:
   *  "do not migrate Q5 or Q7 to full-frame coordinates solely for coherence. Their current
   *  coordinate systems are implementation details; revisit only if they cause alignment,
   *  scaling or maintenance problems." So the trigger for touching them is a BUG -- a mark
   *  that lands wrong, a stroke that scales wrong, a box nobody can re-derive -- and never
   *  tidiness. Q9's treatments specifically will be reconsidered as part of its own pending
   *  redesign, not normalised ahead of it. */
  var FULL_FRAME_VIEWBOX = '0 0 1536 1024';
  function fullFrameMark(kind, paths) {
    return [{ top: 0, height: 100, left: 0, width: 100,
              kind: kind, viewBox: FULL_FRAME_VIEWBOX, paths: paths }];
  }
  /* Spilling is overflow at the NECKLINE and the UNDERARMS -- a curve just above the
     scoop, then one short curve down each underarm edge. It is not a bracket around the
     lower cups; Nuno rejected that reading on 2026-09-13.
     ⚠️ THE UNDERARM CURVES MOVED INWARD IN REVISION 3, and the neckline did not. At the
     pack's original x they sat far enough outside the garment to read as marking the ARM
     rather than the top's edge. They now start at 30.01% / 69.73% and end at 27.34% /
     72.40% of the frame -- about 1.8-3.3px closer at 300px, and slightly shorter.
     ⚠️ THEY ARE STILL OFFSET FROM THE GARMENT ON PURPOSE. These are open EDGE markers,
     and the overhang is the same rule the rest of the flow uses: a mark that stops exactly
     at the fabric edge reads as a piece of pink fabric. Do not close the gap the rest of
     the way. Codex, revision 3. */
  var Q6_SPILL = 'M537 414 C566 475 653 530 763 533 C873 530 960 475 989 414 ' +
                 'M461 436 C445 466 429 500 420 530 ' +
                 'M1071 436 C1087 466 1103 500 1112 530';
  /* Two strap brackets, each drawn as the strap's own two edges rather than as a box, over
     a 6% wash so the pair reads as one part. */
  var Q6_DIG_WASH = 'M452 215 L527 195 L543 395 L465 403 Z ' +
                    'M1005 195 L1080 215 L1067 403 L989 395 Z';
  var Q6_DIG      = 'M466 216 L452 220 L465 400 L480 399 ' +
                    'M513 203 L527 200 L542 390 L528 392 ' +
                    'M1019 203 L1005 200 L990 390 L1004 392 ' +
                    'M1066 216 L1080 220 L1067 400 L1052 399';
  /* The inner lining, dashed: a schematic of a hidden layer, not a construction pattern. */
  var Q6_SHELF = 'M492 444 C548 533 647 579 763 583 C883 579 978 531 1034 444 ' +
                 'C1064 505 1074 603 1035 670 C944 689 848 655 763 655 ' +
                 'C678 655 582 689 491 670 C452 603 462 505 492 444 Z';
  /* ⚠️ ARROWS, AND THEY POINT UP. Riding up is MOVEMENT of the garment, which a firm band
     across the underbust described as a fix rather than as the problem. Arrowheads stop at
     the lower edge of the top. Nothing animates: the photograph never moves. */
  var Q6_RIDEUP = 'M568 858 L568 760 M545 785 L568 760 L591 785 ' +
                  'M964 858 L964 760 M941 785 L964 760 L987 785';
  var Q6_STATE = {
    'spill':     { view: 'exterior', marks: fullFrameMark('q6', [{ d: Q6_SPILL }]),
                   alt: 'The swimsuit top, with pink curves along the neckline and both underarm edges, where it spills over.' },
    'dig':       { view: 'exterior', marks: fullFrameMark('q6', [{ d: Q6_DIG_WASH, cls: 'wash' },
                                                    { d: Q6_DIG }]),
                   alt: 'The swimsuit top, with both shoulder straps bracketed in pink.' },
    'nosupport': { view: 'exterior', marks: fullFrameMark('q6', [{ d: Q6_SHELF, cls: 'lining' }]),
                   alt: 'The swimsuit top, with a dashed pink outline across the bust showing the shelf bra built in inside it.' },
    'rideup':    { view: 'exterior', marks: fullFrameMark('q6', [{ d: Q6_RIDEUP }]),
                   alt: 'The swimsuit top, with two pink arrows beneath it pointing upward, showing the top riding up.' },
    'none':      { view: 'exterior', marks: [], note: '',
                   alt: 'The swimsuit top, with nothing highlighted.' },
    /* Nothing marked, and the alt says why rather than repeating `none`'s. See the note
       on Q5_DETAIL's `unknown`. */
    'unknown':   { view: 'exterior', marks: [], note: '',
                   alt: 'The swimsuit top, with nothing highlighted, for an answer that is not yet known.' }
  };
  /* ⚠️ STATIC, AND THE FOUR ANSWERS DELIBERATELY SHARE IT. Nuno, 2026-09-13: one line that
     explains what the pink IS, not a readout that changes under the pointer. The per-state
     caption `nosupport` used to carry moved into the option's helper copy. */
  var Q6_NOTE = 'Pink illustrates the fit issue or added support.';

  /* ⚠️ QUESTIONS 7 AND 8 DRAW THINGS THE PHOTOGRAPH DOES NOT CONTAIN, which is why both
     say so in the caption. The garment in the picture has no wire, no seam and no pad;
     these are GUIDES to where construction goes, not features you can see on it. The
     `line` kind is unfilled for exactly that reason -- filling a wire would assert a
     part that is not in the photograph.
     ⚠️ AND THE SEAMLESS GARMENT IS WHY QUESTION 7's "no" IS DRAWN RATHER THAN POINTED
     AT. The brief asked to highlight the existing shaping seams; measured, the exterior
     has 80 edge pixels inside the whole garment -- there are no seams there to
     highlight. So both answers draw their own construction, and the only difference is
     which lines: two arcs under the bust for the wire, an underbust seam plus a princess
     seam per cup for the seams that replace it.
     Coordinates are frame percentages; the center of the garment is 49.6%. */
  /* ⚠️ ONE SHAPE, TWO TREATMENTS, AND THE SHAPE IS MEASURED. A U cupping under each
     breast, rising at both ends: the bust apexes sit at 36.2% and 63.2% of the frame
     across rows 52-56%, and the garment runs 26.5%-72.5% there. The right cup is the
     left one with `flip`, so the numbers exist once.
     ⚠️ THE OFFSETS BELOW ARE IN INCHES, CONVERTED, NOT NUDGED BY EYE. Nuno asked for
     "about an inch" down and an inch inward, so the scale was derived: the bust apexes
     are 27.0% of the frame apart and a bust-point separation is about 7.75in, giving
     1in = 3.48% of frame WIDTH = 5.23% of frame HEIGHT.
     ⚠️ THOSE TWO NUMBERS ARE NOT INTERCHANGEABLE. The frame is 3:2, so one percent of
     height is a different real distance from one percent of width; using the width
     figure for a vertical move shrinks it by a third. Cross-checked: the scale makes the
     underband front 11.9in and bust-point to underband 3.1in, both plausible. */
  var Q7_CUP  = 'M2 14 Q12 94 50 94 Q88 94 98 26';
  var Q7_NOTE = 'Guide lines, not stitching you will see.';
  function cupLine(kind) {
    /* ⚠️ A HALF INCH BACK OUT, AND THE GAP IS THE POINT. One inch inward put the two
       cups' inner ends together at the center; a wire gore is a real thing, but drawn
       here it read as ONE line across both breasts rather than two cups. Backed off
       0.5in (1.74% of frame width) each, leaving a 2.4% gap so they stay two lines.
       Do not close this gap again. */
    return [{ top: 54.2, height: 18.0, left: 28.8, width: 19.6, kind: kind, path: Q7_CUP },
            { top: 54.2, height: 18.0, left: 50.8, width: 19.6, kind: kind, path: Q7_CUP, flip: true }];
  }
  var Q7_STATE = {
    'yes': { marks: cupLine('line'),
             note: 'Solid: where the wires would sit.',
             alt: 'The swimsuit top, with a solid pink line cupping under each breast, showing where underwires would sit.' },
    'no':  { marks: cupLine('dotted'),
             note: 'Dotted: the seams that shape it instead.',
             alt: 'The swimsuit top, with a dotted pink line cupping under each breast, showing the shaping seams used instead of wires.' },
    'any': { marks: [], note: 'We will choose the construction for your selected shape.',
             alt: 'The swimsuit top, with nothing highlighted.' }
  };

  /* ⚠️ ALL THREE PADDING ANSWERS ARE ABOUT THE SAME TWO CUPS, so marking the cups would
     say nothing. The difference drawn is the one that is physically real: a removable
     pad goes in through a POCKET and comes back out, a sewn-in one does not. Same pad
     shape in both, dashed with a pocket slot for the one that comes out, solid for the
     one that stays. "None at all" draws nothing, because nothing is there.
     Rejected on the way, both rendered before being dropped: pads floating in the margin
     beside the garment (the frame leaves ~15% and they read as clip art cut off by the
     edge) and pads half-slid out of the cup (they land on the arms). */
  /* ⚠️ THE PADS SIT ON THE BREASTS, NOT ON THE CUPS ABOVE THEM, and the outline is
     curves rather than the polygon it started as: a pad has no corners, and the
     polygon's flat segments read as a cut-out rather than a soft insert. Placed from the
     same measurement as the cup line, then moved one inch down and one inch inward with
     the conversion documented above Q7_CUP.
     ⚠️ HALF AN INCH FURTHER OUT THAN MEASURED, ON PURPOSE. Centerd exactly on the bust
     apexes the two pads nearly touched at the sternum, which is not where a cup sits.
     1.74% of frame width outward each, leaving a 5.6% gap.

     ⚠️ THE PADS DID NOT MOVE IN CODEX'S 2026-09-13 PACK, AND THAT WAS CHECKED, NOT TAKEN
     ON TRUST. The pack states the two pads are the existing boxes converted to full-frame
     pixels rather than new geometry, and the arithmetic agrees exactly: the old boxes were
     left 29.2% / 52.4%, top 54.7%, 17.6% x 16.8% of a 1536x1024 frame holding a 0-100
     outline, so its point (50,1) lands at x 583.680 / y 561.848 and (97,40) at
     x 710.738 / y 628.941 -- the pack's numbers to three decimals. Nothing here is a
     re-placement. Re-run that conversion before believing any future pack that says the
     same thing.
     ⚠️ THE RIGHT PAD IS NO LONGER A `flip` OF THE LEFT. It is its own path now, because
     the pack ships both explicitly; the two are still mirror images about the garment's
     center at 49.6% and must stay so. */
  var Q8_PAD_L = 'M583.68 561.848 C645.857 561.848 710.738 587.653 710.738 628.941 ' +
                 'C710.738 670.228 653.967 730.44 583.68 730.44 ' +
                 'C513.393 730.44 456.622 670.228 456.622 628.941 ' +
                 'C456.622 587.653 521.503 561.848 583.68 561.848 Z';
  var Q8_PAD_R = 'M940.032 561.848 C1002.209 561.848 1067.09 587.653 1067.09 628.941 ' +
                 'C1067.09 670.228 1010.319 730.44 940.032 730.44 ' +
                 'C869.745 730.44 812.974 670.228 812.974 628.941 ' +
                 'C812.974 587.653 877.855 561.848 940.032 561.848 Z';
  /* ⚠️ THE OPENING IS A SHORT SLANT, AND IT REPLACED AN ARC THAT READ AS A CREASE. The
     retired `Q8_SLOT` was a curve in its own box above each pad; on the photograph it
     read as a fold in the fabric rather than as the mouth a pad comes out of.
     ⚠️ THE ARROWS POINT UP AND OUTWARD, INTO THE UPPER OUTER CUP -- not out of the front
     of the garment. A pad leaves through an opening in the LINING; an arrow aimed at the
     viewer would draw a hole in a suit she is being offered. Codex, 2026-09-13.

     ⚠️ THE ARROW NO LONGER CROSSES THE PAD OR THE OPENING, AND THAT IS THE WHOLE POINT OF
     THIS GEOMETRY. The pack had the arrow START INSIDE the dashed pad and travel out
     through both the outline and the marker, which drew an x rather than a pad leaving a
     pocket -- odd at 420px and illegible at 300px. Nuno, 2026-09-13: have the arrow start
     outside the dashed line. The three parts now sit in SEQUENCE along one removal axis,
     which is the order the thing actually happens in:
         pad (dashed)  ->  opening marker  ->  gap  ->  arrow tail  ->  arrow head
     Measured clearances, and they are the reason these numbers and not others: arrow to
     pad outline 48.2 units (9.4px at 300px), arrow to opening marker 30.1 (5.9px), marker
     to pad outline 18.0 (3.5px). Nothing intersects at either width. Re-run that check
     before nudging any of them.
     ⚠️ THE HEAD GREW ON PURPOSE, from 21 units to 30. At the pinned 2.5px stroke the
     pack's arms rendered 4.1px on the 300px frame -- 1.6x the stroke width, which is a dot
     and not an arrowhead. 30 units is 5.9px there (2.3x) and 8.2px at 420px (3.3x). The
     shaft is 72 units; the head cannot simply keep growing, because above y~450 the
     arrowhead leaves the garment's edge and lands on the arm.
     ⚠️ THE AXIS AND THE OPENINGS ARE STILL CODEX'S, UNCHANGED. Only the arrows moved.
     The right side is the left mirrored about x=762.5 (x' = 1525 - x), which is the same
     mirror the pack used -- checked against its own right-hand numbers. */
  var Q8_OPENING = 'M477 569 L515 552 M1010 552 L1048 569';
  var Q8_ARROW   = 'M486 532 L462 464 M457 494 L462 464 L485 483 ' +
                   'M1039 532 L1063 464 M1068 494 L1063 464 L1040 483';
  var Q8_NOTE = 'Pads shown where they sit in the cups.';
  var Q8_STATE = {
    'removable': { marks: fullFrameMark('q8', [{ d: Q8_PAD_L, cls: 'pad-out' },
                                               { d: Q8_PAD_R, cls: 'pad-out' },
                                               { d: Q8_OPENING },
                                               { d: Q8_ARROW }]),
                   note: 'Arrows show pads sliding out through inner pockets.',
                   alt: 'The swimsuit top, with a dashed pad outlined in each cup and an arrow through a short opening showing the pad sliding out.' },
    'sewn':      { marks: fullFrameMark('q8', [{ d: Q8_PAD_L, cls: 'pad' },
                                               { d: Q8_PAD_R, cls: 'pad' }]),
                   note: 'Solid outlines show pads fixed inside the lining.',
                   alt: 'The swimsuit top, with a pad outlined in each cup, sewn in place.' },
    'no':        { marks: [], note: '',
                   alt: 'The swimsuit top, with nothing highlighted.' }
  };
  /* Which half of the pair a TILE shows. The stage always shows both; a tile has room for
     one, so each question gets the view its own answer is legible in. */
  var TILE_VIEW = { volume: 'front', waist: 'front', tummy: 'side' };
  /* Landmark rows and bar widths as a percentage of the TILE BOX, derived from the
     artwork: the figure spans 2.5%-94.5% of the box, and its bust, waist and hip rows sit
     at 0.292, 0.442 and 0.776 of its own height. */
  /* ⚠️ A BAR'S WIDTH IS THE ANSWER, NOT DECORATION. One fixed width per landmark made
     all three waists look identical -- the bars said "waist" but not "how much", which is
     the entire question. Widths below are MEASURED per image at the landmark row and then
     scaled by a constant so the bar overhangs the body enough to read as a marker: the
     measured body widths are waist 43.2 / 39.1 / 36.5 and bust 47.6 / 43.9 / 43.2 percent
     of the tile, and the factors are anchored to the one value that already looked right
     on the rendered tile rather than picked. */
  var BAR_ROW = { bust: 34.6, waist: 43.2, hip: 69.8 };
  var BAR_W = {
    bust:  { upper: 71.4, balanced: 65.9, lower: 64.8 },
    hip:   { upper: 65.8, balanced: 66.0, lower: 71.0 },
    waist: { straight: 53.1, gentle: 48.1, defined: 44.9 }
  };
  /* ⚠️ THE STOMACH GETS A CURVE, NOT A BAR. Its question is about a front-to-back PROFILE,
     so a horizontal rule says nothing about it -- the earlier version traced the belly and
     that is what made the three readable. These are the REAL front contours of the side
     view, traced 1:1 off the artwork, never drawn by hand and never exaggerated: the
     rightmost inked pixel of the right-hand figure on 18 rows spanning 426-741 of 1024,
     converted into tile percentages by the same sprite maths as .mky__art.

     ⚠️ RE-MEASURED FOR v4 AND THE THREE NOW SIT CLOSER TOGETHER. Peaks are 69.0 / 70.1 /
     71.2 percent of the tile; the retired rounded drawing peaked at 76.5, so the spread
     between flat and rounded fell from about 8 points to 2.6 -- roughly 3px on a 137px
     tile. That is what "substantially more restrained" costs, and it is the artwork's
     honest profile. If the three stop reading as three, the fix is the DRAWING, not a
     multiplier here. (The bars above may be scaled for overhang because a bar is a marker
     laid over a body; this curve IS the body's edge, so scaling it would be a lie.) */
  var BELLY = {
    'flat': 'M68.4 44.8 L68.4 46.7 L68.4 48.7 L68.4 50.7 L68.4 52.6 L68.6 54.6 L68.6 56.5 L68.4 58.5 L68.3 60.4 L67.9 62.4 L67.4 64.2 L66.9 66.2 L67.0 68.1 L67.7 70.1 L68.1 72.0 L68.6 74.0 L68.8 75.9 L69.0 77.9',
    'soft': 'M68.1 44.8 L68.1 46.7 L68.3 48.7 L68.8 50.7 L69.5 52.6 L70.1 54.6 L70.1 56.5 L70.0 58.5 L69.4 60.4 L68.7 62.4 L67.9 64.2 L67.0 66.2 L66.7 68.1 L67.6 70.1 L68.0 72.0 L68.4 74.0 L68.7 75.9 L68.8 77.9',
    'rounded': 'M68.1 44.8 L68.4 46.7 L68.8 48.7 L69.7 50.7 L70.5 52.6 L71.1 54.6 L71.2 56.5 L70.9 58.5 L70.4 60.4 L69.4 62.4 L68.4 64.2 L67.2 66.2 L66.7 68.1 L67.4 70.1 L68.0 72.0 L68.4 74.0 L68.7 75.9 L68.8 77.9'
  };
  /* Balanced carries BOTH bars on purpose: "bust and hips about even" is a statement
     about two places at once, and one bar cannot say it. */
  var TILE_BARS = {
    volume: { upper: ['bust'], balanced: ['bust', 'hip'], lower: ['hip'] },
    waist:  { straight: ['waist'], gentle: ['waist'], defined: ['waist'] },
    tummy:  { flat: ['belly'], soft: ['belly'], round: ['belly'] }
  };

  function bodyEntry(key, value) {
    var m = BODY_MAP[key];
    return (m && m[value]) || null;
  }

  /* ⚠️ DECODE BEFORE THE POINTER ARRIVES. Swapping src on hover against a cold cache
     blanks the frame for a beat, which reads as a bug on the one interaction this chapter
     exists for. */
  var bodyPreloaded = {};
  function preloadBody(key) {
    var m = BODY_MAP[key] || {};
    Object.keys(m).forEach(function (k) {
      var url = BODY_ASSETS[m[k].asset];
      if (!url || bodyPreloaded[url]) return;
      bodyPreloaded[url] = true;
      var im = new Image();
      im.decoding = 'async';
      im.src = url;
    });
  }

  var seatPreloaded = false;
  function preloadSeat() {
    var u = root.dataset.q9Seat;
    if (seatPreloaded || !u) return;
    seatPreloaded = true;
    var im = new Image();
    im.decoding = 'async';
    im.src = u;
  }

  var q6Preloaded = false;
  function preloadQ6() {
    if (q6Preloaded) return;
    q6Preloaded = true;
    if (!Q6_ASSETS.exterior) return;
    var im = new Image();
    im.decoding = 'async';
    im.src = Q6_ASSETS.exterior;
  }

  var q5Preloaded = false;
  function preloadQ5() {
    if (q5Preloaded || !Q5_DETAIL_SRC) return;
    q5Preloaded = true;
    var im = new Image();
    im.decoding = 'async';
    im.src = Q5_DETAIL_SRC;
  }

  /** One <path> per group, created once and then only ever re-pointed. */
  function setPath(g, d) {
    var pth = g.firstElementChild;
    if (!pth) {
      pth = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      g.appendChild(pth);
    }
    pth.setAttribute('d', d);
  }

  function clearPath(g) { if (g && g.firstElementChild) g.firstElementChild.remove(); }

  /** Show the example for the CURRENT question. `preview` is {key, value} while an
   *  option is hovered or focused, else null, in which case the saved answer shows. */
  function paintBody(preview) {
    /* ⚠️ GUARDED BECAUSE blur FIRES AFTER THE CHAPTER HAS ALREADY MOVED ON. Clicking an
       option focuses that button; render() then replaces el.step's markup, destroying the
       focused element, which fires `blur` -> restore() -> paintBody(null) -- on the FIT
       step, where the first line below wiped the garment the same paint() had just drawn.
       The `fill` attribute survived, which is why the stage looked blank rather than
       broken. Nothing outside the You chapter may repaint the body. */
    if (!inBody()) return;
    el.top.innerHTML = ''; el.bottom.innerHTML = '';
    var key = screenId();
    var val = (preview && preview.key === key) ? preview.value : state[key];
    var entry = bodyEntry(key, val) || bodyEntry(key, BODY_DEFAULT[key]);
    if (el.bodyImg && entry) {
      var url = BODY_ASSETS[entry.asset];
      if (url && el.bodyImg.getAttribute('src') !== url) el.bodyImg.setAttribute('src', url);
      /* ⚠️ TRANSLATE ONLY, AND ONLY ON HEIGHT. `shift` exists solely to bring the five
         height images onto one baseline; any other question must reset it to none or the
         previous question's offset rides along into a picture it means nothing in. */
      el.bodyImg.style.transform = entry.shift ? 'translateY(' + entry.shift + '%)' : 'none';
      el.bodyImg.alt = Q[key] ? Q[key].q + ' ' + label(Q[key].opts, val || '') : '';
    }
    /* ⚠️ NOTHING IS WRITTEN UNDER THE DRAWING ANY MORE, and one thing went with it.
       The caption used to carry the line disclaiming that the artwork predicts her
       proportions -- which v4 needed more than v3 did, because five separately drawn
       torsos claim a proportion where five zoom levels of one drawing only claimed
       "shorter". Nuno removed it 2026-09-11 as noise on hover. If it is ever wanted
       back, the chapter lede or the question hint is the place, NOT a caption that
       reappears under every option she passes the pointer over. */
    el.summary.textContent = bodyLine();
    if (el.caveat) { el.caveat.textContent = ''; el.caveat.hidden = true; }
  }

  /** Puts one photograph and a list of marks into the figure pane. `entry` is a row out
   *  of any of the FIG tables: an image, marks in frame percentages, an alt line and a
   *  note. `dense` lightens the fills when several answers are drawn at once.
   *  ⚠️ MARKS ARE REBUILT, NOT TOGGLED. A question can need none, one, two or four of
   *  them, so a pool of reused elements would have to grow and shrink anyway.
   *  Rebuilding also guarantees nothing survives from the previous answer. */
  /** The marks of one state, as markup. Split out of paintFigure so the phone's card
   *  deck can draw the SAME overlay geometry into its own frame instead of carrying a
   *  second copy of it. There is one set of Q6 coordinates and one renderer for them;
   *  a card that drew its own would be free to drift from the stage the desktop shows.
   *  ⚠️ THE CARDS RELY ON `.mky__figmark--q6` BEING FRAME-RELATIVE. Every mark here is
   *  positioned in percentages of its container and every path is drawn in the
   *  photograph's own viewBox, so the identical string is correct at 420px, at 300px and
   *  in a 240px card. Nothing here may grow a pixel unit. */
  function figMarkHtml(marks) {
    return (marks || []).map(function (m) {
      /* `flip` mirrors a path instead of storing its mirror image: the wire, the
         princess seam and the pad are all symmetrical about the garment's center, and
         two copies of the same numbers is two places to get them wrong. */
      /* ⚠️ `paths` AND `path` ARE NOT THE SAME SHAPE OF DATA. `path` is one outline in a
         0-100 box stretched to the mark (questions 5, 7, 8, 9). `paths` is a LIST drawn
         in the mark's own `viewBox`, each with its own treatment class, and it keeps the
         image's aspect the way `object-fit: contain` does instead of stretching -- which
         is what question 6's full-frame overlays need, because their coordinates are the
         photograph's own pixels. Do not merge the two branches. */
      var inner = '';
      if (m.paths) {
        inner = '<svg viewBox="' + (m.viewBox || '0 0 100 100') + '" aria-hidden="true" ' +
                'focusable="false">' +
                m.paths.map(function (p) {
                  return '<path' + (p.cls ? ' class="mky__figpath--' + p.cls + '"' : '') +
                         ' d="' + p.d + '"/>';
                }).join('') + '</svg>';
      } else if (m.path) {
        inner = '<svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" ' +
                'focusable="false"><path d="' + m.path + '"' +
                (m.flip ? ' transform="translate(100,0) scale(-1,1)"' : '') + '/></svg>';
      }
      return '<i class="mky__figmark' + (m.kind ? ' mky__figmark--' + m.kind : '') +
             '" style="top:' + m.top + '%;height:' + m.height +
             '%;left:' + m.left + '%;width:' + m.width + '%">' + inner + '</i>';
    }).join('');
  }

  function paintFigure(src, entry, dense) {
    if (el.figImg) {
      if (src && el.figImg.getAttribute('src') !== src) el.figImg.setAttribute('src', src);
      el.figImg.alt = entry.alt || '';
    }
    if (el.figPane) el.figPane.dataset.dense = dense ? 'true' : 'false';
    if (el.figMarks) el.figMarks.innerHTML = figMarkHtml(entry.marks);
    if (el.figNote) el.figNote.textContent = entry.note || '';
  }

  /* ⚠️ ONE TABLE AND ONE PAINTER FOR ALL FOUR PHOTOGRAPH QUESTIONS. There were two
     painters when there were two such questions; adding wire and padding as copies would
     have been four places to fix the next blur bug, the next hover bug and the next
     layout bug. Everything that differs between them is DATA: which photograph, which
     marks, what the caption says, and whether the answer is multi-select. */
  /* ⚠️ THE ONLY QUESTION THAT TURNS THE GARMENT AROUND. The seat question is about what
     happens at the BACK, and every other photograph in the flow is a front view -- so
     this one gets its own image and says "back view" in the caption before she is asked
     to read it. Codex's pack, one clean photograph, annotations drawn here.
     ⚠️ `initial` IS A DISPLAY STATE, NOT AN ANSWER. It gives the unanswered question its
     "back view" caption without writing anything to state, the same way question 5's
     `standard` fallback does. Nothing here selects `no`, and `no` is not this. */
  /* ⚠️ TWO ARROWS, AND THEY REPLACED AN OVAL AND TWO LEG TRACES THAT COMPETED. Codex's
     2026-09-13 pack: the seat oval said WHERE and the leg lines said WHERE TOO, so the
     picture carried three marks and no direction. The arrows say what actually happens --
     the rear leg edges creep UP AND INWARD, exposing more seat.
     ⚠️ THEY CROSS THE LEG EDGE ON PURPOSE. Every other mark in this flow is written to
     avoid sitting on a garment line, because a mark that traces an edge reads as a NEW
     edge -- which is why the retired leg curves were 1.6px, thinner than anything else in
     the flow, so they would not read as a new hem. An arrow is the exception: crossing the
     edge is how it says which way that edge is moving, and there is no other pink outline
     left here for it to collide with. Do not "fix" the crossing.
     ⚠️ THE CAPTION NOW DESCRIBES THE PROBLEM, NOT THE FIX. It used to read "More room
     through the seat, with softer elastic at the leg openings", which is the SOLUTION --
     already said by the answer's own helper copy ("Fuller seat and a softer leg elastic.").
     The drawing shows what goes wrong; the option text says what we do about it. Do not
     put the fix back in the caption, or the screen says it twice and shows it never.
     ⚠️ NOTHING IS FILLED AND NOTHING MAY BE. The pack before this one specified a
     translucent rectangle; built and reviewed, it sat high on the briefs, read as a
     WAISTBAND adjustment, and its fill read as a panel of fabric. That finding outlived the
     shape it was about.
     Geometry is the pack's, verbatim, in the image's own 1536x1024 pixels: tails at
     33.85% / 66.15% across and 74.02% down, tips at 39.19% / 60.81% and 63.48%. The right
     arrow is the left mirrored about x=768 -- checked, exact. Arrowhead arms are 37.4 and
     38.1 units, which is 7.3px at 300px and 10.2px at 420px against the pinned 2.5px
     stroke (2.9x), the most legible head in the flow. */
  var Q9_RIDEUP = 'M520 758 Q550 704 602 650 M566 660 L602 650 L593 687 ' +
                  'M1016 758 Q986 704 934 650 M970 660 L934 650 L943 687';
  var Q9_NOTE = 'Fit example \u2014 back view.';
  var Q9_STATE = {
    'initial': { marks: [], note: Q9_NOTE,
                 alt: 'The back of a swimsuit bottom, shown as a fit example.' },
    'yes':     { marks: fullFrameMark('q9', [{ d: Q9_RIDEUP }]),
                 note: 'Arrows show the fabric riding up at the back.',
                 alt: 'Back view of swimsuit bottoms, with two pink arrows showing the rear leg edges moving upward and inward.' },
    'no':      { marks: [], note: 'No seat adjustment requested.',
                 alt: 'The back of a swimsuit bottom, with nothing highlighted.' },
    /* ⚠️ A DIFFERENT CAPTION FROM `no`, ON PURPOSE. "No seat adjustment requested" is
       what she asked for; this one says nobody knows yet. Same picture, different claim.
       See the note on Q5_DETAIL's `unknown`. */
    /* ⚠️ IT MUST NOT SAY THE SEAT STAYS STANDARD. This read "the seat stays standard
       for now", which is the one thing an unknown answer is not entitled to say: it turns
       "nobody has checked" into a statement about what fits her. Codex, 2026-09-15. */
    'unknown': { marks: [], note: 'Nothing is marked: no seat adjustment selected.',
                 alt: 'The back of a swimsuit bottom, with nothing highlighted.' }
  };

  var FIG = {
    torso:   { src: function () { return Q5_DETAIL_SRC; }, states: Q5_DETAIL,
               fallback: 'standard', note: '' },
    top:     { src: function () { return Q6_ASSETS.exterior; }, states: Q6_STATE,
               note: Q6_NOTE, multi: true },
    wire:    { src: function () { return Q6_ASSETS.exterior; }, states: Q7_STATE,
               note: Q7_NOTE },
    padding: { src: function () { return Q6_ASSETS.exterior; }, states: Q8_STATE,
               note: Q8_NOTE },
    seat:    { src: function () { return root.dataset.q9Seat || ''; }, states: Q9_STATE,
               fallback: 'initial', note: Q9_NOTE }
  };
  function inFig() { return !!FIG[screenId()]; }

  /* ⚠️ THE SUIT-TYPE SCREEN DRAWS PHOTOGRAPHS, NOT THE SILHOUETTE. Four flat-lays out
     of Codex's q10 pack (v2: the crop was re-posed with its sleeves bent inward, because
     the first arrangement read as splayed arms rather than as a folded garment).

     ⚠️ THREE PIECE REUSES THE TWO-PIECE PHOTOGRAPH. There is no separate three-piece
     render and there should not be: the third piece IS the crop, and showing the same
     bikini with the crop beside it is the truest picture of what she gets. It is also
     why this pane needs two image slots when every other stage in the file needs one.

     ⚠️ THESE ARE NOT FABRIC MASKS. The pack is explicit: the renders carry their own
     shading and cut, so a swatch cannot be filled or stretched over them. Fabric is
     chosen three chapters later and is previewed on the SILHOUETTE, which is built for
     it. Do not wire `state.fabric` into this pane. */
  var SUIT_ASSETS = (function () {
    try { return JSON.parse(root.dataset.suitAssets || '{}'); } catch (e) { return {}; }
  })();
  /* Alt text describes the GARMENT, because that is what the question asks her to
     recognize. The pair reads as one alt line in both places it is used. */
  var SUIT_ALT = {
    one:      'A one-piece swimsuit, laid flat.',
    two:      'A bikini top and bottom, laid flat.',
    three:    'A bikini top and bottom, laid flat.',
    monokini: 'A one-piece swimsuit cut away at the sides, laid flat.'
  };
  var SUIT_CROP_ALT = 'A long-sleeve swim crop, laid flat.';
  /* Said in place of a kind name while nothing is chosen, so the picture cannot be read
     as a selection she did not make. */
  var SUIT_UNCHOSEN = 'An example, until you choose.';
  /* Said whenever the render on the stage is not the garment she has specified. */
  var SUIT_STANDIN = 'An example of the kind. Your shapes are named below.';
  function inSuit() { return at('style'); }

  /** Draw the suit-type stage. Reads `state.style`, which is what showStyle() is already
   *  setting and restoreStyle() is already putting back -- so hover and focus preview
   *  come for free and nothing here writes to state.
   *  ⚠️ IT MUST NOT TOUCH SHAPE OR FABRIC. An illustrative picture that reset a saved
   *  shape is the exact failure the You chapter's blur bug was, and the four-field
   *  save/restore in showStyle exists because of it. This function only reads. */
  function paintSuit() {
    /* ⚠️ VALIDATE AGAINST STYLES, NOT AGAINST SUIT_ASSETS. There is no 'three' key in
       the asset map -- three piece is composed from 'two' plus 'crop' -- so asking the
       map "is this a real kind?" answered NO for three piece and fell back to 'two'.
       The images were still right, because they branch on `three` below; the LABEL and
       the alt text were not, so hovering "Three piece" said "Two piece". Caught by
       kind.py, which is the only reason it is not live. */
    /* ⚠️ THIS QUESTION ARRIVES UNANSWERED, AND THE PICTURE MUST NOT ANSWER IT.
       `state.style` starts '' (see the ⚠️ on the state object -- Nuno removed the 'two'
       default on 2026-09-12 because it ticked an option the moment the screen opened).
       The silhouette could draw its two-piece pair into that gap and stay honest, because
       a traced gray ghost reads as a placeholder. A PHOTOGRAPH does not: it reads as the
       suit she has been given. So the artwork is the same either way and the LABEL
       carries the difference -- the same trick Q9's `initial` and Q5's `standard` use. */
    var chosen = label(STYLES, state.style) !== state.style;
    var k = chosen ? state.style : 'two';
    var three = state.style === 'three';
    /* Three piece shows the two-piece artwork; the crop is the second slot. */
    var a = SUIT_ASSETS[three ? 'two' : k] || '';
    if (el.suitFrame) el.suitFrame.dataset.layout = three ? 'three' : 'single';
    if (el.suitImgA) {
      if (a && el.suitImgA.getAttribute('src') !== a) el.suitImgA.setAttribute('src', a);
      el.suitImgA.alt = SUIT_ALT[k] || '';
    }
    if (el.suitImgB) {
      var c = SUIT_ASSETS.crop || '';
      if (three && c) {
        if (el.suitImgB.getAttribute('src') !== c) el.suitImgB.setAttribute('src', c);
        el.suitImgB.alt = SUIT_CROP_ALT;
        el.suitImgB.hidden = false;
      } else {
        el.suitImgB.hidden = true;
        el.suitImgB.alt = '';
      }
    }
    /* ⚠️ THE LABEL NAMES THE KIND, AND IT USED TO NAME THE SHAPE. paint()'s summary
       line prints the top shape, so this screen read "Triangle" -- a shape she has not
       been offered yet, under a picture of a garment type. The kind is the answer to the
       question on screen; the shape is two screens away. */
    if (el.suitNote) el.suitNote.textContent = chosen ? label(STYLES, k) : SUIT_UNCHOSEN;
  }

  /* ⚠️ THE STAGE IS PER GARMENT, NOT PER STYLE, AND THE FIRST VERSION OF THIS GOT IT
     WRONG. It required EVERY shape a style can take to have a render before any of them
     showed, reasoning that a stage flipping between a photograph and a traced ghost as
     she toggles is worse than one medium throughout. That rule belongs to the TILE ROW,
     where the alternatives sit side by side and a half-photographic grid reads as
     unfinished. The stage draws exactly ONE garment, so it can only ever be right or
     wrong about that one -- and holding back a photograph we have, of the very garment
     on screen, because a sibling shape has no render yet, is withholding for nothing.
     Nuno, 2026-09-12, on one piece: Classic had a render and still drew a ghost.

     So: the stage shows a photograph whenever every PIECE of the garment as currently
     chosen has one. Tiles keep the all-or-nothing rule.

     ⚠️ KEYED BY CONTEXT, NOT BY BARE SHAPE ID. 'classic' is a one-piece shape AND a
     two-piece top (where it is labeled Bandeau) -- two different garments under one id.

     Today: one piece Classic, monokini, and a two or three piece sitting on triangle +
     classic brief. Everything else waits on Codex's component renders. */
  var SHAPE_PHOTOS = {
    'one:classic':      SUIT_ASSETS.one || '',
    /* ⚠️ 'one:asymmetrical', NOT 'top:asymmetrical'. They are different garments under one
       word: a one-shoulder ONE PIECE here, an asymmetrical BIKINI TOP in TOPS_TWO. Codex
       flagged it in the pack and it is the reason these keys carry their context. */
    'one:asymmetrical': SUIT_ASSETS.oneAsymmetrical || '',
    'one:monokini':     SUIT_ASSETS.monokini || ''
  };
  var SHAPE_PHOTO_ALT = {
    'one:classic':      SUIT_ALT.one,
    'one:asymmetrical': 'A one-shoulder one-piece swimsuit, laid flat.',
    'one:monokini':     SUIT_ALT.monokini
  };
  function shapePhotoKey(v) { return 'one:' + v; }
  /** True when EVERY shape in a picker has a render, which is the bar for the tile row.
   *  ⚠️ THE TILES KEEP THE ALL-OR-NOTHING RULE and the stage does not. The alternatives
   *  sit side by side here, so one photograph among sketches reads as an unfinished
   *  screen; the stage draws a single garment and can only be right or wrong about it. */
  function tilesArt(kind, pairs) {
    for (var i = 0; i < pairs.length; i++) {
      if (!SHAPE_PHOTOS[kind + ':' + pairs[i][0]]) return false;
    }
    return pairs.length > 0;
  }
  /* ⚠️ THE BIKINI IS ONE IMAGE HOLDING TWO GARMENTS, so it can only stand in for the
     exact pair it depicts. Keyed by that pair rather than by either piece alone: the
     moment Codex delivers separate top and bottom components this table stops growing
     and SHAPE_PHOTOS takes over with `top:`/`bottom:` keys. */
  var PAIR_PHOTOS = { 'triangle+brief': SUIT_ASSETS.two || '' };
  var PAIR_ALT = 'A bikini top and bottom, laid flat.';
  /* ⚠️ COMPONENTS SUPERSEDE THE FUSED PAIR WHEREVER BOTH PIECES EXIST. One render per
     garment, composed at paint time, so switching Triangle to Halter changes the top and
     leaves the bottom exactly where it was -- which the fused image can never do.
     ⚠️ THE KEYS CARRY THEIR CONTEXT for the same reason the one-piece keys do: 'triangle'
     is only ever a two-piece top, but 'classic' is a one-piece shape AND a bandeau, and a
     flat namespace would eventually serve one for the other. */
  /* ⚠️ ONE TABLE, NOT TWO. Components started as their own map and that is a trap: the
     tile gate reads this table, so a second one would leave a completed row still drawing
     sketches while the stage drew photographs, and nothing would say why. Every render in
     the flow is `context:shape` and lives here. */
  SHAPE_PHOTOS['top:triangle'] = SUIT_ASSETS.topTriangle || '';
  SHAPE_PHOTOS['top:halter']   = SUIT_ASSETS.topHalter || '';
  /* ⚠️ 'top:asymmetrical' IS NOT 'one:asymmetrical'. A one-shoulder BIKINI TOP against a
     one-shoulder ONE PIECE -- two garments, one word, and the only thing keeping them
     apart is the context in the key. Codex shipped them in separate packs and said so in
     both READMEs. */
  SHAPE_PHOTOS['top:asymmetrical'] = SUIT_ASSETS.topAsymmetrical || '';
  /* ⚠️ 'top:classic' IS THE BANDEAU. The picker says Bandeau; the stored id is `classic`,
     which is ALSO the classic one-piece's id. Two garments that share a word and a value
     and differ only by the context in this key -- the exact collision these keys exist
     for. Do not add a `bandeau` state value to "fix" the mismatch: the id is what saved
     designs and shared links already carry. */
  SHAPE_PHOTOS['top:classic'] = SUIT_ASSETS.topClassic || '';
  /* ⚠️ 'top:high-neck-crop' IS SLEEVELESS. The long-sleeve crop is the THIRD PIECE of a
     three piece and lives in SUIT_ASSETS.crop; this is a top she picks. Two garments
     called "crop" doing different jobs -- and the flow retired the long-sleeve one from
     TOPS_TWO on 2026-09-12 precisely because it was being read as a two-piece top. */
  SHAPE_PHOTOS['top:high-neck-crop'] = SUIT_ASSETS.topHighNeckCrop || '';
  SHAPE_PHOTOS['top:sports-bra-cut'] = SUIT_ASSETS.topSportsBraCut || '';
  SHAPE_PHOTOS['bottom:brief'] = SUIT_ASSETS.bottomBrief || '';
  SHAPE_PHOTOS['bottom:side-tie'] = SUIT_ASSETS.bottomSideTie || '';
  /* ⚠️ A FRONT VIEW OF THE FULL-BACK STYLE, NOT A PICTURE OF THE BACK. Codex is explicit:
     it must not be read as proof of any particular rear coverage. The one screen in this
     flow that genuinely shows a back is question 9, which has its own photograph. */
  SHAPE_PHOTOS['bottom:high-waisted-full-back-coverage'] = SUIT_ASSETS.bottomHighWaisted || '';
  SHAPE_PHOTOS['bottom:high-thigh-cut'] = SUIT_ASSETS.bottomHighThigh || '';
  SHAPE_PHOTO_ALT['top:triangle'] = 'A triangle bikini top, laid flat.';
  SHAPE_PHOTO_ALT['top:halter']   = 'A halter bikini top with broad straps, laid flat.';
  SHAPE_PHOTO_ALT['top:asymmetrical'] = 'A one-shoulder bikini top, laid flat.';
  SHAPE_PHOTO_ALT['top:classic'] = 'A bandeau bikini top, laid flat.';
  SHAPE_PHOTO_ALT['top:high-neck-crop'] = 'A sleeveless high-neck bikini top, laid flat.';
  SHAPE_PHOTO_ALT['top:sports-bra-cut'] = 'A sports-bra-cut bikini top with broad straps, laid flat.';
  SHAPE_PHOTO_ALT['bottom:brief'] = 'A classic bikini brief, laid flat.';
  SHAPE_PHOTO_ALT['bottom:side-tie'] = 'A side-tie bikini bottom with bows at each hip, laid flat.';
  SHAPE_PHOTO_ALT['bottom:high-waisted-full-back-coverage'] = 'A high-waisted bikini bottom, front view, laid flat.';
  SHAPE_PHOTO_ALT['bottom:high-thigh-cut'] = 'A high-thigh-cut bikini bottom, laid flat.';

  /** The photograph for the garment as currently chosen, or null to draw the silhouette.
   *  ⚠️ THE WINDOW CLOSES AT THE CLOTH PICKER. From FABRIC_PICK on, the stage carries her
   *  actual fabric in `fill-top`/`fill-bottom`, and these renders cannot take a swatch --
   *  they are shaded rasters with their own light and cut, not masks or UV maps (Codex's
   *  pack says so explicitly). The silhouette exists to be fabricked; that is the job it
   *  keeps. Before the picker there is no fabric to lose, so the photograph is strictly
   *  the better picture. */
  function garmentPhoto() {
    /* ⚠️ THE WINDOW CLOSES WHEN THERE IS A FABRIC TO SHOW, NOT AT THE PICKER. The rule
       below used to be `step >= FABRIC_PICK`, which handed the picker to the SVG silhouette
       even before she had chosen anything -- so the empty-result screen, the one screen
       where she has been told we have nothing for her, was also the one showing a flat gray
       ghost instead of the contoured garment she had been looking at for eight screens.
       The reasoning for closing the window was that the stage carries her ACTUAL FABRIC from
       here on. With no fabric there is nothing to carry, so the photograph is strictly the
       better picture, exactly as it is before the picker. It respects her style and shapes
       because `garmentPhoto()` is keyed on them. */
    /* ⚠️ THE WINDOW IS NOW "AFTER THE KIND SCREEN, UNTIL THERE IS A FABRIC", AND THE
       REORDER IS WHY IT CHANGED SHAPE. It used to read `step <= Q_STEPS` -- the body and
       fit chapters plus the kind screen -- because all of those came BEFORE the garment
       was specified. Since 2026-09-15 the body and fit chapters come LAST, by which time
       she has chosen a fabric and the silhouette is carrying it; they are excluded by
       `state.fabric`, and they draw their own stage besides (inBody/inFig return before
       this is consulted). What is left is the rule that was always meant: a photograph
       while there is no fabric to lose, the fabricked silhouette the moment there is. */
    if (at('style') || state.fabric) return null;
    if (isSplit()) {
      /* ⚠️ THE SLOTS ARE JUDGED SEPARATELY, AND THIS USED TO BE ALL-OR-NOTHING. Requiring
         both pieces threw away a render we had: Halter + Side tie has a real halter and
         no side tie, and the whole stage fell back to the fused pair -- which draws a
         TRIANGLE, so the one piece we could have got right was the one she was looking
         at. Nuno, 2026-09-12.

         Each slot now takes her own garment when it exists and a stand-in when it does
         not, and the caption names which half is which. That also means every component
         Codex delivers improves the picture on its own, with no threshold to cross. */
      var topKey = 'top:' + effectiveShape('topShape'),
          botKey = 'bottom:' + effectiveShape('bottomShape');
      var exactTop = !!SHAPE_PHOTOS[topKey], exactBot = !!SHAPE_PHOTOS[botKey];
      /* The stand-ins are the two the pack shipped first. They are chosen by being
         AVAILABLE, not by being typical -- if these two ever lose their renders there is
         no pair to draw and the silhouette is the honest answer. */
      var ct = SHAPE_PHOTOS[topKey] || SHAPE_PHOTOS['top:triangle'];
      var cb = SHAPE_PHOTOS[botKey] || SHAPE_PHOTOS['bottom:brief'];
      if (!ct || !cb) return null;
      var imgs = [ct, cb], layout = 'pair';
      if (state.style === 'three') {
        if (!SUIT_ASSETS.crop) return null;
        imgs.push(SUIT_ASSETS.crop);
        layout = 'pair-three';
      }
      return { images: imgs, layout: layout, exact: exactTop && exactBot,
               exactTop: exactTop, exactBot: exactBot,
               alt: SHAPE_PHOTO_ALT[exactTop ? topKey : 'top:triangle'] || '',
               alt2: SHAPE_PHOTO_ALT[exactBot ? botKey : 'bottom:brief'] || '',
               alt3: SUIT_CROP_ALT };
    }
    /* ⚠️ SINGLE GARMENTS STAY EXACT, and that is a deliberate asymmetry with the pair
       above. A one piece has TWO shapes and they are unmistakably different garments --
       a scoop front against one bare shoulder -- so a stand-in would be arguing with the
       tile she just pressed. The pair's 24 combinations differ by details the render
       cannot show at this size anyway. If Asymmetrical should fall back to the Classic
       render the same way, that is one line here, and it is Nuno's call, not a gap. */
    var k = shapePhotoKey(effectiveShape('topShape'));
    if (!SHAPE_PHOTOS[k]) return null;
    return { images: [SHAPE_PHOTOS[k]], layout: 'single', exact: true,
             alt: SHAPE_PHOTO_ALT[k] || '' };
  }

  /** Put one garment photograph in the suit frame, single layout.
   *  The label stays empty here: unlike the kind screen, the summary line directly below
   *  already names the garment, and two captions saying "Monokini" is one too many. */
  function paintGarmentPhoto(photo) {
    if (el.suitFrame) el.suitFrame.dataset.layout = photo.layout;
    if (el.suitImgA) {
      if (el.suitImgA.getAttribute('src') !== photo.images[0]) el.suitImgA.setAttribute('src', photo.images[0]);
      el.suitImgA.alt = photo.alt || '';
    }
    /* ⚠️ SET THE SRC ONLY WHEN IT CHANGES. Switching Triangle to Halter repaints the
       whole stage, and reassigning the bottom's identical src would make the browser
       re-decode it -- a visible blink on the one piece that is NOT supposed to move. */
    [[el.suitImgB, photo.images[1], photo.alt2], [el.suitImgC, photo.images[2], photo.alt3]]
      .forEach(function (slot) {
        var node = slot[0], src = slot[1];
        if (!node) return;
        if (src) {
          if (node.getAttribute('src') !== src) node.setAttribute('src', src);
          node.alt = slot[2] || '';
          node.hidden = false;
        } else {
          node.hidden = true;
          node.alt = '';
        }
      });
    /* ⚠️ THE CAPTION IS PER SLOT TOO. "An example of the kind" over a picture whose top
       is exactly what she chose undersells it and reads as vaguer than the picture is.
       Empty when both are hers, because the summary line below already names them. */
    if (el.suitNote) {
      var note = '';
      if (!photo.exact) {
        note = photo.exactTop ? 'The top is yours. The bottom is an example.'
             : photo.exactBot ? 'The bottom is yours. The top is an example.'
             : SUIT_STANDIN;
      }
      el.suitNote.textContent = note;
    }
  }

  /* ⚠️ WARM THE SCREEN SHE IS ON, IN THE BRANCH SHE CHOSE -- NOT ALL TWENTY GARMENTS.
     This was `Object.keys(SUIT_ASSETS).forEach(...)`: every garment render, in one burst,
     fired on the kind screen. Against the old lossy white-ground set that cost 0.71 MB and
     nobody noticed. The transparent set is lossless, and the same burst became 4.27 MB on
     the one screen where she has just made her first garment decision -- on a handset, on
     whatever connection she is on. Codex, 2026-09-14, asked for it narrowed.

     ⚠️ NARROWING IS NOT "PRELOAD LESS", IT IS "PRELOAD WHAT A HOVER OR A SWIPE CAN REACH
     FROM HERE". The original comment had the argument right and the scope wrong: a blank
     frame under a passing pointer IS the interaction failing, and on a phone a swipe
     brings the next card into view just as fast. So each group below holds everything
     REACHABLE on its screen -- every option's artwork, plus the component renders needed
     to COMPOSE those options (a three piece's crop rides along with the pair it sits
     over) -- and nothing from a screen she has not reached or a branch she did not pick.

     ⚠️ THE GROUPS ARE BRANCH-DEPENDENT AND THAT IS THE POINT. A one piece never sees
     TOPS_TWO or BOTTOMS; a two piece never sees the monokini or the single-garment
     variation illustrations. Deriving each group from the SAME functions the screen
     renders from -- topsFor(), COVER_TOP/COVER_BOT, VARIATION_ART -- is what stops the
     two drifting: add a shape to a list and its render is warmed with no edit here. */
  var preloadedUrls = {};
  function warm(urls) {
    urls.forEach(function (u) {
      /* ⚠️ KEYED ON THE URL, NOT ON A PER-GROUP FLAG. The groups overlap on purpose --
         the crop is in three of them, a three piece's pair is in two -- and Back is an
         ordinary move in this flow, so a per-group boolean would either refetch on every
         revisit or skip a URL the group genuinely needs. The HTTP cache would absorb a
         repeat, but creating the Image at all costs a decode; this is the cheaper guard
         and it makes "revisiting a question downloads nothing" true rather than likely. */
      if (!u || preloadedUrls[u]) return;
      preloadedUrls[u] = true;
      var im = new Image();
      im.decoding = 'async';
      im.src = u;
    });
  }

  /** Every garment render reachable on `screen`, in the current branch.
   *  `screen` is 'kind' | 'coverage' | 'shapes' | 'variation'. */
  function suitGroup(screen) {
    var A = SUIT_ASSETS, out = [];
    var three = state.style === 'three';
    if (screen === 'kind') {
      /* The four style cards. Three piece draws the TWO-piece render plus the crop
         (suitArtFor), so the crop belongs to this screen even though no card is "crop". */
      return [A.one, A.two, A.monokini, A.crop].concat(desktopOn() ?
        [SHAPE_PHOTOS['top:triangle'], SHAPE_PHOTOS['bottom:brief']] : []);
    }
    if (screen === 'coverage') {
      /* coverPhotoPieces() over all four answers. Skipped entirely on a one piece and a
         monokini in the live flow, but the branch is kept for the same reason
         coverPhotoPieces keeps its own: if the skip is ever lifted the two must agree. */
      if (isSingle()) {
        return Object.keys(COVER_TOP).map(function (v) {
          return SHAPE_PHOTOS['one:' + COVER_TOP[v]];
        });
      }
      Object.keys(COVER_TOP).forEach(function (v) {
        out.push(SHAPE_PHOTOS['top:' + COVER_TOP[v]]);
        out.push(SHAPE_PHOTOS['bottom:' + COVER_BOT[v]]);
      });
      if (three) out.push(A.crop);
      return out;
    }
    if (screen === 'shapes') {
      /* Read off topsFor() and BOTTOMS, the same lists shapeTiles() renders, so a shape
         added to either is warmed without an edit here. */
      topsFor().forEach(function (t) {
        out.push(SHAPE_PHOTOS[(isSingle() ? 'one:' : 'top:') + t[0]]);
      });
      if (isSplit()) {
        BOTTOMS.forEach(function (b) { out.push(SHAPE_PHOTOS['bottom:' + b[0]]); });
        if (three) out.push(A.crop);
      }
      return out;
    }
    if (screen === 'variation') {
      /* ⚠️ A THREE PIECE WARMS NOTHING HERE, AND THAT IS NOT AN OMISSION. Its four cards
         are inline SVG over the shape sprite the page already carries (see
         variationSvgHtml); there is no raster to fetch, and warming the flatlays it will
         never draw would spend three downloads on a handset for pictures nobody sees. */
      if (three) return [];
      var branch = VARIATION_ART[isSingle() ? 'single' : 'separates'] || {};
      return Object.keys(branch).map(function (k) { return A[branch[k]]; });
    }
    return [];
  }

  function preloadSuit(screen) {
    /* ⚠️ THE DEFAULT IS 'kind', NOT "everything". Every caller passes a screen; the
       default exists so a future caller that forgets still warms the smallest useful
       group rather than silently warming none -- a missing preload shows up as a blank
       frame on first hover, which is exactly the failure this function exists to stop. */
    warm(suitGroup(screen || 'kind'));
  }

  /** Draw the CURRENT question's photograph and marks. `preview` is {key, value} while
   *  an option is hovered or focused, else null, in which case the saved answer shows.
   *
   *  ⚠️ MULTI-SELECT SHOWS EVERY CHOSEN ANSWER, not just the latest. Four answers ticked
   *  and one region lit reads as "only this one counted", and the union is the truer
   *  picture: the top really does get all four changes. Hover still isolates, so she can
   *  see which part of a busy picture a line refers to.
   *
   *  ⚠️ NOTHING HERE WRITES TO state. These pictures are examples: the fallback for an
   *  unanswered question draws a neutral image without selecting anything, so the flow
   *  can never advance off an illustration.
   *
   *  ⚠️ GUARDED, because blur fires after the step has already moved on: clicking an
   *  option focuses it, render() destroys that element, and the blur lands here. */
  function paintFig(preview) {
    var key = screenId();
    var cfg = FIG[key];
    if (!cfg) return;
    var showing;
    if (preview && preview.key === key) showing = [preview.value];
    else if (cfg.multi) showing = (state[key] || []).filter(function (v) { return cfg.states[v]; });
    else showing = cfg.states[state[key]] ? [state[key]] : (cfg.fallback ? [cfg.fallback] : []);
    var marks = [];
    showing.forEach(function (v) {
      var e = cfg.states[v];
      if (e) marks = marks.concat(e.marks || []);
    });
    /* One answer on screen explains itself; several share the general line, because four
       sentences stacked under a picture is not a caption any more. */
    var only = showing.length === 1 ? cfg.states[showing[0]] : null;
    var note = only ? (only.note !== undefined ? only.note : cfg.note)
             : (marks.length ? cfg.note : '');
    var alt = only ? only.alt
            : (marks.length ? 'The swimsuit top, with every part you have chosen highlighted in pink.'
                            : 'The swimsuit top, with nothing highlighted.');
    paintFigure(cfg.src(), { marks: marks, alt: alt, note: note }, showing.length > 1);
  }

  /** The bar and the end-state line. Every step has progress, including the ones that
   *  draw a body rather than a suit.
   *  ⚠️ THIS MUST NOT LIVE INSIDE paint(). It did, below the garment painting, and the
   *  You chapter's early return skipped it -- the first four questions showed an empty
   *  track with no count. Called BEFORE the branch so neither path can drop it. */
  /** One dot per step, on the bar.
   *  ⚠️ BACKWARD ONLY. A dot she has not reached is inert: jumping ahead past an
   *  unanswered question is exactly how a design gets assembled out of defaults, which
   *  is the failure the Continue gate exists to prevent. A SKIPPED dot is inert in both
   *  directions -- there is no answer behind it to go back and change, and landing her on
   *  a screen the flow deliberately stepped over would be a bug wearing a feature's hat.
   *  ⚠️ REBUILT EACH TIME, NOT PATCHED. `total` moves mid-flow (picking reversible adds a
   *  fabric to choose), and so does which steps are skipped -- changing the kind of suit
   *  rewrites the whole row. Keeping twenty elements in sync with that is more code than
   *  making twenty. */
  /* ⚠️ THREE ANSWERS REWRITE THE SCREENS AFTER THEM, AND CHANGING ONE FORFEITS THE
     GROUND SHE HAD COVERED. `style` decides which screens exist at all (a monokini skips
     two) and re-snaps both shapes; `coverage` SEEDS topShape and bottomShape; `volume`
     grades bottomSize. Everything else -- height, waist, tummy, torso, top, wire,
     padding, seat -- only ever reaches the spec, so changing one of those leaves the
     road ahead exactly as she left it and maxStep stands.
     ⚠️ THE LIST IS THE POINT. Clamping on EVERY change would punish her for correcting
     her waist answer; clamping on none would let her jump forward to a shapes screen
     that was built from a coverage answer she has since replaced. */
  /* ⚠️ IT NAMES THE SCREEN THAT IS INVALIDATED, NOT JUST "THIS ONE". The old version
     set `maxStep = step`, which was right while every feed-forward answer sat immediately
     before the screens it fed. Since the 2026-09-15 reorder, `style` is the FIRST screen
     in the flow, so forfeiting to it would lock the four cloth questions and the fabric
     she has already chosen behind a change that does not touch any of them -- and Codex's
     brief is explicit that a suit-type change must keep a valid fabric and must not reset
     the fabric preferences. What `style` actually invalidates is the cut and the
     arrangement, which begin at coverage.
     ⚠️ NEVER BELOW THE SCREEN SHE IS STANDING ON, and never a jump forward: this can
     only take ground away. */
  var FEEDS_FORWARD = { style: 'coverage', coverage: 'shapes', volume: 'sizes' };
  function forfeitAfter(key, value) {
    var to = FEEDS_FORWARD[key];
    if (!to || !state[key] || state[key] === value) return;
    var i = indexOfScreen(to);
    if (i < 0) return;
    maxStep = Math.max(step, Math.min(maxStep, i));
  }

  function paintDots(total) {
    if (!el.progdots) return;
    var out = '';
    for (var i = 0; i < total; i++) {
      var skipped = skipsStep(i);
      var cls = i === step ? 'mky__dot mky__dot--now'
              : i <= maxStep ? 'mky__dot ' + (skipped ? 'mky__dot--skip' : 'mky__dot--done')
              : 'mky__dot ' + (skipped ? 'mky__dot--todoskip' : '');
      /* ⚠️ REACHED, NOT PASSED. She can jump back to question four and then forward again
         to seven without re-answering five and six -- maxStep is how far she has got, and
         a dot she has already been through is a dot she can return to in either
         direction. Nuno, 2026-09-12. */
      var can = i !== step && i <= maxStep && !skipped;
      out += '<button type="button" class="' + cls + '" data-dot="' + i + '"' +
             (can ? '' : ' disabled') +
             ' aria-label="' + (skipped ? 'Question ' + (i + 1) + ', not asked for this suit'
                                        : (can ? 'Go to question ' : 'Question ') + (i + 1)) +
             '"></button>';
    }
    el.progdots.innerHTML = out;
    Array.prototype.forEach.call(el.progdots.querySelectorAll('[data-dot]'), function (d) {
      if (d.disabled) return;
      d.addEventListener('click', function () {
        step = parseInt(d.dataset.dot, 10);
        render();
      });
    });
  }

  function paintProgress() {
    /* ⚠️ ONE LEDE PER CHAPTER, SHOWN ON EVERY STEP OF IT. It was question one only, which
       meant the sentence explaining WHY a chapter is asking vanished the moment she
       answered anything. A chapter's lede is true for the whole chapter, so it stays for
       the whole chapter. `Yours` has none: the record explains itself. */
    if (el.lede) {
      var lede = CHAPTER_LEDE[chapterOf(step)] || '';
      el.lede.textContent = lede;
      el.lede.hidden = !lede;
    }
    /* ⚠️ THE TOTAL MOVES, ON PURPOSE. lastStep() grows when she picks mixed or
       reversible, because those genuinely add a fabric to choose. So the denominator can
       rise mid-flow and the bar can slip back a little. That is the honest reading: she
       really does have another decision to make. */
    var total = lastStep() + 1;
    var done = Math.min(step + 1, total);
    /* ⚠️ THE ANNOUNCED COUNT MUST MATCH THE PRINTED ONE. This line is what a screen
       reader says; .mky__qn is what everyone else reads. They were the same number until
       the printed one started counting only her screens, and two different answers to
       "where am I" is worse than either. The BAR keeps raw indices -- its dots draw the
       skipped steps, so its geometry has to include them. */
    var c = stepCount();
    /* The announced count and the printed one must match, so both withhold the total
       until there is a garment to count against. See stepCount().known. */
    if (el.progn) {
      el.progn.textContent = c.known ? 'Question ' + c.done + ' of ' + c.total
                                     : 'Question ' + c.done;
    }
    paintDots(total);
    var pct = done / total * 100;
    if (el.progfill) el.progfill.style.width = pct + '%';
    /* ⚠️ 50% is not arbitrary: it is where the fill is reliably wider than the label at
       every viewport, so the label can sit on it in white. */
    if (el.progn) el.progn.classList.toggle('is-inside', pct >= 50);
    if (el.prog) el.prog.hidden = step >= lastStep();
    if (el.open) {
      el.open.textContent = step >= lastStep()
        ? (state.variation === 'reversible'
            ? (isSingle() ? 'Two' : 'Four') + ' ways to wear it.'
            : 'One suit. This one.')
        : '';
    }
  }

  /* Shown when a question has not been answered yet. */
  var BODY_DEFAULT = { height: '55-57', volume: 'balanced', waist: 'gentle', tummy: 'soft' };

  function paint() {
    applySizeSplit();
    paintProgress();
    /* ⚠️ CLEARED HERE, NOT IN EACH BRANCH, AND IT IS THE ONLY PANE THAT WORKS THIS WAY.
       The other three stages are owned by a CHAPTER, so every branch below sets all of
       them and none can be left behind. The looks rail is owned by ONE SCREEN -- the last
       one -- and every branch below returns before reaching it, so a Back press off the
       record into the Body chapter would have left four garments sitting above her own
       silhouette. Hidden up front; the record branch is the only thing that shows it. */
    if (el.looksPane) el.looksPane.hidden = true;
    if (el.stage) el.stage.classList.remove('mky__stage--looks');
    /* The Body chapter draws HER; every later chapter draws the suit. Switched here
       rather than in each step renderer, so a step that never mentions the stage still
       clears whichever shape does not belong to it. */
    if (inBody()) {
      /* ⚠️ THE SHARED PANE GOES AWAY WHEN THE CARDS CARRY THE PICTURE, or the phone
         shows the same body twice -- once on the stage and once on the card under it.
         paintBody still RUNS: it writes the summary line the layout puts below Continue,
         and its image work is harmless against a hidden pane. */
      if (el.bodyPane) el.bodyPane.hidden = cardsFor(screenId());
      if (el.figPane) el.figPane.hidden = true;
      if (el.suitPane) el.suitPane.hidden = true;
      if (el.svg) el.svg.style.display = 'none';
      paintBody(null);
      return;
    }
    /* ⚠️ THE TORSO QUESTION OWNS THE STAGE TOO, and it returns before the garment code
       for the reason the whole pane exists: the ghost below is a BIKINI until coverage
       is answered, and this question is about one pieces. The summary line is cleared
       rather than left saying "a starting point", which would be pointing at a garment
       that is no longer on screen. */
    if (inFig()) {
      if (el.bodyPane) el.bodyPane.hidden = true;
      /* Hidden on the one fit question that draws its own cards; see the note in the
         body branch above. paintFig still runs and still paints into it. */
      if (el.figPane) el.figPane.hidden = cardsFor(screenId());
      if (el.svg) el.svg.style.display = 'none';
      if (el.caveat) { el.caveat.textContent = ''; el.caveat.hidden = true; }
      el.summary.textContent = '';
      if (el.suitPane) el.suitPane.hidden = true;
      paintFig(null);
      return;
    }
    /* ⚠️ THE KIND QUESTION OWNS THE STAGE, AND IT RETURNS BEFORE THE GARMENT CODE for
       the same reason the torso question does: the silhouette below draws a SHAPE, and
       no shape has been chosen on this screen. Everything past this point -- the fabric
       patterns, the shape `use` hrefs, the summary that names them -- is about a garment
       she has not specified yet. */
    if (inSuit()) {
      if (el.bodyPane) el.bodyPane.hidden = true;
      if (el.figPane) el.figPane.hidden = true;
      /* ⚠️ THE SHARED PANE GOES AWAY WHEN THE CARDS CARRY THE PICTURE, or the phone
         shows the same four garments twice -- once on the stage and once on the card
         under it. Same rule as the body and fit branches above. paintSuit() still runs;
         its image work is harmless against a hidden pane. */
      if (el.suitPane) el.suitPane.hidden = deckOn(step);
      if (el.svg) el.svg.style.display = 'none';
      /* ⚠️ A DIFFERENT CAVEAT, BECAUSE THE STANDING ONE CONTRADICTS THE PICTURE. The
         suit caveat opens "A preview, not a photograph" -- true of the traced silhouette
         and false here, where the stage is four photographic renders. This screen says
         what these actually are instead: an example of the kind, not her fabric or fit. */
      if (el.caveat) {
        el.caveat.textContent = el.caveat.dataset.kind || '';
        el.caveat.hidden = !el.caveat.dataset.kind;
      }
      /* ⚠️ EMPTY ON PURPOSE. The summary slot names the garment on every later screen,
         and here there is no garment to name -- the label under the frame says which kind
         is being shown, and the hint beside the options already says what it decides. A
         caption here was a third sentence saying the same thing in bold. */
      el.summary.textContent = '';
      paintSuit();
      return;
    }
    if (el.bodyPane) el.bodyPane.hidden = true;
    if (el.figPane) el.figPane.hidden = true;
    /* ⚠️ THE SILHOUETTE CODE BELOW STILL RUNS WHEN A PHOTOGRAPH IS SHOWING, and that is
       deliberate. It is hidden rather than skipped, so the summary line, the fabric
       patterns and the shape `use` hrefs are computed exactly as they always were and a
       photographic screen cannot diverge from the record it writes. The only thing the
       photo branch changes is which of the two the customer looks at. */
    var photo = garmentPhoto();
    /* ⚠️ A DECK SCREEN SHOWS NO STAGE AT ALL, AND THAT IS THE ACCEPTED DESIGN. The
       coverage cards draw the garment pair each answer sets and the cloth cards draw the
       examples, so the running preview below them is a second picture of the same
       decision competing with the one under her thumb. The design so far is still
       reachable, behind the "Your design so far" disclosure -- see paintPilotChrome. */
    /* ⚠️ AND THE SIZES SCREEN DROPS IT TOO, ON A PHONE. Nuno, on a handset 2026-09-14:
       that screen is the tallest in the flow and the garment above the chips is not what
       she is answering -- she is reading a band, a cup and a bottom size. The preview is
       still there on every other screen and on every width above the breakpoint, and the
       design so far is still reachable behind the Options disclosure. */
    var deckHere = deckOn(step) || (pilotOn() && at('sizes'));
    /* ⚠️ THE RECORD SCREEN OF A REVERSIBLE SUIT SHOWS FOUR LOOKS, NOT ONE. Nuno, on a
       handset 2026-09-16: the headline picture drew the OUTSIDE only, so the biggest thing
       on the last screen said the least about the suit she had just designed -- the other
       three were a 2x2 grid far below the fold. On a phone the stage becomes a swipeable
       rail of all four and `looksPanel()` drops its grid, so they appear once.
       ⚠️ PHONE ONLY, AND GATED ON THERE BEING LOOKS AT ALL. Above the breakpoint the stage
       sits BESIDE the record and the 2x2 is the right shape; a non-reversible suit has no
       looks and keeps the live garment it always had. paintLooks() returns the count, so
       one call answers both questions and there is no second test to fall out of step. */
    var looksHere = pilotOn() && step === lastStep() && paintLooks() > 0;
    if (el.looksPane) el.looksPane.hidden = !looksHere;
    /* Set beside the pane's `hidden` so the two cannot disagree -- see the CSS note on
       `.mky__stage--looks` for why a rail needs the stage to stretch and an svg does not. */
    if (el.stage) el.stage.classList.toggle('mky__stage--looks', looksHere);
    if (el.suitPane) el.suitPane.hidden = !photo || deckHere || looksHere;
    if (el.svg) el.svg.style.display = (photo || deckHere || looksHere) ? 'none' : '';
    if (photo) paintGarmentPhoto(photo);
    if (el.caveat) {
      /* The standing caveat opens "A preview, not a photograph" -- true of the traced
         ghost, and arguing with the picture whenever the stage IS a photograph. */
      /* ⚠️ NEITHER CAVEAT IS TRUE ON THE PICKER. The kind caveat says "An example of each
         kind", and this screen shows ONE garment; the scale caveat opens "A preview, not a
         photograph", and this one is a photograph. Both were written for screens that are
         choosing between alternatives. Say nothing rather than something false -- the
         question screens keep the behavior they have always had. */
      var cav = photo
        ? (isAtOrAfter('fabric') ? '' : (el.caveat.dataset.kind || ''))
        : (el.caveat.dataset.suit || '');
      el.caveat.textContent = cav;
      el.caveat.hidden = !cav;
    }
    /* ⚠️ THE REVERSE-FABRIC SCREEN DRAWS THE REVERSE, NOT THE OUTSIDE. Everywhere else
       the stage shows the suit as worn, which means fabric A; on `fabricInside` that made
       the picture immune to the only choice the screen asks for -- she tapped a cloth and
       the garment did not move, because it was still painted in the outside fabric.
       Nuno, on a handset 2026-09-16.
       ⚠️ SHOWN INSTEAD OF, NOT BESIDE. Two garments side by side would halve a preview
       that the fit rules have already squeezed to 76-150px on a phone, and the question on
       screen is singular: what does the REVERSE look like. The caption above it already
       names which face is which ("<fabric> is the outside of both pieces. This fabric is
       the reverse of both."), so nothing is left unsaid by showing one.
       ⚠️ IT IS A VIEW, NOT A STATE CHANGE. Nothing here writes to `state`; the record, the
       saved link, the price and the maker line are untouched and still describe the suit
       as worn. Leave the screen and the stage goes back to the outside on its own. */
    var reverseFace = at('fabricInside') && insideFabricCode();
    var f = slotFace(reverseFace || state.fabric);
    /* ⚠️ bottomFabricCode(), NOT THE FIELD. On a reversible suit the bottom is cut
       from fabric A like the top, and the field can still hold a `mixed` answer she
       gave before switching -- drawing it would show a third fabric on the stage. */
    var fb = reverseFace ? f : (slotFace(bottomFabricCode()) || f);
    var fcrop = slotFace(cropFabricCode()) || f;
    var tile = parseInt(root.dataset.tile, 10) || 40;
    [el.fillTop, el.imgTop, el.fillBottom, el.imgBottom,
     el.fillCrop, el.imgCrop].forEach(function (n) {
      if (!n) return;
      n.setAttribute('width', tile); n.setAttribute('height', tile);
    });
    /* A SOLID HAS NO PHOTOGRAPH. Nunez stock solids but never post them as swatches, so
       black and white are carried as a hex and painted as flat color. Feeding an empty
       href to the pattern would have drawn nothing at all. */
    if (f && !f.hex) el.imgTop.setAttribute('href', f.image + '&width=600');
    else el.imgTop.removeAttribute('href');
    if (fb && !fb.hex) el.imgBottom.setAttribute('href', fb.image + '&width=600');
    else el.imgBottom.removeAttribute('href');
    if (el.imgCrop) {
      if (fcrop && !fcrop.hex) el.imgCrop.setAttribute('href', fcrop.image + '&width=600');
      else el.imgCrop.removeAttribute('href');
    }
    var top = effectiveShape('topShape');
    var bot = effectiveShape('bottomShape');
    el.top.setAttribute('fill', fillFor(f, 'fill-top'));
    el.bottom.setAttribute('fill', fillFor(fb, 'fill-bottom'));
    el.top.setAttribute('stroke', edgeFor(f));
    el.bottom.setAttribute('stroke', edgeFor(fb));
    el.top.setAttribute('stroke-width', '1.5');
    el.bottom.setAttribute('stroke-width', '1.5');
    if (el.crop) {
      el.crop.setAttribute('fill', fillFor(fcrop, 'fill-crop'));
      el.crop.setAttribute('stroke', edgeFor(fcrop));
      el.crop.setAttribute('stroke-width', '1.5');
      /* Cleared on every paint: the crop exists on one style only, and a stale <use>
         would leave a long-sleeve crop hanging beside a two piece. */
      el.crop.innerHTML = '';
    }
    /* Reset first: the three-piece branch widens it, and a stale wide viewBox scales
       every other garment down by a third. */
    if (el.svg) el.svg.setAttribute('viewBox', '0 0 420 220');
    if (isSingle()) {
      /* ⚠️ ASK THE CURRENT STYLE'S LIST, NOT TOPS_ONE. This guard predates TOPS_MONO, so
         a monokini failed it and the preview quietly drew `shape-one-classic` instead --
         a plain one piece, with no cutouts, under a record that said "Monokini". The
         fallback is doing its job; it was being asked the wrong question. */
      var oneOk = topsFor().some(function (p) { return p[0] === top; });
      el.top.innerHTML = '<use href="#shape-one-' + (oneOk ? top : 'classic') +
                         '" x="110" y="10" width="200" height="200"/>';
      el.bottom.innerHTML = '';
    } else if (state.style === 'three') {
      /* ⚠️ THE STAGE HAS TO SHOW THE CROP OR A THREE PIECE IS A TWO PIECE. Hovering
         "Three piece" on the kind screen drew exactly what "Two piece" drew, because the
         third piece was never on the stage -- it existed only as a fixed tile further
         down the flow. The viewBox widens with it; it is set here rather than in the
         markup because it is the only style that needs the extra room. */
      el.svg.setAttribute('viewBox', '0 0 620 220');
      /* ⚠️ THE CROP DRAWS IN `piece-crop`, NOT IN `piece-top`, SINCE 2026-09-15. It sat in
         the top's group, so it was always the top's cloth and the customer had no say --
         and on a reversible three piece that is now wrong twice over, because the crop is
         the one piece that does NOT turn and she picks which of the two fabrics it takes.
         Its x stays 10: only the fill moved. */
      if (el.crop) {
        el.crop.innerHTML =
          '<use href="#shape-top-tankini" x="10" y="10" width="200" height="200"/>';
      }
      el.top.innerHTML = '<use href="#shape-top-' + top + '" x="210" y="10" width="200" height="200"/>';
      el.bottom.innerHTML = '<use href="#shape-bottom-' + bot + '" x="410" y="10" width="200" height="200"/>';
    } else {
      el.top.innerHTML = '<use href="#shape-top-' + top + '" x="10" y="10" width="200" height="200"/>';
      el.bottom.innerHTML = '<use href="#shape-bottom-' + bot + '" x="210" y="10" width="200" height="200"/>';
    }
    // The large desktop stage uses its height for the three separate garments.
    // Keep mobile and the compact look thumbnails in their existing horizontal frame.
    if (desktopOn() && state.style === 'three' && el.svg) {
      el.svg.setAttribute('viewBox', '0 0 220 620');
      [el.crop, el.top, el.bottom].forEach(function (group, i) {
        var use = group && group.querySelector('use');
        if (use) { use.setAttribute('x', '10'); use.setAttribute('y', String(10 + i * 200)); }
      });
    }
    // Use the desktop column's height without changing compact or mobile drawings.
    if (desktopOn() && el.svg) {
      if (isSingle()) el.svg.setAttribute('viewBox', '100 0 220 220');
      else if (state.style === 'two') {
        el.svg.setAttribute('viewBox', '0 0 220 420');
        var bottomUse = el.bottom.querySelector('use');
        if (bottomUse) { bottomUse.setAttribute('x', '10'); bottomUse.setAttribute('y', '210'); }
      }
    }
    var tops = topsFor();
    var bits = [];
    if (f) bits.push(fb && fb.code !== f.code ? f.name + ' / ' + fb.name : f.name);
    bits.push(label(tops, top));
    if (isSplit()) bits.push(label(BOTTOMS, bot));
    /* During the fit chapter nothing has been chosen, so naming shapes here would read
       as a decision the customer did not make. The ghost is a starting point and says so. */
    /* ⚠️ THE TEST IS "HAS THE SHAPE BEEN SETTLED", NOT "WHERE IS SHE IN THE FLOW". It
       was `step < Q_STEPS` -- the body and fit chapters, the four screens that came before
       any garment decision. They come last now, so an index cannot answer it, and the
       question it was really asking is the one Codex's brief states outright: any starting
       silhouette shown BEFORE an explicit shape choice must be identified as an example,
       and merely showing it must never count as choosing it.
       ⚠️ SETTLED MEANS THE QUESTION IS BEHIND HER, NOT THAT A FIELD IS TRUTHY. Since
       2026-09-15 `state.topShape` is empty until she chooses or accepts, so a truthiness
       test would be close -- but the monokini case still needs saying: it has no shape
       question at all, because the style IS the shape. Settled when the garment never asks
       (monokini), when she has pressed a tile, or when she has passed the screen, which
       accepts the suggestion. */
    var shapeSettled = !shapesApply() || isAfter('shapes') ||
                       (at('shapes') && shapeIsExplicit('topShape'));
    /* ⚠️ THE FABRIC SURVIVES THE CAVEAT, AND IT DID NOT UNTIL 2026-09-15. The unsettled
       branch replaced the WHOLE summary with "An example, until you choose below." -- so
       on the shapes screen the one line that names her chosen cloth said nothing about it,
       and on a phone that line is the entire contents of the "Your design so far"
       disclosure, because a deck screen hides the stage. Codex asked where the chosen
       fabric remains accessible on mobile without adding another panel; the honest answer
       was "it isn't", so the line now carries it.
       ⚠️ THE CAVEAT IS ABOUT THE SHAPE, NOT ABOUT THE CLOTH. She HAS chosen a fabric; she
       has not chosen a cut. Naming the first and qualifying the second is the accurate
       reading, and it is why the fabric leads rather than being appended. */
    /* ⚠️ THE PREVIEW CAPTION CARRIES THE CAVEAT, BECAUSE THE PREVIEW IS THE CLAIM.
       A flat block of colour on a garment is the most convincing thing on the screen, and
       it is an APPROXIMATION of a cloth nobody has photographed yet. Codex, 2026-09-15:
       "Bright yellow -- illustrative colour; fabric pending confirmation". The caveat
       reads as a clause here rather than a dash, because a dash in user-facing copy is
       banned across this store. */

    var cloth = f ? bits[0] : '';
    el.summary.textContent = shapeSettled
      ? bits.join(' · ')
      : (cloth ? cloth + ' · ' : '') +
        (at('shapes')
          ? 'An example, until you choose below.'
          : 'A starting point. You choose the shapes shortly.');
  }

  /** One small preview per look. Each needs its OWN pattern ids: SVG defs are global to
   *  the document, so reusing #fill-top across tiles makes every tile show the same
   *  fabric. Reversible is the whole point of this screen, so that failure would be
   *  invisible and total. */
  function lookSvg(topF, botF, id, cropOverride) {
    var tile = Math.round((parseInt(root.dataset.tile, 10) || 40) * 0.75);
    var top = effectiveShape('topShape');
    var bot = effectiveShape('bottomShape');
    var one = isSingle();
    /* ⚠️ THE CROP JOINS THE LOOKS, AND IT TAKES A THIRD PATTERN TO DO IT. Until
       2026-09-17 these tiles drew two garments and a sentence underneath said what the
       third one was doing -- so the one screen where a customer sees her finished
       three piece showed her a two piece, four times. Codex: "Show the jacket beside the
       top and bottom so all three garments remain visible", and "Three-piece screens must
       show three pieces."
       ⚠️ IT IS THE SAME CLOTH IN ALL FOUR, WHICH IS EXACTLY WHY IT MUST BE DRAWN. The crop
       does not turn (Evelin, 2026-09-15). Leaving it out made the four tiles honest about
       what CHANGES and silent about what does not; drawing it fixed makes both visible at
       once, and the caption below still says why it never moves.
       ⚠️ A THIRD PATTERN ID, NOT A REUSE OF pt. The crop takes ONE of the two fabrics and
       which one is the customer's answer, so on half the designs it differs from the top;
       sharing the top's pattern would have made `cropFace = 'second'` invisible on the one
       screen that exists to show the finished suit. Same document-wide-ids hazard as the
       other two -- see the note above this function. */
    var three = state.style === 'three';
    /* ⚠️ `cropOverride` IS HOW THE CROP QUESTION ILLUSTRATES ITS OWN ANSWERS. On the
       record the crop's cloth is whatever she has already chosen, so state is the right
       source; on the crop screen each CARD has to show the suit as it would be if she
       pressed THAT one, which state cannot tell it. `undefined` means "ask state", which
       is every existing caller. Passing the face rather than the code keeps this function
       free of the parseSolid/fabricBy branch slotFace() owns. */
    var cropF = cropOverride !== undefined
      ? cropOverride
      : (three ? slotFace(cropFabricCode()) : null);
    var pt = 'lt-' + id, pb = 'lb-' + id, pc = 'lc-' + id;
    var pat = function (pid, f) {
      return '<pattern id="' + pid + '" patternUnits="userSpaceOnUse" width="' + tile +
             '" height="' + tile + '"><image href="' +
             (f && !f.hex ? f.image + '&width=400' : '') + '" x="0" y="0" width="' + tile +
             '" height="' + tile + '" preserveAspectRatio="xMidYMid slice"/></pattern>';
    };
    var defs = '<defs>' + pat(pt, topF) + pat(pb, botF) +
               (three ? pat(pc, cropF) : '') + '</defs>';
    var gt = 'fill="' + fillFor(topF, pt) + '" stroke="' + edgeFor(topF) + '" stroke-width="1.5"';
    var gb = 'fill="' + fillFor(botF, pb) + '" stroke="' + edgeFor(botF) + '" stroke-width="1.5"';
    var gc = 'fill="' + fillFor(cropF, pc) + '" stroke="' + edgeFor(cropF) + '" stroke-width="1.5"';
    /* ⚠️ THE GEOMETRY IS paint()'s, NOT A SECOND ONE. 620-wide box, crop at x=10, top at
       210, bottom at 410, each 200x200 at y=10 -- the identical composition the stage
       makes for a three piece. A tile and the preview above it disagreeing about where the
       crop sits would read as two different garments. */
    var body = one
      ? '<g ' + gt + '><use href="#shape-one-' +
        (topsFor().some(function (q) { return q[0] === top; }) ? top : 'classic') +
        '" x="110" y="10" width="200" height="200"/></g>'
      : three
        ? '<g ' + gc + '><use href="#shape-top-tankini" x="10" y="10" width="200" height="200"/></g>' +
          '<g ' + gt + '><use href="#shape-top-' + top +
          '" x="210" y="10" width="200" height="200"/></g>' +
          '<g ' + gb + '><use href="#shape-bottom-' + bot +
          '" x="410" y="10" width="200" height="200"/></g>'
        : '<g ' + gt + '><use href="#shape-top-' + top +
          '" x="10" y="10" width="200" height="200"/></g>' +
          '<g ' + gb + '><use href="#shape-bottom-' + bot +
          '" x="210" y="10" width="200" height="200"/></g>';
    return '<svg class="mky__look" viewBox="0 0 ' + (three && !one ? 620 : 420) +
           ' 220" role="img">' + defs + body + '</svg>';
  }

  /** Exchange the top's cloth and the bottom's, keeping every other assignment intact.
   *
   *  ⚠️ IT WAS TWO COPIES OF ONE LINE AND BOTH DROPPED THE CROP. `mky-swap` on the
   *  partner picker and `mky-swap2` on the record each did
   *  `var t = state.fabric; state.fabric = state.fabricBottom; state.fabricBottom = t;`
   *  and neither touched `state.cropFace` -- which on a mixed three piece names a PIECE
   *  ("the top's fabric"), not a cloth. So swapping moved the crop onto the other fabric
   *  silently: she chose a crop matching her top, pressed Swap to see the pair the other
   *  way round, and the record, the preview, the saved link and Evelin's cutting line all
   *  came back with the crop in the cloth she had not chosen. Nothing warned, because from
   *  the flow's point of view her answer had not changed. Live on 175710929060 until
   *  2026-09-17; Codex's brief asks for the crop's explicitly chosen fabric to survive a
   *  swap, and this is the whole of that fix.
   *  ⚠️ FLIP THE FACE, DO NOT PIN THE CODE. `cropFace` stays a relationship, which is what
   *  `cropFaceLabel()` and `cropAssignment()` print and what the customer answered; storing
   *  a fabric code here instead would have made the record say "Coral" where she had said
   *  "my top", and would have needed its own reconciliation on every later fabric change.
   *  ⚠️ `mixed` ONLY, AND THAT IS NOT A NARROWING. Both swap buttons are gated on a
   *  second BOTTOM cloth existing -- `bottomFabricCode()` on the record, `state.fabricBottom`
   *  on the picker -- and `needsSlot('bottom')` is true only on `mixed`. A reversible suit
   *  has no top/bottom pair to exchange, so there is no swap to correct there. */
  function swapTopAndBottom() {
    var t = state.fabric; state.fabric = state.fabricBottom; state.fabricBottom = t;
    if (state.style === 'three' && state.variation === 'mixed' && state.cropFace) {
      state.cropFace = state.cropFace === 'second' ? 'primary' : 'second';
    }
  }

  /* ── NOTES: WHAT SHE TOLD US THAT THE QUESTIONS DID NOT ASK ──────────────────────
     Codex, 2026-09-17, sections 4 and 5. A note is kept WITH the in-progress design and
     travels in the submission; adding one sends nothing.

     ⚠️ THE DESIGN NEEDS AN IDENTITY, AND `seed` IS NOT ONE. Two of Codex's constraints
     pull against each other: "editing answers does not orphan notes merely because the
     design URL changes" and "separate designs do not inherit each other's notes". The URL
     changes on every answer, so it cannot be the key. `seed` looked like the answer and
     FAILS BOTH: `fabricSeedFor()` DERIVES it from `prefs|fabric|style` when a link
     carries none, so changing her fabric changes the seed and orphans every note; and
     `startAgain()` deliberately keeps the seed "for the SESSION, not for the design", so
     a brand new design would inherit the previous one's notes.

     ⚠️ SO `did` IS MINTED, OPAQUE, AND CARRIED IN THE URL -- WHICH IS NOT THE THING CODEX
     FORBADE. The ban is on freeform notes and contact details in a shareable link. An
     opaque token is neither, the address already carries two of them (`seed`, `cver`),
     and the NOTES THEMSELVES NEVER LEAVE THIS BROWSER. Someone opening a shared link
     looks `did` up in their own storage and finds nothing, which is the correct and
     private outcome.

     ⚠️ MEMORY IS THE TRUTH, STORAGE IS A MIRROR. Every read and write is wrapped: private
     mode, a full quota and a browser with site data blocked all throw, and Codex is
     explicit that "storage failures do not break the flow or falsely claim persistence".
     So a failed write costs the RELOAD, never the session, and `notesPersist` records
     whether the mirror is actually working so nothing claims persistence it does not
     have. */
  var NOTE_KEY = 'mky-notes-';
  var notesPersist = true;
  function designId() {
    if (!state.designId) state.designId = 'd' + Math.random().toString(36).slice(2, 10);
    return state.designId;
  }
  function notesLoad() {
    try {
      var raw = localStorage.getItem(NOTE_KEY + designId());
      var v = raw ? JSON.parse(raw) : [];
      state.notes = Object.prototype.toString.call(v) === '[object Array]' ? v : [];
    } catch (e) {
      /* ⚠️ NOT SILENT, AND NOT FATAL. An unreadable store means "no notes yet" for this
         page, and it means the panel must stop promising they will come back. */
      state.notes = [];
      notesPersist = false;
    }
  }
  function notesSave() {
    try {
      localStorage.setItem(NOTE_KEY + designId(), JSON.stringify(state.notes));
      notesPersist = true;
    } catch (e) {
      notesPersist = false;
    }
  }
  /* ⚠️ START AGAIN MUST TAKE THE NOTES AND THE UNSENT DRAFT WITH IT. Nuno, 2026-09-17.
     A new design inheriting the last one's notes is the same failure as inheriting its
     fabrics, and worse, because a note is in her own words about a suit that no longer
     exists. The OLD key is removed before a new id is minted, or the row is orphaned in
     storage forever. */
  function notesReset() {
    try { localStorage.removeItem(NOTE_KEY + designId()); } catch (e) { /* nothing to clear */ }
    /* ⚠️ THE INQUIRY'S DRAFT AND ATTEMPT GO WITH THE DESIGN THEY BELONGED TO. Both are
       keyed on `did`, which is reminted below, so a stale row would otherwise sit in
       storage for the life of the tab holding a question about a discarded suit. */
    askDraftSave('');
    askTryClear();
    state.notes = [];
    state.noteDraft = '';
    state.designId = '';
    designId();
  }
  /** Which question this note belongs to, in the customer's words, plus the slot when
   *  the screen is about one particular piece.
   *  ⚠️ CAPTURED WHEN THE NOTE IS ADDED, NOT LOOKED UP WHEN IT IS SHOWN. She may add a
   *  note on the fabric screen and read it back on the review; resolving the context
   *  later would label every note with wherever she happened to be standing. */
  function noteContext() {
    var id = screenId();
    /* ⚠️ THE HEADING CARRIES A STEP COUNTER INSIDE IT, AND `textContent` SWALLOWS IT.
       `stampStepNumber()` appends `<span class="mky__qn" aria-hidden="true">2 of 19</span>`
       to `.mky__q`, so reading the heading whole produced "How much color do you want?2 of
       19" -- and that string went into the note, the review, the POST and Evelin's email.
       Caught by a smoke test printing the stored row, not by any assertion.
       ⚠️ THE COUNTER IS REMOVED FROM A CLONE, NEVER FROM THE LIVE HEADING. Stripping it in
       place would delete the counter off the screen she is looking at, and
       `stampStepNumber` returns early when one already exists, so it would not come back
       until the next screen. Cloning costs nothing here and cannot touch the page.
       ⚠️ NOT A REGEX ON THE TEXT. "2 of 19" is data, and a question that legitimately
       ended in a number would be mangled by a pattern aimed at the counter. */
    var h = document.querySelector('#mky-step .mky__q');
    var q = '';
    if (h) {
      var clone = h.cloneNode(true);
      Array.prototype.forEach.call(clone.querySelectorAll('.mky__qn'), function (n) {
        n.parentNode.removeChild(n);
      });
      q = clone.textContent.replace(/\s+/g, ' ').trim();
    }
    var slot = '';
    if (id === 'fabricBottom') slot = 'the bottom fabric';
    else if (id === 'fabricInside') slot = 'the reverse fabric';
    else if (id === 'fabricCrop' || id === 'cropFabric') slot = 'the ' + CROP_WORD;
    else if (id === 'fabric') slot = 'the main fabric';
    return { screen: id, q: q, slot: slot };
  }
  function noteAdd(text) {
    var t = String(text || '').trim().slice(0, 600);
    if (!t) return null;
    var c = noteContext();
    var n = { id: 'n' + Math.random().toString(36).slice(2, 9),
              screen: c.screen, q: c.q, slot: c.slot, text: t };
    state.notes.push(n);
    notesSave();
    return n;
  }
  function noteEdit(id, text) {
    var t = String(text || '').trim().slice(0, 600);
    for (var i = 0; i < state.notes.length; i++) {
      if (state.notes[i].id !== id) continue;
      /* ⚠️ EMPTYING A NOTE REMOVES IT. Codex asks for editing AND removal "without
         creating duplicate notes"; clearing the box is the same intent as pressing
         Remove, and leaving an empty row behind would put a blank line in Evelin's
         inbox. */
      if (!t) state.notes.splice(i, 1);
      else state.notes[i].text = t;
      notesSave();
      return;
    }
  }
  function noteRemove(id) {
    state.notes = state.notes.filter(function (n) { return n.id !== id; });
    notesSave();
  }
  /** Every note, once, with the question it was written against.
   *  ⚠️ ONE RENDERER FOR THE PANEL, THE REVIEW, THE PAYLOAD AND THE MAKER LINE, so a note
   *  cannot appear in one and be missing from another -- and so it cannot appear TWICE in
   *  the same submission, which is the duplication Codex names. */
  function notesLines() {
    return state.notes.map(function (n) {
      return (n.q ? n.q + ' ' : '') + '\u2192 ' + n.text;
    });
  }

  /* ── THE HELP PANEL ──────────────────────────────────────────────────────────────
     ⚠️ IT MUST CHANGE NOTHING BUT ITSELF. Codex: "Opening or closing the panel must
     preserve the current answer, card, search, filter, and scroll position." So NOTHING
     in here calls render() -- a render rebuilds `#mky-step`, which resets the card rail's
     position, the search box and the scroll. The panel is painted directly and the flow
     underneath is not touched. The one exception is a note being added while the REVIEW
     is on screen, because the review prints the notes; that path repaints the list in
     place rather than re-rendering the screen. */
  var helpReturn = null;

  function helpWhereText() {
    var c = noteContext();
    return c.q ? 'About: ' + c.q : '';
  }

  function paintNotes() {
    var list = document.getElementById('mky-helplist');
    if (list) {
      list.hidden = !state.notes.length;
      list.innerHTML = state.notes.map(function (n) {
        return '<div class="mky__helprow" data-note="' + escAttr(n.id) + '">' +
                 '<b>' + escAttr(n.q || 'Your design') + '</b>' +
                 '<p>' + escAttr(n.text) + '</p>' +
                 '<button type="button" class="mky__reset" data-noteedit="' +
                   escAttr(n.id) + '">Edit</button>' +
                 '<button type="button" class="mky__reset" data-notedel="' +
                   escAttr(n.id) + '">Remove</button>' +
               '</div>';
      }).join('');
    }
    var count = document.getElementById('mky-helpcount');
    if (count) {
      count.hidden = !state.notes.length;
      count.textContent = state.notes.length
        ? state.notes.length + (state.notes.length === 1 ? ' note' : ' notes')
        : '';
    }
  }

  /* ⚠️ FOCUS IS TRAPPED BECAUSE `aria-modal="true"` IS A PROMISE. Declaring it and then
     letting Tab walk out into the flow behind the scrim is worse than not declaring it:
     a screen-reader user is told the rest of the page is inert and then finds themselves
     in it. Codex: "If modal, manage focus appropriately and prevent interaction with
     obscured content." */
  function helpKeydown(e) {
    if (e.key === 'Escape') { e.preventDefault(); closeHelp(); return; }
    if (e.key !== 'Tab') return;
    var box = document.getElementById('mky-helpbox') ||
              document.querySelector('#mky-helppanel .mky__helpbox');
    if (!box) return;
    var f = Array.prototype.filter.call(
      box.querySelectorAll('button,textarea,input,a[href],[tabindex]:not([tabindex="-1"])'),
      function (n) { return !n.disabled && n.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function openHelp() {
    var panel = document.getElementById('mky-helppanel');
    var btn = document.getElementById('mky-help');
    if (!panel) return;
    /* ⚠️ REMEMBER WHAT TO GIVE FOCUS BACK TO, RATHER THAN ASSUMING THE BUTTON. It is
       normally the help button, but the panel is also opened from the review, and
       returning focus to a control she did not press is its own small disorientation.
       Codex: "Return focus to the help button when the panel closes." */
    /* ⚠️ THE CONTROL SHE PRESSED, WHEN IT IS ONE SHE CAN STILL SEE. `document.activeElement`
       is `body` when the panel is opened by script, and on a phone it is the help button
       inside a menu that is about to be shut -- neither is somewhere to send focus back to.
       `focusable()` decides, and `focusAfterClose()` falls through when it cannot. */
    var from = document.activeElement;
    helpReturn = focusable(from) && from !== document.body ? from : null;
    var where = document.getElementById('mky-helpwhere');
    if (where) {
      var w = helpWhereText();
      where.textContent = w;
      where.hidden = !w;
    }
    var ta = document.getElementById('mky-helptext');
    /* ⚠️ THE DRAFT COMES BACK. "Do not discard typed text when the panel is temporarily
       closed." */
    if (ta) ta.value = state.noteDraft || '';
    paintNotes();
    /* ⚠️ NOTHING MAY CLAIM PERSISTENCE THAT IS NOT WORKING. In a private window, with
       site data blocked, or on a full quota, the mirror throws and this sentence would be
       a lie. Nuno, 2026-09-17: "Storage failures do not break the flow or falsely claim
       persistence." */
    Array.prototype.forEach.call(panel.querySelectorAll('.mky__helpnote'), function (n) {
      if (n.textContent.indexOf('stay in this browser') > -1) {
        n.textContent = notesPersist
          ? "Notes stay in this browser. They don't travel with your design link."
          : 'Your note will be sent with this design. This browser is not keeping a copy, '
            + 'so it will not be here if you reload.';
      }
    });
    panel.hidden = false;
    if (btn) btn.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', helpKeydown, true);
    /* ⚠️ `preventScroll`, AND IT IS NOT A NICETY. Codex: "Opening or closing the panel
       must preserve the ... scroll position." Focusing a control scrolls it into view by
       default, and the help button sits near the TOP of the flow -- so closing the panel
       from 300px down threw the page back to 0 and lost her place. Measured at (300, 0)
       before this, on the fabric picker, which is the longest screen in the flow and the
       one where losing your place costs the most. */
    if (ta && ta.focus) { try { ta.focus({ preventScroll: true }); } catch (e) { ta.focus(); } }
  }

  /** Can a customer actually see and press this, right now?
   *
   *  ⚠️ `offsetParent !== null` IS NOT THAT TEST, AND RELYING ON IT PUT FOCUS NOWHERE.
   *  Codex, 2026-09-17. It is null for a `position: fixed` element that is perfectly
   *  visible, and it says nothing about `visibility: hidden`, a zero-size box, or -- the
   *  case that actually broke -- an element inside a CLOSED `<details>`. Since the help
   *  control moved into the Options menu, the button `closeHelp()` fell back to lives
   *  inside a disclosure that is shut by the time the panel closes.
   *  ⚠️ THE `<details>` CHECK IS THE LOAD-BEARING ONE and it is explicit rather than
   *  inferred: walk up, and if any ancestor disclosure is closed, this is unreachable
   *  however the box measures. */
  function focusable(el) {
    if (!el || !el.focus || !el.getBoundingClientRect) return false;
    var r = el.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    var cs = window.getComputedStyle(el);
    if (cs.visibility === 'hidden' || cs.display === 'none') return false;
    /* ⚠️ A `<summary>` IS VISIBLE WHEN ITS OWN `<details>` IS CLOSED -- it is the control
       that OPENS it. The first version of this walk disqualified exactly the element it
       most needed to allow: the Options summary, whose parent disclosure is shut at the
       moment focus has to go somewhere, so `focusAfterClose()` fell through every
       candidate and left focus on `body`. Caught by the new assertion checking the
       ACTUAL focused element rather than an id, which is what Codex asked for -- an
       id-only check would have reported the old bug as a pass and this one as a failure
       with no explanation. */
    for (var n = el.parentNode; n && n.nodeType === 1; n = n.parentNode) {
      if (n.tagName === 'DETAILS' && !n.open &&
          !(el.tagName === 'SUMMARY' && n === el.parentNode)) return false;
      if (n.hasAttribute && n.hasAttribute('hidden')) return false;
    }
    return true;
  }

  /** Where focus goes when a panel closes: the control she actually came from, or the
   *  nearest thing she can still see.
   *
   *  ⚠️ THE ORDER IS "WHERE SHE CAME FROM" FIRST. Codex: "When opened from another visible
   *  control, such as a review-note action, return to that originating control where it
   *  still exists." A note's Edit button, the record's own controls -- whatever opened the
   *  panel keeps focus if it survived.
   *  ⚠️ THEN THE OPTIONS SUMMARY, NOT THE HELP BUTTON, ON A PHONE. The help control is
   *  inside the disclosure and the disclosure is closed; the visible thing in its place is
   *  the summary that opens it, and the menu stays CLOSED -- reopening it would undo the
   *  customer's own action.
   *  ⚠️ NEVER FOCUS SOMETHING UNREACHABLE. If none of the candidates is focusable, focus
   *  nothing and leave it on the body rather than parking it somewhere invisible. */
  function focusAfterClose(preferred) {
    var cands = [preferred, document.getElementById('mky-help')];
    var menu = el.pilotMenu;
    if (menu) cands.push(menu.querySelector(':scope > summary'));
    for (var i = 0; i < cands.length; i++) {
      if (focusable(cands[i])) {
        try { cands[i].focus({ preventScroll: true }); } catch (e) { cands[i].focus(); }
        return cands[i];
      }
    }
    return null;
  }

  function closeHelp() {
    var panel = document.getElementById('mky-helppanel');
    var btn = document.getElementById('mky-help');
    if (!panel || panel.hidden) return;
    var ta = document.getElementById('mky-helptext');
    /* ⚠️ THE DRAFT IS CAPTURED BEFORE ANYTHING MOVES. Codex: "Preserve scroll position and
       typed text." The typed text is read here; the scroll is preserved by
       `preventScroll` on every focus call below. */
    if (ta) state.noteDraft = ta.value;
    panel.hidden = true;
    if (btn) btn.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', helpKeydown, true);
    var preferred = helpReturn;
    helpReturn = null;
    focusAfterClose(preferred);
  }

  /* ── THE UNFINISHED-DESIGN INQUIRY ───────────────────────────────────────────────
     ⚠️ IT DOES NOT TOUCH sendCapture(), AND THAT IS STRUCTURAL. The beacon is bound to
     `#mky-save-form`'s submit event only. This is a DIFFERENT form, so the beacon is not
     merely skipped here -- it is not reachable from here. Codex: the inquiry must "avoid
     creating an order, reservation, completed-design record, or production-ready
     specification", and the beacon is precisely what creates the GeneratedListing.

     ⚠️ IT SENDS WHAT SHE HAS, NOT A DESIGN. No `contact[Design]`, no `contact[Suit]`.
     `contact[Unfinished design]` names the question she is stuck on, the answers that
     exist, and her notes -- and nothing is invented to fill a gap. Codex: "Allow
     unanswered design questions. Avoid inventing answers or treating the design as
     complete." */
  function askStateText() {
    var out = [];
    var h = document.querySelector('#mky-step .mky__q');
    out.push('STUCK ON: ' + (h ? h.textContent.replace(/\s+/g, ' ').trim()
                               : 'the ' + (screenId() || 'studio')));
    out.push('SCREEN: ' + (screenId() || '?') + ' (' + (step + 1) + ' of ' +
             ROUTE.length + ')');
    /* ⚠️ ONLY WHAT SHE HAS ACTUALLY ANSWERED. An empty answer is omitted rather than
       printed blank, so the line cannot be read as "she chose nothing" when the truth is
       "she was never asked". */
    var have = [];
    if (state.style) have.push('style ' + state.style);
    if (state.fabric) {
      var f0 = slotFace(state.fabric);
      have.push('fabric ' + (f0 ? f0.name : state.fabric));
    }
    if (state.variation) have.push('arrangement ' + state.variation);
    if (state.topShape) have.push('top ' + state.topShape);
    if (state.bottomShape) have.push('bottom ' + state.bottomShape);
    if (state.coverage && coverageApplies()) have.push('coverage ' + state.coverage);
    out.push(have.length ? 'CHOSEN SO FAR: ' + have.join('; ')
                         : 'CHOSEN SO FAR: nothing yet');
    /* The address, which IS the partial design, so she can be picked up where she left
       off. It carries no note text -- see syncUrl(). */
    out.push('HER LINK: ' + location.origin + location.pathname + '?' + syncUrl().toString());
    if (state.notes.length) {
      out.push('HER NOTES: ' + notesLines().join(' | '));
    }
    out.push('⚠ UNFINISHED DESIGN - NOT AN ORDER, NOT A RESERVATION, NOTHING IS CUT');
    return out.join('\n');
  }

  function openAsk() {
    var box = document.getElementById('mky-ask');
    if (!box) return;
    closeHelp();
    box.classList.add('mky__ask--on');
    var st = document.getElementById('mky-ask-state');
    if (st) st.value = askStateText();
    askReturn();
    /* ⚠️ CARRY HER WORDS OVER, AND DO NOT CLOBBER THEM ON A SECOND OPEN. Codex: "Carry
       over their note and preserve the unfinished design." The draft she was typing is
       the most likely thing she wanted to send; her saved notes come next. But if she has
       already edited the box here, that edit wins -- reopening must not throw away what
       she has since written. */
    var ta = document.getElementById('mky-ask-text');
    if (ta && !ta.value) {
      ta.value = state.noteDraft ||
                 (state.notes.length ? state.notes[state.notes.length - 1].text : '');
    }
    /* ⚠️ FOCUS FIRST NAME, ONCE, ON OPENING. Codex: "Focus First Name when opening the
       contact form, without stealing focus on subsequent rendering." This runs from a
       click, not from a render, which is what makes the second half true. */
    var first = document.getElementById('mky-ask-first');
    if (first && first.focus) { try { first.focus(); } catch (e) { /* detached */ } }
    box.scrollIntoView({ block: 'center' });
  }

  /** The address Shopify sends her back to after the post.
   *  ⚠️ `contact_posted=true` IS REQUIRED OR NO SUCCESS RENDERS AT ALL. `return_to`
   *  REPLACES Shopify's own redirect, and that redirect is what normally carries the flag
   *  -- the save form has carried it by hand since 2026-09-04 for exactly this reason. The
   *  inquiry did not, so it came back silent. `mky_sent=ask` is what then attributes the
   *  success to this form rather than the other; see revealPosted().
   *  ⚠️ REBUILT AT SUBMIT, NOT ONLY AT OPEN. Codex: "Refresh the return URL at submission
   *  if the customer changed answers after opening the inquiry." She can open this, go
   *  back, change a fabric and return -- and a stale address would restore the design she
   *  had before, silently discarding the change. */
  function askReturn(tryId) {
    var ret = document.getElementById('mky-ask-return');
    if (!ret) return;
    ret.value = location.pathname + '?' + syncUrl().toString() +
                '&contact_posted=true&mky_sent=ask' +
                (tryId ? '&mky_try=' + encodeURIComponent(tryId) : '');
  }

  function closeAsk() {
    var box = document.getElementById('mky-ask');
    if (box) box.classList.remove('mky__ask--on');
    var err = document.getElementById('mky-ask-err');
    if (err) { err.hidden = true; err.textContent = ''; }
  }

  /** Wired ONCE, for the life of the page. Everything it binds lives OUTSIDE `#mky-step`,
   *  so a render cannot destroy a handler and nothing here needs rebinding. */
  function wireHelp() {
    var btn = document.getElementById('mky-help');
    if (btn) btn.addEventListener('click', function () {
      /* ⚠️ CLOSE THE DISCLOSURE THE CONTROL NOW LIVES IN. On a deck screen this button is
         inside the Options menu, and leaving that open behind a modal scrim means Escape
         closes the panel onto a still-open menu, with focus returning to a control the
         customer can no longer see. */
      if (el.pilotMenu && el.pilotMenu.open) el.pilotMenu.open = false;
      var panel = document.getElementById('mky-helppanel');
      if (panel && panel.hidden) openHelp(); else closeHelp();
    });
    var close = document.getElementById('mky-helpclose');
    if (close) close.addEventListener('click', closeHelp);
    var panel = document.getElementById('mky-helppanel');
    /* ⚠️ THE SCRIM CLOSES, THE BOX DOES NOT. Without the target test, a click that
       started inside the box -- selecting text, say -- would close the panel and lose the
       draft's focus. */
    if (panel) panel.addEventListener('click', function (e) {
      if (e.target === panel) closeHelp();
    });
    var ta = document.getElementById('mky-helptext');
    /* The draft is mirrored on every keystroke, so a panel closed by Escape, by the
       scrim, or by the flow moving underneath still has her text when it reopens. */
    if (ta) ta.addEventListener('input', function () { state.noteDraft = ta.value; });
    var add = document.getElementById('mky-helpadd');
    if (add) add.addEventListener('click', function () {
      var box = document.getElementById('mky-helptext');
      if (!box) return;
      /* ⚠️ EDITING REPLACES, IT DOES NOT ADD A SECOND ROW. Codex: "Allow editing or
         removal without creating duplicate notes." `noteEditing` is set by the Edit
         button below and cleared the moment the edit lands. */
      if (state.noteEditing) {
        noteEdit(state.noteEditing, box.value);
        state.noteEditing = '';
      } else if (!noteAdd(box.value)) {
        return;   /* empty: nothing to add, and no confirmation to give */
      }
      box.value = '';
      state.noteDraft = '';
      paintNotes();
      /* ⚠️ A BRIEF CONFIRMATION, IN THE PANEL, NOT A TOAST AND NOT A RENDER. Codex:
         "Confirm briefly that the note was added." A render here would reset the card
         rail, the search box and the scroll position -- the exact thing the brief
         forbids opening and closing this panel from doing. */
      var say = document.getElementById('mky-helpwhere');
      if (say) {
        say.textContent = 'Added. It will be sent with your design.';
        say.hidden = false;
      }
      /* The review prints the notes, so it is the one screen that has to be repainted
         when a note lands. Repaint IN PLACE: see paintAskNotes(). */
      paintReviewNotes();
    });
    var list = document.getElementById('mky-helplist');
    if (list) list.addEventListener('click', function (e) {
      var ed = e.target.closest ? e.target.closest('[data-noteedit]') : null;
      var rm = e.target.closest ? e.target.closest('[data-notedel]') : null;
      if (ed) {
        var id = ed.getAttribute('data-noteedit');
        var found = state.notes.filter(function (n) { return n.id === id; })[0];
        var box2 = document.getElementById('mky-helptext');
        if (found && box2) {
          box2.value = found.text;
          state.noteEditing = id;
          box2.focus();
        }
        return;
      }
      if (rm) {
        noteRemove(rm.getAttribute('data-notedel'));
        if (state.noteEditing === rm.getAttribute('data-notedel')) state.noteEditing = '';
        paintNotes();
        paintReviewNotes();
      }
    });
    var stuck = document.getElementById('mky-helpstuck');
    if (stuck) stuck.addEventListener('click', openAsk);
    var back = document.getElementById('mky-ask-back');
    if (back) back.addEventListener('click', function () {
      /* ⚠️ "Back to my design" GETS THE SAME TREATMENT. Codex: "Apply the same principle
         to 'Back to my design' from the inquiry view." It is the control a customer
         reaches after typing, so sending focus somewhere invisible is worst exactly here.
         Her typed question is untouched: nothing in this path clears it. */
      closeAsk();
      focusAfterClose(null);
    });
    /* ⚠️ VALIDATION HERE, AND THE SUBMIT IS CANCELLED WHEN IT FAILS. The email box is
       `type="email"` so the browser will refuse an obviously malformed one, but it is not
       `required` in the markup -- a `required` control can block a submit with a message
       only the console sees. Codex wants "field-specific errors", so the message names
       the field and the box gets focus. */
    var askForm = document.getElementById('mky-ask-form');
    if (askForm) askForm.addEventListener('submit', function (e) {
      var em = document.getElementById('mky-ask-email');
      var err = document.getElementById('mky-ask-err');
      var st2 = document.getElementById('mky-ask-state');
      /* Refreshed at submit rather than only at open: she may have gone back, changed an
         answer and returned, and the inquiry must describe where she actually is. */
      if (st2) st2.value = askStateText();
      if (!em || !em.value || em.value.indexOf('@') < 0) {
        e.preventDefault();
        if (err) {
          err.textContent = 'Please add an email address so we can reply.';
          err.hidden = false;
        }
        if (em && em.focus) { try { em.focus(); } catch (e2) { /* detached */ } }
        return;
      }
      if (err) { err.hidden = true; err.textContent = ''; }
      /* ⚠️ WRITTEN DOWN BEFORE THE NAVIGATION, WHICH IS THE ONLY MOMENT IT CAN BE. The
         draft survives a rejection; the attempt record is what a return is correlated
         against. `askReturn(id)` then puts the attempt id in the address so the return can
         be matched to THIS attempt rather than to any attempt. */
      var ta2 = document.getElementById('mky-ask-text');
      var txt = ta2 ? ta2.value : '';
      askDraftSave(txt);
      /* ⚠️ THE RETURN URL IS BUILT HERE, NOT AT OPEN, so it carries both the design as it
         stands NOW (she may have gone back and changed an answer) and the id of THIS
         attempt. An empty id means storage refused the record, and the return will then
         report the honest neutral outcome rather than a confirmation. */
      askReturn(askTryMark(txt));
      /* ⚠️ NOTHING IS MARKED SENT HERE, AND NOTHING EVER CAN BE. Shopify's redirect
         carries a flag a customer can type, so the return is correlated with this
         attempt and never asserted as delivery -- see revealPosted() and
         paintAskOutcome(). On a rejection the browser comes back to a fresh document,
         which is why the draft is written above rather than left in memory. */
    });
  }

  /** The notes, on the review, where the brief asks for them with their question context.
   *  ⚠️ PAINTED IN PLACE AND NEVER VIA render(). See the note at the top of the panel
   *  block: a render on the review would rebuild the save form under her. */
  function paintReviewNotes() {
    var slot = document.getElementById('mky-review-notes');
    if (!slot) return;
    slot.hidden = !state.notes.length;
    slot.innerHTML = state.notes.length
      ? '<dt>Your notes</dt><dd>' + state.notes.map(function (n) {
          return '<b>' + escAttr(n.q || 'Your design') + '</b><br>' + escAttr(n.text);
        }).join('<br><br>') + '</dd>'
      : '';
  }

  /* ── WHICH FORM THE RETURN PAGE IS FOR ───────────────────────────────────────────
     ⚠️ LIQUID CANNOT ANSWER THIS, AND ROUND 20 SHIPPED ASSUMING IT COULD.
     `form.posted_successfully?` is true for EVERY `{% form 'contact' %}` on the page
     whenever ANY of them posted. Measured on a real theme 2026-09-17, by loading the page
     with `contact_posted=true` and counting: BOTH success blocks rendered. So an inquiry
     would have told her "Sent. We have your design.", and a save would have told her a
     question was sent. Before round 20 there was one contact form and the flag was
     unambiguous; adding the second is what created this. Codex caught it in review.

     ⚠️ AND THE INQUIRY SHOWED NOTHING AT ALL. Its `return_to` carried `ask_posted=true`,
     which nothing read, and NOT `contact_posted=true` -- so no success branch rendered,
     and the block would have been inside a collapsed `.mky__ask` even if it had. Verified
     the same way: `ask_posted=true` alone renders neither success block.

     ⚠️ NONE OF THIS IS PROOF OF RECEIPT, AND AN EARLIER VERSION OF THIS COMMENT CLAIMED
     IT WAS. It said the marker "exists only inside `posted_successfully?`, which only
     SHOPIFY can make true: that is proof a contact form posted." THAT IS FALSE, and the
     evidence was already in this session's own notes: a `curl` of
     `?contact_posted=true` returns both success markers. Re-measured with a FRESH cookie
     jar on a real theme, 2026-09-17, from a client that had never posted anything:

       GET ...?contact_posted=true&mky_sent=ask   -> BOTH markers rendered
       GET ...  (no flag)                         -> neither

     `form.posted_successfully?` is driven ENTIRELY BY THE URL PARAMETER. Shopify's native
     contact form gives a theme NO server-side signal that distinguishes a genuine
     post-redirect from a hand-typed address. Codex caught the false claim in review.

     ⚠️ SO WHAT THIS FUNCTION DOES IS RENDER A RETURN STATE, NOT CONFIRM A DELIVERY, and
     every name and comment here now says so. What it adds on top is CORRELATION: a
     single-use record written in THIS browser at the moment of an attempt (see
     askPending). A return that matches an attempt this browser actually made is the best
     signal available; it is not proof, and it must never be described as proof. A
     fabricated or stale URL opened anywhere else has no such record and is shown nothing.

     ⚠️ THE SAME LIMITATION APPLIES TO THE SAVE FORM'S SUCCESS, WHICH PREDATES ALL OF THIS
     (2026-09-04) AND IS DELIBERATELY LEFT ALONE. Changing a live, long-standing path was
     not asked for and is reported instead. */
  /* ── THE INQUIRY'S RECOVERY AND CORRELATION STATE ────────────────────────────────
     ⚠️ KEYED PER DESIGN AND PER ATTEMPT, AND IT WAS GLOBAL UNTIL CODEX CAUGHT IT. One
     `mky-ask-text` and one `mky-ask-pending` for the whole tab meant two designs shared
     recovery state: start a new design after an attempt on the old one and the old
     question was offered back against a suit it was never about, and the old attempt
     corroborated the new design's return. `did` already identifies the design (see
     notesLoad); the attempt gets its own opaque id.
     ⚠️ NEITHER KEY NOR ID CARRIES NOTE TEXT OR CONTACT DETAILS. Both are opaque tokens.
     The TEXT lives in the value, in sessionStorage, and never in a key or a URL. */
  function askDraftKey() { return 'mky-ask-draft-' + designId(); }
  function askTryKey()   { return 'mky-ask-try-' + designId(); }
  /* How long an attempt can corroborate a return. Long enough for a slow post and a
     challenge, short enough that a URL reopened later corroborates nothing. sessionStorage
     already dies with the tab; this bounds it inside one. */
  var ASK_TRY_MS = 30 * 60 * 1000;

  function askDraftSave(v) {
    try {
      if (v) sessionStorage.setItem(askDraftKey(), String(v).slice(0, 900));
      else sessionStorage.removeItem(askDraftKey());
    } catch (e) { /* storage unavailable: the draft lives only in the DOM */ }
  }
  function askDraftRead() {
    try { return sessionStorage.getItem(askDraftKey()) || ''; } catch (e) { return ''; }
  }
  /** Record the attempt, with the text as it was sent. Returns the attempt id, or '' when
   *  storage is unavailable -- which the caller must NOT treat as a failure to send.
   *  ⚠️ A CORRELATION RECORD, NOT A RECEIPT. Codex: it "may help correlate a return with
   *  an attempted submission, but it is not itself proof of receipt." */
  function askTryMark(text) {
    var id = 't' + Math.random().toString(36).slice(2, 10);
    try {
      sessionStorage.setItem(askTryKey(), JSON.stringify({
        id: id, at: Date.now(), text: String(text || '').slice(0, 900)
      }));
    } catch (e) { return ''; }
    return id;
  }
  /** The attempt this browser recorded for THIS design, or null.
   *  ⚠️ THE CONFIRMATION IS SHOWN ONCE, AND THAT IS THE ADDRESS BAR'S DOING, NOT THIS
   *  FUNCTION'S. `syncUrl()` rewrites the address from the DESIGN's parameters on the
   *  first render after a return, so `contact_posted`, `mky_sent` and `mky_try` are gone
   *  by the time anyone can reload -- measured, not assumed. A reload therefore lands on
   *  the design itself with no return state. That is the safer behaviour: a return URL
   *  cannot be reshared or reopened as a fresh confirmation.
   *  ⚠️ THE RECORD IS NOT CONSUMED HERE EITHER WAY. It is discarded only on Dismiss or
   *  Start again, which is Codex's "do not automatically delete the recoverable
   *  question". It outliving its own confirmation is the point. */
  function askTryRead() {
    var raw = '';
    try { raw = sessionStorage.getItem(askTryKey()) || ''; } catch (e) { return null; }
    if (!raw) return null;
    try {
      var v = JSON.parse(raw);
      return (v && v.id) ? v : null;
    } catch (e) { return null; }
  }
  function askTryClear() {
    try { sessionStorage.removeItem(askTryKey()); } catch (e) { /* nothing to clear */ }
  }

  function revealPosted() {
    var blocks = document.querySelectorAll('[data-mky-posted]');
    if (!blocks.length) return '';          /* no return branch rendered: nothing to show */
    var q = new URLSearchParams(location.search);
    /* ⚠️ `contact_posted` PROVES NOTHING, AND AN EARLIER COMMENT HERE SAID IT PROVED "a
       post actually happened". It does not: `form.posted_successfully?` is driven entirely
       by this parameter, so a fresh client that never posted renders both success branches
       by typing it. It is read here ONLY to tell a return-shaped URL from an ordinary one,
       and because the local harness renders both Liquid branches on every page and would
       otherwise reveal a state on every screen. It is a shape test, not evidence. */
    if (q.get('contact_posted') !== 'true') return '';
    var which = q.get('mky_sent') === 'ask' ? 'ask' : 'design';
    Array.prototype.forEach.call(blocks, function (b) {
      b.hidden = b.getAttribute('data-mky-posted') !== which;
    });
    if (which !== 'ask') return which;

    var box = document.getElementById('mky-ask');
    if (box) box.classList.add('mky__ask--on');
    /* ⚠️ THREE OUTCOMES, AND "SILENTLY NOTHING" IS NOT ONE OF THEM. Codex: "If storage is
       unavailable or the attempt cannot be matched, show an explicit neutral outcome
       rather than silently hiding everything." A customer who cannot be told what happened
       must at least be told THAT, and given a way back that does not resend. */
    var rec = askTryRead();
    var wanted = q.get('mky_try') || '';
    var matched = !!(rec && rec.id && wanted && rec.id === wanted &&
                     (Date.now() - (rec.at || 0)) < ASK_TRY_MS);
    paintAskOutcome(matched, matched ? rec : null);
    return which;
  }

  /** The inquiry's return state: correlated, or honestly unconfirmed.
   *  ⚠️ NOTHING HERE CLAIMS DELIVERY. The strongest statement available is that this
   *  browser attempted a submission and Shopify returned a success-shaped redirect. That
   *  is a correlated return, not a receipt, and the copy says "submitted" rather than
   *  anything implying a person has it. */
  function paintAskOutcome(matched, rec) {
    var host = document.getElementById('mky-ask-posted');
    if (!host) return;
    var title = host.querySelector('[data-ask-title]');
    var note = host.querySelector('[data-ask-note]');
    var sentWrap = host.querySelector('[data-ask-sent]');
    var sentText = host.querySelector('[data-ask-senttext]');
    if (title) {
      title.textContent = matched
        ? 'Thank you. Your question was submitted.'
        : "We couldn't confirm the submission. Your question may have been sent.";
    }
    if (note) {
      note.textContent = matched
        ? 'Your design is exactly as you left it.'
        : 'Your design is exactly as you left it, and nothing has been resent.';
    }
    /* ⚠️ SHOWN BESIDE THE CONFIRMATION, NOT PUT BACK IN THE COMPOSER. Codex: retain it
       "separated from the active composer so it is not accidentally resubmitted". It is
       read-only here; the composer keeps its own draft, which this never touches. */
    if (sentWrap) sentWrap.hidden = !(matched && rec && rec.text);
    if (sentText && matched && rec && rec.text) sentText.textContent = rec.text;
  }

  /** Keep her question across a FAILED post, and only her question.
   *
   *  ⚠️ A NATIVE FORM POST LEAVES THE PAGE, SO "JS NEVER CLEARS IT" PROVES NOTHING. That
   *  was an earlier claim of mine and Codex was right to reject it: a validation failure,
   *  a captcha rejection or a network error all come back as a NEW document, and
   *  everything in memory is gone. Only what was written down before the navigation
   *  survives.
   *  ⚠️ HER QUESTION ONLY. NOT HER EMAIL, NOT HER NAME. The email box is repopulated from
   *  `form.email`, which the SERVER hands back; First Name is not recovered at all, which
   *  is a platform limitation Shopify imposes and Codex has accepted.
   *  ⚠️ PER DESIGN. See askDraftKey(): a shared key let one design's question be offered
   *  back against another. */
  function askTextRestore() {
    var ta = document.getElementById('mky-ask-text');
    if (!ta || ta.value) return;
    var v = askDraftRead();
    if (v) ta.value = v;
  }

  function syncUrl() {
    var q = new URLSearchParams();
    /* ⚠️ `band` AND `cup` BELONG HERE. They are half the top spec, and this URL is
       what the page calls "your design" and tells her to bookmark. Omitted, a saved
       link silently reopens with the bust blank -- the one answer she was least likely
       to be able to give twice. `bustSkipped` rides along so "I'm not sure" survives
       too, or a resumed link asks her again and blocks Continue. */
    ['height','volume','waist','tummy',
     'torso','wire','padding','seat','coverage','style','topShape','topSize','bottomShape','bottomSize',
     'fabric','variation','fabricBottom','fabricInside','fabricCrop','cropFace',
     'band','cup']
      .forEach(function (k) { if (state[k]) q.set(k, state[k]); });
    /* ⚠️ THE TORSO ANSWER LEAVES THE URL WHEN THE GARMENT CANNOT HAVE ONE. This address
       IS the design -- the page says so and tells her to bookmark it -- so it is the
       specification, and a split suit's specification must not carry a torso adjustment.
       Codex, 2026-09-15: omit it from the effective design and the maker build when she
       switches away from a connected suit.
       ⚠️ THE ANSWER ITSELF IS NOT THROWN AWAY. `state.torso` is untouched, so switching
       back to a one piece during the same design restores it and it reappears here. What
       does not survive is a RELOAD of a split-suit link, which is the correct trade: a
       saved split-suit design that quietly carried a torso answer would be a specification
       claiming an adjustment for a garment with no torso. */
    if (!torsoApplies()) q.delete('torso');
    /* ⚠️ SAME RULE, AND IT IS WHAT KEEPS A NEW LINK INSIDE THE TWO-FABRIC LIMIT. She
       can answer `mixed`, pick a bottom fabric, then switch to `reversible`; the
       answer stays in `state` so going back restores it, and the ADDRESS -- which this
       page calls "your design" and tells her to bookmark -- must not name three
       fabrics. See bottomFabricCode(). */
    /* ⚠️ EXCEPT WHILE A LEGACY LINK IS STILL WAITING FOR HER CORRECTION. syncUrl() runs
       on EVERY render, so dropping the third fabric here would have rewritten the address
       bar the instant the correction screen painted -- and a refresh, or the saved link
       re-opened, would then arrive as a tidy two-fabric design with one of her cloths
       gone and nothing to say so. That is the silent discard the correction screen exists
       to prevent, performed by the serializer instead. Carry all three until she has
       chosen; `fabricExcess` is null the moment she does. */
    if (!needsSlot('bottom') && !state.fabricExcess) q.delete('fabricBottom');
    /* Same rule again: a garment with no crop choice has no crop answer to state. The
       value stays in `state` so switching back restores it. */
    if (!needsSlot('inside')) q.delete('fabricInside');
    if (!needsSlot('crop')) q.delete('fabricCrop');
    if (!cropChoiceApplies()) q.delete('cropFace');
    /* ⚠️ A COLOUR NOTE TRAVELS WITH THE SLOT IT BELONGS TO, AND ONLY WHILE THAT SLOT
       HOLDS A COLOUR REQUEST. Four flat params rather than one packed one: her text can
       contain any character, and a packed encoding is where the separator gets eaten.
       Dropped the moment the slot holds a photographed fabric instead, so a note about a
       yellow she no longer wants cannot ride along to the workroom. */
    SOLID_NOTE_PARAM.forEach(function (pair) {
      var code = pair[0] === 'fabric' ? state.fabric
               : pair[0] === 'fabricBottom' ? bottomFabricCode()
               : pair[0] === 'fabricInside' ? insideFabricCode() : cropSlotCode();
      var note = state.solidNotes[pair[0]];
      if (note && isSolidRequest(code)) q.set(pair[1], note);
    });
    /* Same rule, same reason. See coverageLabel(). */
    if (!coverageApplies()) q.delete('coverage');
    if (state.bustSkipped) q.set('bustSkipped', '1');
    if (state.bustUnlisted) {
      q.set('bustUnlisted', '1');
      if (state.bustLabel) q.set('bustLabel', state.bustLabel);
    }
    if (state.top.length) q.set('top', state.top.join('|'));
    /* ⚠️ THE FOUR CLOTH ANSWERS, THE SEED, AND THE PAGE. Omitting any of them makes a saved
       link show her a different screen than the one she saved. `fabric` keeps Either
       distinguishable from unanswered; `cver` records which catalogue the answers were
       resolved against so a resumed link can tell that the pool moved under it. */
    var cl = prefsToUrl();
    if (cl) q.set('prefs', cl);
    if (state.seed) q.set('seed', state.seed);
    /* ⚠️ AN OPAQUE KEY, NEVER THE NOTES THEMSELVES. `did` is what lets her own browser
       find the notes for THIS design after a reload, an edit or a Back -- it is not the
       notes, and a stranger opening the link finds nothing under it in their own storage.
       See the note above notesLoad() for why `seed` could not do this job. */
    if (state.designId) q.set('did', state.designId);
    if (state.prefsPage) q.set('cpage', String(state.prefsPage));
    if (state.prefsVer) q.set('cver', state.prefsVer);
    /* ⚠️ THE DESIGN GOES INTO THE ADDRESS BAR ON EVERY RENDER, AND A NEW HISTORY ENTRY
       ONLY WHEN THE SCREEN MOVED. Answering a question, paging the suggestions, typing in
       the search box and opening a colour family all re-render -- and every one of them
       would have been a Back press to undo if this pushed unconditionally. See histWrite(). */
    histWrite(location.pathname + '?' + q.toString());
    return q;
  }

  /** A shape as its own drawing. The symbols are the same traced flats the preview uses,
   *  so the tile and the garment can never disagree. */
  /** One garment component's drawing: the photographic render where every shape in the
   *  list has one, the traced silhouette otherwise.
   *  ⚠️ SPLIT OUT OF shapeTiles SO THE PHONE'S COMPONENT CHOOSER DRAWS THE SAME THING.
   *  `art` is tilesArt's all-or-nothing answer for the WHOLE list and is passed in rather
   *  than recomputed per option, because a half-photographic row is the thing that rule
   *  exists to prevent.
   *  ⚠️ `kind` IS WHAT MAKES THE LOOKUP SAFE: 'one', 'top' or 'bottom', the same context
   *  the photo keys carry, so a one-piece Classic can never be served the Bandeau render
   *  that shares its id. */
  function shapeArtHtml(kind, v, art) {
    return art
      /* Same background-image tile the You chapter uses, so the two pickers in this flow
         render as one thing. `--garment` contains the whole render rather than cropping to
         half a strip, which is what the body artwork needs. */
      ? '<span class="mky__art mky__art--garment" aria-hidden="true" style="background-image:url(&quot;' +
        SHAPE_PHOTOS[kind + ':' + v] + '&quot;)"></span>'
      : '<svg viewBox="0 0 200 200" aria-hidden="true" focusable="false">' +
        '<use href="#shape-' + kind + '-' + v + '" x="0" y="0" width="200" height="200"/></svg>';
  }

  /** ⚠️ `value` IS AN EXPLICIT CHOICE OR EMPTY, NEVER THE GUESS. When it is empty the
   *  starting shape is marked as a SUGGESTION -- named, not pressed -- so the grid says the
   *  same thing the deck does and the same thing the stage caption does. Codex, 2026-09-15.
   *  ⚠️ `aria-pressed` FOLLOWS THE CHOICE, NOT THE DRAWING. A suggested tile announcing
   *  itself as pressed is the screen-reader version of the pink card with a checkmark. */
  function shapeTiles(kind, pairs, value, attr) {
    /* ⚠️ ALL OR NOTHING. One piece qualifies today; tops and bottoms wait on component
       renders. See shapeArtHtml. */
    var art = tilesArt(kind, pairs);
    var hint = value ? '' : suggestedShape(attr);
    return '<div class="mky__shapes">' + pairs.map(function (pr) {
      var isSuggested = !value && pr[0] === hint;
      return '<button class="mky__shape' + (art ? ' mky__shape--art' : '') +
             (isSuggested ? ' mky__shape--suggested' : '') +
             '" type="button" data-attr="' + attr + '" data-v="' + pr[0] +
             '" aria-pressed="' + (!!value && pr[0] === value) + '">' +
             shapeArtHtml(kind, pr[0], art) + '<b>' + pr[1] + '</b>' +
             (isSuggested ? '<em class="mky__shape-sug">Suggested starting shape</em>' : '') +
             '</button>';
    }).join('') + '</div>';
  }

  /** A body shape as its own drawing. Same tracer, same tile CSS and same viewBox as
   *  the garment flats, so the two kinds of picker read as one flow. */
  /** A tile shows the same artwork the stage will show for that answer, cropped to the
   *  half where the difference is legible: front for bust/hip and waist, side for the
   *  stomach. Sprite-cropped rather than re-exported, so there is one asset per answer. */
  /** One body answer's artwork: the sprite cropped to the half this question reads in,
   *  plus its landmark bars or belly curve. Split out of bodyTiles so the phone's card
   *  deck shows the SAME crop, the SAME 215.3%/14.7% sprite maths and the SAME measured
   *  bar widths at a larger size -- rather than a second copy of numbers that took three
   *  rounds of measuring to get right.
   *  `extraCls` is how a caller sizes it; everything else about the box is .mky__art. */
  function bodyArtHtml(setKey, value, extraCls) {
    var view = TILE_VIEW[setKey] || 'front';
    var e = bodyEntry(setKey, value) || {};
    var url = BODY_ASSETS[e.asset] || '';
    var bars = ((TILE_BARS[setKey] || {})[value] || []).map(function (bk) {
      if (bk === 'belly') {
        var d = BELLY[e.asset];
        return d ? '<svg class="mky__curve" viewBox="0 0 100 100" width="100%" height="100%" ' +
                   'preserveAspectRatio="none" aria-hidden="true"><path d="' + d + '"/></svg>'
                 : '';
      }
      var w = (BAR_W[bk] || {})[value];
      if (!w) return '';
      return '<i class="mky__bar" style="top:' + BAR_ROW[bk] + '%;width:' + w + '%"></i>';
    }).join('');
    return '<span class="mky__art mky__art--' + view + (extraCls ? ' ' + extraCls : '') +
           '" aria-hidden="true" style="background-image:url(&quot;' + url + '&quot;)">' +
           bars + '</span>';
  }

  function bodyTiles(setKey, opts, value) {
    return '<div class="mky__shapes mky__shapes--body">' + opts.map(function (o) {
      return '<button class="mky__shape mky__shape--art" type="button" data-v="' + o[0] +
             '" aria-pressed="' + (o[0] === value) + '">' +
             bodyArtHtml(setKey, o[0], '') +
             '<b>' + o[1] + '</b>' +
             (o[2] ? '<span>' + o[2] + '</span>' : '') + '</button>';
    }).join('') + '</div>';
  }

  /** Coverage as the pair of garments it actually sets. COVER_TOP and COVER_BOT already
   *  decide the starting shapes for each answer and the preview already draws that pair,
   *  so the tile costs no new artwork and cannot disagree with what happens next.
   *  ⚠️ ASKS isSingle() RATHER THAN ASSUMING TWO PIECES, for the same reason paint() does:
   *  coverage sits before the style screen, but Back exists, and a one piece drawn as a
   *  bikini pair is a picture of a garment she did not choose. */
  /** One coverage answer's drawing, on its own, so the tile row and the phone's card
   *  deck draw the same garments from the same tables at two different sizes.
   *  ⚠️ SPLIT OUT OF coverTiles, NOT COPIED FROM IT. The one-piece fallback, the
   *  three-piece crop and the viewBox width all took a round of correcting each; a second
   *  copy is a second place for the tile and the card to start disagreeing. */
  function coverArtHtml(v, extraCls) {
    var top = COVER_TOP[v] || 'triangle', bot = COVER_BOT[v] || 'brief';
    /* ⚠️ FALL BACK TO shape-one-classic, NOT TO THE BIKINI PAIR. COVER_TOP holds TOP
       shapes, and only four of them have a one-piece symbol -- there is no
       shape-one-triangle. paint() already solved this the same way; both must agree or
       the tile and the preview show different garments. */
    var one = isSingle();
    var oneOk = one && topsFor().some(function (pr) { return pr[0] === top; });
    /* ⚠️ A THREE PIECE SHOWS THREE PIECES, AND ITS TOP IS FIXED AT TRIANGLE. Every
       3-Piece product in the store is tagged Triangle, so topsFor() offers no top
       choice here -- which made the two-piece tile wrong twice: it drew a top she
       cannot change, and it left out the crop that is the reason she chose this style.
       The bottom is the only thing these tiles decide. */
    var three = state.style === 'three';
    var art = one
      ? '<use href="#shape-one-' + (oneOk ? top : 'classic') + '" x="110" y="10" width="200" height="200"/>'
      : three
      ? '<use href="#shape-top-tankini" x="10" y="10" width="200" height="200"/>' +
        '<use href="#shape-top-' + top + '" x="210" y="10" width="200" height="200"/>' +
        '<use href="#shape-bottom-' + bot + '" x="410" y="10" width="200" height="200"/>'
      : '<use href="#shape-top-' + top + '" x="10" y="10" width="200" height="200"/>' +
        '<use href="#shape-bottom-' + bot + '" x="210" y="10" width="200" height="200"/>';
    return '<svg class="mky__coverart' + (extraCls ? ' ' + extraCls : '') +
           '" viewBox="0 0 ' + (three ? 620 : 420) + ' 220" aria-hidden="true" ' +
           'focusable="false">' + art + '</svg>';
  }

  /** The PHOTOGRAPHS for one coverage answer, or null when any piece it maps to has no
   *  render. Coverage is already defined as a pair of garments -- COVER_TOP and COVER_BOT
   *  say which -- and every one of those garments is in SHAPE_PHOTOS, so this costs no new
   *  artwork and cannot disagree with the stage, which composes the same pair from the
   *  same table.
   *
   *  ⚠️ NUNO, 2026-09-14: question 11 drew flat SVG outlines while question 10 drew
   *  photographs, "which seem to have more depth". Two neighbouring questions in two
   *  media is the complaint; this closes it with the renders that already exist.
   *
   *  ⚠️ THE COMPOSITION IS coverArtHtml's, PIECE FOR PIECE -- one garment on a one piece,
   *  top + bottom on a two, crop + top + bottom on a three. If the silhouette's
   *  composition ever changes, this changes with it or the row shows two different suits
   *  depending only on which medium happened to be available.
   *
   *  ⚠️ THE ONE-PIECE BRANCH IS UNREACHABLE TODAY AND IS KEPT ANYWAY. Coverage is SKIPPED
   *  on a one piece and on a monokini (see the ⚠️ at the top of the skip table) -- measured
   *  2026-09-14: picking One piece goes straight to Your shapes, Monokini to Your sizes -- so
   *  in the live flow this function only ever sees a two or a three piece. The branch exists
   *  because coverArtHtml has the matching one, and if the skip is ever lifted the two must
   *  not disagree about what a one piece looks like.
   *  It returns null rather than substituting `one:classic`, because COVER_TOP holds
   *  TWO-PIECE tops -- triangle, halter, high-neck-crop -- and `one:` renders exist only for
   *  classic, asymmetrical and monokini. Standing one photograph in for all four would put
   *  the SAME picture on four tiles that are supposed to differ, which is worse than four
   *  drawings that differ. */
  function coverPhotoPieces(v) {
    var top = COVER_TOP[v] || 'triangle', bot = COVER_BOT[v] || 'brief';
    if (isSingle()) {
      var ok = 'one:' + top;
      return SHAPE_PHOTOS[ok]
        ? { images: [SHAPE_PHOTOS[ok]], alts: [SHAPE_PHOTO_ALT[ok] || ''], layout: 'single' }
        : null;
    }
    var tk = 'top:' + top, bk = 'bottom:' + bot;
    if (!SHAPE_PHOTOS[tk] || !SHAPE_PHOTOS[bk]) return null;
    var imgs = [SHAPE_PHOTOS[tk], SHAPE_PHOTOS[bk]];
    var alts = [SHAPE_PHOTO_ALT[tk] || '', SHAPE_PHOTO_ALT[bk] || ''];
    if (state.style === 'three') {
      /* The crop goes FIRST, the way coverArtHtml puts tankini first: the included piece
         is the one that does not vary, and reading it before the two that do is what
         makes the tile legible as "the crop, plus this pair". */
      if (!SUIT_ASSETS.crop) return null;
      imgs.unshift(SUIT_ASSETS.crop);
      alts.unshift(SUIT_CROP_ALT);
      return { images: imgs, alts: alts, layout: 'three' };
    }
    return { images: imgs, alts: alts, layout: 'pair' };
  }

  /** All four coverage answers, or none. Same all-or-nothing rule tilesArt() encodes for
   *  the shape rows, and for the same reason: these tiles sit side by side, so one
   *  photograph among three drawings reads as an unfinished screen. Evaluated over
   *  Q.coverage's own option list rather than over COVER_TOP's keys, so adding a fifth
   *  answer without a render turns the row back to silhouettes instead of leaving one
   *  tile blank. */
  function coverPhotosReady() {
    var opts = (Q.coverage && Q.coverage.opts) || [];
    for (var i = 0; i < opts.length; i++) {
      if (!coverPhotoPieces(opts[i][0])) return false;
    }
    return opts.length > 0;
  }

  function coverPhotoHtml(v, extraCls, pieces) {
    var p = pieces || coverPhotoPieces(v);
    if (!p) return '';
    return '<div class="mky__coverphoto' + (extraCls ? ' ' + extraCls : '') +
           '" data-layout="' + p.layout + '">' +
           p.images.map(function (src, i) {
             return '<img class="mky__coverphotoimg" src="' + escAttr(src) + '" alt="' +
                    escAttr(p.alts[i] || '') + '" decoding="async">';
           }).join('') + '</div>';
  }

  /** THE ONE ENTRY POINT for coverage artwork, so the tile row and the phone's card deck
   *  can never end up in different media on the same build. Photographs when every answer
   *  has them, the traced silhouettes otherwise. */
  function coverTileArtHtml(v, card) {
    return coverPhotosReady()
      ? coverPhotoHtml(v, card ? 'mky__coverphoto--card' : '')
      : coverArtHtml(v, card ? 'mky__coverart--card' : '');
  }

  function coverTiles(opts, value) {
    return '<div class="mky__shapes mky__shapes--cover' +
           (coverPhotosReady() ? ' mky__shapes--coverphoto' : '') + '">' + opts.map(function (o) {
      return '<button class="mky__shape" type="button" data-v="' + o[0] +
             '" aria-pressed="' + (o[0] === value) + '">' +
             coverTileArtHtml(o[0], false) + '<b>' + o[1] + '</b>' +
             (o[2] ? '<span>' + o[2] + '</span>' : '') + '</button>';
    }).join('') + '</div>';
  }

  function sizeChips(attr, value) {
    return '<div class="mky__chips">' + SIZES.map(function (x) {
      return '<button class="mky__chip" type="button" data-attr="' + attr + '" data-v="' + x +
             '" aria-pressed="' + (x === value) + '">' + x + '</button>';
    }).join('') + '</div>';
  }

  /* Renders into the stage column, under the garment. Returns nothing, because callers used to
     concatenate this into their own innerHTML, which is what buried it below the fold. */
  /* The capture only makes sense once there is a design worth keeping, so it lives on
     the record and nowhere else. The hidden fields are filled from live state rather
     than from the DOM, so what Evelin receives is what the customer is looking at. */
  /* ⚠️ THE SHOPIFY FORM STORES NOTHING. It emails the store and that is all: no
     customer, no subscriber, no row anything can query later. Verified 2026-09-04 on the
     live store. So the opt-in ticked on that form buys a follow-up that has nothing to
     run against, and this beacon is the only thing that makes the capture a record.

     sendBeacon, not fetch: the form navigates away immediately, and a normal request is
     canceled on unload. Consent is passed through rather than gated here, so the one
     place that decides whether an address becomes mailable stays server-side. */
  function sendCapture() {
    var url = root.dataset.captureUrl;
    var shop = root.dataset.shopDomain;
    var emailEl = document.getElementById('mky-save-email');
    if (!url || !shop || !emailEl || !emailEl.value) return;
    /* ⚠️ SCOPED, FOR THE SAME REASON `el.submitRow` NOW IS. This reads the marketing
       tick that travels with a SAVED DESIGN. The inquiry form deliberately has no opt-in
       at all, so today there is exactly one on the page and a bare selector happens to be
       right -- but "happens to be right" is what `.mky__submitrow` was until a second
       form appeared above it, and consent is the last field on this page that should be
       read from whichever element the document happened to hold. */
    var optIn = document.querySelector('#mky-save .mky__optin input');
    var firstEl = document.getElementById('mky-save-first');
    var body = JSON.stringify({
      shop_domain: shop,
      email: emailEl.value,
      /* ⚠️ THE NAME THE CONFIRMATION EMAILS OPEN WITH, AND NOTHING MORE. The capture
         endpoint has taken `first_name` since it was written -- `track_design_saved`
         writes it straight onto the subscriber -- and `{{ first_name }}` is already a
         registered merge tag whose blank value resolves to "there", so an empty field
         sends "Hi there," and never a literal tag. Nothing server-side needed changing;
         the form simply never asked. Bounded here as well as server-side (it clamps to
         100) because this POST is unauthenticated. */
      first_name: firstEl && firstEl.value ? firstEl.value.trim().slice(0, 100) : '',
      consent: !!(optIn && optIn.checked),
      design: {
        url: location.origin + location.pathname + '?' + syncUrl().toString(),
        style: state.style,
        top_shape: effectiveShape('topShape'),
        bottom_shape: isSplit() ? effectiveShape('bottomShape') : '',
        /* The size she is cut to, derived from the bust when it is known. Band and cup
           travel alongside it, never instead of it: the derivation is one-way, so
           dropping them would throw away the only figure that can be recovered from. */
        top_size: effectiveTopSize(),
        band: state.band,
        cup: state.cup,
        bottom_size: state.bottomSize,
        fabric: state.fabric,
        /* The NAME as well as the code. `5283` is a Nunez supplier code and means
           nothing to a customer, but it is all the confirmation email had, so the
           email read "fabric 5283". The flow already holds the name in FABRICS, so
           sending it costs nothing and saves the email a Shopify lookup on the
           send path. */
        fabric_name: (slotFace(state.fabric) || {}).name || '',
        /* ⚠️ THE THIRD PIECE, NEVER A THIRD FABRIC, AND IT IS HER CHOICE NOW. On a two
           fabric three piece the crop takes ONE of the two and she says which; the crop
           itself is never reversible, so this is a single code and not a pair. Evelin,
           2026-09-15. Empty unless this is a three piece. */
        crop_fabric: cropFabricCode(),
        crop_face: cropChoiceApplies() ? state.cropFace : '',
        /* ⚠️ TRUE ONLY ON `samecrop`, AND IT IS THE DIFFERENCE BETWEEN TWO CONSTRUCTIONS
           THAT LOOK THE SAME IN A CODE. `crop_fabric` alone cannot tell "the crop shares
           one of the suit's two cloths" from "the crop IS the second cloth"; the workroom
           cuts and orders differently for each. */
        crop_own_fabric: needsSlot('crop'),
        /* ⚠️ A PENDING COLOUR OCCUPIES A FABRIC ASSIGNMENT AND MUST NOT VANISH FROM THE
           SUBMITTED SPECIFICATION. Codex, 2026-09-15. `fabric`/`fabric_bottom`/
           `fabric_inside` above already carry the `solid:` code -- which cannot be
           mistaken for a Nunez number and was never fabricated into one -- and this
           expands it so the human follow-up has what it needs without parsing a string:
           which slots are pending, the family, the refinement she chose or did not, and
           her own words about the colour.
           ⚠️ IT IS ALSO THE FLAG THE EMAIL AND EVELIN'S TRIAGE BRANCH ON. A design with
           `solid_pending` true is complete and submittable and is NOT ready to cut. */
        solid_pending: pendingSlots().length > 0,
        solid_requests: pendingSlots().map(function (k) {
          var pr = parseSolid(state[k]) || {};
          return { slot: k, piece: SLOT_LABEL[k], code: state[k],
                   family: pr.fam ? pr.fam.key : '',
                   refinement: pr.refine ? pr.refine[0] : '',
                   label: solidName(state[k]),
                   note: (state.solidNotes[k] || '').slice(0, 120) };
        }),
        crop_reversible: false,
        /* ⚠️ THE SPECIFICATION, NOT THE FIELD -- see bottomFabricCode(). Empty on a
           reversible suit even when she answered `mixed` first, because a reversible
           suit has no second bottom fabric to cut. */
        fabric_bottom: bottomFabricCode(),
        fabric_inside: insideFabricCode(),
        variation: state.variation,
        /* Empty when this garment is never asked -- a one piece and a monokini skip the
           screen. See coverageLabel(); the same three-state rule the torso answer follows. */
        coverage: coverageApplies() ? state.coverage : '',
        coverage_applicable: coverageApplies(),
        /* Height and the three shape answers. RAW, and for the same reason the fit
           answers below are raw: prose cannot be parsed back, and these three carry no
           prose at all yet. They are also the only record of what was asked, which is
           what makes "discard them later if they earn nothing" a decision someone can
           actually take rather than a guess. */
        height: state.height,
        volume: state.volume,
        waist: state.waist,
        tummy: state.tummy,
        /* ⚠️ THE RAW FIT ANSWERS, not just their consequences.
           `builds` carries construction PROSE ('Underwired', 'Unlined, no cups'), and
           the store's product pages need the ANSWER: every listing carries a FEATURES
           list with `Padding (Y/N)` and `Wire (Y/N)` rows, and prose cannot be parsed
           back into one reliably. It is worse than unreliable for wire, because
           buildGroups() DELIBERATELY DROPS `wire: yes` on a reversible suit, so the
           prose says nothing where the answer was yes.
           These are preferences, not measurements, so they are storable on the same
           footing as the sizes already sent. They must never be rendered on a public
           product page: a pooled listing's fit answers were another woman's. */
        /* The four fabric answers, not just the fabric they led to: the BANDS she tapped
           (FABRIC_BANDS), null for "Either", key absent when unanswered. Until 2026-09-12
           these were positions on the measured axes; they are labels now, and the server
           (`_clean_prefs` in tracking_webhooks.py) reads this exact shape under this
           exact key. Narrowing ~100 fabrics to a handful and then discarding how she got
           there throws away the only taste signal the flow produces, and it cannot be
           reconstructed from the chosen code.
           ⚠️ `prefs`, NOT `fabric`. `fabric` above in this literal is the CODE she chose, and a
           second `fabric:` in this literal silently replaced it with this object: prod
           listing 40 (2026-09-13) stored `{'color': 'moderate'` as its fabric code and
           failed with "That fabric isn't available any more." Same shape as the note on
           the state literal's `prefs:` key. */
        prefs: state.prefs,
        /* Carried whole rather than folded into a size: an unlisted bust is the one
           answer on this screen that our chart cannot express, so expressing it would
           throw away the only useful thing about it. */
        bustUnlisted: state.bustUnlisted,
        bustLabel: state.bustLabel,
        bustSystem: state.bustUnlisted ? BUST_SYSTEM : '',
        bustSkipped: state.bustSkipped,
        /* ⚠️ THE ANSWER ONLY TRAVELS WHEN THE GARMENT ASKED FOR IT, AND THE FLAG IS
           WHAT KEEPS THREE STATES APART. Empty here means one of two things and
           `torso_applicable` says which: false is "this garment has no connected body, so
           the question was never put"; true with an empty string is "she has not answered
           it". `'unknown'` is a fourth thing again -- answered, and the answer is that she
           does not know. Codex, 2026-09-15: a skipped torso question is not an unknown and
           is not a No, and `standard` must never be written in its place. */
        torso: torsoApplies() ? state.torso : '',
        torso_applicable: torsoApplies(),
        /* The same three states in prose, for the human reading the email. */
        fit_notes: fitNotes(),
        top_fit: state.top,
        wire: state.wire,
        padding: state.padding,
        seat: state.seat,
        price: priceNow(),
        builds: builds(),
        /* Which CTA she pressed. 'save' is the default because a submit that
           somehow reaches here without a click is far likelier to be a save
           than a purchase signal, and over-reporting intent is the costlier
           mistake: it puts Evelin on the phone to someone who was browsing. */
        intent: state.intent || 'save',
        /* Bounded here as well as server side: a textarea is the one free-text field
           on this form and the POST is unauthenticated. */
        feedback: (function () {
          var f = document.getElementById('mky-save-feedback');
          return f && f.value ? f.value.slice(0, 600) : '';
        })(),
        /* ⚠️ ONCE, WITH THE QUESTION EACH ONE WAS WRITTEN AGAINST, AND NOT FOLDED INTO
           `feedback`. Codex: "Include the notes once in the appropriate save/reserve
           payload and maker record... Keep the existing general freeform field without
           duplicating the same text into multiple places." `feedback` is the open-ended
           box on the record and stays exactly what it was; these are a different thing
           and travel in their own key.
           ⚠️ THEY DESCRIBE REQUESTS, NOT DECISIONS. Nothing here overrides a validated
           selection, the two-fabric limit, the price or a construction rule, and a note
           is not acceptance of a custom configuration. The specification above is still
           the specification. */
        notes: state.notes.map(function (n) {
          return { question: n.q, slot: n.slot, text: n.text };
        })
      }
    });
    try {
      /* ⚠️ text/plain, NOT application/json, and that is the whole fix.
         `application/json` is not a CORS-safelisted content type, so it forces a
         PREFLIGHT: the browser must do OPTIONS, wait, then POST. sendBeacon exists
         precisely to outlive the page, and this call fires as the Shopify form
         submits and navigates away, so on iOS Safari the document dies between the
         two round trips and the POST is dropped. Observed on prod 2026-09-09 from
         an iPhone: `OPTIONS /api/v1/tracking/design-saved/ 200` and no POST, and
         nothing recorded. The preflight was answering correctly the whole time.
         text/plain needs no preflight, so the beacon goes in ONE shot and survives
         the navigation. The endpoint does json.loads(request.body) and never reads
         Content-Type, verified against prod. */
      var blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
      /* ⚠️ sendBeacon returns true when the request is QUEUED, not when it lands,
         so `if (sendBeacon(...)) return` made the fetch below unreachable on every
         browser that queued and then failed. The fallback therefore runs ONLY when
         the browser REFUSES to queue (a false return), which is the only failure
         sendBeacon reports synchronously -- a queued-then-dropped beacon is
         undetectable from here, which is exactly why the preflight had to go. */
      if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) return;
    } catch (e) { /* fall through to fetch */ }
    try {
      /* Same reasoning: text/plain keeps this preflight-free too. `no-cors` would
         make the response opaque but does NOT exempt a non-safelisted content type
         from the preflight. */
      fetch(url, { method: 'POST', body: body, keepalive: true, mode: 'no-cors',
                   headers: { 'Content-Type': 'text/plain;charset=UTF-8' } });
    } catch (e) {
      /* Capture is additive. Never let it break the submission the customer
         actually pressed: Evelin still gets the design by email regardless. */
    }
  }

  /** Put Back and Start again in `row`, first and second. Idempotent on purpose: render()
   *  runs on every intent click on the record screen, and re-homing a node that already
   *  sits there would blur it if it happened to hold focus. */
  function moveBottomRow(row) {
    if (!row || !el.back) return false;
    if (el.back.parentNode === row) return true;
    row.insertBefore(el.back, row.firstChild);
    if (el.reset) row.insertBefore(el.reset, el.back.nextSibling);
    return true;
  }

  /** ⚠️ ONLY ONCE THE SEND BOX IS OPEN. The submit row lives INSIDE `#mky-send`, the
   *  disclosure that stays collapsed until she picks Save or Reserve -- so moving Back
   *  and Start again in unconditionally left the record screen with no way back at all
   *  until she committed to one of the two. Caught by measuring, not by looking: the
   *  controls were in the right parent and 0x0. Before the disclosure opens the nav row
   *  is the bottom row, minus Continue; after, the form's row is, with Save on the right.
   *  ⚠️ CALLED FROM THE INTENT CLICK TOO, because that handler opens the box by toggling
   *  a class and never goes through render(). */
  function placeBottomRow() {
    var box = document.getElementById('mky-send');
    var open = step === lastStep() && !!box && box.classList.contains('mky__send--on');
    /* ⚠️ HIDE THE NAV ONLY IF THE CONTROLS ACTUALLY LANDED SOMEWHERE. `el.submitRow` is
       null on the `form.posted_successfully?` render, which emits no submit row at all.
       Today that cannot coincide with `open`, because `#mky-send` lives in the SAME
       Liquid branch as the submit row and `open` tests for it -- but nothing asserts
       that coupling, and if it ever breaks the failure is a record screen with no Back
       and no Start again: a dead end, silently. Cheap to make impossible. */
    var placed = moveBottomRow(open ? el.submitRow : el.nav);
    if (!el.nav) return;
    el.nav.hidden = open && placed;
    /* ⚠️ `space-between` WITH CONTINUE GONE PUTS START AGAIN IN CONTINUE'S SLOT. Two
       children instead of three, and the browser pushes the second to the right edge --
       the exact bottom-right position nineteen screens have trained her to press, which
       is the whole complaint. Quieter than the pink button that was there before, and
       still wrong. Flag the row so it packs left instead. */
    el.nav.classList.toggle('mky__nav--noforward', step === lastStep());
  }

  function paintSave(show) {
    if (!el.save) return;
    /* ⚠️ A CONFIRMED POST KEEPS THIS SECTION OPEN WHATEVER THE STEP SAYS. The success
       block lives INSIDE `#mky-save`, which is collapsed everywhere except the record --
       so a return that did not land exactly on the last step rendered "Sent. We have your
       design." into a hidden container and showed her nothing. Exactly the containment
       bug Codex found on the inquiry side, on the other form, and found here by the test
       written for that one. Normally the restored design does reach the record and this
       changes nothing; it is the "normally" that made it worth removing. */
    if (document.querySelector('#mky-save [data-mky-posted]:not([hidden])')) show = true;
    /* ⚠️ DO NOT PUT THE `hidden` ATTRIBUTE BACK ON THIS FORM. The store runs Shopify's
       storefront hCaptcha on contact forms, and that script arms forms it finds at page
       load. A form marked hidden at load is the kind of thing a bot-protection script
       skips, and the failure mode is silent: the customer clicks, nothing navigates, and
       the form sits there looking untouched. Visibility is a class now, so the element
       is always a plain, visible-to-script form in the DOM. */
    el.save.classList.toggle('mky__save--on', show);
    /* ⚠️ BACK AND START AGAIN MOVE; THE SUBMIT BUTTON NEVER DOES. Save has to sit
       bottom-right, in the slot Continue holds everywhere else -- and the way to get it
       there is to bring the other two controls INTO the form's own row, not to lift the
       submit out to them. That button is a real `type="submit"` inside a
       `{% form 'contact' %}` that Shopify's storefront hCaptcha armed at page load;
       moving it out, even with a `form=` attribute, risks the one failure this screen
       cannot afford -- the silent one, where she presses Save and nothing happens. Back
       and Start again are `type="button"` and inert wherever they sit. */
    placeBottomRow();
    if (!show) return;
    var form = el.save.querySelector('form');
    if (form && !form.dataset.captureBound) {
      form.dataset.captureBound = '1';
      /* ⚠️ INTENT IS CAPTURED ON CLICK, NOT READ AT SUBMIT. The storefront hCaptcha
         defers the real submission, so by the time `submit` fires the activating
         button is gone -- `document.activeElement` and `event.submitter` are both
         unreliable. Each button therefore stamps the intent on mousedown-ish click,
         and `submit` only reads what was stamped. */
      /* ⚠️ THE FEEDBACK FIELD OPENS WITH A CLASS AND IS NEVER RE-CLOSED ONCE OPEN. A
         toggle that could close it would be able to hide text she had already typed, and
         the field is submitted whether or not it is on screen -- so closing it would
         silently send something she thought she had withdrawn. One direction only.
         ⚠️ BOUND HERE, INSIDE THE `captureBound` GUARD, so it is wired exactly once for
         the life of the page -- the record screen re-renders and this block does not. */
      var fbToggle = document.getElementById('mky-feedback-toggle');
      var fbWrap = document.getElementById('mky-feedback-wrap');
      if (fbToggle && fbWrap) {
        fbToggle.addEventListener('click', function () {
          fbWrap.classList.add('mky__feedbackwrap--on');
          fbToggle.setAttribute('aria-expanded', 'true');
          fbToggle.parentNode.style.display = 'none';
          var ta = document.getElementById('mky-save-feedback');
          if (ta) ta.focus();
        });
      }
      var choices = form.querySelectorAll('[data-mky-intent]');
      var sendBox = document.getElementById('mky-send');
      var emailEl = document.getElementById('mky-save-email');
      var firstEl = document.getElementById('mky-save-first');
      var submitEl = document.getElementById('mky-save-submit');
      /* ⚠️ THE ERROR HAS TO NAME THE FIELD, BECAUSE NOTHING ELSE ON SCREEN DOES ANY MORE.
         Codex, 2026-09-16: "Make validation errors identify the affected field even after
         typing has replaced its placeholder." With the visible captions gone, the browser's
         own "Please fill out this field" points at a box whose placeholder has already been
         replaced by whatever she typed -- and on a two-field row that is genuinely
         ambiguous. The custom message leads with the field's name.
         ⚠️ AND IT IS CLEARED ON EVERY KEYSTROKE. A customValidity that is set and never
         reset makes the field permanently invalid, so the form would refuse to submit
         however correct the address became. */
      if (emailEl) {
        emailEl.addEventListener('invalid', function () {
          emailEl.setCustomValidity(
            emailEl.value.trim()
              ? 'Email: that does not look like an email address.'
              : 'Email: please enter your email so we can send you your design.');
        });
        emailEl.addEventListener('input', function () { emailEl.setCustomValidity(''); });
      }
      Array.prototype.forEach.call(choices, function (btn) {
        btn.addEventListener('click', function () {
          state.intent = btn.getAttribute('data-mky-intent');
          var reserving = state.intent === 'reserve';
          Array.prototype.forEach.call(choices, function (b) {
            b.setAttribute('aria-pressed', String(b === btn));
          });
          var label = document.getElementById('mky-save-intent-label');
          if (label) {
            label.value = reserving
              ? 'Design Studio: RESERVED, wants this one made'
              : 'Design Studio: saved design';
          }
          if (submitEl) {
            submitEl.textContent = reserving
              ? root.dataset.reserveSubmit
              : root.dataset.saveSubmit;
          }
          /* ⚠️ WAS IT ALREADY OPEN? Read BEFORE the class goes on, because it decides
             whether this press is an OPENING -- the only thing allowed to move focus. */
          var opening = !(sendBox && sendBox.classList.contains('mky__send--on'));
          if (sendBox) sendBox.classList.add('mky__send--on');
          /* The bottom row becomes the form's row the moment the box opens: Save has to
             land in the slot Continue held on every screen before this one. */
          placeBottomRow();
          /* Set only once the field is visible: a `required` control inside a
             display:none wrapper makes the browser refuse to submit, and it
             reports that only to the console. */
          if (emailEl) emailEl.setAttribute('required', 'required');
          /* ⚠️ FIRST NAME TAKES THE FOCUS, NOT EMAIL, AND ONLY ON THE PRESS THAT OPENS
             THE FORM. Both Save and Reserve focused the EMAIL box while First Name sat
             above it, so the caret and the keyboard landed on the second field of two and
             a customer filling the form top-down had to go back up for the first. Codex,
             2026-09-16, on both entry points.
             ⚠️ AND SWITCHING INTENT WITH THE FORM ALREADY OPEN MOVES NOTHING. Pressing
             Reserve after Save changes the label and the hidden field; it must not yank
             the caret out of whatever she is halfway through typing.
             ⚠️ NOT ON THE FIRST PAINT EITHER -- focusing on load would pull a phone
             keyboard up over the garment she has just made. This is inside a click. */
          if (opening) {
            var into = firstEl || emailEl;
            if (into && into.focus) {
              try { into.focus(); } catch (e) { /* detached: nothing to focus */ }
            }
          }
        });
      });
      /* On submit, not on click: hCaptcha defers the real submission, and the
         submit event is the one signal that fires on the path a person takes. */
      form.addEventListener('submit', sendCapture);
    }
    var url = location.origin + location.pathname + '?' + syncUrl().toString();
    var f = slotFace(state.fabric), fb = slotFace(bottomFabricCode()),
        fin = slotFace(insideFabricCode());
    var bits = [styleLabel(), label(topsFor(), effectiveShape('topShape'))];
    if (isSplit()) bits.push(label(BOTTOMS, effectiveShape('bottomShape')));
    if (coverageLabel()) bits.push('Coverage: ' + coverageLabel());
    /* ⚠️ THIS IS WHERE HEIGHT AND THE THREE SHAPE ANSWERS EARN THEIR PLACE. None of them
       adds a line to `builds`, so the record tells the customer nothing new; they reach
       the person who cuts, on the line that lands in Evelin's inbox. Dropping this makes
       all four questions decorative. */
    if (bodyLine()) bits.push(bodyLine());
    /* ⚠️ THE THREE STATES `builds` CANNOT CARRY. Not applicable, not sure, and
       recorded-but-not-built all reach the workroom here, because none of them is a
       construction change and `builds` is only construction. See fitNotes(). */
    fitNotes().forEach(function (n) { bits.push(n); });
    if (heightBeyondGrade()) bits.push('⚠ HEIGHT PAST STANDARD GRADE - talk before cutting');
    if (state.band && state.cup) bits.push('Bust ' + state.band + state.cup);
    else if (state.bustUnlisted && state.bustLabel) {
      bits.push('Bust ' + state.bustLabel + ' (read as ' + BUST_SYSTEM +
                ' sizing, NOT on our chart - needs review, no size has been graded)');
    } else if (state.bustSkipped) bits.push('Bust not known - to be confirmed');
    bits.push(effectiveTopSize() + ' top, ' + state.bottomSize + ' bottom');
    if (f) bits.push(f.name + (fb ? ' / ' + fb.name : '') + (fin ? ' / ' + fin.name + ' inside' : ''));
    /* ⚠️ THE WORKROOM IS TOLD IN CAPITALS, ON ITS OWN LINE, THAT THIS IS NOT READY TO CUT.
       Codex, 2026-09-15: a customer may complete and submit a design with a pending solid,
       and the review and the maker instructions must say "Solid fabric confirmation
       needed". A colour direction is not a fabric until Evelin has photographed what she
       actually has and the customer has approved it. */
    if (pendingSlots().length) {
      bits.push(SOLID_MAKER_NOTE + ': ' + pendingSlots().map(function (k) {
        return SLOT_LABEL[k] + ' ' + solidName(state[k]) +
               (state.solidNotes[k] ? ' (' + state.solidNotes[k] + ')' : '');
      }).join('; '));
    }
    /* ⚠️ THE CROP'S CLOTH REACHES THE WORKROOM IN WORDS. It was only ever implied by the
       picture; a three piece's cutting line named two fabrics and three pieces and left
       the third to be inferred. See cropAssignment(). */
    if (cropAssignment()) bits.push(cropAssignment());
    /* ⚠️ THE WORKROOM SEES WHAT SHE ASKED FOR, IN HER WORDS, ONCE. Same list as the POST
       and the review -- notesLines() is the single renderer, which is what stops a note
       appearing on one and missing from another. */
    if (state.notes.length) {
      bits.push('HER NOTES: ' + notesLines().join(' | '));
    }
    bits.push('$' + priceNow());
    var b = builds();
    if (b.length) bits.push(b.join('. ') + '.');
    var set = function (id, v) { var n = document.getElementById(id); if (n) n.value = v; };
    set('mky-save-url', url);
    set('mky-save-suit', bits.join(' | '));
    /* ⚠️ `return_to` REPLACES SHOPIFY'S OWN REDIRECT, WHICH IS WHAT CARRIES
       `contact_posted=true`. Setting it to the bare design URL preserved the design and
       silently destroyed the confirmation: the customer submitted, landed back on an
       identical-looking page, and had no way to tell whether anything had happened.
       Verified 2026-09-04: loading the page WITH the param renders the success block, so
       the Liquid was never at fault. Carry the param through by hand. */
    set('mky-save-return', location.pathname + '?' + syncUrl().toString() + '&contact_posted=true');
    try { sessionStorage.setItem('mky-last', url); } catch (e) { /* private mode */ }
  }

  function paintBuilds(show) {
    var g = show ? buildGroups() : [];
    el.builtSlot.innerHTML = g.length
      ? '<div class="mky__built"><p class="mky__built-h">Cut for you</p><dl>' +
        g.map(function (row) {
          return '<dt>' + row.label + '</dt><dd>' +
                 row.items.map(function (t) { return '<span>' + t + '</span>'; }).join('') + '</dd>';
        }).join('') + '</dl></div>'
      : '';
  }

  /** Which stage, if any, a question repaints on hover and focus.
   *  ⚠️ ADDED RATHER THAN COPYING THE WIRING. The You chapter's preview handling had
   *  already been through two bugs -- the blur that wiped the garment, and the
   *  per-option mouseleave that flashed the default in every gap -- and a second copy
   *  for the torso question would have been a second place to fix each of them. Both
   *  painters take the same (preview|null) argument and guard their own step, so one
   *  set of listeners drives both. */
  function painterFor(key) {
    if (BODY_KEYS.indexOf(key) > -1) return paintBody;
    if (FIG[key]) return paintFig;
    return null;
  }

  /** Where she is among the screens SHE will actually see.
   *  ⚠️ NOT RAW STEP INDICES. A monokini skips coverage and shapes, so the raw count
   *  jumps 10 -> 13 and reads as a bug the moment it is on screen. It was always computed
   *  that way; it only mattered once the number stopped being invisible. The dots can
   *  afford raw indices because they draw a skipped step as a hollow dot, which says
   *  "not yours" without arithmetic. A number cannot say that, so it counts hers. */
  function stepCount() {
    var total = 0, done = 0;
    for (var i = 0; i <= lastStep(); i++) {
      if (skipsStep(i)) continue;
      total++;
      if (i <= step) done++;
    }
    /* ⚠️ THE TOTAL IS NOT KNOWN UNTIL SHE HAS CHOSEN A KIND, AND SINCE 2026-09-15 THAT
       IS THE FIRST SCREEN. Which screens apply is decided by the garment: a two piece is
       asked about coverage, a monokini is not; a one piece is asked about torso length, a
       two piece is not. With `style` still empty none of those predicates has an answer,
       so the number this loop produces is arithmetic over a garment nobody has specified.
       It USED to be harmless -- the kind screen was step ten, so by the time a total was
       printed it was a real one -- and it is not harmless now: the flow opened on
       "1 of 18" and the very first tap changed it to "2 of 19", which reads as a counter
       that cannot count.
       ⚠️ SO IT IS WITHHELD, NOT GUESSED. Printing a plausible total for an unspecified
       garment is a number she could plan around and we would then move. Callers that show
       a count check `known` and say nothing rather than something provisional. */
    return { done: done, total: total, known: !!state.style };
  }

  /** Put "13 of 20" beside whatever question has just been rendered.
   *  ⚠️ STAMPED AFTER THE FACT RATHER THAN BUILT INTO EACH HEADING. Every step renderer
   *  writes its own `<h2 class="mky__q">`, and there are nine of them plus questionStep --
   *  ten places to add the same span, and ten places for the next person to forget it.
   *  One stamp, called at the two points where a step finishes rendering. */
  /** Everything that has to happen once a step -- any step -- has finished rendering.
   *  ⚠️ THREE CALL SITES, AND THEY ARE THE THREE PLACES A STEP FINISHES: the deck, the
   *  question list, and the tail of render() for every other screen. Anything added here
   *  reaches all of them; anything added to one of them reaches a third of the flow, which
   *  is how the progress track went missing from the non-deck screens the first time. */
  /* ⚠️ THE NEW QUESTION TAKES FOCUS, AND SINCE 2026-09-14 IT HAS TO. An answer advances
     now, so the control she pressed is destroyed by the render that follows -- and the
     browser then drops focus onto whatever comes first in the new screen. On the kind
     screen that first thing is an option carrying a focus PREVIEW, so simply ARRIVING
     there previewed "One piece", ran snapShapesForStyle() against a style she had not
     chosen, and turned a linked halter into a classic before she touched anything. The
     next click's `stylePreview = null` then threw away the saved halter for good.
     Measured by kind2.py's linked-shape walk, 2026-09-14.
     ⚠️ AND IT IS THE RIGHT THING FOR A SCREEN READER REGARDLESS: what should be
     announced on arriving at a new question is the question, not the first button on it.
     `tabindex="-1"` makes the heading focusable without putting it in the tab order, and
     `preventScroll` leaves scrollToFlow() in charge of where the page sits.
     ⚠️ ONLY ON A STEP CHANGE. A re-render in place (a checkbox ticked, a review panel
     opened) must leave focus where she put it -- that is what `cardRefocus` is for, and
     the two never collide because cardRefocus is only set when the screen stays. */
  var stepJustChanged = false;
  function focusNewStep() {
    var h = el.step && el.step.querySelector('.mky__q');
    if (!h) return;
    h.setAttribute('tabindex', '-1');
    try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); }
  }

  /* ------------------------------------------------------------------------------
     THE SUIT PREVIEW ON THE FABRIC SCREENS, ON A PHONE (Codex's Studio brief, item 3).

     ⚠️ WHAT WAS WRONG, MEASURED RATHER THAN ARGUED. At 390x659, on the main fabric
     picker with a fabric chosen, `#preview-stage` sat at y=764 -- 105px BELOW the bottom
     of the viewport, under the browse controls, the search field, the heading line, two
     rows of swatches and the browse-all row. The one screen whose entire question is
     "what does this cloth look like on my suit" was the one screen where her suit was not
     on screen at all. `.mky--pilot .mky__body--split` puts the panes at `order: 2` and
     the question at `order: 1`, so every phone screen draws its picture under the answers;
     on the four pickers the answers are a catalogue and the picture falls off the page.

     ⚠️ THE PANE IS BORROWED, NOT COPIED. There is ONE preview in this flow and it is
     `#preview-stage` (or `#mky-suitpane` while there is no fabric to carry yet). A second
     renderer inside the step is how two layouts came to say different things twice
     already in this file. paint() writes into these nodes by id and does not care where
     they are, so moving them is free and duplicating them is not.

     ⚠️ AND A BORROWED NODE INSIDE `#mky-step` IS ONE innerHTML AWAY FROM BEING DESTROYED.
     Every step renderer assigns `el.step.innerHTML`, so a pane left in the question column
     across a render would be wiped and `el.svg` would point at a detached node for the
     rest of the session -- silently, with the stage simply never drawing again. So the
     panes are sent HOME at the top of render(), before any markup is written, and
     borrowed back at the end of finishStep(), after it exists. Those two calls are the
     only owners of where these nodes live; nothing else may move them.

     ⚠️ `pointer-events: none` ON THE PANES STAYS ON, and the CSS note beside it predicted
     it would have to come off for this. It does not: the pane is still decorative and the
     control is a BUTTON laid over it, which is also what keeps the markup valid -- the
     suit pane contains a `<p>`, and a `<p>` inside a `<button>` is not a thing.
     ------------------------------------------------------------------------------ */

  /** The four catalogue screens. `fabricFix` is deliberately NOT one of them: it is a
   *  correction screen naming three cloths, and a preview drawing two of them would be
   *  arguing with the question. */
  function fabricPickerNow() {
    var id = screenId();
    return id === 'fabric' || id === 'fabricBottom' || id === 'fabricInside' ||
           id === 'fabricCrop';
  }

  /** Put both panes back exactly where the section put them. Unconditional and cheap:
   *  called at the top of every render, so however a pane came to be somewhere else --
   *  the question column, the enlarged view, a render interrupted half way -- the next
   *  render starts from the same known position. */
  function stagePanesHome() {
    /* ⚠️ NOTHING IN HERE MAY THROW, AND THAT IS THE SECOND HALF OF THE FIX. This runs on
       the FIRST LINE of render(), so one DOM exception here does not misplace a picture --
       it aborts the entire render, on every render, for the rest of the session. That is
       exactly what shipped: a `NotFoundError` from a stale anchor turned every swatch tap,
       every keystroke in the search box and every browse control into a no-op, with the
       screen still looking perfectly healthy. A picture in the wrong place is a defect; a
       flow that silently stops responding is a different order of thing. */
    [[el.svg, el.svgHomeAt], [el.suitPane, el.suitHomeAt]].forEach(function (pair) {
      var node = pair[0], at = pair[1];
      if (!node || !at || !at.parentNode) return;
      if (node.parentNode === at.parentNode && node.nextSibling === at) return;
      try {
        at.parentNode.insertBefore(node, at);
      } catch (e) {
        /* Belt and braces. The marker cannot go stale by design; if it somehow does, a
           pane parked in the question column is far worse than a pane in the wrong
           place, because the next innerHTML destroys it. */
        if (el.stage) { try { el.stage.appendChild(node); } catch (e2) { /* give up quietly */ } }
      }
    });
  }

  /** Borrow the panes into the fabric screen's own preview slot, on a phone.
   *  ⚠️ BOTH PANES MOVE, NOT "THE VISIBLE ONE". Which of the two is showing is paint()'s
   *  decision and it changes the moment she picks a cloth: with no fabric yet the stage is
   *  a photograph of the garment (`#mky-suitpane`), and from her first swatch on it is the
   *  silhouette carrying her actual cloth. Moving only the one that happened to be visible
   *  would leave the other behind the moment that flipped. */
  function placeFabricPreview() {
    if (!pilotOn() || !fabricPickerNow()) return;
    var slot = document.getElementById('mky-fabprev-stage');
    if (!slot) return;
    if (el.suitPane) slot.appendChild(el.suitPane);
    if (el.svg) slot.appendChild(el.svg);
    var host = document.getElementById('mky-fabprev');
    if (host) {
      /* ⚠️ THE SLOT HIDES ITSELF WHEN THERE IS NOTHING IN IT TO SEE. paint() may have
         hidden both panes -- there is no branch on the pickers that does today, but an
         empty bordered box above the catalogue is the failure this costs nothing to make
         impossible, and it is the kind of thing a later screen change introduces. */
      var shown = (el.svg && el.svg.style.display !== 'none') ||
                  (el.suitPane && !el.suitPane.hidden);
      host.hidden = !shown;
    }
    var zoom = document.getElementById('mky-fabprev-zoom');
    if (zoom && !zoom.dataset.wired) {
      zoom.dataset.wired = '1';
      zoom.addEventListener('click', openFabricPreview);
    }
  }

  /** The preview slot's markup. Emitted by each picker directly under its heading, so the
   *  suit is the first thing under the question and above every control.
   *  ⚠️ THE LABEL IS A REAL SENTENCE, NOT AN ICON. It is the only thing telling her the
   *  picture can be opened, and it names what she gets rather than decorating the corner. */
  function fabricPreviewHtml() {
    if (!pilotOn()) return '';
    return '<div class="mky__fabprev" id="mky-fabprev">' +
             '<div class="mky__fabprevstage" id="mky-fabprev-stage"></div>' +
             '<button class="mky__fabprevzoom" type="button" id="mky-fabprev-zoom">' +
               '<span class="mky__fabprevlab">See it larger</span>' +
             '</button>' +
           '</div>';
  }

  /** The enlarged view: the SAME pane, moved, never a copy.
   *  ⚠️ A CLONE WOULD HAVE DUPLICATED EVERY PATTERN ID IN THE STAGE. The garment is filled
   *  with `url(#fill-top)` and friends, which resolve to the FIRST matching id in the
   *  document -- so a second copy of the stage is two elements answering to one name, and
   *  the enlarged picture would be painted from the small one's defs. Moving the node has
   *  no such question to get wrong.
   *  ⚠️ AND IT IS SAFE TO BE DESTROYED. The box lives inside `#mky-step`, so any render
   *  wipes it -- and the pane inside it was already sent home by stagePanesHome() at the
   *  top of that render. There is no state to unwind. */
  function openFabricPreview() {
    if (!el.step || document.getElementById('mky-fabprev-big')) return;
    var box = document.createElement('div');
    box.className = 'mky__modal mky__modal--prev';
    box.id = 'mky-fabprev-big';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-labelledby', 'mky-fabprev-big-t');
    box.innerHTML =
      '<div class="mky__modalbox mky__prevbox">' +
        '<h3 class="mky__modalt" id="mky-fabprev-big-t" tabindex="-1">Your suit, larger</h3>' +
        '<div class="mky__prevstage" id="mky-fabprev-bigstage"></div>' +
        '<button class="mky__modalback" type="button" data-prevclose="1">Close</button>' +
      '</div>';
    el.step.appendChild(box);
    var into = document.getElementById('mky-fabprev-bigstage');
    if (into) {
      if (el.suitPane) into.appendChild(el.suitPane);
      if (el.svg) into.appendChild(el.svg);
    }
    function shut() {
      var b = document.getElementById('mky-fabprev-big');
      /* ⚠️ THE PANES GO BACK FIRST AND THE BOX IS REMOVED SECOND. The other order takes
         the live stage out of the document with the box that contains it. */
      placeFabricPreview();
      if (b && b.parentNode) b.parentNode.removeChild(b);
      var z = document.getElementById('mky-fabprev-zoom');
      if (z && z.focus) z.focus();
    }
    Array.prototype.forEach.call(box.querySelectorAll('[data-prevclose]'), function (b) {
      b.addEventListener('click', shut);
    });
    /* A press on the backdrop and Escape are the same as Close, exactly as the reversible
       conflict box already behaves. */
    box.addEventListener('click', function (e) { if (e.target === box) shut(); });
    box.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.keyCode === 27) { e.stopPropagation(); shut(); }
    });
    var t = document.getElementById('mky-fabprev-big-t');
    if (t && t.focus) t.focus();
  }

  function desktopOn() {
    return !!(CARD_MQ && !CARD_MQ.matches) && cardMarkupReady();
  }

  // The same wording is used while designing and on the final record. Only explicit
  // answers belong here; suggested shapes and hover examples never become selections.
  function savedDesignRows() {
    var rows = [];
    if (state.style) rows.push(['Style', styleLabel() +
      (state.style === 'three' ? ', with a long-sleeve crop over it' : '')]);
    var shape = [];
    if (state.topShape) shape.push(label(topsFor(), state.topShape));
    if (isSplit() && state.bottomShape) shape.push(label(BOTTOMS, state.bottomShape));
    if (shape.length) rows.push(['Shape', shape.join(' with ')]);
    if (coverageLabel()) rows.push(['Coverage', coverageLabel()]);
    var primary = slotFace(state.fabric);
    var bottom = state.variation === 'mixed' ? slotFace(state.fabricBottom) : null;
    var inside = state.variation === 'reversible' ? slotFace(state.fabricInside) : null;
    if (primary) rows.push(['Fabric', primary.name + (bottom ? ' top, ' + bottom.name + ' bottom' : '') +
      (inside ? '; ' + inside.name + ' inside' : '')]);
    if (state.variation) rows.push(['Arrangement', label(variations(), state.variation)]);
    if (cropAssignment()) rows.push(['Crop', cropAssignment()]);
    if (bodyLine()) rows.push(['Your shape', bodyLine()]);
    return rows;
  }
  function savedRowsHtml() {
    return savedDesignRows().map(function (row) {
      return '<dt>' + escAttr(row[0]) + '</dt><dd>' + escAttr(row[1]) + '</dd>';
    }).join('');
  }
  function paintSavedChoices() {
    if (!el.reference) return;
    var saved = document.getElementById('mky-saved-choices');
    if (!saved) {
      saved = document.createElement('dl');
      saved.id = 'mky-saved-choices';
      saved.className = 'mky__savedchoices';
      el.reference.insertBefore(saved, el.reference.firstChild);
    }
    saved.innerHTML = savedRowsHtml();
    // Fit notes carry explicit none/unknown answers; builds carries adjustments.
    var notes = state.style ? fitNotes() : [];
    if (notes.length) saved.innerHTML += '<dt>Noted</dt><dd>' + notes.map(escAttr).join('<br>') + '</dd>';
    saved.hidden = !saved.textContent.trim();
  }

  // Render the mobile specs as a desktop comparison grid, using their artwork and
  // selection callbacks. Browsing renders art only, without temporarily mutating state.
  function desktopChoices(spec) {
    if (!spec) return;
    var hint = (spec.hint || '').replace(/Swipe to compare/g, 'Hover or focus to compare')
      .replace(/\bTap /g, 'Choose ');
    if (spec.multi) hint = 'Select any that apply, then continue.';
    el.step.innerHTML = '<h2 class="mky__q">' + spec.q + '</h2>' +
      '<p class="mky__hint text-sm text-subdued">' + hint + '</p>' +
      (spec.sub ? '<h3 class="mky__sub">' + spec.sub + '</h3>' : '') +
      '<div class="mky__desktopchoices mky__opts" data-multi="' + !!spec.multi + '">' + spec.opts.map(function (o, i) {
        var on = spec.isOn(i), value = escAttr(String(o[0]));
        var body = '<span class="mky__desktopart">' + spec.art(i) + '</span>' +
          '<b>' + (spec.title ? spec.title(i) : o[1]) + '</b>' +
          '<span class="mky__desktopdesc">' + (spec.help(i) || '') + '</span>';
        var attrs = ' data-ix="' + i + '" data-v="' + value + '"' +
          (spec.id === 'style' ? ' data-style="' + value + '"' : '') +
          (spec.id === 'cropFabric' ? ' data-crop="' + value + '"' : '') +
          (spec.id.indexOf('shape:') === 0 ? ' data-attr="' + spec.id.slice(6) + '"' : '') +
          (spec.suggested && spec.suggested(i) ? ' data-suggested="true"' : '');
        if (spec.multi) return '<label class="mky__desktopchoice mky__desktopfit mky__card--fit"' + attrs +
          ' data-on="' + on + '">' + body + '<span class="mky__desktopcheck mky__cardcheck">' +
          '<input type="checkbox" data-v="' + value + '"' + (on ? ' checked' : '') + '>' +
          spec.checkLabel(i) + '</span></label>';
        return '<button type="button" class="mky__opt mky__desktopchoice' + (spec.id === 'cropFabric' ? ' mky__swatch' : '') + '"' + attrs +
          ' aria-pressed="' + on + '">' + body + '<span class="mky__desktopstatus">' +
          (on ? '✓ Selected' : spec.suggested && spec.suggested(i) ? 'Suggested starting shape' : 'Choose this') +
          '</span></button>';
      }).join('') + '</div>' +
      (spec.legend ? '<p class="mky__cardlegend">' + spec.legend + '</p>' : '') +
      (spec.extra ? spec.extra() : '');
    var example = document.getElementById('mky-desktop-example');
    if (!example) {
      example = document.createElement('div');
      example.id = 'mky-desktop-example';
      el.stage.insertBefore(example, el.stage.firstChild);
    }
    el.stage.classList.add('mky__stage--example');
    var selected = spec.opts.findIndex(function (_, i) { return spec.isOn(i); });
    var initial = selected >= 0 ? selected : 0;
    if (selected < 0 && spec.suggested) spec.opts.forEach(function (_, i) { if (spec.suggested(i)) initial = i; });
    function preview(i, browsing) {
      // A checkbox render restores focus through the previous handler. Read the
      // current answer so its caption never describes the pre-click selection.
      var selectedNow = spec.opts.findIndex(function (_, ix) { return spec.isOn(ix); });
      if (!browsing && selectedNow >= 0) i = selectedNow;
      // Cloth preferences keep the chosen garment beside the example swatch cards.
      if (['color', 'intensity', 'density', 'ground'].indexOf(spec.id) >= 0) {
        el.stage.classList.remove('mky__stage--example'); example.hidden = true; return;
      }
      example.hidden = false;
      // Inline SVG patterns need their own ids in the second, larger rendering.
      var art = spec.art(i), ids = [];
      // Match coverage's individual garment frames instead of scaling a fused pair.
      if (spec.id === 'style' && spec.opts[i][0] === 'three' &&
          SUIT_ASSETS.crop && SHAPE_PHOTOS['top:triangle'] && SHAPE_PHOTOS['bottom:brief']) {
        art = coverPhotoHtml(null, '', {
          images: [SUIT_ASSETS.crop, SHAPE_PHOTOS['top:triangle'], SHAPE_PHOTOS['bottom:brief']],
          alts: [SUIT_CROP_ALT, SHAPE_PHOTO_ALT['top:triangle'] || '', SHAPE_PHOTO_ALT['bottom:brief'] || ''],
          layout: 'three'
        });
      }
      if (FIG[spec.id] && !browsing && el.figPane) {
        var frame = el.figPane.querySelector('.mky__figframe');
        if (frame) { var copy = frame.cloneNode(true); copy.classList.add('mky__cardfig'); art = copy.outerHTML; }
      }
      art.replace(/\bid="([^"]+)"/g, function (_, id) { ids.push(id); return _; });
      ids.forEach(function (id) { art = art.split('"' + id + '"').join('"desktop-' + id + '"')
        .split('#' + id + ')').join('#desktop-' + id + ')').split('"#' + id + '"').join('"#desktop-' + id + '"'); });
      example.innerHTML = '<p class="mky__previewlabel">' + (browsing ? 'Previewing' : selectedNow >= 0 ? 'Selected' : 'Example') +
        '</p><div class="mky__desktopexampleart">' + art + '</div><p class="mky__previewname">' +
        (spec.multi && !browsing && selectedNow >= 0 ? 'Your selected fit adjustments' : spec.opts[i][1]) + '</p>' + (spec.legend ? '<p class="mky__cardlegend">' + spec.legend + '</p>' : '');
      if (FIG[spec.id]) {
        var cfg = FIG[spec.id], entry = cfg.states[spec.opts[i][0]] || {};
        var note = browsing ? (cfg.multi ? cfg.note || '' : entry.note || '') : (el.figNote ? el.figNote.textContent : '');
        var caption = document.createElement('p'); caption.id = 'mky-desktop-fig-note';
        caption.className = 'mky__cardlegend'; caption.textContent = note;
        example.appendChild(caption);
      }
      // Reposition whole SVG groups so reversible clip paths travel with each piece.
      example.querySelectorAll('svg[viewBox="0 0 620 220"]').forEach(function (svg) {
        svg.setAttribute('viewBox', '0 0 220 620');
        svg.classList.add('mky__stackedthree');
        Array.prototype.forEach.call(svg.children, function (group) {
          if (group.tagName.toLowerCase() === 'use') {
            var directX = Number(group.getAttribute('x'));
            group.setAttribute('x', '10'); group.setAttribute('y', String(directX));
            return;
          }
          if (group.tagName.toLowerCase() !== 'g') return;
          var use = group.querySelector('use');
          if (!use) return;
          var x = Number(use.getAttribute('x'));
          group.setAttribute('transform', 'translate(' + (10 - x) + ' ' + (x - 10) + ')');
        });
      });
    }
    preview(initial, false);
    el.step.querySelectorAll('.mky__desktopchoice').forEach(function (card) {
      var i = Number(card.dataset.ix), control = spec.multi ? card.querySelector('input') : card;
      card.addEventListener('mouseenter', function () { if (CAN_HOVER) preview(i, true); });
      control.addEventListener('focus', function () { preview(i, true); });
      control.addEventListener(spec.multi ? 'change' : 'click', function () {
        var held = spec.select(i, spec.multi ? control.checked : undefined) === false;
        if (spec.multi || held) {
          render();
          if (spec.multi) {
            var next = el.step.querySelector('[data-ix="' + i + '"] input');
            if (next) { next.focus({preventScroll: true}); preview(initial, false); }
          }
        } else goNext();
      });
    });
    var grid = el.step.querySelector('.mky__desktopchoices');
    grid.addEventListener('mouseleave', function () { if (!grid.contains(document.activeElement) || !document.activeElement.matches(':focus-visible')) preview(initial, false); });
    grid.addEventListener('focusout', function (e) { if (!grid.contains(e.relatedTarget)) preview(initial, false); });
    if (el.actionSel) {
      el.actionSel.hidden = false;
      el.actionSel.textContent = spec.selText();
    }
    if (spec.wire) spec.wire(el.step);
    finishStep();
  }

  function finishStep() {
    /* Before focus moves to the heading, so a screen reader arriving at a new question
       is not read the transition line after the question it introduces. */
    stampTransition();
    if (stepJustChanged) { stepJustChanged = false; focusNewStep(); }
    stampPilotProgress();
    /* After the step, never before: paintBuilds() fills the Cut-for-you slot and
       paintBody()/paintFig() write the summary line, and asking whether the block has
       anything in it before those have run answers for the PREVIOUS question. */
    paintSavedChoices();
    paintPilotDesign(pilotOn() || desktopOn());
    /* ⚠️ HERE, NOT ONLY IN THE NOTE HANDLERS. The record's notes slot is built by the
       render that draws the record, so arriving at it with notes already saved -- from a
       reload, a Back, or a resumed link -- left the slot empty and the notes invisible on
       the one screen the brief asks for them by name. The handlers repaint it when a note
       changes; this paints it when the SCREEN changes. Cheap and safe everywhere else:
       it returns immediately when the slot is not on this screen. */
    paintReviewNotes();
    stampStepNumber();
    /* Last, so it sits over a finished screen. See the note on paintComfortConflict:
       this is the one place every layout converges on. */
    paintComfortConflict();
    /* ⚠️ AFTER THE STEP'S MARKUP EXISTS, which is the whole reason it is here and not in
       render(). The slot it borrows the stage into is written by the picker's own
       innerHTML; asking for it any earlier finds the PREVIOUS screen's. */
    placeFabricPreview();
    /* Immediately after, because it measures what that call put on screen. */
    fitFabricPreview();
    /* ⚠️ AFTER EVERYTHING ELSE HAS DRAWN, because it measures what they drew. A transition
       line, the component label and the progress track all sit above the rail and all are
       stamped by the calls above this one. And now also after the fabric preview, which
       adds a block above the answers on four screens. */
    fitDeck();
  }

  /* ── THE DECK'S HEIGHTS ARE MEASURED, NOT RESERVED ────────────────────────────────────
   *
   *  ⚠️ WHAT THIS REPLACES, AND WHY A CONSTANT COULD NEVER HAVE WORKED. The card used to
   *  take `min-height: clamp(200px, calc(100dvh - 366px), 320px)`, where 366 was the
   *  flow's chrome on a phone measured ONCE, on ONE screen. It assumes a ONE-LINE QUESTION
   *  AND A ONE-LINE HINT. Measured on the real development theme at 390x659, five of the
   *  sixteen deck screens have neither -- a heading that wraps costs 29px, a hint that
   *  wraps 19-39px more, and the shapes screen adds a 19px component label on top -- so
   *  `cardTop` ran 140-164px on the healthy screens and 189-212px on these five:
   *
   *      How much color do you want?              -25px behind the action bar
   *      How much do you want covered?            -26px
   *      Your shapes                              -20px
   *      What does a top get wrong?                -5px
   *      Does your swimwear ride up at the back?   -2px
   *
   *  The CARD was innocent on every one of them: all five sat at exactly the 293px the
   *  constant reserved. The chrome ABOVE it had grown, and because the action bar is
   *  `position: sticky; bottom: 0` at the end of a `100dvh` column, that overflow does not
   *  push the bar down -- it slides the pager UNDER it.
   *
   *  ⚠️ SO THE NUMBER IS READ OFF THE RENDERED PAGE. `--mky-above` already works this way
   *  (see measureAvailable); this is the same idea carried down to the card.
   *
   *  ⚠️ AND IT SETS TWO VARIABLES, NOT ONE, BECAUSE THE PICTURE AND THE BOX ARE DIFFERENT
   *  QUESTIONS. Cards are flex items in a row, so they are all as tall as the tallest by
   *  construction; if the ARTWORK simply took whatever each card's own text left over, the
   *  picture would change size as she swiped between a one-line answer and a four-line one.
   *  `--mky-cardart` is therefore computed from the TALLEST card's text, so every card on a
   *  rail shows the same size picture -- which is the property `.mky__cardsuit` and the
   *  coverage pairs already held with their fixed heights, kept rather than lost.
   *
   *  ⚠️ NOTHING HERE SHRINKS TEXT OR A TOUCH TARGET. The text block is measured and then
   *  subtracted; it is never an input to how small anything may get. When the space runs
   *  out the artwork stops at ART_FLOOR, the card is grown back to fit its own contents,
   *  and THE PAGE SCROLLS -- which is what the sticky bar being ordered last is for.
   *  Codex, 2026-09-15: "Text and touch targets are not reduced to force a fit... smaller
   *  screens and enlarged text retain natural vertical scrolling."
   *
   *  ⚠️ THE ARTWORK ONLY EVER SHRINKS. Every CSS rule that consumes `--mky-cardart` wraps
   *  it in `min()` with the size that art was designed at, so on a tall phone the variable
   *  is large, `min()` picks the designed size, and a 390x844 screen renders exactly what
   *  it rendered before this existed. */
  var ART_FLOOR = 96;     /* below this a 2x2 reversible grid stops being readable */
  var CARD_FLOOR = 180;   /* a card smaller than this is not worth drawing */
  /* ⚠️ SLACK IS NOT A FUDGE FACTOR, AND ZERO WAS MEASURABLY WRONG. Spending the column
     down to the last pixel put the pager's bottom edge EXACTLY on the bar's top on
     fifteen of sixteen screens, and sub-pixel rounding took the sixteenth to -1px --
     "How tall are you?" on the real development theme at 390x659. A boundary that is
     right only when nothing rounds is not clear of anything. This is also the visible
     breathing room between the dots and the bar's rule, which is why it is 12 and not 1. */
  var DECK_SLACK = 12;

  /** Everything in a card that is NOT its picture: padding, the title, the help text, the
   *  pick line or the checkbox, and every margin between them.
   *  ⚠️ THE PICTURE IS THE FIRST CHILD WHEN THERE IS ONE. `deckCardHtml` emits `art`
   *  before `head`, always. Read positionally rather than by class because the art slot is
   *  eight different elements across the flow -- a photo frame, an svg, a body frame, a
   *  coverage drawing, a swatch row -- and a list of them here would go stale the next time
   *  one is added. A text-led card has no picture and its first child is the title. */
  function cardArtEl(card) {
    var first = card.firstElementChild;
    if (!first || first.classList.contains('mky__card-h')) return null;
    return first;
  }

  function outerH(node) {
    if (!node) return 0;
    var cs = window.getComputedStyle(node);
    return node.getBoundingClientRect().height +
           (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
  }

  /** Everything between the card rail and the action bar, whatever it happens to be.
   *
   *  ⚠️ THIS USED TO BE `outerH(pager)` AND THAT WAS TOO NARROW BY EXACTLY ONE ELEMENT.
   *  The pager is not the last thing under the rail: `.mky__cardlegend` follows it inside
   *  `.mky__cardbrowse`, and the review pane follows THAT one level up. Question 6 carries
   *  a legend -- "Pink illustrates the fit issue..." -- so the card was sized as though the
   *  legend were free and the legend ended up behind the action bar. The pager cleared; the
   *  sentence under it did not. Codex, 2026-09-15, reading the screenshot rather than the
   *  number: "Include visible legends and their margins... Check all decks with legends,
   *  not only pager bounds."
   *
   *  ⚠️ SO IT IS COUNTED STRUCTURALLY, NOT BY NAME. Walking the rail's ancestors up to the
   *  step and summing each LATER SIBLING catches the pager, the legend, the review pane and
   *  anything a future screen adds down there, without this function having to be told it
   *  exists. Naming elements is what produced the bug -- the next one would have been
   *  missed the same way.
   *
   *  ⚠️ NONE OF THESE DEPEND ON THE CARD'S HEIGHT, which is what makes it safe to measure
   *  them before setting it. They sit BELOW the rail; the rail growing or shrinking moves
   *  them without resizing them. A sibling that wrapped differently at a different card
   *  height would reintroduce the feedback loop this function is careful to avoid.
   *
   *  Hidden things are skipped: a `[hidden]` review pane and a closed disclosure occupy no
   *  space, and charging the card for them would shrink every deck to pay for a panel
   *  nobody is looking at. */
  function chromeBelowRail(rail) {
    var total = 0, node = rail, sib;
    while (node && node !== el.step) {
      for (sib = node.nextElementSibling; sib; sib = sib.nextElementSibling) {
        if (sib.hidden) continue;
        /* `offsetParent` is null for `display:none` and for anything inside it. A visible
           element always has one here -- nothing in this column is `position: fixed`. */
        if (!sib.offsetParent) continue;
        total += outerH(sib);
      }
      node = node.parentElement;
    }
    return total;
  }

  /* ⚠️ THE PREVIEW IS MEASURED, NOT RESERVED, AND THIS FILE ALREADY LEARNED THAT LESSON
     ONCE. `.mky__card` took `clamp(200px, calc(100dvh - 366px), 320px)` where 366 was the
     chrome measured on ONE screen, and five of sixteen deck screens then drew their pager
     behind the action bar. A constant here would be the same mistake with a different
     number: these four screens differ by a browse row, a search field, a match line that
     is sometimes three sentences, a "no longer one of your suggestions" pin and a colour
     palette instead of a grid, and each of those moves the first row of choices.

     So: hide the preview, ask the page where the first row of choices actually lands
     without it, and give the picture whatever is left above the action bar.

     ⚠️ THE FLOOR IS WHERE THIS STOPS AND THE SCROLL STARTS, exactly as ART_FLOOR is for
     the deck. Below about 76px the garment stops being a picture of a suit, and shrinking
     past that to buy a row of swatches would be answering the question with a smudge; the
     column overflows instead and the page scrolls, which is the honest failure.
     ⚠️ AND THE CEILING IS WHAT KEEPS IT COMPACT. With a short match line and no search
     results there can be 200px going spare, and spending it here would push the catalogue
     off a screen whose job is choosing from the catalogue. */
  var FABPREV_FLOOR = 76;
  var FABPREV_CEIL = 150;
  /* ⚠️ 20, AND IT IS NOT A FUDGE FACTOR -- the same lesson DECK_SLACK records, learned
     again on this screen. At 10 the first row of swatches cleared the action bar by ONE
     pixel with a page above the studio and a fabric chosen (542 against 543, measured at
     390x659). A boundary that is right only when nothing rounds is not clear of anything,
     and what sits on this one is a tap target rather than a row of dots. */
  var FABPREV_SLACK = 20;
  /* ⚠️ THE PICTURE ONLY EVER SHRINKS WHILE SHE IS ON ONE SCREEN, and that is the same rule
     `--mky-cardart` already carries for the deck: the artwork must not change size under
     her while she is looking at it. The action bar on these four screens GROWS by about
     21px the moment there is a selection to name, and shrinks again when she clears a
     search -- so an unconstrained fit would resize the garment on every one of those, in
     both directions, which is the thing a preview must not do. It settles once, downward,
     and holds until the screen changes.
     ⚠️ AND THE ROW OF SWATCHES IS WHAT WINS THE ONE SETTLE. Holding the first height
     instead would leave the first row of choices 12px behind the action bar with a fabric
     chosen -- measured at 390x659 -- and a row she cannot tap is worse than a picture that
     resizes once. */
  var fabPrevStep = -1;
  var fabPrevHeld = 0;

  function fitFabricPreview() {
    if (!root) return;
    var host = document.getElementById('mky-fabprev');
    var stage = document.getElementById('mky-fabprev-stage');
    /* ⚠️ CLEARED WHENEVER IT DOES NOT APPLY. A height left behind from the fabric screen
       would size a slot on some later screen that happens to reuse the class, and -- far
       more likely -- would be the value a rotation back onto this screen measured against
       instead of measuring afresh. Same rule as fitDeck's two removeProperty calls. */
    if (!pilotOn() || !host || !stage || host.hidden || !el.step || !el.body || !el.actions) {
      root.style.removeProperty('--mky-fabprev');
      return;
    }
    var row = el.step.querySelector('.mky__swatch, .mky__solid');
    if (!row) { root.style.removeProperty('--mky-fabprev'); return; }
    /* ⚠️ HIDDEN TO MEASURE, NOT SET TO ZERO. At `--mky-fabprev: 0` the slot still lays out
       its caption and its margin, so "the chrome without the preview" would have quietly
       included part of the preview. `hidden` takes it out of flow completely, which is the
       question actually being asked. */
    host.hidden = true;
    var colTop = el.body.getBoundingClientRect().top;
    var need = row.getBoundingClientRect().bottom - colTop;
    var barH = el.actions.getBoundingClientRect().height;
    host.hidden = false;
    var above = parseFloat(window.getComputedStyle(root).getPropertyValue('--mky-above')) || 0;
    var colAvail = (window.innerHeight || 0) - above;
    var h = Math.min(FABPREV_CEIL, colAvail - need - barH - FABPREV_SLACK);
    if (!(h > 0) || h < FABPREV_FLOOR) h = FABPREV_FLOOR;
    /* ⚠️ HELD EXACTLY, NOT MERELY CAPPED, WHILE SHE IS CHOOSING. Shrinking is correct
       on arrival -- it is how the first row of swatches stays clear of the action bar --
       and it is a page jump under her finger when a swatch tap triggers it, because
       everything below the preview moves by the difference. See renderKeepingPlace().
       everything below the preview moves by the difference. See renderKeepingPlace(). */
    if (fabPrevStep === step && fabPrevHeld) {
      h = keepingPlace ? fabPrevHeld : Math.min(h, fabPrevHeld);
    }
    fabPrevStep = step;
    fabPrevHeld = h;
    root.style.setProperty('--mky-fabprev', Math.round(h) + 'px');
  }

  function fitDeck() {
    if (!root || !el.step || !el.body || !el.actions) return;
    var rail = el.step.querySelector('[data-mky-rail]');
    var pager = el.step.querySelector('.mky__cardpager');
    var cards = rail ? rail.querySelectorAll('.mky__card') : null;
    /* Off a deck, or above the phone breakpoint, the variables must not linger: a stale
       card height would size the NEXT deck against the LAST screen's chrome. */
    if (!pilotOn() || !rail || !pager || !cards || !cards.length) {
      root.style.removeProperty('--mky-cardh');
      root.style.removeProperty('--mky-cardart');
      return;
    }
    /* ⚠️ CLEARED BEFORE MEASURING, OR THIS READS ITS OWN LAST ANSWER. The chrome above the
       rail does not depend on the card's height, but the card's height plainly does, and a
       second pass over an un-cleared page compounds instead of converging. */
    root.style.removeProperty('--mky-cardh');
    root.style.removeProperty('--mky-cardart');

    var above = parseFloat(window.getComputedStyle(root).getPropertyValue('--mky-above')) || 0;
    var colAvail = (window.innerHeight || 0) - above;
    /* ⚠️ MEASURED INSIDE THE COLUMN, NOT AGAINST THE VIEWPORT, so the answer does not
       change with the scroll position. `chromeAbove` is the head, the heading, the hint,
       the component label and the progress track -- whatever this particular screen
       actually rendered, which is the entire point. */
    var colTop = el.body.getBoundingClientRect().top;
    var chromeAbove = rail.getBoundingClientRect().top - colTop;
    var belowRail = chromeBelowRail(rail);
    /* ⚠️ THE RAIL'S OWN BOX, AND LEAVING IT OUT IS WHAT MADE DECK_SLACK A FICTION.
       `--mky-cardh` sizes the CARD; the rail that holds it is `.2rem 0 .55rem` of padding
       taller, so the rail occupies cardH + 12 while every term below charged it cardH.
       That 12 came out of DECK_SLACK, which is also 12 -- so the designed margin under the
       pager was structurally ZERO on every arrangement screen, and `clear >= 0` passed at
       exactly 0 for a week without anyone being able to name why.
       ⚠️ AND THE FLUSH RUNS LOOKED FINE, WHICH IS WHY THE HARNESS COULD NOT SEE IT.
       With nothing above the studio scrollToFlow() asks for `max(0, 0 - stickyPad())` and
       the clamp wins, so root lands at viewport 0 while `--mky-above` had already reserved
       stickyPad() for a gap that was never spent. The windfall is 12px and it read as
       margin. A real theme has a real sticky header, the scroll always lands where it was
       predicted, and there is no windfall -- so 0 was the honest number all along.
       ⚠️ MEASURED, NOT THE CONSTANT 12. The padding is in rem; a customer at a
       larger root font has a proportionally taller rail, and the whole point of this
       function is that it reads the page instead of remembering it.
       Margin-bottom belongs here too: `chromeAbove` already absorbs the rail's margin-TOP
       (a border-box rect top sits below it), and chromeBelowRail() starts at the rail's
       next SIBLING, so a margin under the rail is counted by neither. It is 0 today. */
    var rs = window.getComputedStyle(rail);
    var railBox = (parseFloat(rs.paddingTop) || 0) + (parseFloat(rs.paddingBottom) || 0) +
                  (parseFloat(rs.borderTopWidth) || 0) + (parseFloat(rs.borderBottomWidth) || 0) +
                  (parseFloat(rs.marginBottom) || 0);
    var barH = el.actions.getBoundingClientRect().height;

    /* The tallest card's words. Cards are stretched to the tallest by construction, so the
       picture has to clear the WORST case or it changes size as she swipes. */
    var maxText = 0;
    var i, c, art, text, kid;
    for (i = 0; i < cards.length; i++) {
      c = cards[i];
      art = cardArtEl(c);
      var cs = window.getComputedStyle(c);
      text = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      for (kid = c.firstElementChild; kid; kid = kid.nextElementSibling) {
        if (kid !== art) text += outerH(kid);
      }
      if (art) {
        var as = window.getComputedStyle(art);
        text += (parseFloat(as.marginTop) || 0) + (parseFloat(as.marginBottom) || 0);
      }
      if (text > maxText) maxText = text;
    }

    var cardH = colAvail - chromeAbove - railBox - belowRail - barH - DECK_SLACK;
    var artH = cardH - maxText;
    /* ⚠️ THE FLOOR IS WHERE THIS STOPS AND THE SCROLLBAR STARTS. Growing the card back to
       fit its own contents is what keeps the card from clipping; the COLUMN overflows
       instead, and the page scrolls. */
    if (artH < ART_FLOOR) artH = ART_FLOOR;
    var needed = artH + maxText;
    if (cardH < needed) cardH = needed;
    if (cardH < CARD_FLOOR) cardH = CARD_FLOOR;

    root.style.setProperty('--mky-cardh', Math.round(cardH) + 'px');
    root.style.setProperty('--mky-cardart', Math.round(artH) + 'px');
  }

  /** The thin progress track, under the hint, on every phone screen.
   *  ⚠️ IT IS NOT THE STANDING BAR RESTYLED. `.mky__prog` lives outside `.mky__body`, so
   *  no amount of `order` can put it below the hint where the approved design has it --
   *  `order` cannot sort across parents. The standing bar is hidden by `mky--pilot` and
   *  this one is inserted into the step, which is the only way to get that sequence.
   *  ⚠️ INSERTED AFTER THE HINT IF THERE IS ONE, AFTER THE HEADING IF THERE IS NOT.
   *  Several screens render a heading and go straight to their controls. */
  function stampPilotProgress() {
    if (!el.step || !(pilotOn() || desktopOn())) return;
    if (el.step.querySelector('.mky__pilotprog')) return;
    var after = el.step.querySelector('.mky__hint') || el.step.querySelector('.mky__q');
    if (!after) return;
    var box = document.createElement('div');
    box.className = 'mky__pilotprog';
    box.setAttribute('aria-hidden', 'true');
    var c = stepCount();
    var span = document.createElement('span');
    span.style.width = (c.total ? (c.done / c.total * 100) : 0).toFixed(2) + '%';
    box.appendChild(span);
    after.parentNode.insertBefore(box, after.nextSibling);
  }

  /* ── THE TRANSITIONS ──────────────────────────────────────────────────────────────
     ⚠️ FIVE SHORT LINES, INSIDE THE EXISTING SHELL, AND NOT INTERSTITIAL SCREENS.
     Codex, 2026-09-15, is explicit about the form: the reordered flow moves between four
     very different kinds of decision -- what the suit looks like, what a top gets wrong,
     her own proportions, the record -- and without a word at each seam the customer is
     simply handed a different sort of question. An interstitial "Now let's talk about
     comfort" screen would be a tap that answers nothing, which is the thing this flow has
     removed twice already.
     ⚠️ ON THE SCREEN THAT OPENS THE PASSAGE, NOT ON THE ONE THAT CLOSES IT. Each line
     is keyed to the FIRST screen of what it introduces, so it is read on the way in.
     ⚠️ THE SUIT LINE IS THE ONLY ONE THAT INTERPOLATES, and it reads her own answer
     back to her -- "Let's find a fabric for your two piece" -- which is what makes the
     first transition confirm that the first question landed.
     ⚠️ STAMPED, NOT BUILT INTO EACH RENDERER, for the same reason the step number is:
     ten step renderers write their own heading, and ten places to add the same paragraph
     is ten places to forget it. */
  var TRANSITION = {
    color: function () {
      var l = styleLabel();
      return l ? 'Let\u2019s find a fabric for your ' + l.toLowerCase() + '.'
               : 'Let\u2019s find a fabric.';
    },
    coverage: function () { return 'Now choose how you\u2019d like it cut.'; },
    /* ⚠️ SHAPES CARRIES THE CUT LINE TOO, BECAUSE COVERAGE IS SKIPPED ON HALF THE
       GARMENTS. A one piece and a monokini never see the coverage screen, so a line that
       lived only there would be missing from exactly the flows that jump from the fabric
       picker straight to a shape. Shown on whichever of the two she actually reaches
       first; `shownTransition` is what stops it being said twice on a two piece. */
    shapes: function () { return 'Now choose how you\u2019d like it cut.'; },
    top: function () { return 'You\u2019ve chosen the look. Let\u2019s make it comfortable.'; },
    height: function () { return 'Now a few details about your fit.'; },
    sizes: function () { return 'Let\u2019s add your sizes to this design.'; }
  };
  /* The cut line is one transition across two screens; this remembers that it has been
     said. Screen state: it never reaches `state`, the URL or the record, and "Start
     again" clears it with everything else. */
  var shownTransition = {};
  function transitionFor(id) {
    var f = TRANSITION[id];
    if (!f) return '';
    /* Coverage and shapes share a line. Whichever comes first says it. */
    var group = (id === 'coverage' || id === 'shapes') ? 'cut' : id;
    if (shownTransition[group] && shownTransition[group] !== id) return '';
    shownTransition[group] = id;
    return f();
  }
  /** Put the transition line above the question, in whichever layout is on screen. */
  function stampTransition() {
    if (!el.step) return;
    if (el.step.querySelector('.mky__transition')) return;
    var h = el.step.querySelector('.mky__q');
    if (!h) return;
    var text = transitionFor(screenId());
    if (!text) return;
    var p = document.createElement('p');
    p.className = 'mky__transition text-sm text-subdued';
    p.textContent = text;
    h.parentNode.insertBefore(p, h);
  }

  function stampStepNumber() {
    if (!el.step) return;
    var h = el.step.querySelector('.mky__q');
    if (!h || h.querySelector('.mky__qn')) return;
    /* Not on the last screen: it is the record, not a question, and "18 of 18" over
       "Yours" reads as a step still to do. */
    if (step >= lastStep()) return;
    var c = stepCount();
    /* Nothing on the kind screen: see the note on stepCount().known. */
    if (!c.known) return;
    var n = document.createElement('span');
    n.className = 'mky__qn';
    n.setAttribute('aria-hidden', 'true');
    n.textContent = c.done + ' of ' + c.total;
    h.appendChild(n);
  }

  /* ── THE PHONE'S CARD DECK, FOR EXACTLY TWO QUESTIONS ──────────────────────────────
     Questions 2 and 6 are the two in the flow where the PICTURE is the question, and on
     a phone both of them used to stack one shared picture above a list of words: one
     illustration and three tile thumbnails on question 2, one photograph and five text
     rows on question 6. So the thing being compared was never beside the thing it
     described, and on question 6 the stage drew the UNION of every ticked answer, which
     is the right picture for "what am I getting" and the wrong one for "is this me".

     Here each answer is its own card carrying its own artwork, and a horizontal swipe
     browses them. Codex's pack, revision 2, 2026-09-13, and revision 1's checkbox list
     for question 6 is SUPERSEDED -- do not resurrect it.

     ⚠️ BROWSING IS NOT ANSWERING, AND THAT IS THE WHOLE DESIGN. A swipe, a previous/next
     press and a keyboard focus all move which card is in view and NONE of them touch
     `state`. Selecting is always an explicit activation: a press on the card (question 2,
     one answer) or a tick of that card's own checkbox (question 6, any number). This is
     why the saved answer is repeated above Continue -- so that what will be submitted is
     on screen even while she is looking at a card she has not chosen.

     ⚠️ PHONES ONLY, AND THE TEST IS LIVE. `CAN_HOVER` is evaluated once because a device
     does not grow a mouse; a VIEWPORT does change -- rotation, a desktop window being
     dragged narrower -- so this one listens and re-renders. Desktop keeps the tiles and
     the list it has always had, untouched.

     ⚠️ NOTHING HERE IS PART OF THE DESIGN. `cardIx` is which card is in view and
     `cardReview` is whether the review panel is open; both are SCREEN state, like
     `pendingReversible`. Neither may reach `state`, the URL or the saved record. */
  /* ⚠️ THE REGISTRY IS THE ARTWORK, NOT A BOOLEAN. A question becomes a deck exactly
     when it has a picture PER ANSWER to put on a card, so the list of deck questions and
     the list of "where does one answer's artwork come from" are the same list -- and
     keeping them as one table is what stops a question being switched on with no picture
     to show. Every function here calls the helper the desktop layout already calls; none
     of them draws anything new.

     ⚠️ HEIGHT WAS BRIEFLY LISTED AS HAVING NO ARTWORK AND THAT WAS WRONG. It has five
     drawings -- `BODY_MAP.height` maps every answer to h1..h5 and the section supplies
     `mky-body-height-1-v4-transparent-v1.webp` through `-5-v4-…`. The mistake was reading `Q.height`,
     which carries no `tiles` key, and concluding from its absence; the tiles key says which
     questions crop the shared SPRITE, not which questions have a picture. Ask BODY_MAP.
     Corrected 2026-09-14 after Codex caught it.

     ⚠️ ONE QUESTION IS GENUINELY ABSENT. The variation question ("One fabric, or more?")
     has no artwork anywhere in this flow -- no tile, no photograph, no overlay, no swatch
     -- so its card is TEXT-LED, which is what Codex's brief asks for in place of invented
     imagery. It is named in DECK_TEXT_ONLY so the gap is reported rather than quietly
     absorbed, and so that the day artwork exists for it, adding it here is the whole
     change.

     ⚠️ THE SHAPES SCREEN IS NOT IN EITHER LIST, AND IT IS NOT A DECK. It is not one
     question: it holds two or three independent garment-component choosers on one screen,
     each with its own heading and its own silhouette, and there is no single answer for a
     card to carry or for a selection to advance past. It gets the compact shell and keeps
     its tile rows. */
  var CARD_ART = {
    height:   function (v) { return heightCardArtHtml(v); },
    volume:   function (v) { return bodyArtHtml('volume', v, 'mky__art--card'); },
    waist:    function (v) { return bodyArtHtml('waist', v, 'mky__art--card'); },
    tummy:    function (v) { return bodyArtHtml('tummy', v, 'mky__art--card'); },
    torso:    function (v) { return figCardArtHtml('torso', v); },
    top:      function (v) { return figCardArtHtml('top', v); },
    wire:     function (v) { return figCardArtHtml('wire', v); },
    padding:  function (v) { return figCardArtHtml('padding', v); },
    seat:     function (v) { return figCardArtHtml('seat', v); },
    coverage: function (v) { return coverTileArtHtml(v, true); },
    variation: function (v) { return variationCardArtHtml(v); }
  };
  /* ⚠️ THE GAP, WRITTEN DOWN RATHER THAN LEFT TO BE NOTICED. Every other deck card
     carries a picture of its own answer; these two carry words in a box. That is the
     correct behaviour and it is still a gap -- reported to Codex with the migration
     inventory, not fixed by generating something.
     ⚠️ READ FROM, NEVER WRITTEN TO, AT RUNTIME. It exists so the text-led card can say
     so in its class and so the report cannot drift from the code. */
  /* ⚠️ EMPTY SINCE 2026-09-14, AND THE EMPTINESS IS THE POINT. The variation question was
     the last card in the flow carrying words in a box; Codex's artwork pack closed it, so
     every deck card now draws its own answer. The table is KEPT rather than deleted: it is
     how a future text-led card declares itself instead of being noticed later, and the
     deck suite asserts against it either way. */
  var DECK_TEXT_ONLY = {};
  /* The same 749px the figure frame already narrows at, reused rather than invented:
     one phone breakpoint in this file, not two that can drift apart. */
  var CARD_MQ = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
  /* ⚠️ THE DECK NEEDS MARKUP THE SECTION HAS TO SUPPLY, AND THE TWO FILES CAN SHIP APART.
     `assets/make-yours.js` is a static asset with its own cache and `sections/make-yours.
     liquid` is rendered server-side; a `--only` push, a cached asset or a half-landed
     release can run new JS against the previous section. Every relocation in
     paintPilotChrome() is guarded on its own destination, which reads as careful and is
     not enough on its own -- `mky--pilot` is what HIDES the chapter rail, the chapter lede
     and the progress bar, so against old markup the guards correctly move nothing and the
     CSS removes the originals anyway. That is a deck with no chapter navigation, no
     progress and no way to start again.
     Checking it HERE rather than in paintPilotChrome is what makes the fallback whole: a
     false answer takes the tile grid, the option list, the shared stage and the standard
     chrome with it, which is the layout the flow had before any of this existed.
     ⚠️ AND IT SAYS SO ONCE. A silent fallback is indistinguishable from the phone layout
     never having shipped, which is the bug it would be hiding. */
  var cardMarkupWarned = false;
  function cardMarkupReady() {
    var ready = !!(el.pilotHead && el.pilotRail && el.actions && el.actionSel);
    if (!ready && !cardMarkupWarned && window.console) {
      cardMarkupWarned = true;
      console.warn('Design Studio: the phone card deck needs the compact head, the ' +
                   'Options rail slot and the action wrapper from ' +
                   'sections/make-yours.liquid. One is missing, so the deck is off and ' +
                   'the standard layout is being used. The section and the asset are ' +
                   'probably out of step.');
    }
    return ready;
  }
  /* ⚠️ ARTWORK IS NO LONGER THE TEST. It was, while the deck was the questions that
     had a picture per answer; Codex's 2026-09-14 brief extends the card layout to the
     whole question flow and says plainly what to do where no picture exists -- "use a
     clear text-led card and report the gap", never invent one. So every discrete question
     is a deck on a phone, CARD_ART says which of them has artwork, and the two questions
     missing from it render text-led and are reported. See DECK_TEXT_ONLY. */
  function cardsFor(key) {
    return !!(CARD_MQ && CARD_MQ.matches && Q[key]) && cardMarkupReady();
  }
  /* ⚠️ WHICH SCREENS HAVE A DECK, ASKED WITHOUT THE VIEWPORT. render() needs "is this
     screen a deck RIGHT NOW"; the media-query listener needs "could this screen ever be
     one", so it knows whether a rotation has to redraw. Two questions, one table, and the
     step arithmetic lives here rather than being spelled out at each call site.
     ⚠️ THE THREE FAMILIES STORE THEIR ANSWERS IN THREE DIFFERENT PLACES -- state[key],
     state.style and state.prefs[key] -- which is exactly what the deck spec exists to
     hide. The ids never collide: no question key is 'style', and no fabric dimension
     shares a name with a fit question. */
  function deckIdFor(s) {
    var id = screenAt(s);
    if (!id) return null;
    /* The id carries the COMPONENT, so moving between components is a different deck and
       `cardIx` cannot carry the top's browsed position onto the bottom's rail. */
    if (id === 'shapes') return 'shape:' + shapeParts()[shapePartIx()].attr;
    /* ⚠️ THE DECK ID IS THE SCREEN ID, AND SINCE THE ROUTE LANDED THEY ARE THE SAME
       STRINGS. `deckIdFor` used to re-derive each family from offsets, which is how the
       fabric-preference ids and the question keys came to be computed in two different
       ways. The screens that are NOT decks are the ones with no single answer to carry on
       a card: the fabric pickers (a catalogue with its own completion action), the sizes
       composite, and the record. They are named rather than arithmetic'd. */
    /* `fabricFix` joins them: it is a legacy-link correction with three bespoke
       choices and no single answer to carry on a card. */
    if (id === 'fabric' || id === 'fabricBottom' || id === 'fabricInside' ||
        id === 'fabricCrop' || id === 'fabricFix' || id === 'sizes' ||
        id === 'review') return null;
    return id;
  }
  /** Is the screen at `s` rendering as a deck right now? */
  function deckOn(s) {
    return !!(CARD_MQ && CARD_MQ.matches) && !!deckIdFor(s) && cardMarkupReady();
  }
  /** Is the compact phone shell on?
   *  ⚠️ THE SHELL IS THE PHONE LAYOUT, NOT THE DECK LAYOUT, SINCE 2026-09-14. It used
   *  to follow the deck, which meant the header, the Options menu, the progress track and
   *  the sticky action area appeared and disappeared as she moved between a card question
   *  and the shapes rows or the fabric picker -- the transition Codex's brief asks to be
   *  verified, and the cheapest way to pass it is not to have one. Every screen on a phone
   *  now wears the same shell; what differs is the CONTROLS inside it, which is the
   *  distinction the brief actually draws. */
  function pilotOn() {
    return !!(CARD_MQ && CARD_MQ.matches) && cardMarkupReady();
  }
  /* null means "not browsed yet", which is NOT the same as card 0: on arriving at the
     question the deck opens on the answer she already has, so a saved link and a Back
     both land on her own answer rather than on the first option.
     ⚠️ ONE MAP, KEYED BY DECK ID, REBUILT EMPTY. It was a literal with one entry per
     deck question; at twelve decks that is twelve names to keep in step with CARD_ART and
     a silent `undefined` the day one is missed. An empty object answers `== null` for a
     deck it has never seen, which is the same "not browsed yet" the literal encoded. */
  var cardIx = {};
  var cardReview = false;
  /* Where focus goes after the render that a selection triggers. Without it every tick
     drops focus to the top of the document, which is the keyboard user paying for the
     redraw the mouse user never notices. */
  var cardRefocus = '';
  /* ⚠️ cardRefocus IS SCREEN STATE TOO, AND LEAVING IT OUT OF HERE COST A REAL BUG.
     It is a SELECTOR, so a value left over from the last screen still matches an element
     on the next one -- and on a deck that element is a card, and focusing a card browses
     to it. Belt and braces beside the handler that no longer sets it across an advance. */
  function resetCards() { cardIx = {}; cardReview = false; cardRefocus = ''; shapePart = 0; }
  /* Rotate the phone, or drag a desktop window across 749px, and the question has to
     change layout under her rather than keep a deck that no longer fits or a list the
     stage is no longer drawing for. Only re-renders when the question on screen is one
     of the two that has two layouts. */
  /* ⚠️ THE AVAILABLE SCREEN IS NOT A CONSTANT. Rotating the phone, a desktop window
     being dragged, and Safari's toolbars collapsing all change it, and the first two also
     change what is above the column. Recomputed on both events rather than only at render,
     so the bar does not sit at yesterday's bottom. */
  /* ⚠️ BOTH, AND IN THIS ORDER. `fitDeck` reads `--mky-above`, so a rotation that changed
     the chrome above the flow would otherwise size the cards from the old value. */
  /* ⚠️ ALL THREE, AND IN THIS ORDER. `fitDeck` and `fitFabricPreview` both read
     `--mky-above`, so a rotation that changed the chrome above the flow would otherwise
     size the cards and the preview from the old value. */
  /* ⚠️ A ROTATION IS A NEW MEASUREMENT, NOT A CONTINUATION. `fabPrevHeld` exists to stop
     the picture resizing under her on one screen at one size; carrying it across a turn of
     the phone would hold the portrait height on a landscape screen. */
  function remeasure() {
    fabPrevHeld = 0;
    measureAvailable(); fitFabricPreview(); fitDeck();
  }
  window.addEventListener('resize', remeasure, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', remeasure, { passive: true });
  }

  if (CARD_MQ) {
    /* ⚠️ THE FABRIC PICKERS NOW HAVE TWO LAYOUTS TOO, AND THIS TEST ONLY KNEW ABOUT THE
       DECK. They are not decks -- a catalogue has no single answer to carry on a card --
       but since the preview slot landed they DO draw differently either side of 749px:
       on a phone the stage is borrowed into the question column, above the breakpoint it
       belongs beside it. Without this, rotating a phone into landscape on a picker left
       the garment sitting in the question column of a pointer layout until the next time
       something happened to re-render. Ask whether the SCREEN has two layouts, which is
       what this test always meant. */
    var onCardMq = function () {
      if (deckIdFor(step) || fabricPickerNow()) { resetCards(); render(); }
      else if (step === lastStep()) {
        // Switch results presentation without rebuilding a partially completed form.
        paintPilotChrome(pilotOn());
        paint();
        desktopResultLooks();
        finishStep();
      }
    };
    if (CARD_MQ.addEventListener) CARD_MQ.addEventListener('change', onCardMq);
    else if (CARD_MQ.addListener) CARD_MQ.addListener(onCardMq);
  }

  /* ⚠️ THE HINT IS REWRITTEN FOR THE DECK, because the list's instruction is about a
     list. "Tap the form closest to yours" is true of a tile grid where all three are
     visible; on a deck the first thing to say is that there is more to the side. */
  var CARD_HINT = {
    volume:   'Swipe to compare. Tap the form closest to yours.',
    waist:    'Swipe to compare. Tap the one closest to yours.',
    tummy:    'Swipe to compare. Tap the one closest to yours.',
    /* ⚠️ THE DECK HINT REPLACES `def.hint`, IT DOES NOT SIT BESIDE IT, so anything the
       desktop hint carries that the customer still needs has to be repeated here or it is
       simply gone on a phone. "This includes monokinis" was exactly that: Codex,
       2026-09-15, after the round-one fix put it in `Q.torso.hint` and the mobile swipe
       instruction went on overwriting it.
       ⚠️ IT MATTERS MOST ON THE MONOKINI BRANCH, which is the one garment whose customer
       has real reason to think the question is about something she is not buying -- and
       the compact heading says "one-pieces". Said first, before the swipe instruction,
       because it is the part that decides whether she answers at all. */
    torso:    'This includes monokinis. Swipe to compare. Tap the one that happens to you.',
    top:      'Swipe to compare. Select any that apply.',
    wire:     'Swipe to compare. Tap the one you want.',
    padding:  'Swipe to compare. Tap the one you want.',
    seat:     'Swipe to compare. Tap the one that happens to you.',
    style:    'Swipe to compare. Tap the kind you want.',
    coverage: 'Swipe to compare. Tap how much you want covered.',
    /* The four cloth questions share one instruction because they share one card: a row
       of example swatches under an answer. Naming the examples is the whole job -- a
       woman who reads "tap the one you want" on this screen is being told she is choosing
       a fabric, four screens before she is. */
    color:     'Swipe to compare the examples. Tap the answer that fits.',
    intensity: 'Swipe to compare the examples. Tap the answer that fits.',
    density:   'Swipe to compare the examples. Tap the answer that fits.',
    ground:    'Swipe to compare the examples. Tap the answer that fits.',
    /* ⚠️ THESE TWO SAY "SWIPE TO READ", NOT "SWIPE TO COMPARE". There is nothing to
       compare on a text-led card -- the instruction has to describe what is actually on
       the rail, or the first swipe looks like a picture that failed to load. */
    height:    'Swipe to compare. Tap the one that is yours.',
    /* ⚠️ THE LEGEND IS CODEX'S EXACT WORDING AND IT IS LOAD-BEARING. The pictures show
       charcoal and sage garments that are not in the catalogue and are not her cut; without
       this line a customer reads them as the suit she is about to be sent. */
    variation: 'Examples of fabric combinations, not your chosen print or cut.'
  };
  /* ⚠️ THE DECK ASKS THE SAME QUESTION IN FEWER WORDS, AND ONLY THE DECK. "What does a
     swimsuit top usually get wrong for you?" runs to two lines at 390px and the screen it
     is on is the one short of height; the garment is in the picture directly underneath,
     so "swimsuit" is being said twice. Codex's approved compact heading, 2026-09-13.
     ⚠️ `Q.top.q` IS UNTOUCHED, which is what keeps the desktop question and everything
     that reads it -- the record, the payload, the rail -- exactly as they were. */
  var CARD_Q_TITLE = {
    top: 'What does a top get wrong?',
    /* "Do one-piece suits ride up on you, or pull at the shoulders?" is 62 characters and
       runs to three lines at the deck's heading size on a 390px screen -- on the question
       whose card is the tallest in the flow. The garment is in the picture underneath and
       the two failures are the same failure, so the compact heading asks for one of them
       and the answers name both.
       ⚠️ Q.torso.q IS UNTOUCHED, which is what keeps the desktop question, the record,
       the rail and the payload exactly as they were. Same rule as `top` above. */
    /* ⚠️ BOTH FAILURES ARE NAMED, AND THE SHORT VERSION USED TO NAME ONLY ONE. It read
       "Do one-pieces ride up on you?", which drops the shoulder pull entirely -- so on a
       phone, where this heading is the whole question, half of what is being asked was
       invisible and only the answers hinted at it. Codex, 2026-09-15: preserve the
       shoulder-pull information somewhere visible on mobile.
       ⚠️ STILL SHORTER THAN Q.torso.q, WHICH IS THE POINT OF THIS TABLE. The full
       question is 62 characters and ran to three lines at the deck's heading size on a
       390px screen; this is 47 and the garment is in the picture underneath. */
    torso: 'Do one-pieces ride up, or pull at the shoulders?'
  };
  /* Said instead of the swipe instruction while the review panel is open: there is
     nothing to swipe in there, and an instruction for a screen she is not on is noise. */
  var CARD_REVIEW_HINT = 'Review your choices or return to the cards.';
  /* ⚠️ SAID ON THE CARD, BEFORE IT IS PRESSED. "Nothing. Tops fit me fine" clears every
     other answer -- it always has, in both layouts -- and on a deck the other answers are
     off screen when it is pressed, so the consequence is invisible unless it is written
     down. The option carries no helper copy of its own in Q.top (the list did not need
     one, because the list showed the answers it was about). */
  /* ⚠⚠ THE EXCLUSIVE ANSWERS ON A MULTI-SELECT, AND THERE ARE TWO OF THEM SINCE
     2026-09-15. `none` says the tops she owns fit her; `unknown` says she does not know
     yet. Both are answers, both mean "and none of the issues above", and they exclude each
     other as well -- she cannot simultaneously report that her tops fit and that she is
     not sure. Codex, 2026-09-15.
     ⚠️ ONE TABLE, BECAUSE FOUR PLACES READ IT. The deck's select(), the option list's
     click handler, the saved-answer line and the URL restore all have to agree, and
     `=== 'none'` was written out at each of them. Adding `unknown` as a second literal in
     four places is how the deck and the list come to disagree about what clears what. */
  var EXCLUSIVE = { none: 1, unknown: 1 };
  function isExclusive(v) { return EXCLUSIVE[v] === 1; }
  var CARD_EXCLUSIVE_HELP = 'Choosing this clears the other fit issues.';
  /* Said once under the height deck. See the legend note in questionDeckSpec. */
  var HEIGHT_LEGEND = 'Examples of the range, not a drawing of your proportions.';
  /* ⚠️ THE STAGE'S CAPTION, KEPT. In the list layout Q6_NOTE sits under the shared
     photograph; the deck hides that pane, so the one line that says what the pink MEANS
     would have gone with it. It is said once, under the deck, not once per card. */

  function cardOn(key, v) {
    return Q[key].multi ? state[key].indexOf(v) > -1 : state[key] === v;
  }

  /** The saved answer, in her words, for the line that sits above Continue. */
  function cardSelText(key) {
    var def = Q[key];
    if (!def.multi) {
      return state[key] ? 'Selected: ' + label(def.opts, state[key]) : 'No answer selected';
    }
    var sel = state[key] || [];
    if (!sel.length) return 'No answer selected';
    /* ⚠️ AN EXCLUSIVE ANSWER IS NAMED, NOT COUNTED. "1 issue selected" over "Nothing.
       Tops fit me fine" says the opposite of what she chose, and over "I'm not sure yet"
       it invents an issue she did not report. */
    for (var xi = 0; xi < sel.length; xi++) {
      if (isExclusive(sel[xi])) return 'Selected: ' + label(def.opts, sel[xi]);
    }
    return sel.length + (sel.length === 1 ? ' issue selected' : ' issues selected');
  }

  /** One photograph question's artwork at card size: the same image the shared stage
   *  draws, with ONLY this answer's overlay on it.
   *  ⚠️ ONE OVERLAY PER CARD, NEVER THE UNION. The union is what the shared stage draws
   *  and it answers "what is my top getting". A card answers "is this the thing that
   *  happens to me", and four overlays at once cannot.
   *  ⚠️ figMarkHtml AND cfg.src ARE THE STAGE'S OWN, CALLED NOT COPIED. A card draws the
   *  same paths, the same sprite crop and the same measured bar widths the stage does, so
   *  there is one copy of the pack's numbers. */
  function figCardArtHtml(key, v) {
    var cfg = FIG[key];
    var st = (cfg && cfg.states[v]) || {};
    return '<div class="mky__figframe mky__cardfig">' +
             '<img class="mky__figimg" src="' + escAttr(cfg.src()) + '" alt="' +
             escAttr(st.alt || '') + '" decoding="async">' +
             '<div class="mky__figmarks">' + figMarkHtml(st.marks) + '</div>' +
           '</div>';
  }

  /** Height at card size: the SAME frame, the SAME per-image offset and therefore the
   *  same shared hem the stage draws.
   *  ⚠️ NOT `bodyArtHtml`, AND NOT AN AUTO-FIT. The other three body questions crop one
   *  sprite; height is FIVE separately drawn figures whose whole point is that their
   *  proportions differ, brought onto one baseline by translating each one down by its own
   *  measured `shift`. Fitting each figure to its own box independently would put five
   *  hems in five places and equalise the very difference the question is asking about.
   *  ⚠️ THE 3/2 FRAME AND `overflow: hidden` ARE LOAD-BEARING, not decoration. The
   *  offset uncovers a strip at the top of the frame, and the artwork's own ground is
   *  white, so the frame is white and the overflow is clipped. Percentages all the way
   *  down, so the identical numbers are correct at 460px and in a card. */
  function heightCardArtHtml(v) {
    var e = bodyEntry('height', v) || {};
    var url = BODY_ASSETS[e.asset] || '';
    return '<div class="mky__bodyframe mky__cardbody">' +
             '<img class="mky__bodyimg" src="' + escAttr(url) + '" alt=""' +
             (e.shift ? ' style="transform:translateY(' + e.shift + '%)"' : '') +
             ' decoding="async"></div>';
  }

  /** One variation answer, illustrated. Five images across two garment branches.
   *
   *  ⚠️ BRANCHED ON isSingle(), THE SAME TEST variations() USES. A one piece and a
   *  monokini are SINGLE and are never offered `mixed`; a two piece and a three piece are
   *  SEPARATES. Reading the branch from anywhere else is how the picker and the picture
   *  start disagreeing, which the coverage tiles already had to be fixed for once.
   *
   *  ⚠️ `mixed` ON A SINGLE GARMENT RETURNS NOTHING, AND THAT IS NOT A MISSING ASSET.
   *  variations() filters the answer out entirely there, so the card is never built. If
   *  this ever returns '' for a card that IS on screen, the filter has broken, not the art.
   *
   *  ⚠️ THE PICTURES ARE FABRIC ARRANGEMENTS, NOT HER GARMENT. Codex's pack is explicit:
   *  generic AI-generated silhouettes in charcoal and sage, illustrating how many cloths
   *  there are and where they sit. They are NOT her chosen print, NOT her chosen cut, and
   *  sage/charcoal are NOT selectable -- nothing here may become a stored choice or grow a
   *  catalogue fabric code. The legend under the deck says so in as many words.
   *
   *  ⚠️ THE FRAME IS `.mky__cardsuit`, SHARED WITH THE KIND PHOTOGRAPHS, AND THE IMAGE IS
   *  CONTAINED RATHER THAN FITTED. All five masters are 1536x1024 -- exactly 3:2 -- and the
   *  whole canvas must stay visible: separates-reversible is a 2x2 grid of four complete
   *  flatlays, so a crop or an auto-fit would eat one of the four combinations the picture
   *  exists to show. */
  var VARIATION_ART = {
    separates: { same: 'varSeparatesSame', mixed: 'varSeparatesMixed',
                 reversible: 'varSeparatesReversible' },
    single:    { same: 'varSingleSame', reversible: 'varSingleReversible' }
  };
  var VARIATION_ALT = {
    'separates:same': 'A swimsuit top and bottom in the same charcoal fabric.',
    'separates:mixed': 'A charcoal swimsuit top paired with a sage bottom.',
    'separates:reversible': 'One reversible two-piece shown in four combinations: '
      + 'charcoal with charcoal, charcoal with sage, sage with charcoal, and sage with sage.',
    'single:same': 'A one-piece swimsuit in a single charcoal fabric.',
    'single:reversible': 'One reversible swimsuit shown twice, with its charcoal face '
      + 'outward and with its sage face outward.'
  };
  /* ⚠️ A DECLARED, REPORTED ARTWORK GAP -- NOT A SILENT ONE, AND NOT AN INVENTED PICTURE.
     `samecrop` arrived on 2026-09-15 with no render behind it: the artwork pack draws a
     suit in one fabric and a suit in two, and there is no plate showing a suit in one
     fabric beside a crop in another. Codex's standing rule for this is "use a clear
     text-led card and report the gap", never generate one. Naming it here is the report,
     and it is what stops variationCardArtHtml's missing-ASSET warning from firing on a
     card that is correctly text-led. The day the plate exists, adding `samecrop` to
     VARIATION_ART and deleting this entry is the whole change.
     ⚠️ IT IS PER-ANSWER, WHICH IS WHY IT IS NOT IN `DECK_TEXT_ONLY`. That table names a
     whole DECK with no imagery; three of this deck's four cards have a picture, and
     marking the deck text-led would throw them away. */
  /* ⚠️ THE GAP IS CLOSED AND THE TABLE IS GONE. `VARIATION_ART_NONE` named exactly one
     entry -- `separates:samecrop` -- and `samecrop` is offered on a THREE PIECE and
     nowhere else (see variations()). Every three-piece card is now composed by
     variationSvgHtml() below, which reaches `samecrop` like the other three, so the
     text-led card Codex's standing rule had allowed no longer exists and a table saying
     "this answer has no artwork" would be describing a state the code cannot enter.
     Codex, 2026-09-17: "The different-jacket card must not remain text-only." */

  /* ── THE THREE-PIECE ARRANGEMENT CARDS ───────────────────────────────────────────────
     ⚠️ SVG SILHOUETTES, NOT THE PHOTOGRAPHIC PACK, AND THE PACK IS WHY. The transparent
     renders are shaded rasters with their own light and cut -- "not masks or UV maps",
     Codex's own pack says so, and garmentPhoto() carries the same note. They cannot take
     a fabric. So a three-piece arrangement card composed from them would draw the SAME
     three garments on all four cards: "One fabric throughout" and "Different top and
     bottom" would be pixel-identical, and the one thing the question asks about would be
     the one thing the picture could not show. Reported to Nuno 2026-09-17 before any of
     this was written; his ruling is the SVG route.
     ⚠️ NOTHING NEW IS DRAWN. The symbols are the flow's own shape sprite and the third
     piece is `#shape-top-tankini` at x=10 -- the identical composition paint() puts on the
     stage for a three piece, down to the 620-wide viewBox and the three 200x200 boxes. A
     card and the preview above it therefore cannot disagree about what a three piece is.
     ⚠️ TWO EXAMPLE TONES, HER OWN SHAPES. Codex: "Before the second fabric is chosen, use
     consistent examples to explain arrangements." The second fabric does not exist yet on
     this screen, so inventing one would be a promise; the tones are the charcoal and sage
     of the existing flatlays, so the two idioms do not argue on a store where a customer
     may see both. The CUT is hers, because shapes are answered one screen earlier and
     showing a triangle to someone who chose a halter is a worse lie than a stand-in
     colour. variationArtNote() says which half is an example.
     ⚠️ TWO-PIECE AND ONE-PIECE ARE UNTOUCHED. Codex: "Preserve the correct presentations
     for other suit types." They have no third piece, their flatlays are approved and live,
     and the brief asks for nothing there. */
  var VAR_EX_A = { hex: '#3f4145', lightness: 0.25 };
  var VAR_EX_B = { hex: '#94a487', lightness: 0.58 };
  var VAR_EX_ALT = {
    same: 'A long-sleeve crop, a top and a bottom, all three in one charcoal fabric.',
    mixed: 'A charcoal long-sleeve crop and a charcoal top, with a sage bottom.',
    samecrop: 'A sage long-sleeve crop, with a charcoal top and a charcoal bottom.',
    reversible: 'A charcoal long-sleeve crop, with a top and a bottom each shown half '
      + 'charcoal and half sage to stand for their two faces.'
  };
  /* ⚠️ DOCUMENT-WIDE IDS, SO THEY ARE COUNTED RATHER THAN NAMED. An SVG `clipPath` id is
     global to the page exactly as the `pattern` ids in lookSvg() are, and that function
     already carries the scar: reusing one id across tiles made every tile show the same
     fabric. Four cards render at once here, so a per-answer id would be safe only until
     the day a second deck is on screen. A counter cannot collide. */
  var varSvgSeq = 0;
  function variationSvgHtml(v) {
    var top = effectiveShape('topShape'), bot = effectiveShape('bottomShape');
    var cropF = VAR_EX_A, topF = VAR_EX_A, botF = VAR_EX_A;
    if (v === 'mixed') botF = VAR_EX_B;
    else if (v === 'samecrop') cropF = VAR_EX_B;
    var piece = function (f, href, x, extra) {
      return '<g fill="' + fillFor(f, '') + '" stroke="' + edgeFor(f) +
             '" stroke-width="1.5"' + (extra || '') + '>' +
             '<use href="#' + href + '" x="' + x + '" y="10" width="200" height="200"/></g>';
    };
    var body = piece(cropF, 'shape-top-tankini', 10) +
               piece(topF, 'shape-top-' + top, 210) +
               piece(botF, 'shape-bottom-' + bot, 410);
    var defs = '';
    if (v === 'reversible') {
      /* ⚠️ THE CROP IS NOT SPLIT, AND THAT IS THE PICTURE DOING ITS JOB. Only the top and
         the bottom turn (Evelin, 2026-09-15); a crop drawn half and half would illustrate
         a construction the workroom does not cut. The two triangles cover the top's box
         and the bottom's box and nothing else. */
      var cid = 'mkyrev' + (++varSvgSeq);
      defs = '<defs><clipPath id="' + cid + '">' +
             '<polygon points="210,210 410,210 410,10"/>' +
             '<polygon points="410,210 610,210 610,10"/>' +
             '</clipPath></defs>';
      body += piece(VAR_EX_B, 'shape-top-' + top, 210, ' clip-path="url(#' + cid + ')"') +
              piece(VAR_EX_B, 'shape-bottom-' + bot, 410, ' clip-path="url(#' + cid + ')"');
    }
    return '<div class="mky__cardsuit mky__cardvar">' +
             '<svg class="mky__cardvarsvg" viewBox="0 0 620 220" role="img" aria-label="' +
             escAttr(VAR_EX_ALT[v] || '') + '">' + defs + body + '</svg>' +
           '</div>';
  }
  /** What the arrangement pictures are, said once, on the garment that needs it said.
   *  ⚠️ IT DID NOT EXIST BEFORE AND NOW IT HAS TO. The flatlays are unmistakably generic
   *  -- other people's garments in other people's colours -- so "these are examples" went
   *  without saying. A three-piece card draws HER cut, which makes the tone the only
   *  invented thing on it and therefore the only thing that can be misread as chosen. */
  function variationArtNote() {
    /* ⚠️ TWO FAULTS IN ONE LINE, BOTH CODEX'S, 2026-09-17. It read "Your shapes, in
       example colours. You choose the second fabric next."
       (a) `colours` is British. ILLIXIS copy is US English -- the rule is stated at the
           top of this file for the fabric refinement keys and it applies to every string.
       (b) THE SECOND SENTENCE WAS A PROMISE THE ONE-FABRIC ROUTE NEVER KEEPS. This note
           sits over ALL FOUR cards, and "One fabric throughout" asks for no second
           fabric at all. A caption telling her what happens next, on the card where
           nothing of the sort happens, is worse than no caption. */
    return state.style === 'three'
      ? 'Your shapes, shown in example colors.'
      : CARD_HINT.variation;
  }
  function variationCardArtHtml(v) {
    if (state.style === 'three') return variationSvgHtml(v);
    var branch = isSingle() ? 'single' : 'separates';
    var key = (VARIATION_ART[branch] || {})[v];
    /* ⚠️ TWO REASONS TO HAVE NO ARTWORK, AND ONLY ONE OF THEM IS FINE. No KEY means this
       answer does not exist on this branch -- `single` + `mixed`, which variations()
       filters out so the card is never built. That is by design and silent is correct.
       A key with no ASSET is a wiring failure: the theme did not ship the file, or
       data-suit-assets lost an entry. Both used to return '' identically, so a card would
       render with a blank frame and nothing anywhere would say why. Warns like the data
       blocks above do. */
    var src = key && SUIT_ASSETS[key];
    if (key && !src && window.console && console.warn) {
      console.warn('[make-yours] variation artwork missing for ' + branch + ':' + v +
                   ' (expected SUIT_ASSETS.' + key + ')');
    }
    if (!src) return '';
    /* ⚠️ NO `data-layout`, AND ITS ABSENCE IS THE SIZING. `data-layout="single"` was
       copied from the kind-photo composer and dragged its rule with it:
       `.mky__cardsuit[data-layout="single"] .mky__cardsuitimg { max-width: 62% }` is
       (0,3,0) and beat the variation rule at (0,2,0), so a 1536x1024 canvas rendered
       175px wide inside a 282px frame -- 282 x 62% = 175 exactly. That 62% is right for a
       single garment centred in a card and wrong for a full-canvas composition, most of
       all for the 2x2 reversible grid. Setting no layout leaves nothing for it to match. */
    return '<div class="mky__cardsuit mky__cardvar">' +
             '<img class="mky__cardsuitimg" src="' + escAttr(src) + '" alt="' +
             escAttr(VARIATION_ALT[branch + ':' + v] || '') + '" decoding="async">' +
           '</div>';
  }

  /** One kind of suit, photographed, at card size.
   *  ⚠️ THREE PIECE REUSES THE TWO-PIECE PHOTOGRAPH AND ADDS THE CROP, the same
   *  composition paintSuit() makes on the stage -- there is no separate three-piece render
   *  and there should not be, because the third piece IS the crop.
   *  ⚠️ THE STAGE'S LAYOUT CSS IS KEYED BY ELEMENT ID and cannot be reused here; a card
   *  lays its one or two images out with its own class. The IMAGES and the ALT TEXT are
   *  the stage's, which is what the question is actually about. */
  function suitCardArtHtml(v) {
    var three = v === 'three';
    var a = SUIT_ASSETS[three ? 'two' : v] || '';
    var c = SUIT_ASSETS.crop || '';
    return '<div class="mky__cardsuit" data-layout="' + (three ? 'three' : 'single') + '">' +
             '<img class="mky__cardsuitimg" src="' + escAttr(a) + '" alt="' +
             escAttr(SUIT_ALT[v] || '') + '" decoding="async">' +
             (three && c
               ? '<img class="mky__cardsuitimg" src="' + escAttr(c) + '" alt="' +
                 escAttr(SUIT_CROP_ALT) + '" decoding="async">'
               : '') +
           '</div>';
  }

  /** One card: its own artwork, the answer, what it means, and the control that selects
   *  it. Single choice is a button that reports `aria-pressed`; multi-select is an
   *  article carrying its own checkbox, because a group of pressed buttons cannot say
   *  "any number of these, independently" to a screen reader.
   *  ⚠️ ADDRESSED BY INDEX, NOT BY VALUE. One answer in the flow is genuinely `null`
   *  -- the fabric ground question's "Either", which means ANSWERED WITH NO PREFERENCE and
   *  is not the same as unanswered. `data-v="null"` is the string "null" and compares
   *  unequal to everything, which is the bug the option list already fixed the same way.
   *  `data-v` is still emitted for the answers that have one, because it is what the
   *  walking suites select on. */
  function deckCardHtml(spec, o, i) {
    var on = spec.isOn(i);
    var vAttr = typeof o[0] === 'string' ? ' data-v="' + escAttr(o[0]) + '"' : '';
    var art = spec.art(i);
    /* ⚠️ A TEXT-LED CARD SAYS SO IN ITS CLASS. Without the modifier it is a card with a
       collapsed picture slot -- the same height reservation, the same centred column --
       which reads as artwork that failed to load rather than as a question that has
       none. See DECK_TEXT_ONLY. */
    /* ⚠️ A SUGGESTION IS NOT A SELECTION, AND IT GETS ITS OWN MARKER RATHER THAN THE
       SELECTED ONE. Only the shapes deck supplies `suggested` today; every other question
       arrives genuinely unanswered and has nothing to suggest. Codex, 2026-09-15. */
    var suggested = !on && spec.suggested ? spec.suggested(i) : false;
    var cls = 'mky__card' + (art ? '' : ' mky__card--text') +
              (suggested ? ' mky__card--suggested' : '');
    var head = '<b class="mky__card-h">' + (spec.title ? spec.title(i) : o[1]) + '</b>' +
               '<span class="mky__card-desc">' + (spec.help(i) || '') + '</span>';
    if (!spec.multi) {
      return '<button class="' + cls + ' mky__card--pick" type="button"' + vAttr +
             ' data-ix="' + i + '" aria-pressed="' + on + '">' +
             art + head +
             '<span class="mky__card-pick">' +
             (on ? '\u2713 Selected'
                 : suggested ? 'Suggested starting shape \u00b7 tap to accept'
                 : 'Tap to select') +
             '</span></button>';
    }
    return '<article class="' + cls + ' mky__card--fit"' + vAttr + ' data-ix="' + i +
           '" data-on="' + on + '">' + art + head +
           '<label class="mky__cardcheck"><input type="checkbox"' + vAttr +
           (on ? ' checked' : '') + '><span>' + spec.checkLabel(i) +
           '</span></label></article>';
  }

  /** The review panel: what is actually selected, with a way to drop each one.
   *  ⚠️ IT IS A VIEW OF THE ANSWER, NOT A SECOND COPY OF IT. Removing here runs the same
   *  spec.select() ticking the card off runs, so the two can never disagree. */
  function deckReviewHtml(spec) {
    var chosen = [];
    spec.opts.forEach(function (o, i) { if (spec.isOn(i)) chosen.push(i); });
    return '<div class="mky__cardreviewpane"' + (cardReview ? '' : ' hidden') + '>' +
      '<b class="mky__card-h">Your selections</b>' +
      (chosen.length
        /* ⚠️ THE BUTTON CARRIES BOTH THE ANSWER AND ITS INDEX. `data-mky-remove` is the
           ANSWER, which is what the suites select on and what a person reading the DOM
           needs; `data-ix` is what the handler uses, because one answer in the flow is
           genuinely `null` and an index always addresses a card. */
        ? '<ul class="mky__reviewlist">' + chosen.map(function (i) {
            var v = spec.opts[i][0];
            return '<li><span>' + spec.opts[i][1] + '</span>' +
                   '<button type="button" data-mky-remove="' +
                   escAttr(typeof v === 'string' ? v : String(i)) +
                   '" data-ix="' + i + '" aria-label="Remove ' + escAttr(spec.opts[i][1]) +
                   '">Remove</button></li>';
          }).join('') + '</ul>'
        : '<p class="mky__card-desc">Nothing selected yet. Return to the cards to choose.</p>') +
      '<button type="button" class="mky__cardreturn" data-mky-review-close>' +
      'Return to cards</button></div>';
  }

  /** Fill the sticky action bar's summary line, or clear it.
   *  ⚠️ IT IS BUILT HERE RATHER THAN LEFT IN THE LIQUID because "Review selections" is
   *  a control that appears and disappears with the answer, and rebuilding the pair
   *  together is what stops a stale count sitting beside a live button. A null spec means
   *  this is not a deck screen: empty it and hide it, or last question's answer is
   *  sitting under the next one.
   *  ⚠️ THE BAR IS STICKY, SO ANYTHING PUT HERE IS ON SCREEN AT ALL TIMES. Nothing that
   *  is not about the answer she is submitting belongs in it. */
  function paintActionSel(spec) {
    if (!el.actionSel) return;
    if (!spec) {
      el.actionSel.innerHTML = '';
      el.actionSel.hidden = true;
      return;
    }
    var some = spec.opts.some(function (o, i) { return spec.isOn(i); });
    el.actionSel.hidden = false;
    el.actionSel.innerHTML =
      '<span class="mky__actionsel-line">' + spec.selText() + '</span>' +
      (spec.multi && some && !cardReview
        ? '<button type="button" class="mky__cardreview" data-mky-review-open>' +
          'Review selections</button>'
        : '');
  }

  /** Render one deck and wire it. Everything that differs between the screens that use
   *  it lives in the SPEC -- the question, the answers, one answer's artwork, and the two
   *  functions that read and write the answer wherever it happens to be stored.
   *  ⚠️ THREE FAMILIES OF SCREEN SHARE THIS ONE BODY. The question list writes to
   *  `state[key]`, the kind screen to `state.style` and the cloth questions to
   *  `state.prefs[key]`, and each of those has its own side effects on selection --
   *  forfeitAfter, snapShapesForStyle, resetting the suggestions page. None of that may
   *  leak in here: the spec's select() owns it, and this function's only job is browsing,
   *  drawing and calling it.
   *  ⚠️ NOTHING HERE MAY WRITE TO `state`. Browsing is not answering; see the block
   *  comment above CARD_ART. */
  function deckStep(spec) {
    var opts = spec.opts;
    var multi = !!spec.multi;
    var id = spec.id;
    if (cardIx[id] == null) {
      /* Open on her own answer. For a multi-select that is the FIRST one she ticked,
         which is the only one of several that has a defensible claim to being first. */
      var at = 0;
      for (var i = 0; i < opts.length; i++) { if (spec.isOn(i)) { at = i; break; } }
      /* ⚠️ AND ON THE SUGGESTION WHEN THERE IS NO ANSWER. The shapes deck opens on the
         starting shape rather than card 1, exactly as it opened on her own answer before
         -- what changed is that the card is now labelled a suggestion instead of being
         ticked. Without this the rail would open on card 1 and the marked card would be
         somewhere off to the side. */
      if (!spec.isOn(at) && spec.suggested) {
        for (var sgi = 0; sgi < opts.length; sgi++) {
          if (spec.suggested(sgi)) { at = sgi; break; }
        }
      }
      cardIx[id] = at;
    }
    if (cardIx[id] > opts.length - 1) cardIx[id] = opts.length - 1;

    el.step.innerHTML =
      '<h2 class="mky__q">' + spec.q + '</h2>' +
      '<p class="mky__hint text-sm text-subdued">' +
      /* ⚠️ THE INLINE CONTINUE IS DROPPED HERE, AND ONLY HERE, BECAUSE ITS REASON IS
         GONE. It exists because the multi-select question does not advance on a tap, and
         "press Continue" was an instruction pointing at a button below the fold. On a
         deck screen Continue is in a bar pinned to the bottom of the viewport -- it is
         never off screen, so naming it in the sentence adds a third control to a screen
         built to have fewer.
         ⚠️ IT STAYS EVERYWHERE ELSE. The list layout still has the problem. Do not
         "tidy" the branch in questionStep() to match this one. */
      (cardReview ? CARD_REVIEW_HINT : spec.hint) + '</p>' +
      /* Names the part of the garment being chosen, under the parent question rather than
         instead of it. Only the shape chooser has one. */
      (spec.sub ? '<h3 class="mky__sub mky__cardsub">' + spec.sub + '</h3>' : '') +
      /* The thin track is stamped by finishStep(), on every phone screen and not only on
         a deck -- one place, so a screen type cannot be built without one. */
      '<div class="mky__cards" data-mky-cards="' + escAttr(id) + '">' +
        '<div class="mky__cardbrowse"' + (cardReview ? ' hidden' : '') + '>' +
          '<div class="mky__cardrail" data-mky-rail>' +
            opts.map(function (o, ix) { return deckCardHtml(spec, o, ix); }).join('') +
          '</div>' +
          railPagerHtml(opts.length, 'Browse options') +
          (spec.legend ? '<p class="mky__cardlegend">' + spec.legend + '</p>' : '') +
        '</div>' +
        (multi ? deckReviewHtml(spec) : '') +
      '</div>' +
      /* A screen that has to ASK before it commits puts its question here, below the
         rail. Only the variation question uses it, and only when reversible collides with
         a fit answer she has already given. */
      (spec.extra ? spec.extra() : '');
    /* ⚠️ THE SAVED ANSWER LIVES IN THE ACTION BAR, NOT IN THE STEP, SINCE 2026-09-13. It
       was the last thing inside `.mky__step`, which put it above the nav in the column
       and read as part of the question. Codex asked for one compact bottom area holding
       the summary, Back and Continue together -- and on a phone that area is STICKY, so
       anything that must travel with Continue has to be inside it. `#mky-actionsel` is
       filled here and emptied by paintActionSel() on every other screen. */

    var rail = el.step.querySelector('[data-mky-rail]');
    /* The pager's own three nodes are looked up by wireRailPager(); only the review button
       is this screen's, because only a deck has something to review. */
    var openBtn = el.actions ? el.actions.querySelector('[data-mky-review-open]') : null;

    /* ⚠️ THE INDEX LIVES IN `cardIx`, NOT IN THE HELPER, AND THAT IS THE WHOLE REASON
       wireRailPager() TAKES A READER AND A WRITER. A deck's position has to survive a
       render -- she answers, the screen rebuilds, and the rail must come back where she
       left it -- while the looks rail is rebuilt from scratch every time and starts at
       zero. One pager, two lifetimes for the number it points at. */
    var browseTo = wireRailPager(el.step, rail, opts.length,
                                 function () { return cardIx[id]; },
                                 function (v) { cardIx[id] = v; });

    /* ⚠️ A SWIPE MUST NOT END IN AN ANSWER, AND SINCE AN ANSWER NOW ADVANCES, A MISSED
       GUARD COSTS HER THE SCREEN RATHER THAN A STRAY TICK. The browser suppresses the
       click after a gesture only once IT has decided the gesture was a scroll, and a short
       flick that moves the rail thirty pixels is often not that -- which is exactly the
       gesture a card rail invites.
       ⚠️ MEASURED TWO WAYS, BECAUSE EITHER ONE ALONE MISSES A REAL CASE. The rail's own
       scrollLeft catches the flick that moved the deck; the pointer's travel catches the
       drag that did not move it because the rail was already at an end. Four pixels of
       scroll and ten of travel are slop, not intent.
       ⚠️ CAPTURE PHASE ON THE RAIL, NOT A CHECK INSIDE EACH HANDLER. It has to stop the
       CHECKBOX too, and a checkbox toggles on the default action rather than in a listener
       -- preventDefault from an ancestor's capture listener is the only thing that reaches
       it before it flips.
       ⚠️ NO POINTER MEANS A KEYBOARD, WHICH IS ALWAYS DELIBERATE. Enter on a focused
       card arrives with no pointerdown before it, so `press` is null and nothing is
       cancelled. */
    var press = null;
    if (rail) {
      rail.addEventListener('pointerdown', function (e) {
        press = { x: e.clientX, y: e.clientY, left: rail.scrollLeft };
      }, { passive: true });
      rail.addEventListener('click', function (e) {
        var swiped = false;
        if (press) {
          if (Math.abs(rail.scrollLeft - press.left) > 4) swiped = true;
          else if (Math.abs(e.clientX - press.x) > 10) swiped = true;
          else if (Math.abs(e.clientY - press.y) > 10) swiped = true;
        }
        press = null;
        if (swiped) { e.preventDefault(); e.stopPropagation(); }
      }, true);
    }

    Array.prototype.forEach.call(rail ? rail.children : [], function (card, ix) {
      /* ⚠️ FOCUS BROWSES AND DOES NOT SELECT. Tabbing through the deck has to show the
         card being described or the keyboard gets a rail it cannot see; it must not tick
         anything on the way past, which is the failure mode the list layout never had
         because the list had nothing to scroll. */
      var focusable = card.tagName === 'BUTTON' ? card : card.querySelector('input');
      if (focusable) {
        focusable.addEventListener('focus', function () { browseTo(ix, false); });
      }
      if (!multi) {
        card.addEventListener('click', function () {
          cardIx[id] = ix;
          /* ⚠️ EXACTLY ONCE, AND THE SPEC DECIDES. select() may answer `false` to mean
             "I have asked her something, stay here" -- the reversible conflict is the one
             screen that does. Everything else saves and moves on. Neither branch calls
             both: goNext() ends in render(). */
          var go = spec.select(ix, true);
          if (go === false) {
            /* ⚠️ SET ONLY WHEN THE SCREEN STAYS, AND THAT IS A BUG FIX. Setting it
               before the branch meant an ADVANCE carried the selector into the next
               deck, which focused ITS card at the same index -- and focus browses, so
               every question after a tap on card 2 opened on card 2 with nothing
               selected. Caught by deck.py's inventory walk, on the coverage screen after
               a two piece was chosen from the second kind card.
               ⚠️ AND "THE SCREEN STAYS" IS NOT THE SAME AS "THE STEP STAYS". The shape
               chooser refuses the advance and then shows a DIFFERENT component on the same
               step, which is a new question arriving even though the step index did not
               move. Ask which deck is on screen now: the same one gets the card back, a
               different one gets its heading focused, exactly as a step change would. */
            var nowId = (deckSpecFor(step) || {}).id;
            if (nowId === id) cardRefocus = '[data-ix="' + ix + '"]';
            else stepJustChanged = true;
            render();
          } else {
            goNext();
          }
        });
      } else if (focusable) {
        focusable.addEventListener('change', function () {
          cardIx[id] = ix;
          cardRefocus = '[data-ix="' + ix + '"] input';
          spec.select(ix, focusable.checked);
          /* Multi-select stays put so more can be chosen. Nothing can know she has
             finished. */
          render();
        });
      }
    });

    var closeBtn = el.step.querySelector('[data-mky-review-close]');
    if (closeBtn) closeBtn.addEventListener('click', function () {
      cardReview = false;
      cardRefocus = '[data-ix="' + cardIx[id] + '"] input';
      render();
    });
    Array.prototype.forEach.call(el.step.querySelectorAll('[data-mky-remove]'), function (b) {
      b.addEventListener('click', function () {
        spec.select(parseInt(b.dataset.ix, 10), false);
        /* Focus stays in the panel she is working in, on the one control that is
           guaranteed to survive the redraw. */
        cardRefocus = '[data-mky-review-close]';
        render();
      });
    });

    if (spec.wire) spec.wire(el.step);

    paintActionSel(spec);
    openBtn = el.actions ? el.actions.querySelector('[data-mky-review-open]') : null;
    if (openBtn) {
      openBtn.addEventListener('click', function () {
        cardReview = true;
        cardRefocus = '[data-mky-review-close]';
        render();
      });
    }
    browseTo(cardIx[id], false);
    if (cardRefocus) {
      var target = el.step.querySelector(cardRefocus);
      cardRefocus = '';
      if (target) {
        try { target.focus({ preventScroll: true }); } catch (err) { target.focus(); }
      }
    }
    finishStep();
  }

  /** The deck spec for one of the questions in `Q`: the body and fit questions, plus
   *  coverage. The answer lives in `state[key]`. */
  function questionDeckSpec(key) {
    var def = Q[key];
    var art = CARD_ART[key];
    return {
      id: key,
      q: CARD_Q_TITLE[key] || def.q,
      /* ⚠️ ON A THREE PIECE THE COVERAGE QUESTION IS ONLY ABOUT WHAT GOES UNDER THE
         CROP, and the list layout rewrites its hint after the fact to say so. The deck
         has to say the same thing or the screen reads as if all three pieces were up for
         decision. Chosen here rather than patched after render() because the question,
         the answers and everything they seed are identical; only the framing differs. */
      hint: (key === 'coverage' && state.style === 'three')
        ? 'Swipe to compare. The long-sleeve crop is included; this is about the top ' +
          'and bottom that go under it.'
        : (CARD_HINT[key] || def.hint),
      opts: def.opts,
      multi: !!def.multi,
      /* ⚠️ THE STAGE'S CAPTION, KEPT, ON THE ONE QUESTION THAT NEEDS IT. In the list
         layout Q6_NOTE sits under the shared photograph; the deck hides that pane, so the
         line that says what the pink MEANS would have gone with it. It is said once,
         under the deck, not once per card -- and only where the artwork is annotated and
         multi-select, which is what makes the union on the stage the thing it explains. */
      /* ⚠️ HEIGHT'S LEGEND IS THE DISCLAIMER, AND PUTTING IT HERE IS DELIBERATE. The
         line saying the drawings do not predict her proportions used to sit in a caption
         under the stage; Nuno removed it on 2026-09-11 because it reappeared under every
         option the pointer crossed, and the note at paintBody() says the place for it is
         the lede or the hint, never a per-hover caption. A deck legend is neither: it is
         said ONCE, under the rail, on the one question where five separately drawn
         figures sit side by side and the proportion claim is at its strongest. */
      legend: key === 'height' ? HEIGHT_LEGEND
            : (def.multi && FIG[key] && FIG[key].note) ? FIG[key].note : '',
      /* ⚠️ NO ENTRY IN CARD_ART MEANS NO PICTURE EXISTS, NOT THAT ONE IS MISSING HERE.
         Every question in `Q` has one today. Never substitute a neighbouring question's
         artwork to fill a blank; add the drawing, or leave the card text-led and say so in
         DECK_TEXT_ONLY. */
      art: function (i) { return art ? art(def.opts[i][0]) : ''; },
      isOn: function (i) { return cardOn(key, def.opts[i][0]); },
      help: function (i) {
        return isExclusive(def.opts[i][0]) ? CARD_EXCLUSIVE_HELP : (def.opts[i][2] || '');
      },
      checkLabel: function (i) {
        /* ⚠️ THREE LABELS, NOT TWO. "This describes me" is right for "my tops fit me
           fine" and wrong for "I'm not sure yet" -- a checkbox reading "This describes me"
           under an uncertain answer asks her to assert something about herself that the
           answer is refusing to assert. */
        var vv = def.opts[i][0];
        if (vv === 'none') return 'This describes me';
        if (vv === 'unknown') return 'This is where I am';
        return 'This happens to me';
      },
      selText: function () { return cardSelText(key); },
      select: function (i, checked) {
        var v = def.opts[i][0];
        if (!def.multi) {
          /* Same two lines the list layout runs, in the same order: `volume` FEEDS
             FORWARD into bottomSize and `coverage` seeds both shapes, so the forfeit has
             to read the OLD answer. */
          var prevV = state[key];
          forfeitAfter(key, v);
          state[key] = v;
          /* ⚠️ COVERAGE SEEDS THE SHAPES, AND THE LIST LAYOUT DOES IT IN ITS OWN HANDLER.
             Without this the deck sets the coverage answer and leaves the shapes screen
             on the previous answer's garments -- the tile row has done this since it was
             built. The `linked` guard is the one from that handler, verbatim: a shape the
             LINK chose outranks a shape this table guesses. */
          /* ⚠️ COVERAGE NO LONGER WRITES A SHAPE, AND THE GUARD THAT USED TO PROTECT AN
             EXPLICIT ONE IS GONE WITH IT. The suggestion is derived from `state.coverage`
             on every read (see suggestedShape), so changing this answer moves the starting
             shape and cannot touch one she has chosen -- there is nothing here to
             overwrite. */
          /* ⚠️ THE ANSWER IS SAVED FIRST AND THE ADVANCE IS HELD. She pressed a card and
             the card has to show as chosen while she decides what to do about it; what
             stops is only the move to the next question. Returning `false` is the same
             signal the variation screen's own conflict uses. */
          if (openComfortConflict(key, v, prevV)) return false;
          return;
        }
        /* ⚠️ THE SAME EXCLUSIVITY THE LIST HAS, WRITTEN AS A CHECKBOX. Ticking "none"
           clears the rest; ticking anything else drops "none". Unticking "none" leaves
           the question UNANSWERED, which is not the same as answering "none" -- the
           Continue gate reads `length > 0` and must keep being able to tell them
           apart. */
        /* The WHOLE previous answer, so dismissing the conflict restores every other
           issue she had ticked rather than just untucking the one she pressed. */
        var prevArr = state[key].slice();
        if (isExclusive(v)) {
          /* Unticking an exclusive answer leaves the question UNANSWERED, which is not
             the same as answering it -- the Continue gate reads `length > 0` and must keep
             being able to tell them apart. Ticking one replaces whatever was there,
             including the OTHER exclusive answer. */
          state[key] = checked ? [v] : [];
          return;
        }
        var arr = state[key].filter(function (x) { return !isExclusive(x); });
        var j = arr.indexOf(v);
        if (checked) { if (j < 0) arr.push(v); }
        else if (j > -1) arr.splice(j, 1);
        state[key] = arr;
        /* Only a TICK can create a conflict; unticking one can only resolve it. */
        if (checked) openComfortConflict(key, v, prevArr);
      }
    };
  }

  /** The kind-of-suit screen as a deck. Its answer is `state.style`, and choosing one
   *  has three consequences the list layout's handler owns -- see wireStyleButtons.
   *  ⚠️ NO HOVER PREVIEW, BECAUSE THERE IS NOTHING TO PREVIEW INTO. On a deck each card
   *  carries its own photograph and the shared stage is hidden, so `showStyle`/
   *  `restoreStyle` would be writing to a pane nobody can see. That pair stays wired on
   *  the pointer layout, where hovering IS the preview. */
  function styleDeckSpec() {
    return {
      id: 'style',
      q: 'What kind of suit?',
      hint: CARD_HINT.style,
      opts: STYLES,
      multi: false,
      legend: '',
      art: function (i) { return suitCardArtHtml(STYLES[i][0]); },
      isOn: function (i) { return state.style === STYLES[i][0]; },
      help: function (i) { return STYLES[i][2] || ''; },
      checkLabel: function () { return ''; },
      selText: function () {
        return state.style ? 'Selected: ' + label(STYLES, state.style)
                           : 'No answer selected';
      },
      select: function (i) {
        /* No hover preview is wired on a deck -- the photograph is on the card -- so this
           is a no-op here. Written as the same call the list handler makes so the two
           cannot drift; see the note there. */
        restoreStyle();
        forfeitAfter('style', STYLES[i][0]);
        state.style = STYLES[i][0];
        /* The two repairs the list handler makes, for the same reasons: a one piece is
           only cut classic or asymmetrical, and it cannot be "different top and bottom". */
        snapShapesForStyle();
        normalizeVariation();
      }
    };
  }

  /** One cloth-preference question as a deck. The answer lives in `state.prefs[key]`.
   *  ⚠️ THE EXAMPLES ARE THE ARTWORK AND THEY ARE UNCHANGED. fabricExampleRow() picks
   *  them, one family each, ordered by the reviewer's confidence -- the same three
   *  swatches the option list shows, from the same call. Nothing here reclassifies a
   *  fabric or reorders an example.
   *  ⚠️ `undefined` IS UNANSWERED AND `null` IS "Either". The ground question's middle
   *  answer is a real answer that stops constraining; `in` is what tells them apart and
   *  is what the Continue gate reads. Never compare with `==`. */
  function fabricDeckSpec(cq) {
    return {
      id: cq.key,
      q: cq.q,
      hint: CARD_HINT[cq.key] || cq.hint,
      opts: cq.opts,
      multi: false,
      /* Said once under the deck rather than once per card, and it is the same sentence
         the option list prints under its row. Three swatches inside a card read as three
         things to tap unless something says what they are. */
      legend: 'These show the range. You’ll choose your fabric shortly.',
      art: function (i) {
        var row = fabricExampleRow(cq.key, cq.opts[i][0]);
        return '<span class="mky__cardex">' + row + '</span>';
      },
      isOn: function (i) {
        return (cq.key in state.prefs) && state.prefs[cq.key] === cq.opts[i][0];
      },
      help: function (i) { return cq.opts[i][2] || ''; },
      checkLabel: function () { return ''; },
      selText: function () {
        if (!(cq.key in state.prefs)) return 'No answer selected';
        var cur = state.prefs[cq.key], name = '';
        cq.opts.forEach(function (o) { if (o[0] === cur) name = o[1]; });
        return 'Selected: ' + name;
      },
      select: function (i) {
        state.prefs[cq.key] = cq.opts[i][0];
        /* Changing an answer invalidates where she was in the suggestions. Reset the
           page deliberately rather than paging into a list that no longer exists. */
        state.prefsPage = 0;
      }
    };
  }

  /** The variation question as a deck: one fabric, two fabrics, or reversible.
   *  ⚠️ TEXT-LED. There is no artwork for "One fabric throughout" and none is invented
   *  here; see DECK_TEXT_ONLY.
   *  ⚠️ IT IS THE ONE DECK THAT MAY REFUSE TO ADVANCE. Reversible can collide with a fit
   *  answer she has already given -- an underwire, a shelf bra or sewn-in cups have no
   *  inside to sit in -- and that used to be resolved silently by buildGroups() dropping
   *  them from her record. Naming them and asking is the whole fix, so select() answers
   *  `false`, the conflict panel renders below the rail, and the two buttons in it are the
   *  only way the choice gets made.
   *  ⚠️ NOTHING IS MUTATED WHILE IT ASKS, which is why going Back restores her preferences
   *  for free. `wire`, `top` and `padding` are left exactly as she answered them; only
   *  `variation` moves. Do not "tidy up" by clearing them here. */
  /** The crop question as a card deck. Two cards, each showing the fabric it means.
   *  ⚠️ IT READS cropOptions() RATHER THAN RESTATING THE QUESTION. See the note there. */
  function cropDeckSpec() {
    var cops = cropOptions();
    return {
      id: 'cropFabric',
      q: cropQ(),
      hint: cropHint(),
      opts: cops.map(function (o) { return [o.v, o.label]; }),
      multi: false,
      // The suit illustrates the fabric; its name and role identify it below.
      // A duplicate swatch chip crowded the label, especially in the desktop grid.
      art: function (i) {
        var outside = slotFace(state.fabric);
        return '<div class="mky__cardsuit mky__cardvar">' +
                 lookSvg(outside, slotFace(bottomFabricCode()) || outside,
                         200 + i, cops[i].art) +
               '</div>';
      },
      isOn: function (i) { return state.cropFace === cops[i].v; },
      help: function (i) {
        return cops[i].sub;
      },
      checkLabel: function () { return ''; },
      selText: function () {
        if (!state.cropFace) return 'No answer selected';
        var n = '';
        cops.forEach(function (o) { if (o.v === state.cropFace) n = o.label; });
        return 'Selected: ' + n;
      },
      select: function (i) { state.cropFace = cops[i].v; }
    };
  }

  function variationDeckSpec() {
    var opts = variations();
    var conflict = reversibleConflicts();
    return {
      id: 'variation',
      q: 'One fabric, or more?',
      hint: variationArtNote(),
      opts: opts,
      multi: false,
      /* ⚠️ NO DECK LEGEND ON THIS QUESTION, AND THAT IS A DELETION, NOT AN OMISSION.
         It carried "a reversible two-piece gives four ways to wear one suit", which the
         REVERSIBLE CARD already says in its own help text -- so the same fact was on
         screen twice, and the second copy wrapped to two lines and ran 25px BEHIND the
         sticky action bar at 390x659. Measured on all five cases; visible in the
         screenshots as the sentence sliced through mid-line. Codex, 2026-09-14: remove
         the redundant explanation, keep the card's.
         ⚠️ WHAT SURVIVES, AND WHERE: the per-card help (`help()` below) keeps the second
         face and the four/two ways AND the conflict warning; the illustrative-fabrics note
         stays by the heading as `hint`; and the POINTER LAYOUT keeps its own branched
         paragraph, which is untouched by this and is the desktop explanation. */
      /* ⚠️ THIS RETURNED '' UNTIL THE ARTWORK EXISTED, and it is the ONLY art hook this
         screen has -- `variationDeckSpec` is its own spec and never consults CARD_ART, so
         registering the composer there alone left the cards blank while every count and
         label looked correct. Measured 2026-09-14: 3 cards, 3 values, zero images. */
      art: function (i) { return variationCardArtHtml(opts[i][0]); },
      /* The price rider is part of the answer, not a note about it: it is the difference
         between two of these three. Same place the option list puts it. */
      title: function (i) {
        return opts[i][1] +
               (PRICE[opts[i][0]] !== PRICE.same ? ' · $' + PRICE[opts[i][0]] : '');
      },
      isOn: function (i) { return state.variation === opts[i][0]; },
      help: function (i) {
        var v = opts[i];
        if (v[0] !== 'reversible') return v[2];
        var note = isSingle()
          ? 'A second face. Two ways to wear it.'
          : 'A second face on both pieces. Four ways to wear it.';
        /* ⚠️ SAID ALWAYS SINCE 2026-09-15, NOT ONLY WHEN SHE HAS ALREADY ASKED FOR ONE
           OF THEM. It used to be conditional on `conflict.length`, which worked while the
           support questions came FIRST -- by the time she reached this screen the flow knew
           whether she wanted an underwire. Codex's order puts those questions after this
           one, so on the ordinary path `conflict` is empty here and a conditional warning
           is silent exactly when it is the only warning she will get before choosing.
           Codex, 2026-09-15: at reversible selection, give the concise explanation of the
           restrictions. */
        note += ' ' + reversibleRestriction();
        if (conflict.length) note += ' You have asked for ' + andList(conflict) + '.';
        return note;
      },
      checkLabel: function () { return ''; },
      selText: function () {
        return state.variation ? 'Selected: ' + label(opts, state.variation)
                               : 'No answer selected';
      },
      select: function (i) {
        var v = opts[i][0];
        /* Ask before committing. Every other variation commits straight away. */
        if (v === 'reversible' && reversibleConflicts().length &&
            state.variation !== 'reversible') {
          pendingReversible = true;
          return false;
        }
        pendingReversible = false;
        setVariation(v);
      },
      extra: function () {
        if (!(pendingReversible && conflict.length)) return '';
        /* ⚠️ A MODAL, BECAUSE IN FLOW IT WAS BELOW THE FOLD AND READ AS A DEAD TAP.
           Nuno, handset, 2026-09-14: "the message is below the fold, so it seems like the
           click to select the reversible answer just isn't working." The panel rendered
           under the card rail on a 659px screen, so the only response to choosing
           Reversible was invisible. A decision she must make cannot be somewhere she has
           to scroll to find.

           ⚠️ AND BOTH BUTTONS COME BACK, WHICH THIS FILE PREDICTED IN WRITING. When "Keep
           an underwire" was removed hours earlier, the note said: "IF THE PANEL EVER
           BECOMES MODAL, THIS BUTTON HAS TO COME BACK, or picking reversible becomes a
           one-way door out of her own underwire answer." A modal covers the variation
           cards, so the escape hatch that justified removing it -- tapping another answer
           -- is gone while it is open. Declining needs its own control again.

           ⚠️ EACH BUTTON SAYS WHAT IT COSTS. Nuno asked for the consequence on both, not
           just the labels: one keeps the reversible suit and gives up the wire, the other
           keeps the wire and gives up reversible. Neither is a default and neither is
           styled as the safe choice. */
        return '<div class="mky__modal" id="mky-revconflict" role="dialog" aria-modal="true" ' +
                 'aria-labelledby="mky-revconflict-t">' +
                 '<div class="mky__modalbox">' +
                   '<h3 class="mky__modalt" id="mky-revconflict-t" tabindex="-1">' +
                   'Reversible cannot carry ' + andList(conflict) + '.</h3>' +
                   /* Equal weight, same reason as the comfort-side panel. */
                   '<button class="button button--secondary mky__modalbtn" type="button" data-rev="go">' +
                     'Remove ' + bareList(conflict) +
                     '<span>Keeps the reversible suit. Your support comes from the cut instead.</span>' +
                   '</button>' +
                   '<button class="button button--secondary mky__modalbtn" type="button" data-rev="keep">' +
                     'Keep ' + bareList(conflict) +
                     '<span>Keeps your ' + bareList(conflict) + '. The suit will not be reversible.</span>' +
                   '</button>' +
                 '</div></div>';
      },
      wire: function (stepEl) {
        Array.prototype.forEach.call(stepEl.querySelectorAll('[data-rev]'), function (b) {
          b.addEventListener('click', function () {
            pendingReversible = false;
            if (b.dataset.rev === 'go') { setVariation('reversible'); goNext(); }
            else { render(); }  /* keep them: variation is untouched, still whatever it was */
          });
        });
      }
    };
  }

  /* ── THE SHAPES SCREEN, ONE COMPONENT AT A TIME ───────────────────────────────────
     ⚠️ IT IS STILL ONE QUESTION. "Your shapes" keeps its heading, its answer fields and
     its branching; what changes on a phone is that its two or three stacked grids become
     one component at a time, each a swipeable rail of the same garment cards. Codex,
     2026-09-14. Desktop keeps the grids: they fit there, and seeing top and bottom
     together is the point of that layout.

     ⚠️ THE SUBSTEPS ARE NOT STEPS. `shapePart` is SCREEN state, like `cardIx` and
     `cardReview`: it never reaches `state`, the URL or the record, and `stepCount()` never
     sees it -- which is what keeps the main question count at what it always was rather
     than inflating it by a component. Back walks the components before it leaves the
     screen; see goBack().

     ⚠️ THE CROP IS NOT A COMPONENT. On a three piece the long-sleeve crop is included and
     fixed, so it is said once as the first component's legend rather than given a substep
     of its own that offers no choice. */
  var shapePart = 0;

  /** The garment components this style actually asks about, in order. */
  function shapeParts() {
    var parts = [{
      attr: 'topShape',
      kind: isSingle() ? 'one' : 'top',
      pairs: topsFor(),
      /* A monokini IS the shape and a one piece has only one, so neither is a "top". */
      label: isSingle() ? 'Shape' : 'Top shape'
    }];
    if (isSplit()) {
      parts.push({ attr: 'bottomShape', kind: 'bottom', pairs: BOTTOMS,
                   label: 'Bottom shape' });
    }
    return parts;
  }

  /** Land on the coverage default so whichever layout draws it agrees with the preview,
   *  which has been drawing that default since question one.
   *  ⚠️ BOTH LAYOUTS CALL THIS, because a shape that is only seeded on one of them is a
   *  shape the other one shows blank. */
  /* ⚠️ seedShapes() IS GONE AND acceptSuggestedShapes() IS NOT ITS RENAME. It ran on
     every render of the shapes screen and WROTE the coverage guess into the state, which
     is exactly how a suggestion came to render as a selection. Nothing writes a shape now
     except an explicit act: a tile press, or Continue. See suggestedShape(). */

  /** What a style change took away, in her words, or '' if nothing did.
   *  ⚠️ IT NAMES THE SHAPE AND SAYS THE CURRENT TILE IS A STARTING POINT, which is the
   *  difference between explaining and pretending. The screen cannot leave the slot empty
   *  -- every garment needs a shape and the preview has been drawing one since the fabric
   *  screen -- so something is selected; what it must not do is present that something as
   *  a decision she made. Codex, 2026-09-15. */
  function shapeDroppedNote() {
    var said = [];
    ['topShape', 'bottomShape'].forEach(function (a) {
      if (shapeDropped[a]) said.push(shapeDropped[a]);
    });
    if (!said.length) return '';
    return andList(said) + (said.length > 1 ? ' are not' : ' is not') +
           ' cut for this kind of suit, so ' + (said.length > 1 ? 'they have' : 'it has') +
           ' been set back to a starting point. Choose again below.';
  }

  /** Which component is on screen, clamped.
   *  ⚠️ CLAMPED BECAUSE THE LIST CAN SHRINK UNDER IT. Going Back to the kind screen from
   *  the bottom-shape component and choosing a one piece leaves `shapePart` at 1 with one
   *  component left. */
  function shapePartIx() {
    var n = shapeParts().length;
    if (shapePart > n - 1) shapePart = n - 1;
    if (shapePart < 0) shapePart = 0;
    return shapePart;
  }

  /** One garment component as a deck. The answer lives in `state[part.attr]`, exactly
   *  where the grid put it, so nothing downstream can tell which layout chose it. */
  function shapeDeckSpec() {
    var parts = shapeParts();
    var ix = shapePartIx();
    var part = parts[ix];
    /* All or nothing for the whole list, computed once. See shapeArtHtml. */
    var art = tilesArt(part.kind, part.pairs);
    return {
      id: 'shape:' + part.attr,
      /* ⚠️ THE PARENT QUESTION IS UNTOUCHED. The component is named below it, not instead
         of it, so she can see that these substeps are one question. */
      q: 'Your shapes',
      sub: part.label + (parts.length > 1 ? ' · ' + (ix + 1) + ' of ' + parts.length : ''),
      hint: (linked[part.attr]
              ? 'The shape you were browsing. '
              : state[part.attr]
                ? 'Your choice. '
                : 'A starting point from your answers, not a verdict. ') +
            'Swipe to compare. Tap the one you want, or carry on to keep the suggestion.',
      /* Said on the first component only, and only on a three piece: it is a fact about
         the garment, not about the component being chosen. */
      /* ⚠️ THE DROPPED-SHAPE NOTE TAKES THE LEGEND WHEN THERE IS ONE, because it is
         about a choice of hers that has just gone and the crop line is a standing fact
         about the garment. Both on one line would bury the one she needs to act on. */
      legend: shapeDroppedNote() ||
        ((ix === 0 && state.style === 'three')
          ? 'The long-sleeve crop is included. It is the third piece.' : ''),
      opts: part.pairs,
      multi: false,
      art: function (i) { return shapeArtHtml(part.kind, part.pairs[i][0], art); },
      /* ⚠️ BY VALUE, WHICH IS WHAT PRESERVES BANDEAU. Its label is "Bandeau" and its
         stored value is `classic`; the pairs list is the one the grid uses, untouched, so
         the record, the preview and the payload all keep reading `classic`. */
      /* ⚠️ ONLY A SHAPE SHE CHOSE IS "ON". The suggestion is marked separately, by
         `suggested` below, and must NOT get the selected state: Codex, 2026-09-15, reading
         the mobile screenshot -- arriving showed a pink card, a checkmark and
         "Shape: Classic" for a shape nobody had picked, while desktop said "An example,
         until you choose below". Two layouts describing the same state differently is the
         defect; a card that claims a selection nobody made is the worse half of it. */
      isOn: function (i) { return !!state[part.attr] && state[part.attr] === part.pairs[i][0]; },
      /* Which card is the starting shape, when she has not chosen one. The deck opens on
         it, names it, and leaves it unticked. */
      suggested: function (i) {
        return !state[part.attr] && suggestedShape(part.attr) === part.pairs[i][0];
      },
      help: function () { return ''; },
      checkLabel: function () { return ''; },
      selText: function () {
        var cur = state[part.attr];
        /* ⚠️ IT SAYS WHICH OF THE TWO THIS IS. "Shape: Classic" over an untouched
           suggestion reads as her decision; "Suggested: Classic" is the same information
           without the claim. The line above Continue is the one place the saved answer is
           stated, so it is the one place the distinction has to hold. */
        if (!cur) {
          return 'Suggested starting shape: ' + label(part.pairs, suggestedShape(part.attr));
        }
        return part.label + ': ' + label(part.pairs, cur);
      },
      select: function (i) {
        /* Commits THIS component and nothing else: the other one keeps whatever it had,
           whether that came from her, from a link, or from the coverage seed. */
        state[part.attr] = part.pairs[i][0];
        /* Her choice from here on; `state[part.attr]` IS the explicit-choice record. */
        shapeDropped[part.attr] = '';
        if (ix + 1 < parts.length) {
          shapePart = ix + 1;
          /* `false` means "stay on this screen": the next component is not a new step, and
             making it one would put a component in the question count. */
          return false;
        }
        /* The last component advances to the next main question, like any other answer. */
      }
    };
  }

  /** The deck for the step at `s`, or null if that screen has none. */
  function deckSpecFor(s) {
    var id = deckIdFor(s);
    if (!id) return null;
    if (id === 'style') return styleDeckSpec();
    if (id === 'variation') return variationDeckSpec();
    if (id === 'cropFabric') return cropDeckSpec();
    if (id.indexOf('shape:') === 0) return shapeDeckSpec();
    if (Q[id]) return questionDeckSpec(id);
    var cq = prefQFor(id);
    if (cq) return fabricDeckSpec(cq);
    /* ⚠️ UNREACHABLE WHILE THIS AND deckIdFor AGREE, AND THAT IS EXACTLY WHY IT IS HERE.
       They are two functions deciding the same thing from the same route: deckIdFor says
       which screens HAVE a deck, this one builds it. The old tail was an unguarded
       `fabricDeckSpec(FABRIC_Q[s - FABRIC_START])`, so the day the two disagreed the flow
       would have thrown inside render() and drawn nothing at all -- a blank screen, from a
       one-line edit to a list. Say so once and fall through to the pointer layout, which
       is the same fallback cardMarkupReady() already uses for a half-landed release. */
    if (window.console) {
      console.warn('Design Studio: no deck spec for screen "' + id + '". deckIdFor and ' +
                   'deckSpecFor disagree about which screens are decks; using the ' +
                   'pointer layout for this one.');
    }
    return null;
  }

  /* ⚠️ AN EXPLICIT SELECTION SAVES THE ANSWER AND MOVES ON. EVERY QUESTION, EVERY
     DEVICE. Codex, 2026-09-14, and it supersedes three earlier rules that are now dead
     and must not be re-derived from the notes they left behind:

       1. The 2026-09-12 removal of auto-advance from all single-choice questions.
       2. The 2026-09-13 tile whitelist (waist, stomach, coverage) and the `!CAN_HOVER`
          clause that made it touch-only, which existed to keep a pointer's click from
          advancing past the Shapes screen a test was about to assert on.
       3. "A deck never auto-advances", which was argued from three properties of the
          accepted card design -- the answer replaceable without Back, the saved answer on
          screen while browsing elsewhere, Continue carrying the saved selection. Those
          three are still TRUE and still built; they are simply no longer a reason to
          stay. Back restores the answer AND the card it was chosen on, Continue is still
          on screen for a restored answer, and browsing still never selects.

     The failure that motivated (1) was real and is answered by the deck rather than by
     waiting: the tap used to select while the picture the answer was FOR lived in a
     separate pane that then scrolled away. On a card the picture is INSIDE the thing she
     taps, so the consequence is under her thumb before she commits to it.

     ⚠️ WHAT STILL DOES NOT ADVANCE, AND WHY IT IS NOT AN EXCEPTION TO THE ABOVE:
       - MULTI-SELECT, anywhere. Nothing can know she has finished choosing.
       - BROWSING. A swipe, a scroll, a hover, a focus move and the pager arrows change
         which card is in view and touch no answer at all. See the swipe guard in
         deckStep(): a flick that ends over a card is a browse, and now that selecting
         also advances, a missed guard costs her the screen rather than a stray tick.
       - THE SHAPES SCREEN, which holds two or three independent choosers at once, so
         there is no "the answer" to advance on.
       - THE REVERSIBLE CONFLICT, which asks before committing. Its select() answers
         `false` and the screen stays put on purpose.
     ⚠️ NO TIMED AUTO-ADVANCE AS A COMPROMISE. A delay long enough to read is long
     enough to feel broken, and it takes the choice of when to move away from her twice. */
  function autoAdvances(key) {
    var def = Q[key];
    return !!def && !def.multi;
  }

  function questionStep(key) {
    var def = Q[key];
    var painter = painterFor(key);
    if (inBody()) preloadBody(key);
    /* ⚠️ DECODE BEFORE THE POINTER ARRIVES, same as the You chapter. Three details, one
       of which is already on screen; without this the first hover blanks the frame for a
       beat on the one interaction this question exists for. */
    if (inTorso()) preloadQ5();
    if (FIG[key] && key !== 'torso' && key !== 'seat') preloadQ6();
    if (key === 'seat') preloadSeat();
    /* ⚠️ THE DECK REPLACES THE LIST, IT DOES NOT SIT BESIDE IT. On a phone, questions 2
       and 6 render as cards and return here -- there are no `.mky__opt`s to wire and no
       stage pane to preview into, because the artwork is on the cards. Everything after
       this line is the desktop layout, unchanged. See CARD_ART. */
    if (cardsFor(key)) { deckStep(questionDeckSpec(key)); return; }
    var cur = state[key];
    var isOn = function (v) { return def.multi ? cur.indexOf(v) > -1 : cur === v; };
    el.step.innerHTML =
      '<h2 class="mky__q">' + def.q + '</h2>' +
      /* ⚠️ ONE QUESTION BEHAVES DIFFERENTLY AND MUST SAY SO. Single-choice advances on
         the tap; multi-choice cannot, because there is no way to know the customer has
         finished choosing. That difference is invisible until it has already surprised
         them, so the instruction is prepended to every multi hint automatically rather
         than written into one question's copy and forgotten on the next. */
      '<p class="mky__hint text-sm text-subdued">' +
      /* ⚠️ THE WORD "Continue" IS THE BUTTON HERE. This is the only multi-select question
         in the flow, and the five single-choice questions before it all advance on the
         tap -- so by the time she reaches it she has been trained not to look for a
         button, and the real Continue is often below the fold on a phone. Telling her to
         press something she cannot see is not an instruction. The word in the sentence
         she is already reading does the job instead.
         ⚠️ IT FORWARDS TO el.next RATHER THAN ADVANCING ITSELF. Continue is gated --
         whyBlocked(), the "choose at least one" message, focus handling -- and a second
         path to the same action is a second place for that gate to be forgotten. */
      (def.multi ? '<strong>Choose as many as apply</strong>, then press ' +
                   '<button type="button" class="mky__inlinego" data-mky-go>Continue</button>. '
                 : '') +
      def.hint + '</p>' +
      (def.pairTiles
        ? coverTiles(def.opts, cur)
        : def.tiles
        ? bodyTiles(def.tiles, def.opts, cur)
        : '<div class="mky__opts" data-multi="' + (def.multi ? 'true' : 'false') + '">' +
          def.opts.map(function (o) {
            return '<button class="mky__opt" type="button" data-v="' + o[0] + '" aria-pressed="' +
                   isOn(o[0]) + '"><b>' + o[1] + '</b>' +
                   (o[2] ? '<span>' + o[2] + '</span>' : '') + '</button>';
          }).join('') + '</div>');
    var go = el.step.querySelector('[data-mky-go]');
    if (go) go.addEventListener('click', function () { el.next.click(); });
    /* Both selectors, because a tiled question renders `.mky__shape` and a written one
       renders `.mky__opt`. Nothing else inside a question step carries `data-v`. */
    Array.prototype.forEach.call(el.step.querySelectorAll('.mky__opt, .mky__shape'), function (b) {
      /* ⚠️ HOVER IS THE POINT OF THE BODY CHAPTER, and it is also why the answers still
         have to work without it. A phone has no hover, so selection alone must redraw
         the body -- which it does, through render() -> paint(). This only adds the
         try-before-you-commit preview a pointer makes possible. */
      if (painter && CAN_HOVER) {
        /* ⚠️ FOCUS PREVIEWS TOO, NOT JUST HOVER. Arrowing through the options with a
           keyboard has to show the same thing the pointer shows, or the whole thing is
           a pointer-only feature. Leaving restores the SAVED answer -- or the neutral
           default when nothing is chosen yet -- so a preview can never be mistaken for
           a selection.

           ⚠️ ONLY THE ENTER HALF IS PER-OPTION. The leave half is on the LIST (below),
           because the options are separated by a 10px gap and `mouseleave` fires every
           time the pointer crosses one. Per-option it read
           h1 -> DEFAULT -> h2 -> DEFAULT -> h4 -> DEFAULT while sweeping down five
           options: the default flashed in every gap. The gap is INSIDE the list, and a
           pointer traveling through it has not stopped previewing. */
        b.addEventListener('mouseenter', function () { painter({ key: key, value: b.dataset.v }); });
        b.addEventListener('focus', function () { painter({ key: key, value: b.dataset.v }); });
      }
      b.addEventListener('click', function () {
        var v = b.dataset.v;
        if (def.multi) {
          var prevArr = state[key].slice();
          var ticking = state[key].indexOf(v) < 0;
          if (isExclusive(v)) state[key] = [v];
          else {
            var arr = state[key].filter(function (x) { return !isExclusive(x); });
            var i = arr.indexOf(v);
            if (i > -1) arr.splice(i, 1); else arr.push(v);
            state[key] = arr;
          }
          /* Same call the deck makes, for the same reason and with the same arguments --
             the two layouts must not differ about what happens when she asks a reversible
             suit for a shelf bra. */
          if (ticking && !isExclusive(v)) openComfortConflict(key, v, prevArr);
          render();                       // multi-select stays put so more can be chosen
        } else {
          var prevV = state[key];
          forfeitAfter(key, v);
          state[key] = v;
          /* A shape the link chose survives this; only the ones it left open are
             guessed from coverage. See `linked` above. */
          /* Coverage writes no shape; the suggestion is derived. See the deck's handler. */
          /* ⚠️ IT USED TO ADVANCE ON THE TAP, AND THAT MADE THE PICTURE UNREADABLE ON A
             PHONE. Every one of these questions redraws the stage from the answer -- the
             body artwork, the fit photographs and their annotations, the coverage pair --
             and a pointer can study that by hovering. A finger cannot: the tap selected
             the answer, the screen advanced, and the one thing the answer was for went
             past before she saw it. Now the tap selects and redraws, and Continue is what
             moves on. Codex, 2026-09-12.
             ⚠️ NO TIMED AUTO-ADVANCE AS A COMPROMISE. A delay long enough to read is long
             enough to feel broken, and it takes the choice of when to move away from her
             a second time. */
          /* ⚠️ THE ANSWER IS SAVED AND THE ADVANCE IS HELD, exactly as the deck's
             single-choice path does it. See openComfortConflict: asking a reversible suit
             for an underwire is a decision she has to make before the flow moves on. */
          if (openComfortConflict(key, v, prevV)) { render(); return; }
          /* See autoAdvances(): this is the branch that note is about, and it still takes
             it whenever the consequence is somewhere she has not been able to look. */
          if (autoAdvances(key)) goNext(); else render();
        }
      });
    });
    /* ⚠️ THE PREVIEW ENDS WHEN SHE LEAVES THE LIST, NOT WHEN SHE LEAVES AN OPTION.
       Scoped here rather than per-option so the gaps between options cannot restore the
       default mid-sweep (see the note on `mouseenter` above).
       `focusout` needs the relatedTarget test because it fires on EVERY move within the
       list too -- arrowing from one option to the next would otherwise flash the default
       exactly the way the pointer did. A null relatedTarget (focus left the document, or
       the focused button was destroyed by render()) correctly counts as leaving;
       paintBody's own inBody() guard handles the destroyed-by-render case. */
    if (painter && CAN_HOVER) {
      var list = el.step.querySelector('.mky__opts, .mky__shapes');
      if (list) {
        list.addEventListener('mouseleave', function () { painter(null); });
        list.addEventListener('focusout', function (e) {
          if (!list.contains(e.relatedTarget)) painter(null);
        });
      }
    }
    finishStep();
  }

  /** Show the looks rather than listing them. Nuno, 2026-09-03, on choosing reversible:
   *  "the results don't show the other options" - reading four names is not the same as
   *  seeing four suits. */
  /* ⚠️ FOUR COMBINATIONS OUT OF TWO FABRICS, AND THE CAPTIONS HAVE TO SAY WHICH FACE.
     `fb2` is now always empty on a reversible suit -- there is no separate bottom fabric
     to cut (see extraPicks()) -- so `outB` is fabric A, the same cloth as the top's
     outside. The four looks come from each PIECE being turned independently, not from a
     third fabric, and a caption that only names cloth cannot tell "both pieces outside"
     from "both pieces turned" when the fabrics happen to coincide. */
  /* ⚠️ ONE SOURCE FOR THE COMBINATIONS, TWO PLACES THAT DRAW THEM. The record's 2x2 grid
     and the phone's swipeable stage rail are the SAME four looks in two layouts; a second
     copy of this list is two places for the captions to drift, and the captions are the
     only thing that can tell "both pieces outside" from "both pieces turned" when the
     fabrics coincide. Returns [] when the suit is not reversible, which is what both
     callers test. */
  /** The browse control under a horizontal rail: two arrows, "Option N of M", a dot band.
   *  ⚠️ ONE COPY, TWO RAILS, AND THE SECOND RAIL IS WHY IT IS EXTRACTED. The card deck has
   *  had this since 2026-09-15; the record screen's four looks asked for "the scrolling
   *  indicators here as well" (Nuno, 2026-09-16) and a second hand-rolled pager is two
   *  places for the dots to stop agreeing with the words.
   *  ⚠️ THE DOTS RIDE INSIDE THE PAGER ROW, NOT UNDER IT, AND THAT IS WHAT MAKES THEM FREE.
   *  A band of their own cost ~12px between the rail and the sticky action bar -- and
   *  because that bar is `position: sticky; bottom: 0` at the end of a min-height-100dvh
   *  column, the overflow does not push the bar down, it slides the pager UNDER it.
   *  Measured at 390x659: the shapes and arrangement screens needed 23px and 53px of
   *  scroll to clear, at rest, on the shortest screen the flow supports. The arrows are
   *  already 44px for the touch target and the position text is ~13px, so the row has ~30px
   *  of unused height in its middle cell and stacking them there adds NOTHING to the
   *  column. Codex, 2026-09-15: "Keep the indicators compact within the pager area."
   *  ⚠️ aria-hidden ON THE DOTS, BECAUSE THE ROW ALREADY SAYS IT IN WORDS. "Option 3 of 6"
   *  is the accessible readout and it is the sibling directly above; announcing six
   *  unlabelled dots after it is duplication, not access. */
  function railPagerHtml(n, label) {
    var dots = '';
    for (var i = 0; i < n; i++) dots += '<span class="mky__carddot" data-dot-ix="' + i + '"></span>';
    return '<nav class="mky__cardpager" aria-label="' + escAttr(label) + '">' +
      '<button type="button" class="mky__cardarrow" data-mky-page="-1" ' +
      'aria-label="Previous option">\u2039</button>' +
      '<span class="mky__cardpagermid">' +
        '<span class="mky__cardpos" data-mky-pos></span>' +
        '<span class="mky__carddots" data-mky-dots aria-hidden="true">' + dots + '</span>' +
      '</span>' +
      '<button type="button" class="mky__cardarrow" data-mky-page="1" ' +
      'aria-label="Next option">\u203a</button>' +
    '</nav>';
  }

  /** Wires that control to a rail. Returns `browseTo(i, smooth)`; the caller owns where the
   *  index LIVES, because the deck keeps one per screen in `cardIx` and survives a render
   *  while the looks rail is rebuilt from scratch each time.
   *  ⚠️ SCROLLS THE RAIL, NEVER THE PAGE. `scrollIntoView` on a child scrolls every
   *  scrollable ancestor, so browsing sideways would drag the question out from under her.
   *  The rail's own scrollLeft is the only thing that may move.
   *  ⚠️ THE SCROLL LISTENER MATCHES NEAREST LEFT EDGE, because the children snap to start --
   *  matching on centres disagrees with the snap on the last one, which can never reach the
   *  middle of the rail.
   *  ⚠️ IT DOES NOT TOUCH ANSWERING, SWIPE SUPPRESSION OR FOCUS BROWSING. Those are the
   *  deck's, and they exist because a deck card is a control; a look is a picture. Moving
   *  them here would put a swipe-versus-click guard on a rail with nothing to click. */
  function wireRailPager(scope, rail, n, read, write) {
    var pos = scope.querySelector('[data-mky-pos]');
    var dots = scope.querySelector('[data-mky-dots]');
    var prevBtn = scope.querySelector('[data-mky-page="-1"]');
    var nextBtn = scope.querySelector('[data-mky-page="1"]');
    function paintPos() {
      var ix = read();
      if (pos) pos.textContent = 'Option ' + (ix + 1) + ' of ' + n;
      if (prevBtn) prevBtn.disabled = ix === 0;
      if (nextBtn) nextBtn.disabled = ix === n - 1;
      /* One class toggle per dot, painted from the same place and at the same moment as the
         words and the arrows, so the three can never disagree about where she is. */
      if (dots) {
        Array.prototype.forEach.call(dots.children, function (d, i) {
          d.classList.toggle('mky__carddot--now', i === ix);
        });
      }
    }
    function browseTo(i, smooth) {
      var k = Math.max(0, Math.min(n - 1, i));
      write(k);
      var kid = rail && rail.children[k];
      if (kid && rail.scrollTo) {
        rail.scrollTo({ left: kid.offsetLeft - rail.children[0].offsetLeft,
                        behavior: smooth ? 'smooth' : 'auto' });
      } else if (kid) {
        rail.scrollLeft = kid.offsetLeft - rail.children[0].offsetLeft;
      }
      paintPos();
    }
    if (rail) {
      rail.addEventListener('scroll', function () {
        var base = rail.getBoundingClientRect().left;
        var pad = parseFloat(getComputedStyle(rail).paddingLeft) || 0;
        var best = 0, bestD = Infinity;
        Array.prototype.forEach.call(rail.children, function (c, i) {
          var d = Math.abs(c.getBoundingClientRect().left - base - pad);
          if (d < bestD) { bestD = d; best = i; }
        });
        if (best !== read()) { write(best); paintPos(); }
      }, { passive: true });
    }
    if (prevBtn) prevBtn.addEventListener('click', function () { browseTo(read() - 1, true); });
    if (nextBtn) nextBtn.addEventListener('click', function () { browseTo(read() + 1, true); });
    paintPos();
    return browseTo;
  }

  function looksCombos(f, fb2, fin) {
    if (state.variation !== 'reversible' || !f || !fin) return [];
    var outT = f, outB = fb2 || f;
    return isSingle()
      ? [[outT, outT, 'Outside'], [fin, fin, 'Turned']]
      : [[outT, outB, 'Both pieces outside \u2014 ' + outT.name.toLowerCase()],
         [outT, fin,  'Top outside, bottom turned'],
         [fin,  outB, 'Top turned, bottom outside'],
         [fin,  fin,  'Both pieces turned \u2014 ' + fin.name.toLowerCase()]];
  }

  /* ⚠️ THE GRID AND THE RAIL ARE MUTUALLY EXCLUSIVE, NEVER BOTH. On a phone the stage
     carries the four looks and this returns nothing; the same four pictures twice on one
     screen is worse than either alone. Above the breakpoint the stage sits BESIDE the
     record rather than above it, the 2x2 reads fine, and the rail is not drawn. */
  function desktopResultLooks() {
    ['mky-desktop-resultlooks', 'mky-desktop-resultlarge'].forEach(function (id) {
      var old = document.getElementById(id); if (old) old.remove();
    });
    el.stage.classList.remove('mky__stage--resultlooks');
    if (!desktopOn() || step !== lastStep()) return;
    var f = slotFace(state.fabric), bottom = slotFace(bottomFabricCode()) || f;
    var inside = slotFace(state.fabricInside), crop = slotFace(cropFabricCode());
    var combos = looksCombos(f, bottom, inside);
    // A non-reversible two-fabric suit has one arrangement, not extra wearable faces.
    if (!combos.length && f && ((bottom && bottom.code !== f.code) ||
        (crop && crop.code !== f.code))) combos = [[f, bottom, 'Your fabric combination']];
    if (!combos.length) return;
    var panel = document.createElement('div'); panel.id = 'mky-desktop-resultlooks';
    panel.innerHTML = '<div class="mky__looks">' + combos.map(function (c, i) {
      return '<button type="button" class="mky__resultlook" aria-label="Preview: ' + escAttr(c[2]) + '">' +
        lookSvg(c[0], c[1], 600 + i) + '<span>' + escAttr(c[2]) + '</span></button>';
    }).join('') + '</div>';
    if (state.style === 'three' && crop) {
      panel.innerHTML += '<p class="mky__hint text-sm text-subdued">Your long-sleeve crop stays in ' +
        escAttr(crop.name) + '. It is not reversible.</p>';
    }
    var save = document.getElementById('mky-save'); save.insertBefore(panel, save.firstChild);
    var large = document.createElement('div'); large.id = 'mky-desktop-resultlarge';
    el.stage.appendChild(large); el.stage.classList.add('mky__stage--resultlooks');
    function show(i) {
      large.innerHTML = lookSvg(combos[i][0], combos[i][1], 900) + '<p class="mky__summary">' + escAttr(combos[i][2]) + '</p>';
      var svg = large.querySelector('svg');
      svg.setAttribute('viewBox', isSingle() ? '100 0 220 220' : state.style === 'three' ? '0 0 220 620' : '0 0 220 420');
      if (!isSingle()) svg.querySelectorAll('g > use').forEach(function (use, n) {
        use.setAttribute('x', '10'); use.setAttribute('y', String(10 + n * 200));
      });
      panel.querySelectorAll('button').forEach(function (button, n) { button.dataset.previewing = String(n === i); });
    }
    panel.querySelectorAll('button').forEach(function (button, i) {
      button.addEventListener('mouseenter', function () { if (CAN_HOVER) show(i); });
      button.addEventListener('focus', function () { show(i); });
      button.addEventListener('click', function () { show(i); });
    });
    show(0);
  }

  function looksPanel(f, fb2, fin) {
    var combos = looksCombos(f, fb2, fin);
    if (!combos.length) return '';
    /* ⚠️ THE CROP IS IN EVERY TILE SINCE 2026-09-17, AND THE SENTENCE STILL EARNS ITS
       PLACE. The superseded note here argued the opposite -- that drawing the crop four
       times would suggest it were part of the variation -- and the cost of that argument
       was that the one screen showing a customer her finished THREE piece showed her a two
       piece, four times over. Codex: "Show the jacket beside the top and bottom so all
       three garments remain visible", and "The jacket stays in its chosen fabric in every
       look." Drawing it fixed while the other two change is what SHOWS the distinction;
       the line below says why, which a picture cannot. */
    var cropNote = '';
    if (state.style === 'three') {
      var cf = slotFace(cropFabricCode());
      if (cf) {
        cropNote = '<p class="mky__hint text-sm text-subdued">Your long-sleeve crop is ' +
                   cf.name.toLowerCase() + ' in all four. It is worn over the suit and ' +
                   'does not turn, so only the top and the bottom change.</p>';
      }
    }
    /* ⚠️ THE PICTURES GO, THE SENTENCE STAYS. On a phone the stage rail draws these four,
       so drawing them again here would be the same four pictures twice on one screen --
       but the crop note is not one of the four, it is the line explaining what the crop
       does INSTEAD of being one, and dropping it with the grid would silently lose it on
       exactly the garment that needs it. Returning it alone is the whole phone branch. */
    if (pilotOn()) return cropNote;
    return '<div class="mky__looks">' + combos.map(function (c, i) {
      return '<figure>' + lookSvg(c[0], c[1], i) + '<figcaption>' + c[2] + '</figcaption></figure>';
    }).join('') + '</div>' + cropNote;
  }

  /** The four looks as the phone's stage: a swipeable rail above the record.
   *  ⚠️ IDs ARE OFFSET FROM THE GRID'S. `lookSvg(top, bot, id)` builds its fabric patterns
   *  as `lt-<id>` / `lb-<id>`, and those ids are DOCUMENT-WIDE. The grid uses 0..3, so the
   *  rail uses 100.. -- otherwise, on any width where both could exist, four patterns would
   *  be defined twice and every tile would take whichever the document happened to hold.
   *  ⚠️ IT RETURNS THE COUNT so the caller can decide whether there is a stage at all; a
   *  non-reversible suit has no looks and must keep the live garment it always had. */
  function paintLooks() {
    if (!el.looksRail) return 0;
    /* ⚠️ THE SAME THREE ACCESSORS THE RECORD USES, NOT `state` DIRECTLY. `bottomFabricCode()`
       and `insideFabricCode()` resolve the fallbacks (a reversible suit carries no separate
       bottom cloth, so `state.fabricBottom` is empty and the bottom is fabric A); reading
       the raw fields here would draw a different four from the grid it replaces. */
    var combos = looksCombos(slotFace(state.fabric),
                             slotFace(bottomFabricCode()),
                             slotFace(insideFabricCode()));
    if (!combos.length) { el.looksRail.innerHTML = ''; return 0; }
    el.looksRail.innerHTML = combos.map(function (c, i) {
      return '<figure>' + lookSvg(c[0], c[1], 100 + i) +
             '<figcaption>' + c[2] + '</figcaption></figure>';
    }).join('');
    /* ⚠️ THE DECK'S PAGER, NOT A SECOND ONE. Nuno, 2026-09-16: "can we add the scrolling
       indicators here as well". The first pass wrote "Swipe -- 4 ways to wear it", which
       says there are more but never says WHICH ONE SHE IS LOOKING AT, and gives a thumb
       nothing to press. Arrows, "Option 2 of 4" and the dot band are one shared control --
       see railPagerHtml() for why a second hand-rolled copy was the thing to avoid.
       ⚠️ REBUILT WITH THE RAIL, AND STARTING AT ZERO IS CORRECT HERE. The deck keeps its
       index in `cardIx` because answering re-renders the screen under her; this rail is
       only ever drawn on arrival at the record, so a remembered position would restore a
       look she had scrolled past in a previous visit. The closure is the index. */
    if (el.looksPos) {
      var ix = 0;
      el.looksPos.innerHTML = railPagerHtml(combos.length, 'Browse the ways to wear it');
      wireRailPager(el.looksPos, el.looksRail, combos.length,
                    function () { return ix; },
                    function (v) { ix = v; });
    }
    return combos.length;
  }

  /** First step of a chapter, so the rail can jump straight to it.
   *  ⚠️ FOUND, NOT HARD-CODED. Six of these were literal offsets and every one of them
   *  had to move whenever a question did. The route knows where its chapters start.
   *  ⚠️ THE FIRST APPLICABLE ONE. A chapter's opening screen can be skipped for this
   *  garment (a monokini's Your look would have landed on coverage), and the rail must
   *  never jump to a screen the flow steps over. */
  function firstStepOf(c) {
    for (var i = 0; i < ROUTE.length; i++) {
      if (ROUTE[i].ch === c && !skipsStep(i)) return i;
    }
    return lastStep();
  }

  /* The rail was decoration. Getting from Yours back to Fit meant pressing Back nine
     times. A chapter is reachable once it has been reached at least once, so nobody can
     skip forward past a question the next screen depends on. */
  var maxStep = 0;

  /* ── THE DECK'S OWN CHROME ────────────────────────────────────────────────────────
     Everything above the question, on a phone deck screen only.

     ⚠️ IT EXISTS BECAUSE THE FLOW'S STANDING CHROME COSTS 360px ON A HANDSET and the
     deck was measured against the wrong thing. The first pass proved the page was
     SHORTER than before and stopped there; on a real iPhone the chapter rail still
     wrapped to two rows, the chapter lede took two more, the progress pill is 2rem
     tall, and Back and Continue were off the bottom of the screen under Safari's
     toolbar. "Shorter than the old page" is not "matches the approved design".

     ⚠️ EVERY PIECE IS REVERSIBLE, AND THAT IS THE POINT. Nothing here is deleted -- the
     rail is moved and moved back, the lede is hidden and unhidden, the dots are hidden
     by a class. Leave a deck screen, or drag the window past 750px, and the flow is
     exactly what it was. */
  function paintPilotChrome(on) {
    var phone = on;
    on = on || desktopOn();
    if (el.pilotHead) el.pilotHead.hidden = !on;
    root.classList.toggle('mky--pilot', phone);
    root.classList.toggle('mky--desktop', desktopOn());
    /* ⚠️ THE CHAPTER LIST IS RELOCATED, NOT REBUILT. renderRail() owns `#mky-rail`'s
       contents and runs after this; moving the node leaves its handlers and its markup
       alone, and `el.rail` still points at it wherever it lives. */
    var home = on ? el.pilotRail : el.railHome;
    if (home && el.rail && el.rail.parentNode !== home) home.appendChild(el.rail);
    /* ⚠️ THE HELP CONTROL IS RELOCATED, NOT DUPLICATED, EXACTLY LIKE THE CHAPTER LIST
       ABOVE IT. One control, two homes: on a deck screen it goes INSIDE the Options menu,
       off the deck it sits at the right of the question heading. Two copies would be two
       things to keep in sync, two ids and two sets of handlers -- and `wireHelp()` binds
       once, at start-up, so the second copy would be the dead one.

       ⚠️ IT MOVED INTO THE MENU ON 2026-09-17, AND IT WAS BESIDE IT BEFORE. Codex
       specified "beside Options"; Nuno, on a handset, found the header cramped, and the
       measurement backs him: with the control in the row the chapter label gets 152px and
       WRAPS TO TWO LINES; without it the label gets 216px and fits on one. My first answer
       said removing it would not help, which was wrong -- that test hid the control only
       AFTER shortening the label, so it proved nothing about the full label. Nuno's call,
       and the deviation from Codex's placement goes in the update to them.

       ⚠️ FIRST IN THE MENU BODY, NOT APPENDED. Someone who cannot continue is opening this
       menu to find a way out; the way out should not be under the chapter list and Start
       again. */
    if (el.helpDock) {
      var hHome = on ? el.pilotMenuBody : el.helpHome;
      if (hHome && el.helpDock.parentNode !== hHome) {
        if (on) hHome.insertBefore(el.helpDock, hHome.firstChild);
        else el.helpHome.insertBefore(el.helpDock, el.helpNext);
      }
    }
    /* ⚠️ THE REFERENCE BLOCK LEAVES THE QUESTION COLUMN ON A DECK SCREEN. Codex,
       2026-09-13. It is the running summary and the "Cut for you" panel: worth having,
       never the next thing to do, and on a 659px Safari viewport they were the difference
       between the pager clearing the action bar and starting behind it.
       ⚠️ PUT BACK WHERE IT CAME FROM, NOT APPENDED. `insertBefore(..., referenceNext)`
       restores its exact position among the stage's children; `appendChild` would leave
       it after the caveat on the way out and the desktop order would drift one step per
       trip. The anchor is captured once, at wiring time, before anything has moved. */
    if (el.reference) {
      var refHome = on ? el.pilotDesignBody : el.referenceHome;
      if (refHome && el.reference.parentNode !== refHome) {
        if (on) refHome.appendChild(el.reference);
        else el.referenceHome.insertBefore(el.reference, el.referenceNext);
      }
    }
    /* ⚠️ COLLAPSED WHEN THE SHELL GOES AWAY, AND SEPARATELY WHEN THE SCREEN CHANGES.
       These two used to be one test, because the shell only existed on a deck screen and
       leaving the deck WAS changing the screen. The shell is now on for the whole phone
       flow, so `!on` fires almost never and a menu opened on one question would still be
       covering the next one. render() collapses them on a step change; this is only the
       exit back to the pointer layout. */
    if (el.pilotMenu && !on) el.pilotMenu.open = false;
    if (el.pilotDesign && !on) el.pilotDesign.open = false;
    if (on) {
      if (el.pilotChapter) {
        el.pilotChapter.textContent = 'Design Studio \u00b7 ' + (CHAPTERS[chapterOf(step)] || '');
      }
      /* HER screens, not raw step indices -- the same count `.mky__qn` prints, which is
         hidden on these screens so the number is said once. */
      if (el.pilotCount) {
        var c = stepCount();
        el.pilotCount.textContent = c.known ? c.done + ' of ' + c.total : String(c.done);
      }
    }
  }

  /** Show "Your design so far" only once there is a design so far.
   *  ⚠️ CALLED AFTER THE STEP HAS RENDERED, NOT BEFORE. paintBuilds() fills the Cut-for-you
   *  slot and paintBody()/paintFig() write the summary line; asking whether the block has
   *  anything in it before those have run answers for the PREVIOUS question. */
  function paintPilotDesign(on) {
    if (!el.pilotDesign) return;
    var saved = document.getElementById('mky-saved-choices');
    var has = !!(on && ((saved && saved.textContent.trim()) ||
                       (el.builtSlot && el.builtSlot.textContent.trim())));
    el.pilotDesign.hidden = !has;
    if (!has) el.pilotDesign.open = false;
  }

  function renderRail() {
    var c = chapterOf(step);
    el.rail.innerHTML = CHAPTERS.map(function (t, i) {
      var open = firstStepOf(i) <= maxStep;
      return '<li' + (i === c ? ' aria-current="step"' : '') +
             (i < c ? ' data-done="true"' : '') + '>' +
             (open && i !== c
               ? '<button type="button" data-chapter="' + i + '">' + t + '</button>'
               : '<span>' + t + '</span>') + '</li>';
    }).join('');
    Array.prototype.forEach.call(el.rail.querySelectorAll('[data-chapter]'), function (b) {
      b.addEventListener('click', function () {
        step = firstStepOf(parseInt(b.dataset.chapter, 10));
        render();
      });
    });
  }

  /* ⚠️ ADVANCING A STEP MUST TAKE THE VIEW WITH IT. On a phone the stage and the
     question stack, so Continue sits far down the page: pressing it swapped in the next
     question above the fold the customer could no longer see, and left them looking at
     the footer. It reads as a dead button.

     Scroll ONLY when the step actually changes. render() also runs on every multi-select
     tick, every shape tile and every swatch, and scrolling there would yank the page
     away mid-choice. And never on the first render, which would drag someone opening a
     shared link straight past the heading. */
  var lastRenderedStep = -1;

  /** How much of the top of the viewport a sticky site header is holding, plus a gap.
   *  ⚠️ MEASURED LIVE RATHER THAN ASSUMED: Prestige's header height differs between
   *  breakpoints and with the announcement bar on or off.
   *  ⚠️ ONE COPY, TWO CALLERS. scrollToFlow() uses it to decide where to stop, and
   *  measureAvailable() uses it to decide how much screen the flow actually has. They have
   *  to agree, or the flow is sized for one position and scrolled to another. */
  function stickyPad() {
    var pad = 12;
    Array.prototype.forEach.call(document.querySelectorAll('header, .shopify-section-header'),
      function (h) {
        var pos = getComputedStyle(h).position;
        if (pos === 'sticky' || pos === 'fixed') pad = Math.max(pad, h.offsetHeight + 12);
      });
    return pad;
  }

  /** Publish how much of the viewport sits ABOVE the question column once the flow has
   *  been scrolled to, so the CSS can give that column the rest of the screen.
   *  ⚠️ THIS IS WHY THE ACTION BAR REACHES THE BOTTOM ON A SHORT QUESTION. Sticky
   *  `bottom: 0` rests at the END of its container, so on a question whose content ran out
   *  at 480px the bar sat at 480px with the rest of the screen blank under it. The column
   *  now has a MINIMUM height of whatever is left of the viewport, the bar is pushed to the
   *  end of it with `margin-top: auto`, and nothing is stretched to get there.
   *  ⚠️ A MINIMUM, NOT A HEIGHT, AND `dvh` NOT `vh`. A taller question simply overflows
   *  it and the page scrolls as it always did; and the dynamic viewport unit is what tracks
   *  Safari's toolbars collapsing, which is the behaviour the old blanket ban on
   *  viewport-based sizing was worried about. Codex lifted that ban on 2026-09-14 as a
   *  blanket rule; the device check on real Safari toolbars stays open.
   *  ⚠️ THE PILOT HEAD COUNTS, SO THIS RUNS AFTER paintPilotChrome(). It is inside the
   *  flow and above the column, and it is hidden on a pointer layout. */
  function measureAvailable() {
    if (!root || !el.body) return;
    var above = stickyPad() +
                (el.body.getBoundingClientRect().top - root.getBoundingClientRect().top);
    root.style.setProperty('--mky-above', Math.max(0, Math.round(above)) + 'px');
  }

  function scrollToFlow() {
    var target = root.getBoundingClientRect().top + window.pageYOffset;
    var pad = stickyPad();
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try {
      window.scrollTo({ top: Math.max(0, target - pad), behavior: reduce ? 'auto' : 'smooth' });
    } catch (e) {
      window.scrollTo(0, Math.max(0, target - pad));   // older Safari: no options object
    }
  }

  /** The style buttons, wired the same way wherever they are drawn.
   *  ⚠️ ADVANCING IS THE CALLER'S CHOICE. On its own screen the tap should move her on,
   *  the way every other single-choice question does. Kept as a parameter rather than
   *  assumed, because this used to live inside the Shapes screen where advancing would
   *  have skipped the shape tiles she had come there to pick. */
  /* ⚠️ HOVERING A KIND DRAWS THAT KIND, and it has to put back everything it touched.
     Changing style is not a one-field change: it re-snaps topShape and bottomShape to
     lists the new style can actually be, and normalizeVariation() can rewrite the
     variation too. A preview that restored only `style` would leave her design quietly
     altered by a pointer that never clicked. All four are saved and restored together. */
  /** Re-snaps both shapes to lists the chosen style can actually draw, keeping a shape
   *  the LINK chose over the coverage table's guess.
   *  ⚠️ THIS IS THE GUARD FROM THE COVERAGE HANDLER, AND IT WAS MISSING HERE. `linked`
   *  exists because a collection page sends her in with `topShape=halter` and the
   *  coverage answer used to overwrite it; that site gates on `!linked.topShape` and
   *  holds. But the KIND step runs BEFORE coverage, and both of its snap sites passed
   *  `COVER_TOP[state.coverage]` as the wanted shape unconditionally -- so a shopper
   *  arriving from /collections/halter-top and clicking "Two piece" had her halter
   *  replaced with `triangle` (coverage is still unanswered there, so the fallback wins)
   *  and then met the Shapes screen with triangle pressed under "The shape you were
   *  browsing", with Halter sitting unselected two tiles away. The coverage guard never
   *  got its turn because the shape was already gone. Reproduced end to end 2026-09-13.
   *  ⚠️ VALIDITY IS STILL CHECKED, so this is not "the link always wins". A one piece
   *  cannot be a halter; when the linked shape is not in the new style's list the
   *  coverage guess is used exactly as before. A choice outranks a guess -- it does not
   *  outrank the garment.
   *  ⚠️ AND IT READS `state.topShape`, NOT A STORED COPY. `linked` records only WHICH
   *  keys the link carried, never their values (see its declaration), so if she has since
   *  picked a shape herself on the Shapes screen this keeps THAT. Holding the value twice
   *  is how the two drift. */
  /** Keep a shape SHE chose when the new style can still draw it; otherwise fall back to
   *  the coverage guess and record what was lost.
   *  ⚠️ IT USED TO KEEP ONLY LINKED SHAPES, WHICH WAS RIGHT UNDER THE OLD ORDER AND IS
   *  NOT UNDER THIS ONE. A shape she pressed on the shapes screen is every bit as much her
   *  choice as one a collection link carried; the only reason the old version could ignore
   *  it is that the kind screen came before the shapes screen and there was nothing to
   *  ignore. Going Back to change the kind is now an ordinary move.
   *  ⚠️ AND WHEN IT REALLY CANNOT BE KEPT, THE REPLACEMENT IS NOT PRESENTED AS HERS.
   *  Codex, 2026-09-15: explain choices that are no longer available; do not pretend a
   *  replacement was explicitly selected. So the explicit flag is dropped with the shape
   *  and the screen says which one went. */
  function keepExplicitShape(attr, pairs, current) {
    if (!current) return '';                 // nothing of hers; the suggestion draws it
    for (var i = 0; i < pairs.length; i++) if (pairs[i][0] === current) return current;
    /* Her choice, and this garment cannot take it. */
    shapeDropped[attr] = label(pairs, current) === current ? '' : label(pairs, current);
    if (!shapeDropped[attr]) shapeDropped[attr] = current;
    linked[attr] = false;
    /* ⚠️ BACK TO UNCHOSEN, NOT TO A SUBSTITUTE. Codex's brief: explain choices that are
       no longer available; do not pretend a replacement was explicitly selected. Returning
       the guess here is exactly that pretence -- the screen now says what went and offers
       the starting shape as a suggestion she can accept or replace. */
    return '';
  }
  function snapShapesForStyle() {
    /* ⚠️ AN UNCHOSEN SLOT STAYS EMPTY. This used to assign the coverage guess into
       both fields whatever their state, which is one of the two places a suggestion became
       indistinguishable from a decision. `keepExplicitShape` now returns '' when there was
       nothing of hers to keep, and suggestedShape() supplies the drawing. */
    state.topShape = keepExplicitShape('topShape', topsFor(), state.topShape);
    state.bottomShape = keepExplicitShape('bottomShape', BOTTOMS, state.bottomShape);
  }

  var stylePreview = null;
  function showStyle(v) {
    if (!stylePreview) {
      stylePreview = { style: state.style, topShape: state.topShape,
                       bottomShape: state.bottomShape, variation: state.variation };
    }
    state.style = v;
    snapShapesForStyle();
    normalizeVariation();
    paint();
  }
  function restoreStyle() {
    if (!stylePreview) return;
    state.style = stylePreview.style;
    state.topShape = stylePreview.topShape;
    state.bottomShape = stylePreview.bottomShape;
    state.variation = stylePreview.variation;
    stylePreview = null;
    paint();
  }

  function wireStyleButtons(advance) {
    /* Cleared before wiring, so a preview cannot survive the screen redrawing under it. */
    stylePreview = null;
    if (advance && CAN_HOVER) {
      /* ⚠️ ONLY ON ITS OWN SCREEN. Inside the Shapes screen the stage is showing the
         garment she is editing, and repainting it under a passing pointer is noise.
         ⚠️ LEAVE IS ON THE LIST, NOT THE OPTION -- the same gap that made the You
         chapter flash its default every time the pointer crossed between two options. */
      Array.prototype.forEach.call(el.step.querySelectorAll('[data-style]'), function (b) {
        b.addEventListener('mouseenter', function () { showStyle(b.dataset.style); });
        b.addEventListener('focus', function () { showStyle(b.dataset.style); });
      });
      var list = el.step.querySelector('.mky__opts');
      if (list) {
        list.addEventListener('mouseleave', restoreStyle);
        list.addEventListener('focusout', function (e) {
          if (!list.contains(e.relatedTarget)) restoreStyle();
        });
      }
    }
    Array.prototype.forEach.call(el.step.querySelectorAll('[data-style]'), function (b) {
      b.addEventListener('click', function () {
        /* ⚠️ RESTORE, DO NOT DISCARD. This was `stylePreview = null`, which threw away
           the snapshot showStyle() took -- and showStyle() does not merely draw, it WRITES
           state.style and runs snapShapesForStyle(). So if a preview was live when the
           click landed, `state.topShape` had already been snapped for the PREVIEWED kind,
           and nulling the snapshot left that corruption in place for the snap below to
           build on. A link carrying `topShape=halter` arrived at this screen, the pointer
           rested on "One piece" (which cannot be a halter), and the halter was gone before
           she chose anything. Restoring first puts the real value back and then this
           handler applies her actual choice to it. Measured by kind2.py, 2026-09-14.
           ⚠️ IT BECAME REACHABLE WHEN AN ANSWER STARTED ADVANCING, because the pointer
           and the focus now land on the next screen's first option without her moving --
           but the defect is older than that and is not a mobile one. */
        restoreStyle();
        forfeitAfter('style', b.dataset.style);
        state.style = b.dataset.style;
        /* ⚠️ THE STYLES DO NOT SHARE A SHAPE LIST. A one piece is only cut classic or
           asymmetrical, so carrying "triangle" across left no tile selected while the
           preview quietly drew something else, and the summary printed the raw id. Snap
           to a shape the chosen style can actually be -- and keep a shape the LINK chose
           when the new style can draw it. See snapShapesForStyle. */
        snapShapesForStyle();
        /* A one piece cannot be "different top and bottom", and a one piece reversible
           needs no bottom fabric. Fix the state here rather than downstream. */
        normalizeVariation();
        /* ⚠️ `advance` MEANS "WIRE THE HOVER PREVIEW", AND IT HAS MEANT THAT SINCE
           2026-09-12. It is NOT the flag that decides the line below -- that decision is
           now the same one every other single-choice question makes (see autoAdvances):
           an explicit pick saves the answer and moves on, on every device. The hover
           preview is why a pointer can afford it here: by the time the click lands she
           has already watched the stage change to the kind she is choosing. */
        goNext();
      });
    });
  }

  /** Tag the page title + intro section so the stylesheet can hide it on a phone.
   *  ⚠️ THE SECTION IS FOUND ONCE AND TAGGED, not queried on every render: it is the
   *  previous sibling section, and walking the DOM on every step to find something that
   *  cannot move is work for nothing. Absent (a different template, a theme update that
   *  renames things) the whole thing is a no-op.
   *
   *  ⚠️ TAGGING IS ALL THIS DOES NOW, AND THE TIMING IS THE REASON. It used to also toggle
   *  `mky-headerless` on `step > 0`, and the stylesheet hid the intro only under that class
   *  -- which IS the "not until question two" behaviour Codex superseded on 2026-09-14. The
   *  breakpoint now hides `.mky-pagehead` on its own, so the intro is gone before any of
   *  this runs: correct on initial load, on a restored link and on Back to question one,
   *  none of which a step-gated class could ever have been.
   *
   *  ⚠️ `mky-headerless` IS DELETED, NOT LEFT TOGGLING HARMLESSLY. It styled exactly one
   *  rule and that rule is gone, so a class that still appeared and disappeared on the
   *  documentElement would read like live state to the next person and invite them to hang
   *  something off it. Desktop is untouched either way: the old rule lived inside the
   *  859px media query and never applied there. */
  (function tagPageHead() {
    var sec = root.closest('.shopify-section');
    var prev = sec && sec.previousElementSibling;
    if (prev && prev.querySelector('.section-header, .prose')) prev.classList.add('mky-pagehead');
  })();

  /* ── KEEPING HER PLACE WHEN SHE CHOOSES ──────────────────────────────────

     ⚠️ WHAT WAS BROKEN. Every swatch rail on a phone is a horizontally scrolling flex
     row. Choosing a fabric re-rendered the step, `#mky-step.innerHTML` was replaced, and
     the new rail arrived at `scrollLeft = 0` -- so scrolling to the eleventh fabric and
     tapping it threw her back to the first, on every fabric slot and in every search
     result. Codex, 2026-09-16: "Selecting a fabric should leave the customer looking at
     the tile they selected, so they can continue comparing nearby choices."

     ⚠️ IT RESTORES RATHER THAN AVOIDS THE REBUILD, AND THAT IS THE DELIBERATE CHOICE.
     The brief allows either -- "If rebuilding is unavoidable, restore each rail using
     stable item identity and its prior position" -- and a partial-update path would have
     had to re-implement the dozen things render() does for a selection: the pressed
     state, the pinned row appearing or disappearing, the suit preview, the confirmation
     label, the Continue gate, the address bar. That is a near-copy of render() kept in
     step with render() by hand, which is the class of duplication this file already
     refuses elsewhere. One render, and the place put back inside the same frame, is the
     smaller thing to get right and the easier thing to test.

     ⚠️ BY IDENTITY, NOT BY OFFSET. A rail's contents can legitimately change under a
     selection -- the pinned "Your fabric" row disappears when the choice is back among
     the suggestions -- so the anchor is the CODE of the tile at the left edge and the
     gap it sat at, never a raw pixel count. The raw count is the fallback for a rail
     whose anchor is genuinely gone.

     ⚠️ AND THE PREVIEW IS HELD, NOT REFITTED. fitFabricPreview() shrinks to keep the
     first row of swatches clear of the action bar, and it is allowed to shrink WITHIN a
     step. Re-running it on a selection is what made the page jump under her finger: a
     longer fabric name pushes the first row down, the preview gives back the difference,
     and everything below it moves. The fit that was computed on arrival is the right one
     for the whole visit. See `keepingPlace` in fitFabricPreview(). */

  /** What a tile is, for the purpose of finding it again after a rebuild. */
  function tileKey(node) {
    if (!node || !node.getAttribute) return '';
    return node.getAttribute('data-code') || node.getAttribute('data-solidpick') ||
           node.getAttribute('data-solid') || '';
  }

  /** Where every rail on the step is scrolled to, plus the page scroll and the focus. */
  function placeSnapshot() {
    var snap = { rails: [], scrollY: window.pageYOffset || 0, focus: null };
    if (!el.step) return snap;
    Array.prototype.forEach.call(el.step.querySelectorAll('[data-rail]'), function (r) {
      var left = r.scrollLeft, anchor = '', off = 0;
      Array.prototype.forEach.call(r.children, function (c) {
        if (anchor) return;
        /* The first tile whose right edge is still on screen: the one she is looking at,
           not the one that has just scrolled out of view. */
        if (c.offsetLeft + c.offsetWidth > left + 1) {
          anchor = tileKey(c);
          off = c.offsetLeft - left;
        }
      });
      snap.rails.push({ key: r.getAttribute('data-rail'), left: left,
                        anchor: anchor, off: off });
    });
    var a = document.activeElement;
    if (a && a !== document.body && el.step.contains(a)) {
      var rail = a.closest ? a.closest('[data-rail]') : null;
      snap.focus = { id: a.id || '', key: tileKey(a),
                     rail: rail ? rail.getAttribute('data-rail') : '' };
    }
    return snap;
  }

  function placeRestore(snap) {
    if (!snap || !el.step) return;
    var by = {};
    snap.rails.forEach(function (x) { if (x.key && !(x.key in by)) by[x.key] = x; });
    Array.prototype.forEach.call(el.step.querySelectorAll('[data-rail]'), function (r) {
      var x = by[r.getAttribute('data-rail')];
      if (!x) return;
      var want = x.left;
      if (x.anchor) {
        var found = null;
        Array.prototype.forEach.call(r.children, function (c) {
          if (!found && tileKey(c) === x.anchor) found = c;
        });
        if (found) want = found.offsetLeft - x.off;
      }
      var max = Math.max(0, r.scrollWidth - r.clientWidth);
      r.scrollLeft = Math.max(0, Math.min(want, max));
    });
    if (snap.focus) {
      var want = null;
      if (snap.focus.id) want = document.getElementById(snap.focus.id);
      if (!want && snap.focus.key) {
        var scope = snap.focus.rail
          ? el.step.querySelector('[data-rail="' + snap.focus.rail + '"]') : el.step;
        if (scope) {
          Array.prototype.forEach.call(scope.querySelectorAll('.mky__swatch'), function (c) {
            if (!want && tileKey(c) === snap.focus.key) want = c;
          });
        }
      }
      /* ⚠️ `preventScroll`, OR PUTTING FOCUS BACK UNDOES PUTTING THE RAIL BACK. Focusing
         a control scrolls its nearest scrollable ancestors to reveal it, which on a rail
         is exactly the movement this function exists to prevent. */
      if (want && want.focus) {
        try { want.focus({ preventScroll: true }); } catch (e) { /* older browsers */ }
      }
    }
    if (typeof snap.scrollY === 'number' &&
        Math.abs((window.pageYOffset || 0) - snap.scrollY) > 1) {
      window.scrollTo(0, snap.scrollY);
    }
  }

  /* True for the duration of a render that must not move anything the customer is
     looking at. Read by fitFabricPreview(). */
  var keepingPlace = false;

  /** Re-render without moving the rail, the page or the keyboard focus. */
  function renderKeepingPlace() {
    var snap = placeSnapshot();
    keepingPlace = true;
    try { render(); } finally { keepingPlace = false; }
    placeRestore(snap);
  }

  function render() {
    if (step > maxStep) maxStep = step;
    /* ⚠️ FIRST, BEFORE ANY MARKUP IS WRITTEN. The fabric screens borrow `#preview-stage`
       and `#mky-suitpane` into the question column on a phone (see placeFabricPreview),
       and every step renderer assigns `el.step.innerHTML` -- so a pane still sitting there
       when the new screen paints would be destroyed, `el.svg` would point at a detached
       node, and the stage would simply stop drawing for the rest of the session with no
       error anywhere. Sending them home unconditionally also means the enlarged view, a
       resize mid-flow and anything else that moved them all start from one known state. */
    stagePanesHome();
    if (el.stage) el.stage.classList.remove('mky__stage--example');
    if (el.stage) el.stage.classList.remove('mky__stage--resultlooks');
    var oldResultLooks = document.getElementById('mky-desktop-resultlooks');
    if (oldResultLooks) oldResultLooks.remove();
    var oldLarge = document.getElementById('mky-desktop-resultlarge');
    if (oldLarge) oldLarge.remove();
    var stepChanged = lastRenderedStep !== -1 && step !== lastRenderedStep;
    lastRenderedStep = step;
    renderRail();
    /* The stage is up from question one. It used to appear only after the four fit
       questions, which meant four screens of nothing but prose before anything on the
       page looked like swimwear. paint() already degrades honestly with no fabric and no
       chosen shapes (a gray ghost on the defaults) and stillOpen() counts the space
       that is still open rather than claiming a suit has been decided. */
    el.stage.hidden = false;
    el.body.classList.toggle('mky__body--split', step < lastStep());
    /* ⚠️ THE DECK NEEDS A FULL-WIDTH STEP, AND THE PHONE COLUMN DOES NOT GIVE IT ONE.
       `.mky__body` sets `align-items: start`, so every child SHRINK-WRAPS to its own
       content instead of stretching -- the same trap `.mky__suitpane`'s `width: 100%` note
       already records. Measured 2026-09-13: question 2's step came out 335px and question
       6's 390px inside the same 390px column, purely from what each deck's content
       happened to resolve to. Toggled here, beside --split, so one place decides both and
       no step renderer has to remember to add it. */
    var onDeck = deckOn(step);
    el.step.classList.toggle('mky__step--cards', onDeck);
    /* The phone column's 2.5rem gap is generous, and on a deck screen it is the last
       thing between the selection line and Continue. Tightened here and only here --
       every other question keeps the spacing it was approved with. */
    el.body.classList.toggle('mky__body--cards', onDeck);
    /* ⚠️ THE SHELL FOLLOWS THE VIEWPORT, THE CARD CLASSES FOLLOW THE DECK. Two
       different questions since 2026-09-14: every phone screen wears the compact shell,
       and only a deck screen needs a full-width step and the tightened column gap. */
    paintPilotChrome(pilotOn());
    /* After the chrome, because the compact header is inside the flow and above the
       column, so whether it is showing changes the answer. */
    measureAvailable();
    /* Cleared BEFORE the step renders, so a screen that is not a deck can never inherit
       the previous question's summary line in a bar that is always on screen. deckStep()
       fills it again on the way back in. */
    if (!onDeck) paintActionSel(null);
    /* Fit questions and the shapes step, exactly as before. Driven from here rather than
       from the individual step renderers, so steps that never mention it still clear it. */
    /* Runs to the end of the flow now. It used to stop after Shapes, so the panel
       disappeared at Sizes with no explanation. The final record carries its own
       copy, so the slot clears there rather than printing it twice. */
    paintBuilds(step < lastStep());
    paintSave(step === lastStep());
    /* ⚠️ THE CONFLICT QUESTION DIES WITH THE SCREEN. Press Reversible, get asked, press
       Back: without this the flag survives and the box is sitting there unprompted when
       she returns, asking about a choice she walked away from. */
    /* ⚠️ THE DECK'S SCREEN STATE DIES WITH THE SCREEN, for the same reason
       pendingReversible does: an open review panel or a browsed card index belongs to the
       question that was on screen, and carrying either into the next one is a panel
       nobody opened sitting over a question nobody asked. */
    if (stepChanged) {
      pendingReversible = false;
      /* Same rule, same reason: a question she walked away from must not be waiting for
         her when she comes back. The answer she had chosen stays; only the asking dies. */
      pendingComfortConflict = null;
      resetCards();
      scrollToFlow();
      /* Read and cleared by finishStep(), once the new step's markup exists to focus. */
      stepJustChanged = true;
      /* ⚠️ BLUR BEFORE THE OLD STEP IS REPLACED, AND MOVING FOCUS AFTERWARDS IS NOT
         ENOUGH. When the focused control is removed by an innerHTML assignment, the
         browser picks the next focusable thing in the new content and fires a real
         `focus` on it -- synchronously, during the render, before anything downstream can
         put focus somewhere sensible. On the kind screen that thing is an option whose
         focus handler PREVIEWS, so arriving previewed "One piece" and snapped a linked
         halter to classic. Blurring first sends focus to the document, which has no
         listeners, and finishStep() then puts it on the question heading. */
      if (el.step && document.activeElement && el.step.contains(document.activeElement)) {
        try { document.activeElement.blur(); } catch (e) { /* nothing to blur */ }
      }
      /* A panel left open across Continue is a panel covering a question nobody asked
         for. See the note in paintPilotChrome. */
      if (el.pilotMenu) el.pilotMenu.open = false;
      if (el.pilotDesign) el.pilotDesign.open = false;
    }
    el.back.disabled = step === 0;
    el.next.textContent = 'Continue';
    /* ⚠️ THE FABRIC SCREENS CONFIRM, THEY DO NOT MERELY CONTINUE. Codex, 2026-09-15:
       "Use this fabric" for a specific swatch, "Use this colour direction" for an
       unconfirmed solid request. Every other screen advances on the answer itself; these
       are catalogues with a deliberate completion action, and the button is it. */
    var confirmSlot = { fabric: 'fabric', fabricBottom: 'fabricBottom',
                        fabricInside: 'fabricInside', fabricCrop: 'fabricCrop' }[screenId()];
    if (confirmSlot) {
      /* ⚠️ ONE LABEL FOR BOTH KINDS OF ASSIGNMENT, AND IT REVERSES AN EARLIER RULE ON
         PURPOSE. It read "Use this color" for a pending colour and "Use this fabric" for
         a swatch, so the confirmation button was the last place the flow still treated
         the two as different kinds of act. Codex, 2026-09-16: "Use 'Use this fabric'
         consistently for photographed fabrics and solid colors. Keep the pending-color
         note visible when relevant."
         ⚠️ THE DISTINCTION DID NOT GO ANYWHERE, IT MOVED TO WHERE IT IS READ. A pending
         colour says so on its tile ("Blue · Solid"), in the preview caption, on the
         review as "STILL TO CONFIRM", and in capitals on the maker line. What it no
         longer does is make her press a differently-worded button to choose one. */
      el.next.textContent = 'Use this fabric';
    }
    /* ⚠️ THE RECORD SCREEN HAS NO CONTINUE, AND NOTHING ELSE MAY TAKE ITS SLOT. There is
       nowhere to continue to, and whatever sits bottom-right on that screen inherits
       nineteen screens' worth of muscle memory pointing at it. The only thing that has
       earned it is the action she came for. */
    el.next.hidden = step === lastStep();
    /* Hidden only on the FIRST screen, where there is nothing yet to throw away. It is
       now the ONLY way to start again, on the record screen as everywhere else. */
    if (el.reset) el.reset.hidden = step === 0;
    /* ⚠️ CONTINUE IS GATED ON AN ANSWER. It was not, and a customer could click
       straight through every question to a "design" assembled entirely from
       defaults: a suit she never chose, with `builds` empty and the fit answers
       blank in what reaches Evelin and ILLIXIS -- indistinguishable from someone
       who genuinely has no fit problems.

       There is no legitimate reason to skip, because every question already
       carries its own way out: "No, they sit fine", "Nothing. Tops fit me fine",
       "No strong feeling", "No, they stay put". The escape hatch belongs inside
       the question, where it is recorded, not in the Continue button, where it
       is silent. */
    /* The message is per-question: clear it whenever the screen changes, or a stale
       "Pick one to continue" sits under a question she has already answered. */
    needs('');
    /* ⚠️ CLEARED ON EVERY RENDER. It is a closure over one screen's pool and one screen's
       slot; left behind, a keystroke on a later screen would repaint results for the
       previous one into a node that is no longer there. Each picker sets its own. */
    paintSearchResults = function () {};
    paint();
    syncUrl();

    var sid = screenId();

    /* ⚠️ WARM THE GARMENT ARTWORK HERE, ONCE, FOR BOTH LAYOUTS. It used to be two calls
       to `preloadSuit()` -- one in the deck branch, one in the pointer branch -- both on
       the kind screen, both warming all twenty renders. Now each screen warms its own
       group (see suitGroup), which means coverage, shapes and variation need warming too,
       and duplicating four calls across two layouts is four chances for the deck and the
       grid to disagree about what is decoded. One dispatch, above the split.
       ⚠️ SUBSEQUENT GROUPS LOAD WHEN THEIR SCREEN IS REACHED, deliberately -- not one
       screen ahead. Guessing forward is how the all-twenty burst happened in the first
       place: the shapes she can reach depend on a `style` she has not picked yet on the
       kind screen, so "prefetch the next screen" means prefetching every branch of it. */
    var SUIT_SCREEN = { style: 'kind', coverage: 'coverage', shapes: 'shapes',
                        variation: 'variation' };
    if (SUIT_SCREEN[sid]) preloadSuit(SUIT_SCREEN[sid]);

    /* ⚠️ EVERY PLAIN QUESTION GOES THROUGH questionStep, AND IT HAS TO HAPPEN BEFORE THE
       DECK BRANCH BELOW. questionStep owns the photograph preloads for the questions that
       have one -- the body sprite, question 5's detail, question 6's exterior, the seat's
       back view -- and only THEN hands over to the deck. Taking the deck branch first
       would skip all four, which is a card rail scrolling onto undecoded images.
       Coverage is excluded because the pointer layout rewrites its hint after the fact on
       a three piece; it is handled in the chain below, and its deck spec says the same
       thing for itself. */
    if (desktopOn() && deckIdFor(step)) {
      desktopChoices(deckSpecFor(step)); return;
    }
    if (Q[sid] && sid !== 'coverage') { questionStep(sid); return; }

    /* ⚠️ THE DECK REPLACES THE SCREEN, IT DOES NOT SIT BESIDE IT -- the same rule
       questionStep() follows for the question list. The kind screen, the coverage screen
       and the four cloth questions each render as cards on a phone and return here; every
       branch below this line is the pointer layout, unchanged.
       ⚠️ THE PRELOAD STILL RUNS. It belongs to the question, not to the layout: the kind
       cards draw the same four photographs the stage does and a card scrolled into view
       with an undecoded image is the same blank frame a hover used to produce. */
    if (onDeck) {
      /* ⚠️ NOTHING IS SEEDED ON RENDER ANY MORE. `seedShapes()` used to write the
         coverage guess here so the deck had something to tick; the deck asks
         `suggestedShape()` instead and marks it as a suggestion. See shapeDeckSpec. */
      /* A null spec means deckIdFor and deckSpecFor disagree; the warning is theirs. Fall
         through to the pointer layout rather than throwing, so the screen still works. */
      var spec = deckSpecFor(step);
      if (spec) { deckStep(spec); return; }
    }

    if (sid === 'style') {
      /* ⚠️ THE FIRST THING ASKED ABOUT THE GARMENT, AND IT USED TO BE BURIED. This block
         sat at the top of the Shapes screen, two steps later, so coverage asked "how much
         do you want covered?" over a drawing of a bikini pair before she had said she
         wanted two pieces. On its own screen, and first.
         ⚠️ IT ARRIVES UNANSWERED, AND THIS COMMENT USED TO SAY THE OPPOSITE. `state.style`
         defaulted to 'two', which ticked the second option the moment the screen opened;
         Nuno removed that on 2026-09-12 and the state object is the authority (see the
         ⚠️ on `style: ''`). Continue is dead until she chooses, and the stage draws an
         example that says so rather than a suit that looks picked. */
      el.step.innerHTML =
        '<h2 class="mky__q">What kind of suit?</h2>' +
        '<p class="mky__hint text-sm text-subdued">' +
        'It decides what the next screens can offer you. Change it whenever you like; ' +
        'the preview follows.</p>' +
        '<div class="mky__opts" data-multi="false">' +
        STYLES.map(function (o) {
          return '<button class="mky__opt" type="button" data-style="' + o[0] + '" aria-pressed="' +
                 (state.style === o[0]) + '"><b>' + o[1] + '</b><span>' + o[2] + '</span></button>';
        }).join('') + '</div>';
      /* Decoded before the pointer arrives by the dispatch above -- hover IS the
         interaction on this screen, and a frame that blanks for a beat on first hover is
         the feature failing on its first use. The call moved out of this branch when the
         preload became per-screen; the rule it encodes has not changed. */
      wireStyleButtons(true);

    } else if (sid === 'coverage') {
      questionStep('coverage');
      /* ⚠️ ON A THREE PIECE THIS QUESTION IS ONLY ABOUT WHAT GOES UNDER THE CROP. The
         crop is included and the top is fixed at triangle -- every 3-Piece product in
         the store is tagged Triangle -- so without saying so the screen reads as if all
         three pieces were up for decision. Rewritten after the fact rather than branched
         inside Q.coverage, because the question, the answers and everything they seed
         are identical; only the framing differs. */
      if (state.style === 'three') {
        var hint3 = el.step.querySelector('.mky__hint');
        if (hint3) {
          hint3.textContent = 'The long-sleeve crop is included. This is about the top ' +
            'and bottom that go under it.';
        }
      }

    } else if (sid === 'shapes') {
      /* ⚠️ SHAPES ARE PICKED BY LOOKING, NOT BY READING A MENU. These were three native
         <select>s, which in Prestige render as bordered fields: they read as things to
         type into, they hide every option until clicked, and they named garments in
         words on the one screen where we already own an accurate drawing of each one.
         The eleven traced silhouettes exist; use them. */
      var tops2 = topsFor();
      el.step.innerHTML =
        '<h2 class="mky__q">Your shapes</h2>' +
        /* The hint says where the selection CAME FROM. Arriving from a collection, the
           shape is the one she was browsing, and calling it "from your answers" would
           be untrue on the one screen that should confirm the link worked. */
        '<p class="mky__hint text-sm text-subdued">' +
        (linked.topShape || linked.bottomShape
          ? 'The shape you were browsing, with the rest from your answers. Not a verdict: '
          : 'A starting point from your answers, not a verdict. ') +
        'Change anything and the preview follows.</p>' +
        /* Same sentence the deck puts in its legend, from the same function. See
           shapeDroppedNote(). */
        (shapeDroppedNote()
          ? '<p class="mky__beyond">' + shapeDroppedNote() + '</p>'
          : '') +
        /* ⚠️ NEVER RENDER AN EMPTY SHAPE SECTION. Monokini used to suppress this block
           entirely, which left a heading-less gap where the tiles belong and no
           confirmation of what she had just picked. One tile, selected, is a worse
           chooser and a far better answer. */
        '<h3 class="mky__sub">' +
          (state.style === 'monokini' ? 'Shape' : isSingle() ? 'Shape' : 'Top shape') +
        '</h3>' +
        shapeTiles(isSingle() ? 'one' : 'top', tops2, state.topShape, 'topShape') +
        /* THE THIRD PIECE, shown because it is the whole point of the style. Without it
           a three piece looked identical to a two piece with one option missing, which
           reads as a bug rather than as a garment. */
        (state.style === 'three'
          ? '<h3 class="mky__sub">Worn over it</h3>' +
            /* Rendered with the SAME tile markup as a real choice so it sits in the
               grid identically, but carries no `data-attr`, which is what the click
               handler dispatches on -- so it cannot be selected and cannot deselect
               the top. `disabled` would gray it out, and it is included, not
               unavailable. */
            '<div class="mky__shapes mky__shapes--fixed">' +
            '<span class="mky__shape" aria-hidden="false">' +
            '<svg viewBox="0 0 200 200" aria-hidden="true" focusable="false">' +
            '<use href="#shape-top-tankini" x="0" y="0" width="200" height="200"/>' +
            '</svg><b>Long-sleeve crop</b>' +
            '<em>Included. It is the third piece.</em></span></div>'
          : '') +
        (isSplit()
          ? '<h3 class="mky__sub">Bottom shape</h3>' +
            shapeTiles('bottom', BOTTOMS, state.bottomShape, 'bottomShape')
          : '');
      Array.prototype.forEach.call(el.step.querySelectorAll('.mky__shape'), function (b) {
        b.addEventListener('click', function () {
          state[b.dataset.attr] = b.dataset.v;
          /* Her choice from here on: a style change keeps it wherever the new style can
             still draw it, and the coverage table cannot touch it because the suggestion is
             derived rather than stored. See suggestedShape(). */
          shapeDropped[b.dataset.attr] = '';
          /* ⚠️ NOTHING ADVANCES HERE ANY MORE, AND THE OLD RULE'S OWN REASON IS WHY. It
             read: "a one piece has a single shape list, so tapping it is the whole answer
             and staying put reads as a tap that did not register -- EVERY SINGLE-CHOICE
             QUESTION BEFORE THIS ONE MOVES ON." That last clause was the whole argument,
             and it stopped being true on 2026-09-12: no single-choice question moves on
             now, so staying put is the consistent behavior rather than the odd one.

             It also mattered most here. This screen carries six photographic tiles and a
             composed garment that redraws from the tap, so it was the screen with the most
             to look at and the only one that still moved before she could. Nuno,
             2026-09-12. A two or three piece never advanced, because it still has a bottom
             to choose. */
          render();
        });
      });

    } else if (sid === 'sizes') {
      /* The last two <select>s. Five short values are a row of chips, not a menu: every
         option is visible, and it matches how every other choice in the flow is made. */
      el.step.innerHTML =
        '<h2 class="mky__q">Your sizes</h2>' +
        '<p class="mky__hint text-sm text-subdued">' + root.dataset.sizeNote + '</p>' +

        '<h3 class="mky__sub">' + (isSingle() ? 'Top half' : 'Top size') + '</h3>' +
        /* Bra vocabulary first, XS-XL only as the fallback. See BANDS/CUPS above for
           why one letter cannot describe a bust, and why this is not a measurement. */
        '<div class="mky__bust">' +
          '<div><span class="mky__bustlab">Band</span>' +
          '<div class="mky__chips">' + BANDS.map(function (b) {
            return '<button class="mky__chip" type="button" data-attr="band" data-v="' + b +
                   '" aria-pressed="' + (b === state.band) + '">' + b + '</button>';
          }).join('') + '</div></div>' +
          '<div><span class="mky__bustlab">Cup</span>' +
          '<div class="mky__chips">' + CUPS.map(function (c) {
            return '<button class="mky__chip" type="button" data-attr="cup" data-v="' + c[0] +
                   '" aria-pressed="' + (c[0] === state.cup) + '">' + c[0] + '</button>';
          }).join('') + '</div></div>' +
        '</div>' +
        /* Converts most "I don't know" into a real answer for free: the number and the
           letter are printed on a bra she already owns. */
        '<p class="text-sm text-subdued mky__busthint">' + root.dataset.bustHint + '</p>' +
        /* ⚠️ THE "wear a larger band or cup" LINE IS GONE ON PURPOSE. It invited anyone
           past the chart to say so -- but the chips top out at 40 + DD, which is 45"
           against a 42" ceiling, so ANY combination at or beyond the top of the chart
           already trips bustBeyondGrade() and gets the longer, better message below.
           It was a second invitation to the same conversation on a screen Nuno was
           already reading past. */
        /* ⚠️ TWO DOORS, NOT ONE, AND THEY LEAD SOMEWHERE DIFFERENT. "I'm not sure" means
           she cannot give a number and we should ask for a top size instead. "My size
           isn't listed" means she can give it exactly and our chips cannot hold it. They
           were the same button, so the second kind was sent down the first kind's route
           and her real size was never recorded. */
        '<p class="mky__actions"><button class="button button--secondary" type="button" ' +
          'id="mky-bust-skip" aria-pressed="' + (state.bustSkipped ? 'true' : 'false') + '">' +
          root.dataset.bustSkip + '</button>' +
          '<button class="button button--secondary" type="button" id="mky-bust-unlisted" ' +
          'aria-pressed="' + (state.bustUnlisted ? 'true' : 'false') + '">' +
          root.dataset.bustUnlisted + '</button></p>' +
        (state.bustUnlisted
          ? '<div class="mky__bustnote">' +
              '<p class="text-sm text-subdued">' + root.dataset.bustUnlistedNote + '</p>' +
              '<p class="mky__unlisted">' +
                '<label class="mky__unlistedlab" for="mky-bust-label">Your band and cup (US)</label>' +
                '<input class="mky__unlistedin" id="mky-bust-label" type="text" maxlength="24" ' +
                  'autocomplete="off" placeholder="e.g. 32G" value="' + escAttr(state.bustLabel) + '">' +
              '</p></div>'
          : '') +
        /* Shown only once she has SAID she does not know. Offering both at once asks
           the same question twice and invites two different answers. */
        (state.bustSkipped
          ? '<p class="text-sm text-subdued mky__bustnote">' + root.dataset.bustFallback + '</p>' +
            sizeChips('topSize', state.topSize)
          : bustBeyondGrade()
            ? '<p class="mky__beyond">' + root.dataset.bustBeyond + '</p>'
            : (bustToStandard(state.band, state.cup)
              ? '<p class="text-sm text-subdued mky__derived">' +
                'That is a ' + bustToStandard(state.band, state.cup) +
                ' top on our chart. We cut to your band and cup, not the letter.</p>'
              : '')) +
        '<h3 class="mky__sub">' + (isSingle() ? 'Bottom half' : 'Bottom size') + '</h3>' +
        /* ⚠️ THE EXPLANATION SITS WITH THE CHIPS IT EXPLAINS. It was at the top of the
           screen, roughly 800px above them, which is the same as not being there: Nuno
           had to ask where it was. A sentence about a pre-selected control belongs beside
           the control.
           Shown only while the inference still stands -- the moment she moves a chip,
           sizesTouched retires the grade and this goes with it -- and only when the grade
           actually SHIFTED something: SIZE_SPLIT.balanced is 0, so "balanced" says
           nothing rather than claiming a change it did not make. */
        (!sizesTouched && SIZE_SPLIT[state.volume]
          ? '<p class="mky__hint text-sm text-subdued">Starts one step ' +
            (SIZE_SPLIT[state.volume] > 0 ? 'up' : 'down') + ' from the top, because you ' +
            'said you carry more through the ' +
            (SIZE_SPLIT[state.volume] > 0 ? 'hips' : 'bust') + '. Change either one.</p>'
          : '') +
        sizeChips('bottomSize', state.bottomSize) +
        /* ⚠️ THE SECOND EXPLANATION IS GONE, AND IT SAID THE SAME THING AS THE FIRST. The
           paragraph ABOVE the chips already reads "Starts one step down from the top,
           because you said you carry more through the bust. Change either one."; this one
           then repeated the reason and the permission under them. Two sentences for one
           fact, on the screen that was already the tallest in the flow. Nuno, on a handset,
           2026-09-14. The line above is the one kept: it is read BEFORE the chips, which is
           when the offset needs explaining. */
        '<p class="text-sm text-subdued mky__sizelink">' +
        '<a href="/pages/size-chart" target="_blank" rel="noopener">See the size chart</a></p>';
      Array.prototype.forEach.call(el.step.querySelectorAll('.mky__chip'), function (b) {
        b.addEventListener('click', function () {
          state[b.dataset.attr] = b.dataset.v;
          /* Moving a size chip retires the volume-derived split for good. She has now
             said what she wants; an inference must not overwrite it on the next paint. */
          if (b.dataset.attr === 'bottomSize' || b.dataset.attr === 'topSize') sizesTouched = true;
          /* Answering the bust withdraws "I'm not sure": leaving both set would send
             Evelin a band, a cup AND a contradicting XS-XL. */
          if (b.dataset.attr === 'band' || b.dataset.attr === 'cup') {
            state.bustSkipped = false; state.bustUnlisted = false;
          }
          render();
        });
      });
      var skipBtn = document.getElementById('mky-bust-skip');
      if (skipBtn) skipBtn.addEventListener('click', function () {
        state.bustSkipped = !state.bustSkipped;
        /* The two routes are mutually exclusive: she either knows it or she does not. */
        if (state.bustSkipped) { state.band = ''; state.cup = ''; state.bustUnlisted = false; }
        render();
      });
      var unlistedBtn = document.getElementById('mky-bust-unlisted');
      if (unlistedBtn) unlistedBtn.addEventListener('click', function () {
        state.bustUnlisted = !state.bustUnlisted;
        if (state.bustUnlisted) { state.band = ''; state.cup = ''; state.bustSkipped = false; }
        else { state.bustLabel = ''; }
        render();
      });
      /* ⚠️ `input`, NOT `change`, AND NO render() ON EVERY KEYSTROKE. Continue is gated on
         this box having something in it, so the gate has to see the text as she types --
         but re-rendering would destroy the field she is typing into and take the caret
         with it. Write to state, refresh only the things that read it. */
      var labelIn = document.getElementById('mky-bust-label');
      if (labelIn) labelIn.addEventListener('input', function () {
        state.bustLabel = labelIn.value.slice(0, 24);
        syncUrl();
        needs('');
      });

    } else if (prefQFor(sid)) {
      var cq = prefQFor(sid);
      el.step.innerHTML =
        '<h2 class="mky__q">' + cq.q + '</h2>' +
        '<p class="mky__hint text-sm text-subdued">' + cq.hint + '</p>' +
        '<div class="mky__opts mky__opts--fabric">' + cq.opts.map(function (o, i) {
          /* ⚠️ `data-v` CARRIES THE INDEX, NOT THE BAND. The Either answer is null, which
             stringifies to the text "null" on a data- attribute and then compares unequal
             to everything, leaving the option looking unselected forever. The index is
             always a number and the band is read out of the table. */
          var on = (cq.key in state.prefs) && state.prefs[cq.key] === o[0];
          return '<button class="mky__opt mky__opt--ex" type="button" data-v="' + i +
                 '" aria-pressed="' + on + '"><b>' + o[1] + '</b>' +
                 fabricExampleRow(cq.key, o[0]) + '</button>';
        }).join('') + '</div>' +
        /* ⚠️ SHE HAS NOT CHOSEN A CLOTH YET AND THE EXAMPLES MUST NOT LOOK LIKE A CHOICE.
           Three swatches inside a button read as three things to tap; the row is one
           answer and the line under it says what they are for. */
        '<p class="mky__hint mky__exnote text-sm text-subdued">' +
        'These show the range. You&rsquo;ll choose your fabric shortly.</p>';
      Array.prototype.forEach.call(el.step.querySelectorAll('.mky__opt'), function (b) {
        b.addEventListener('click', function () {
          state.prefs[cq.key] = cq.opts[parseInt(b.dataset.v, 10)][0];
          /* Changing an answer invalidates where she was in the suggestions. Reset the
             page deliberately rather than paging into a list that no longer exists. */
          state.prefsPage = 0;
          /* ⚠️ AND IT ADVANCES, LIKE EVERY OTHER SINGLE-CHOICE ANSWER. The examples under
             each answer are the preview, so there is nothing left on this screen to look
             at once she has picked one. See autoAdvances(). */
          goNext();
        });
      });

    } else if (sid === 'fabric') {
      var answered = FABRIC_DIMS.some(function (d) { return d in state.prefs; });
      var sc = answered ? fabricScreen(state.prefs, state.prefsPage, 6)
                        : { exact: [], near: [], miss: {}, more: false,
                            exactTotal: 0, nearTotal: 0, page: 0, broaden: [] };
      var showingAll = state.showAll || !answered;
      /* ⚠️ THE COUNT ON "Browse all fabrics (N)" FOLLOWS THE FILTER. It is computed from
         the pool this button actually opens -- that rule predates the filter and still
         holds -- so narrowing to prints has to narrow the number with it, or the label
         states a total she cannot reach. */
      var allPool = offerable().filter(function (f) {
        return state.browse !== 'prints' || isPrint(f);
      });
      var pool = showingAll ? allPool.slice(0, Math.min(fabricShown, allPool.length)) : [];
      var head = '', body = '';

      if (showingAll) {
        head = '<p class="mky__hint text-sm text-subdued">' +
               (answered ? 'Every fabric we can cut this month.'
                         : 'Every fabric we can cut this month. Answer the four questions ' +
                           'and we will narrow them down.') + '</p>';
        body = '<div class="mky__swatches" data-rail="browse">' +
               pool.map(function (f) { return fabricTile(f, '', 'fabric'); }).join('') +
               '</div>';
      } else {
        /* ⚠️ FOUR DIFFERENT SENTENCES, AND THEY ARE NOT INTERCHANGEABLE. "We found a few
           suggestions" is a fact about the catalogue; "you have seen every fabric we can
           suggest" is a fact about how far she has paged. Printing the first on page two
           of a screen that had suggestions on page one tells her the drop is thinner than
           it is.
           ⚠️ NONE OF THEM CLAIMS OR DENIES A MATCH ANY MORE, and the reason is the data
           underneath. The labels are AI-reviewed and not human-approved, and 47 of the
           102 fabrics carry `ai_confidence_low` -- the reviewer's own flag that the call
           could go either way. "6 fabrics match everything you said" was a promise the
           dataset cannot keep on nearly half the drop, beside a photograph that can
           contradict it; "Nothing matches all three of your answers" was the same
           overconfidence inverted, and wrong in the direction that costs her a suit
           rather than a click. Codex, 2026-09-12 and 2026-09-13.
           ⚠️ THE COUNT STAYS, and so does the per-fabric trade-off under each tile, the
           broadening buttons and browse-all: softening the sentence must not cost her the
           information or the way out.
           ⚠️ AND EVERY SENTENCE READS THE SAME WHETHER SHE ANSWERED FOUR, TWO, OR CHOSE
           Either. The old lines swapped between "everything you said" and "all three of
           your answers", so the strength of the claim moved with how much she had
           constrained -- exactly where a wrong label does the most damage. That is why
           the phrase that used to build those variants is gone rather than kept for one
           last caller. */
        if (sc.exact.length) {
          head = '<p class="mky__matchhead">' + sc.exact.length + ' fabric' +
                 (sc.exact.length === 1 ? '' : 's') +
                 ' suggested for your preferences.</p>';
        } else if (sc.exactTotal) {
          head = '<p class="mky__matchhead">You have seen every fabric we can suggest ' +
                 'for your preferences.</p>';
        } else if (sc.alt && sc.alt.length) {
          /* ⚠️ A DEAD END WAS THE OLD BEHAVIOUR AND IT IS NOT AN ANSWER. This branch used
             to print "we don't have suggestions" and stop, so a woman whose answers
             matched nothing had to press a button before she could see a single fabric.
             She now gets six, ordered by how far each sits from what she said, under a
             heading that does not pretend they match. Copy set by Codex, 2026-09-13.
             ⚠️ HER ANSWERS ARE NOT TOUCHED. Nothing here writes to state.prefs -- these
             are shown, not applied -- so the broadening buttons below still name changes
             she has not made, and choosing one of these keeps every answer she gave. */
          head = '<p class="mky__matchhead mky__matchhead--near">More fabrics to ' +
                 'explore</p>' +
                 '<p class="mky__hint text-sm text-subdued">These differ from some of ' +
                 'your preferences. You can explore them or adjust your answers.</p>';
        } else if (sc.offerable === 0) {
          /* ⚠️ AN EMPTY DROP IS A FACT, NOT A DEAD END TO PAPER OVER. If nothing is
             available we say so; we never pad the screen with fabrics we cannot cut. */
          head = '<p class="mky__matchhead mky__matchhead--near">We have no fabric ' +
                 'available to cut just now. Please check back, or contact us and we ' +
                 'will tell you when the next cloth lands.</p>';
        } else if (sc.near.length) {
          /* ⚠️ NEITHER OF THESE MAY DENY A MATCH EITHER. They read "Nothing matches all
             three of your answers" and "Nothing we can cut this month is close to...",
             which is the old claim inverted: just as categorical, and wrong in the
             direction that costs her a suit rather than a click. 47 of 102 labels are
             flagged low-confidence, so a fabric we announce as unsuitable may simply be
             mislabelled. Copy set by Codex, 2026-09-13.
             ⚠️ THE TRADE-OFF IS STILL NAMED PER FABRIC, under each tile, and the
             broadening buttons still appear below -- softening the sentence must not cost
             her the reason or the way out. And broadening still only happens when she
             presses it. */
          head = '<p class="mky__matchhead mky__matchhead--near">We found a few ' +
                 'suggestions, each with a trade-off shown below.</p>';
        } else {
          head = '<p class="mky__matchhead mky__matchhead--near">We don&rsquo;t have ' +
                 'suggestions for this combination of preferences. Try adjusting a ' +
                 'preference, or browse all available fabrics.</p>';
        }
        if (state.prefsRefreshed) {
          head += '<p class="mky__hint mky__note text-sm text-subdued">Our fabrics have ' +
                  'changed since this link was saved, so these suggestions have been ' +
                  'worked out again.</p>';
        }
        if (state.fabricGone) {
          head += '<p class="mky__hint mky__note text-sm text-subdued">The fabric on your ' +
                  'saved design is no longer available, so nothing is selected. Choose ' +
                  'another and everything else you answered is kept.</p>';
        }
        body = sc.exact.length
          ? '<div class="mky__swatches mky__swatches--row" data-rail="sugg">' + sc.exact.map(function (f) {
              return fabricTile(f, '');
            }).join('') + '</div>'
          : '';
        /* Every alternative says how it differs, on every axis it differs on -- a
           fallback fabric can miss three at once where a near miss only ever misses one. */
        if (sc.alt && sc.alt.length) {
          body += '<div class="mky__swatches mky__swatches--row" data-rail="alt">' + sc.alt.map(function (f) {
            return fabricTile(f, fabricDiffText(f, state.prefs));
          }).join('') + '</div>';
        }
        if (sc.near.length) {
          body += '<p class="mky__nearhead text-sm text-subdued">' +
                  (sc.exact.length ? 'Close, but&hellip;' : '') + '</p>' +
                  '<div class="mky__swatches mky__swatches--row" data-rail="near">' + sc.near.map(function (f) {
                    return fabricTile(f, fabricMissText(f, state.prefs, sc.miss[f.code]));
                  }).join('') + '</div>';
        }
      }

      /* ⚠️ A DEAD END PLUS "browse 102" IS NOT AN ANSWER, and a preference is never changed
         on her behalf. The button names the change and the count; pressing it is the only
         thing that applies it. */
      var wider = '';
      if (!showingAll && sc.broaden && sc.broaden.length) {
        wider = '<p class="mky__actions mky__actions--quiet mky__widen">' +
          sc.broaden.map(function (o, i) {
            return '<button class="mky__reset" type="button" data-widen="' + i + '">' +
                   'Try &ldquo;' + o.label + '&rdquo; instead &middot; ' + o.count +
                   ' fabric' + (o.count === 1 ? '' : 's') + '</button>';
          }).join('') + '</p>';
      }

      /* ⚠️ HER CHOICE NEVER FALLS OFF THE SCREEN. Paging, broadening a preference or
         changing an answer can all move the chosen fabric out of the suggestions; the
         selection is not silently dropped, it is pinned with a line saying why it is no
         longer among them. */
      var pinned = '';
      if (state.fabric) {
        var chosen = slotFace(state.fabric);
        var onScreen = sc.exact.concat(sc.near).some(function (f) {
          return f.code === state.fabric;
        }) || (showingAll && pool.some(function (f) { return f.code === state.fabric; }));
        /* ⚠️ A PENDING COLOUR HAS NO BANDS TO BE VERDICT'D AGAINST. `fabricVerdict` reads
           the reviewed classification off a catalogue row; a colour direction has none, so
           it is pinned with the caveat it always carries instead of with a match claim
           nobody could make about it. */
        if (chosen && chosen.pending && !onScreen) {
          pinned = '<p class="mky__nearhead text-sm text-subdued">Your color direction' +
                   '</p><div class="mky__swatches" data-rail="pinned">' +
                   fabricTile(chosen, '', 'fabric') + '</div>';
        } else if (chosen && !onScreen) {
          var v = fabricVerdict(chosen, state.prefs);
          pinned = '<p class="mky__nearhead text-sm text-subdued">Your fabric' +
                   (answered && v.kind === '' ? ' &mdash; no longer one of your suggestions, ' +
                    'still chosen' : '') + '</p>' +
                   '<div class="mky__swatches" data-rail="pinned">' +
                   fabricTile(chosen, v.kind === 'near'
                     ? fabricMissText(chosen, state.prefs, v.miss) : '') + '</div>';
        }
      }

      /* ⚠️ THE SOLIDS PALETTE REPLACES THE GRID, IT DOES NOT SIT UNDER IT. A colour
         direction and a photographed swatch are different KINDS of choice, and showing
         both at once invites her to read a block of colour as another swatch. */
      if (state.browse === 'solids') {
        head = '';
        body = solidsPanelHtml('fabric');
        wider = '';
        pinned = '';
      }
      /* ⚠️ A QUERY REPLACES THE SUGGESTIONS AND LEAVES HER ANSWERS ALONE. Typing a word is
         a more specific instruction than four taste answers given several screens ago, so
         it wins on screen -- and `state.prefs` is untouched, which is what lets "Clear
         search" give them back. It searches the WHOLE eligible pool, never the shortlist. */
      var searching = !!state.query.trim();
      if (searching) {
        head = '';
        pinned = '';
        wider = '';
        body = '<div id="mky-searchres">' +
               searchResultsHtml('fabric', offerable()) + '</div>';
      }
      el.step.innerHTML =
        /* ⚠️ THE SUIT COMES FIRST, ABOVE EVERY CONTROL ON THIS SCREEN. Not only above the
           swatches: the browse row, the search field and the match line are all chrome for
           choosing, and the question is what the cloth looks like ON the garment. See
           fabricPreviewHtml(); it is empty above the phone breakpoint, where the stage
           already sits beside the column. */
        '<h2 class="mky__q">Your fabric</h2>' + fabricPreviewHtml() + browseControlsHtml() +
        searchFieldHtml() + head + pinned + body + wider +
        /* ⚠️ THE BROWSE CONTROLS DO NOT EXIST WHILE A SEARCH IS RUNNING, AND THEY WERE
           DEAD RATHER THAN ABSENT. A query replaces the body with its results; these
           links change which page of the BROWSE pool is built, and that pool is not on
           screen -- so pressing them re-rendered the identical search results and looked
           broken. Nuno, on a handset 2026-09-17: "these links under the swatch options
           are not working (i.e. they're not loading new swatches)."
           He is right that they were not working, and right that there were too many:
           four ways to widen the pool stacked under a fifth. While she is searching the
           only control that means anything is the one that ends the search, and
           searchResultsHtml() already emits it. */
        (searching ? '' :
        '<p class="mky__actions mky__actions--quiet">' +
        (!showingAll && sc.more
          ? '<button class="mky__reset" type="button" id="mky-more-sugg">Show more options</button>'
          : '') +
        '<button class="mky__reset" type="button" id="mky-all">' +
        /* ⚠️ THE COUNT IS COMPUTED FROM THE POOL THIS BUTTON ACTUALLY OPENS, never
           written down. `allPool` is offerable() -- in the drop and not sold out -- and
           it is the same array the browse view pages through, so the number on the label
           and the number she can reach cannot drift apart. A hardcoded 102 would be a lie
           the first time a fabric sells out. Codex, 2026-09-13.
           ⚠️ THE PARTNER-FABRIC SCREEN KEEPS ITS OWN LABEL, deliberately. Its pool
           excludes the fabrics already chosen for the other faces, so it is neither
           "all" nor this count; giving it the same words would state a number that is
           wrong for that screen. */
        (showingAll && answered
          ? 'Back to my suggestions'
          : 'Browse all fabrics (' + allPool.length + ')') +
        '</button>' +
        (showingAll && pool.length < allPool.length
          ? '<button class="mky__reset" type="button" id="mky-more">See next ' +
            Math.min(FABRIC_PAGE, allPool.length - pool.length) + '</button>'
          : '') +
        (showingAll ? '<span class="mky__pagecount">' + pool.length + ' of ' +
                      allPool.length + '</span>' : '') + '</p>');

      bindBrowseControls();
      bindSearchField();
      if (state.browse === 'solids') bindSolidsPanel('fabric');
      if (searching) bindSearchResults();
      bindSolidTiles('fabric');
      /* Redraw ONLY the results, so the field she is typing into survives the keystroke. */
      paintSearchResults = function () {
        var host = document.getElementById('mky-searchres');
        if (!host) { render(); return; }
        if (!state.query.trim()) { render(); return; }
        host.innerHTML = searchResultsHtml('fabric', offerable());
        bindSearchResults();
        bindFabricTiles('fabric');
        bindSolidTiles('fabric');
      };
      /* ⚠️ CHOOSING KEEPS HER PLACE. The handler re-renders, and paging and the expanded
         view both survive because they live in `state`, not in the DOM. See
         bindFabricTiles() for the two rules every copy of this had to remember. */
      bindFabricTiles('fabric');
      Array.prototype.forEach.call(el.step.querySelectorAll('[data-widen]'), function (b) {
        b.addEventListener('click', function () {
          var o = sc.broaden[parseInt(b.dataset.widen, 10)];
          state.prefs[o.dim] = o.value;
          state.prefsPage = 0;
          /* ⚠️ HER CHOSEN CLOTH SURVIVES A BROADENED PREFERENCE. It may no longer be a
             match, and the tile says so rather than the selection vanishing. */
          render();
        });
      });
      var moreSugg = document.getElementById('mky-more-sugg');
      if (moreSugg) moreSugg.addEventListener('click', function () {
        state.prefsPage += 1; render();
      });
      var moreBtn = document.getElementById('mky-more');
      if (moreBtn) moreBtn.addEventListener('click', function () {
        fabricShown += FABRIC_PAGE; render();
      });
      /* ⚠️ GUARDED, BECAUSE THE BUTTON IS NOW ABSENT WHILE A SEARCH IS RUNNING. It was
         fetched unconditionally, which would throw a TypeError on the first keystroke and
         abort the rest of this render -- the class of silent break stagePanesHome() already
         carries a note about. */
      var allBtn = document.getElementById('mky-all');
      if (allBtn) allBtn.addEventListener('click', function () {
        state.showAll = !state.showAll; fabricShown = FABRIC_PAGE; render();
      });

    } else if (sid === 'variation') {
      var conflict = reversibleConflicts();
      el.step.innerHTML =
        '<h2 class="mky__q">One fabric, or more?</h2>' +
        /* ⚠️ THE COUNT BRANCHES, AND IT DID NOT UNTIL 2026-09-14. This line read "a
           reversible two-piece gives FOUR ways to wear one suit" for EVERY garment,
           including a one piece, which has two faces and two ways -- so the one screen
           explaining a $100 step was describing combinations she cannot have. Codex:
           "Never display the current four-way legend on a one-piece."
           ⚠️ THE REASON DIFFERS TOO, NOT JUST THE NUMBER. Four comes from either PIECE
           being reversible independently; two comes from one garment having a second
           face. Swapping only the digit would leave "because either piece can be worn
           either way round" on a garment that has one piece. */
        '<p class="mky__hint text-sm text-subdued">' +
        (isSingle()
          ? 'A reversible one-piece gives two ways to wear one suit, because it has a '
            + 'second face on the inside.'
          : 'A reversible two-piece gives four ways to wear one suit, because either '
            + 'piece can be worn either way round.') + '</p>' +
        /* ⚠️ THE POINTER LAYOUT HAD NO "these are examples" LINE AT ALL, and it did not
           need one while the pictures were obviously other people's garments. A
           three-piece card now draws HER cut in a colour nobody chose, so the caption
           that was deck-only has to be said here too -- and it is the SAME function, so
           the two layouts cannot drift apart the way the reversible warning and the deck
           legend each did once. */
        (state.style === 'three'
          ? '<p class="mky__hint text-sm text-subdued">' + variationArtNote() + '</p>'
          : '') +
        '<div class="mky__opts" data-multi="false">' + variations().map(function (v) {
          var note = v[2];
          if (v[0] === 'reversible') {
            note = isSingle()
              ? 'A second face. Two ways to wear it.'
              : 'A second face on both pieces. Four ways to wear it.';
            /* Unconditional, and the deck says the same sentence from the same function.
               See the note on the deck's help(). */
            note += ' ' + reversibleRestriction();
            if (conflict.length) note += ' You have asked for ' + andList(conflict) + '.';
          }
          return '<button class="mky__opt" type="button" data-v="' + v[0] + '" aria-pressed="' +
                 (state.variation === v[0]) + '"><b>' + v[1] +
                 (PRICE[v[0]] !== PRICE.same ? ' &middot; $' + PRICE[v[0]] : '') + '</b>' +
                 '<span>' + note + '</span></button>';
        }).join('') + '</div>' +
        /* ⚠️ THE CONFLICT IS A DECISION SHE MAKES, NOT ONE WE MAKE FOR HER. Picking
           reversible used to commit immediately, and buildGroups() then removed the
           underwire, the shelf or the sewn-in pads from her record without another word.
           Naming them and asking is the whole fix: the two buttons below are the only way
           the choice gets made, and neither of them edits her fit answers.

           ⚠️ NOTHING IS MUTATED, WHICH IS WHY GOING BACK RESTORES HER PREFERENCES FOR
           FREE. `wire`, `top` and `padding` are left exactly as she answered them; only
           `variation` moves. Choose reversible and the record drops them for display;
           choose one fabric again and they are all still there. Do not "tidy up" by
           clearing them here -- that is the silent removal this replaces. */
        (pendingReversible && conflict.length
          /* ⚠️ THE SAME PANEL AS THE DECK'S, AND IT MUST STAY THE SAME. This layout keeps
             its own copy of the markup, so the shortened text was applied in BOTH places --
             the deck legend was changed in one place earlier this session and the phone
             went on showing the old wording. Desktop and mobile say the same thing here. */
          /* ⚠️ THE SAME TWO CHOICES AS THE PHONE'S MODAL, IN FLOW. The pointer layout does
             not need a modal -- the option list stays visible beside it, which is the
             escape hatch the phone loses when the modal covers the cards -- but it must
             offer the SAME decision with the SAME consequences, or the two layouts drift
             into different products. This file has already been bitten twice by markup
             kept in two places and changed in one. */
          /* ⚠️ BOTH BUTTONS ARE SECONDARY HERE TOO, for the reason Codex gave about the
             other panel: the comment below claims neither is the safe choice while the
             markup made one of them the primary pink. Two live options, no opinion. */
          ? '<div class="mky__beyond" id="mky-revconflict">' +
              '<p><b>Reversible cannot carry ' + andList(conflict) + '.</b></p>' +
              '<p class="mky__actions">' +
                '<button class="button button--secondary mky__modalbtn" type="button" data-rev="go">Remove ' +
                  bareList(conflict) +
                  '<span>Keeps the reversible suit. Your support comes from the cut instead.</span>' +
                  '</button>' +
                '<button class="button button--secondary mky__modalbtn" type="button" data-rev="keep">Keep ' +
                  bareList(conflict) +
                  '<span>Keeps your ' + bareList(conflict) + '. The suit will not be reversible.</span>' +
                  '</button>' +
              '</p></div>'
          : '');
      Array.prototype.forEach.call(el.step.querySelectorAll('.mky__opt'), function (b) {
        b.addEventListener('click', function () {
          var v = b.dataset.v;
          /* Ask before committing. Every other variation commits straight away. */
          if (v === 'reversible' && reversibleConflicts().length && state.variation !== 'reversible') {
            pendingReversible = true;
            render();
            return;
          }
          pendingReversible = false;
          setVariation(v);
          goNext();
        });
      });
      Array.prototype.forEach.call(el.step.querySelectorAll('[data-rev]'), function (b) {
        b.addEventListener('click', function () {
          pendingReversible = false;
          if (b.dataset.rev === 'go') { setVariation('reversible'); goNext(); }
          else { render(); }   /* keep them: variation is untouched, still whatever it was */
        });
      });

    } else if (sid === 'cropFabric') {
      var cops = cropOptions();
      el.step.innerHTML =
        '<h2 class="mky__q">' + cropQ() + '</h2>' +
        '<p class="mky__hint text-sm text-subdued">' + cropHint() + '</p>' +
        '<div class="mky__swatches mky__swatches--row" data-rail="crop">' + cops.map(function (o) {
          return '<button class="mky__swatch" type="button" data-crop="' + o.v +
                 '" aria-pressed="' + (state.cropFace === o.v) + '">' +
                 swatchFace(o.art) + '<b>' + o.label + '</b>' +
                 '<em class="mky__miss">' + o.sub +
                 '</em></button>';
        }).join('') + '</div>';
      Array.prototype.forEach.call(el.step.querySelectorAll('[data-crop]'), function (b) {
        b.addEventListener('click', function () {
          state.cropFace = b.dataset.crop;
          /* ⚠️ IT ADVANCES, LIKE EVERY OTHER SINGLE-CHOICE ANSWER SINCE 2026-09-14. The
             two fabric PICKERS do not, because they are catalogues with their own
             completion action; this is a two-option question and the preview above it has
             already redrawn by the time she lifts her finger. */
          goNext();
        });
      });

    } else if (sid === 'fabricFix') {
      /* ⚠️ NOTHING IS APPLIED UNTIL SHE PRESSES ONE, AND NO OPTION IS FAVOURED. Three
         cloths are named, three pairs are offered, each says which fabric it drops and in
         her words rather than in slot names. The same rule the reversible/comfort conflict
         follows: name the cost on the button, give them equal weight, never pre-select.
         ⚠️ THE FABRICS ARE STILL IN `state` WHILE SHE READS THIS. That is deliberate --
         Back out of here and the link is exactly as it arrived, so nothing has been lost
         by looking. */
      var ex = state.fabricExcess || {};
      var exTop = slotFace(ex.top), exBot = slotFace(ex.bottom), exIn = slotFace(ex.inside);
      var nm = function (x) { return x ? x.name : 'a fabric we no longer have'; };
      var lower = function (x) { return nm(x).toLowerCase(); };
      var opts = [
        { k: 'rev-top',
          label: 'Reversible in ' + lower(exTop) + ' and ' + lower(exIn),
          sub: nm(exTop) + ' on the outside of both pieces, ' + lower(exIn) +
               ' on the reverse. ' + nm(exBot) + ' is not used.' },
        { k: 'rev-bottom',
          label: 'Reversible in ' + lower(exBot) + ' and ' + lower(exIn),
          sub: nm(exBot) + ' on the outside of both pieces, ' + lower(exIn) +
               ' on the reverse. ' + nm(exTop) + ' is not used.' },
        { k: 'mixed',
          label: 'Different top and bottom, in ' + lower(exTop) + ' and ' + lower(exBot),
          sub: nm(exTop) + ' on the top, ' + lower(exBot) + ' on the bottom. Not ' +
               'reversible, so ' + lower(exIn) + ' is not used.' }
      ];
      el.step.innerHTML =
        '<h2 class="mky__q">This saved design names three fabrics</h2>' +
        '<p class="mky__hint text-sm text-subdued">A suit is cut from at most two. Your ' +
        'link asks for <strong>' + nm(exTop) + '</strong> on the top, <strong>' +
        lower(exBot) + '</strong> on the bottom and <strong>' + lower(exIn) +
        '</strong> on the reverse, which we cannot make. Nothing has been changed &mdash; ' +
        'choose which two you want and the rest of your design is kept exactly as it is.' +
        '</p>' +
        '<div class="mky__opts">' + opts.map(function (o, i) {
          return '<button class="mky__opt" type="button" data-fix="' + i + '">' +
                 '<b>' + o.label + '</b><em>' + o.sub + '</em></button>';
        }).join('') + '</div>';
      Array.prototype.forEach.call(el.step.querySelectorAll('[data-fix]'), function (b) {
        b.addEventListener('click', function () {
          var o = opts[parseInt(b.dataset.fix, 10)];
          if (o.k === 'rev-top') {
            setVariation('reversible');
            state.fabric = ex.top; state.fabricBottom = ''; state.fabricInside = ex.inside;
          } else if (o.k === 'rev-bottom') {
            setVariation('reversible');
            state.fabric = ex.bottom; state.fabricBottom = ''; state.fabricInside = ex.inside;
          } else {
            setVariation('mixed');
            state.fabric = ex.top; state.fabricBottom = ex.bottom; state.fabricInside = '';
          }
          /* setVariation already cleared the excess; this is the press that earned it. */
          state.fabricExcess = null;
          goNext();
        });
      });

    } else if (sid === 'fabricBottom' || sid === 'fabricInside' || sid === 'fabricCrop') {
      var which = sid === 'fabricBottom' ? 'bottom'
                : sid === 'fabricInside' ? 'inside' : 'crop';
      var key = sid;
      var chosen = slotFace(state.fabric);
      /* The second fabric is a PAIRING problem, not a taste problem, so it is ranked by
         what actually goes with the first: away from it in loudness and busyness, close
         to it in lightness. Same machinery as the narrowing, pointed at a different
         question - which is why this is six options and not another hundred-item browse. */
      /* ⚠️ EXCLUDE WHAT IS ALREADY CHOSEN. If the inside repeats the bottom, looks 2 and 4
         collapse into 1 and 3 and "four ways" becomes false while still being printed.
         The claim is arithmetic, so the inputs have to make it true. */
      var taken = [state.fabric, bottomFabricCode()].filter(Boolean);
      var avail = offerable().filter(function (x) {
        if (state.browse === 'prints' && !isPrint(x)) return false;
        return taken.indexOf(x.code) === -1 || x.code === state[key];
      });
      /* ⚠️ THE SOLIDS HERE ARE ASSUMED STOCK, NOT HARVESTED SWATCHES. Nunez hold solids
         but never post them, so black and white are entered by hand in the register with
         source=assumed and carry a hex instead of a photograph. Everything else in this
         pool was measured from a real swatch image. Confirm the solid list with Evelin
         before treating it as an offer. Ranked by busyness plus variety, the two axes
         that track how much a fabric shouts, so the solids sort to the front. */
      /* ⚠️ "Show me the plainest fabrics" IS GONE, 2026-09-17, on Nuno's call. It was a
         THIRD way to re-rank this one screen, next to the Prints/Solids filter row and a
         search box that both already answer "I want something quiet" -- and it was one of
         four links stacked under the swatches on a phone. Two of those four did nothing
         at all while a search was running; this one worked and still cost a line on the
         most crowded screen in the flow.
         The ranking it used -- busyness + variety ascending -- is not preserved anywhere,
         because the Solids filter reaches the plainest thing in the catalogue in one tap
         and the search reaches a named one. */
      var partners = state.showAll ? avail : avail.slice().sort(function (x, y) {
        function d(z) {
          if (!chosen) return 0;
          return -Math.abs(z.boldness - chosen.boldness) - Math.abs(z.busyness - chosen.busyness)
                 + 2 * Math.abs(z.lightness - chosen.lightness);
        }
        return d(x) - d(y);
      });
      if (!state.showAll) {
        var seen2 = {}, six = [];
        partners.forEach(function (x) {
          if (six.length < 6 && !seen2[x.name]) { seen2[x.name] = 1; six.push(x); }
        });
        partners = six;
      }
      /* ⚠️ TWENTY AT A TIME, NOT A HUNDRED AND TWO. Going from six suggestions to the
         whole catalogue in one press is not "more choice", it is the narrowing thrown
         away -- which is the thing this chapter's lede promises. fabricShown grows by
         FABRIC_PAGE each time she asks. Reset whenever she leaves the expanded view, so
         coming back starts at twenty again rather than wherever she stopped. */
      var shown = avail.length;
      if (state.showAll) {
        shown = Math.min(fabricShown, partners.length);
        partners = partners.slice(0, shown);
      }
      /* ⚠️ HER CHOSEN PARTNER NEVER FALLS OFF THIS SCREEN EITHER, AND UNTIL 2026-09-15 IT
         DID. The MAIN picker has carried a pinned row since the fabric rebuild -- "Your
         fabric, no longer one of your suggestions, still chosen" -- because paging or a
         changed answer can move a selection out of the list. This screen has exactly the
         same problem and had none of the fix: the six partners are ranked by what pairs
         well with the first cloth, so a saved link naming a partner OUTSIDE those six
         rendered with nothing selected at all. She could not see her own choice, and the
         only way to get it back on screen was to browse the whole catalogue.
         ⚠️ FOUND BY A TEST WALKER, NOT BY A SCREENSHOT, AND THAT IS WHY IT SURVIVED.
         `twofabric.py` resumed a link carrying 5177 on the bottom, saw no pressed swatch,
         concluded the screen was unanswered and clicked the first suggestion -- silently
         replacing her fabric. A person would have done the same thing.
         ⚠️ PREPENDED, NOT SORTED IN. It is her answer, so it leads; putting it back
         through the pairing rank would bury it again on the next render. */
      if (state[key] && !partners.some(function (x) { return x.code === state[key]; })) {
        /* ⚠️ slotFace(), NOT fabricBy(), AND THE FIRST VERSION OF THIS PIN USED THE WRONG
           ONE. `fabricBy` finds catalogue rows only, so a held SOLID COLOUR REQUEST --
           which has no catalogue row by definition, because Nunez never post solids --
           came back null and was not pinned. The screen then showed nothing selected and
           the walker replaced her colour direction with the first suggested print, which
           is the identical failure this pin was added to fix, one week old and reappearing
           for the other kind of assignment. Ask for the ASSIGNMENT's face, not for a row. */
        var held = slotFace(state[key]);
        if (held) partners = [held].concat(partners);
      }
      /* ⚠️ SAY WHICH PIECE THE FIRST CLOTH IS ON, AND LET THEM SWAP. The fabric chosen in
         the Fabric chapter was silently assigned to the top: nothing said so, and nothing
         could undo it. Which print goes on which half is most of the decision in a
         two-fabric suit. */
      /* ⚠️ IF A SAVED LINK NAMED A PARTNER CLOTH WE CAN NO LONGER CUT, SAY SO HERE. She was
         brought to this screen ON PURPOSE rather than shown a finished record with a hole in
         it; the rest of her design is intact and only this slot is empty. */
      var goneHere = state.fabricGoneSlots && state.fabricGoneSlots[sid];
      /* The same palette, from the same function, on the same terms. See solidsPanelHtml. */
      var partnerSolids = state.browse === 'solids';
      el.step.innerHTML =
        /* ⚠️ "CHOOSE YOUR REVERSE FABRIC", AND IT IS THE ONLY QUESTION LEFT ON A
           REVERSIBLE SUIT. It read "The inside" beside a separate bottom-fabric screen;
           that screen is gone (see extraPicks()), so this is fabric B for BOTH pieces
           and the heading has to say which face it is rather than which piece. */
        '<h2 class="mky__q">' +
          (which === 'bottom' ? 'Fabric for the bottom'
           : which === 'crop' ? 'Fabric for the long-sleeve crop'
           : 'Choose your reverse fabric') +
        '</h2>' +
        /* The same slot, in the same place, as the main picker's. These three screens need
           it MORE, not less: the suit already carries one cloth, so the picture is the only
           thing on screen saying how the second one sits against it. */
        fabricPreviewHtml() +
        (goneHere
          ? '<p class="mky__hint mky__note text-sm text-subdued">The fabric you had ' +
            (which === 'bottom' ? 'on the bottom'
             : which === 'crop' ? 'on the crop' : 'inside') +
            ' is no longer available, so ' +
            'it needs replacing. Everything else you chose is kept.</p>'
          : '') +
        '<p class="mky__hint text-sm text-subdued">' +
        (which === 'crop'
          ? (chosen ? '<strong>' + chosen.name + '</strong> is your suit. ' : '') +
            'The long-sleeve crop is cut from this one. It is worn over the suit and is ' +
            'not reversible.'
          : which === 'bottom'
          ? (chosen ? '<strong>' + chosen.name + '</strong> is on the top. ' : '') +
            'These sit well against it.'
          /* ⚠️ ONE FABRIC, BOTH PIECES, BOTH FACES -- say it in as many words. A
             reversible two piece is cut from exactly two fabrics: the one she chose is
             the outside of the top AND the bottom, and this one is the reverse of both.
             That is what makes four ways to wear it out of two fabrics. */
          : (isSplit()
              ? (chosen ? '<strong>' + chosen.name + '</strong> is the outside of both ' +
                  'pieces. ' : '') +
                'This fabric is the reverse of both. A quiet one here gives you the ' +
                'biggest change between the two sides.'
              : 'This is the face you see when you turn it. A quiet fabric here ' +
                'gives you the biggest change between the two sides.')) + '</p>' +
        (which === 'bottom' && isSplit() && chosen && state.fabricBottom
          ? '<p class="mky__actions mky__swapwrap"><button class="button button--secondary" ' +
            'type="button" id="mky-swap">Swap fabrics: put ' +
            slotFace(state.fabricBottom).name.toLowerCase() + ' on top instead</button></p>'
          : '') +
        /* ⚠️ A ROW ON A PHONE, BECAUSE THE GARMENT IS THE FEEDBACK AND IT WAS OFF SCREEN.
           Nuno, handset, 2026-09-14: tapping a swatch here "looks like the option isn't
           being registered" -- the suit redraws with the chosen fabric, but as a 2-wide
           GRID of six the preview sat below the fold, so the only visible response to a
           tap was a border. A single scrolling row keeps the garment partly on screen, so
           the tap has a visible consequence.
           ⚠️ THE MODIFIER ALREADY EXISTED AND THIS SCREEN WAS MISSING IT. The ranked
           shortlists on the fabric question already use `--row`; this partner picker -- the
           reversible inside, and the second fabric on a split -- kept the grid. Browse-all
           deliberately stays a grid: there the point IS to scan a hundred at once, and no
           preview can help with that.
           ⚠️ ROW ON THE PILOT LAYOUT ONLY. `.mky--pilot .mky__swatches--row` is scoped to
           the phone shell, so the desktop grid is untouched. */
        browseControlsHtml() + searchFieldHtml() +
        (state.query.trim()
          ? '<div id="mky-searchres">' + searchResultsHtml(key, avail) + '</div>'
          : partnerSolids
          ? solidsPanelHtml(key)
          : '<div class="mky__swatches mky__swatches--row" data-rail="partners">' + partners.map(function (f2) {
              return '<button class="mky__swatch" type="button" data-code="' + f2.code + '" aria-pressed="' +
                     (f2.code === state[key]) + '">' + swatchFace(f2) + '<b>' + f2.name + '</b></button>';
            }).join('') + '</div>') +
        /* ⚠️ THE WAY OUT IS NOT THE OFFER. "Show me all 102" was a full-width solid
           button under six suggestions, so the loudest thing on a screen whose whole
           point is that four questions narrowed a hundred fabrics down to six. It is for
           the woman who does not like any of the six; everyone else should barely notice
           it. Quiet text now, the same treatment as Start again.
           ⚠️ AND IT NO LONGER GOES FROM 6 TO 102. Twenty at a time, with a second quiet
           link for the next twenty -- see FABRIC_PAGE. Nuno, 2026-09-12. */
        /* Same rule as the main picker: while a query is running these are dead
           controls over a pool that is not on screen. See the ⚠️ there. */
        (state.query.trim() ? '' :
        '<p class="mky__actions mky__actions--quiet">' +
        '<button class="mky__reset" type="button" id="mky-all">' +
        (state.showAll ? 'Back to my suggestions' : 'Show me more fabrics') + '</button>' +
        (state.showAll && shown < avail.length
          ? '<button class="mky__reset" type="button" id="mky-more">See next ' +
            Math.min(FABRIC_PAGE, avail.length - shown) + '</button>'
          : '') +
        (state.showAll ? '<span class="mky__pagecount">' + shown + ' of ' + avail.length + '</span>' : '') +
        '</p>');
      bindBrowseControls();
      bindSearchField();
      if (partnerSolids && !state.query.trim()) bindSolidsPanel(key);
      if (state.query.trim()) bindSearchResults();
      bindSolidTiles(key);
      paintSearchResults = function () {
        var host = document.getElementById('mky-searchres');
        if (!host || !state.query.trim()) { render(); return; }
        host.innerHTML = searchResultsHtml(key, avail);
        bindSearchResults();
        bindFabricTiles(key);
        bindSolidTiles(key);
      };
      bindFabricTiles(key);
      /* ⚠️ "See next 20" WAS RENDERED AND NEVER BOUND ON THIS SCREEN. The main picker
         has wired `#mky-more` since paging shipped; the partner picker drew the identical
         button and attached nothing to it, so on the reverse, bottom and crop screens it
         sat there looking like a control and did nothing. `20 of 101` underneath it never
         moved. Nuno photographed it on a handset, 2026-09-17.
         ⚠️ IT IS NOT A COPY OF THE MAIN PICKER'S HANDLER, because the pools differ: this
         screen pages `avail`, which excludes the fabrics already chosen for the other
         faces, so it must not grow past `avail.length`. */
      var moreBtn2 = document.getElementById('mky-more');
      if (moreBtn2) moreBtn2.addEventListener('click', function () {
        fabricShown = Math.min(fabricShown + FABRIC_PAGE, avail.length);
        render();
      });
      /* Guarded for the same reason as the main picker's; see the note there. */
      var allBtn2 = document.getElementById('mky-all');
      if (allBtn2) allBtn2.addEventListener('click', function () {
        state.showAll = !state.showAll;
        fabricShown = FABRIC_PAGE; render();
      });
      var swapBtn = document.getElementById('mky-swap');
      if (swapBtn) swapBtn.addEventListener('click', function () {
        swapTopAndBottom();
        render();
      });

    } else {
      var f = slotFace(state.fabric);
      var tops3 = topsFor();
      /* Called for its side effect, not its return: syncUrl() replaceState's the
         answers into the address bar, which is what makes this page THE design and
         lets her bookmark or copy it. Its return value was only ever the query
         string for the removed request link. */
      syncUrl();
      var b = builds();
      /* Yardage language is gone (Nuno, 2026-09-03): we do not track yards, so we do not
         claim to. What survives is the promise that stands on its own. */
      var fb2 = slotFace(bottomFabricCode());
      var fin = slotFace(insideFabricCode());
      /* Count the looks that are genuinely distinct rather than asserting four. If two
         fabrics ever coincide the claim quietly becomes true again instead of lying. */
      var looks = 1;
      if (state.variation === 'reversible') {
        var oT = state.fabric, oB = bottomFabricCode() || state.fabric, iN = insideFabricCode();
        looks = isSingle()
          ? (iN && iN !== oT ? 2 : 1)
          : Object.keys([[oT, oB], [oT, iN], [iN, oB], [iN, iN]]
              .filter(function (c) { return c[0] && c[1]; })
              .reduce(function (acc, c) { acc[c[0] + '|' + c[1]] = 1; return acc; }, {})).length;
      }
      var claim = looks > 1
        ? looks + ' ways to wear it, and this combination will not be made again.'
        : 'This combination will not be made again.';
      /* ⚠️ FOUR LOOKS IS TWO-PIECE ONLY. A reversible one piece has two faces, so two
         looks: same construction, same price, half the combinations. Saying four there
         would be a small overclaim that costs trust for nothing. */
      var lookList = '';
      if (state.variation === 'reversible' && isSplit() && f && fin) {
        /* Two fabrics, four ways, because each piece turns on its own. The names are
           the same two throughout -- that is the point, and it is what the two-fabric
           limit buys her. */
        var oB2 = (fb2 || f);
        lookList = '<dt>The four ways</dt><dd>' +
          [f.name + ' top, ' + oB2.name.toLowerCase() + ' bottom',
           f.name + ' top, ' + fin.name.toLowerCase() + ' bottom',
           fin.name + ' top, ' + oB2.name.toLowerCase() + ' bottom',
           fin.name + ' top, ' + fin.name.toLowerCase() + ' bottom'].join('<br>') + '</dd>';
      }
      el.step.innerHTML =
        '<h2 class="mky__q">Yours</h2>' +
        '<p class="mky__claim">' + claim + '</p>' +
        /* Swap sits with the GARMENT, not with the CTAs. It changes what she is
           looking at, so stranding it below the spec and next to Save/Reserve read
           as a third thing to submit. Directly under the preview, above the written
           record, it reads as what it is: an adjustment. */
        (fb2 && isSplit()
          /* ⚠️ SAME WRAPPER AS THE OTHER SWAP, rather than a second margin rule. The fabric
             screen's swap already sat flush on what followed it and got .mky__swapwrap for
             exactly this; this one sits flush on the written record below it. One class,
             one place to change the spacing for both. */
          ? '<p class="mky__actions mky__swapwrap"><button class="button button--secondary" type="button" ' +
            /* ⚠️ "Swap fabrics", NOT "Swap top and bottom". Codex, 2026-09-17, asked for a
               clearly labelled swap; the old label named the PIECES and read, on a screen
               that also carries reversible preview controls, as though it turned the suit
               over. It exchanges CLOTH. The pieces stay where they are. */
            'id="mky-swap2">Swap fabrics: top and bottom</button></p>'
          : '') +
        '<dl class="mky__record">' +
          savedRowsHtml() + lookList +
          /* ⚠️ AN EMPTY SLOT, FILLED BY paintReviewNotes() AFTER THIS RENDER. The notes
             are shown on the review with their question context and can be edited or
             removed there (the panel is one tap away and lists them) -- Codex, section 5.
             It is a SLOT rather than inline markup because adding a note while standing
             on the review must not re-render this screen: that would rebuild the save
             form underneath her, losing a half-typed email. */
          '<span id="mky-review-notes" hidden></span>' +
          (state.band && state.cup
            ? '<dt>Bust</dt><dd>' + state.band + state.cup +
              ' <span class="text-sm text-subdued">(' + effectiveTopSize() + ' on our chart)</span></dd>'
            /* ⚠️ NEVER PRINT A CHART SIZE BESIDE AN UNLISTED ONE. The row above earns its
               "(M on our chart)" because the band and cup came off the chart. An unlisted
               size has not been graded and saying so would be the silent conversion this
               route exists to prevent -- so the row carries her own words and says plainly
               that it is still to be reviewed. */
            : state.bustUnlisted && state.bustLabel
              ? '<dt>Bust</dt><dd>' + escAttr(state.bustLabel) +
                ' <span class="text-sm text-subdued">(read as ' + BUST_SYSTEM +
                ' sizing — not on our chart, Evelin will review this with you)</span></dd>'
              : state.bustSkipped
                ? '<dt>Bust</dt><dd class="text-subdued">Not known — to be confirmed</dd>'
                : '') +
          '<dt>Sizes</dt><dd>' + effectiveTopSize() + ' top, ' + state.bottomSize + ' bottom</dd>' +
          (b.length ? '<dt>Cut for you</dt><dd>' + b.join('. ') + '.</dd>' : '') +
          /* ⚠️ THE RECORD HAS TO SHOW WHAT WAS *NOT* DECIDED, OR IT OVERSTATES ITSELF.
             Three answers reach this screen carrying no construction: a question this
             garment never asked, and the two "I'm not sure" answers added on 2026-09-15.
             Without a row they are invisible here and appear only in Evelin's copy, so the
             customer reviewing her design cannot see that she left something open -- and
             an absent row reads as "nothing to say", which is how "not sure" quietly
             becomes "standard". Same words the workroom gets; one source. */
          (fitNotes().length
            ? '<dt>Noted</dt><dd class="text-subdued">' +
              fitNotes().join('<br>') + '</dd>'
            : '') +
          /* ⚠️ NO PRICE ROW. Nuno, 2026-09-09. Nothing is quoted until the fabric is
             confirmed and she has seen a picture made from it, so a figure here is a
             number she could hold us to before anyone has looked at the real fabric.
             The confirmation emails carry no price either, for the same reason.
             PRICE still reaches the variation picker (where the reversible upcharge
             has to be visible at the moment of choosing) and the saved record that
             reaches the workroom. Do not reinstate it here. */
        '</dl>' +
        (heightBeyondGrade()
          ? '<p class="mky__beyond">' + root.dataset.heightBeyond + '</p>'
          : '') +
        (desktopOn() ? '' : looksPanel(f, fb2, fin)) +
        /* ⚠️ DO NOT REINSTATE A CTA HERE. Two have been removed from this slot:
           a primary-styled `Request this suit` link to /pages/submit-your-request,
           which passed the whole design in a query string that page IGNORES
           (verified 2026-09-08: it renders byte-identically with and without a full
           parameter set), so the most prominent button on the screen silently
           discarded everything she had just chosen; and a `Copy link` button, which
           duplicated Save my design with a worse outcome -- it kept nothing on our
           side and nothing on hers unless she then pasted it somewhere. Keeping the
           design is what Save is for, and the note below still tells her the address
           IS the design. The only submit buttons on this screen are Save and
           Reserve. */
        '<p class="mky__hint">' + root.dataset.resultNote + '</p>';
      desktopResultLooks();
      var swap2 = document.getElementById('mky-swap2');
      if (swap2) swap2.addEventListener('click', function () {
        swapTopAndBottom();
        render();
      });
    }
    finishStep();
  }

  el.back.addEventListener('click', function () { goBack(); });
  if (el.reset) {
    el.reset.addEventListener('click', function () {
      /* ⚠️ CONFIRMS, BECAUSE IT DESTROYS EVERYTHING. Same two-tap pattern the last
         screen's Start again uses; the confirm text is the merchant's own setting. */
      if (!window.confirm(root.dataset.restartConfirm || 'Start again?')) return;
      /* The design is thrown away only once we are standing on the entry that will
         carry the fresh one; see histUnwind(). */
      histUnwind(function () {
        startAgain();
        /* A REPLACE, not a push: the entry we have just walked back to becomes the new
           opening screen, so Back from it leaves the studio the way it did on arrival. */
        histPopping = true;
        try { render(); } finally { histPopping = false; }
      });
    });
  }
  el.next.addEventListener('click', function () {
    /* ⚠️ CONTINUE IS AN EXPLICIT ACCEPTANCE ON THE SHAPES SCREEN, AND NOWHERE ELSE.
       Codex, 2026-09-15: "Continue may explicitly accept the suggestion." Carrying on past
       a suggested shape IS choosing it -- she has seen it, and she is moving on with it --
       so it is written here, before the step moves, which is what puts it in the record
       and in the saved link as a decision rather than a guess. Every other screen arrives
       genuinely unanswered and has nothing to accept. */
    if (at('shapes') && desktopOn()) {
      var partSpec = shapeDeckSpec();
      var chosen = partSpec.opts.findIndex(function (_, i) { return partSpec.isOn(i) || partSpec.suggested(i); });
      if (chosen >= 0) {
        if (partSpec.select(chosen) === false) { render(); return; }
      }
    } else if (at('shapes')) acceptSuggestedShapes();
    var why = whyBlocked();
    if (why) {
      needs(why);
      /* Send her to the answers, not just to a sentence about them. Focus rather
         than scroll alone, so a keyboard and a screen reader both land there. */
      var first = el.step.querySelector('.mky__opt, .mky__swatch, .mky__shape');
      if (first && first.focus) first.focus();
      return;
    }
    /* ⚠️ NOTHING TO DO ON THE LAST SCREEN. Continue is hidden there and the primary
       action is the form's own Save/Reserve, which submits itself. */
    goNext();
  });

  (function fromUrl() {
    var q = new URLSearchParams(location.search);
    ['height','volume','waist','tummy',
     'torso','wire','padding','seat','coverage','style','topShape','topSize','bottomShape','bottomSize',
     'fabric','variation','fabricBottom','fabricInside','fabricCrop','cropFace',
     'band','cup']
      .forEach(function (k) { if (q.get(k)) state[k] = q.get(k); });
    /* A link can carry anything, and this one reaches the cutting line. Two values only. */
    if (state.cropFace !== 'primary' && state.cropFace !== 'second') state.cropFace = '';
    /* ⚠️ A LINK CAN CARRY ANYTHING, AND SINCE 2026-09-15 THREE QUESTIONS HAVE AN
       `unknown` ANSWER THAT MUST BE TOLD APART FROM A TYPO. `unknown` is ANSWERED and
       differs from unanswered: it survives Back, refresh, a saved link, the review and
       the maker payload, and it creates no fit adjustment. That only holds if the value
       is validated on the way in -- an unrecognised string would otherwise sit in `state`
       looking answered, tick no option, satisfy the Continue gate and travel to Evelin as
       a cutting instruction nobody wrote.
       ⚠️ THIS NARROWS WHAT IS ACCEPTED; IT DOES NOT WIDEN IT. Codex, 2026-09-15: do not
       expand accepted arbitrary URL values. Every list here is the question's OWN option
       list, so an answer is accepted exactly when the flow offers it. */
    Object.keys(Q).forEach(function (k) {
      if (Q[k].multi || !state[k]) return;
      var ok = Q[k].opts.some(function (o) { return o[0] === state[k]; });
      if (!ok) state[k] = '';
    });
    if (state.style && !STYLES.some(function (o) { return o[0] === state.style; })) {
      state.style = '';
    }
    /* The arrangement decides which extra fabric screens exist and what the suit costs,
       so an unrecognised one is dropped rather than left to read as answered. */
    /* ⚠️ `indexOf`, NOT `some(o => o[0] === ...)`. VARIATIONS held [value, label, desc]
       rows until 2026-09-17 and now holds the values themselves, so `o[0]` here became
       the first CHARACTER of each value -- 's', 'm', 's', 'r' -- and the guard would have
       rejected every arrangement a saved link carried, silently blanking it. Found by
       grepping the identifier after changing its shape, which is the only reason it did
       not ship: nothing here throws, the design simply loses its arrangement. */
    if (state.variation && VARIATIONS.indexOf(state.variation) < 0) {
      state.variation = '';
    }
    SOLID_NOTE_PARAM.forEach(function (pair) {
      var v = q.get(pair[1]);
      if (v) state.solidNotes[pair[0]] = v.slice(0, 120);
    });
    state.bustSkipped = q.get('bustSkipped') === '1';
    state.bustUnlisted = q.get('bustUnlisted') === '1';
    state.bustLabel = (q.get('bustLabel') || '').slice(0, 24);
    /* ⚠️ ONLY A SHAPE THIS STYLE CAN DRAW COUNTS AS CHOSEN. A link can carry any
       string; pinning an unknown one would stop coverage from guessing AND leave no
       tile selected, so the preview would draw one thing and the record print
       another. An unrecognized value is dropped here and treated as never sent. */
    if (q.get('topShape')) {
      linked.topShape = topsFor().some(function (t) { return t[0] === state.topShape; });
      if (!linked.topShape) state.topShape = '';
    }
    if (q.get('bottomShape')) {
      linked.bottomShape = BOTTOMS.some(function (b) { return b[0] === state.bottomShape; });
      if (!linked.bottomShape) state.bottomShape = '';
    }
    /* A link can carry anything. An unrecognized band or cup would survive into the
       record and the email as a cutting instruction, and parseInt('x') is NaN, which
       fails every comparison in bustToStandard and silently derives XL. Drop both
       unless both are known, so a bad link asks the question again. */
    if (!BANDS.some(function (b) { return b === state.band; }) ||
        !CUPS.some(function (c) { return c[0] === state.cup; })) {
      state.band = ''; state.cup = '';
    }
    /* ⚠️ VALIDATED AND DE-DUPLICATED, AND THE EXCLUSIVITY IS ENFORCED HERE TOO. The
       screen cannot be reached in a state where "Nothing. Tops fit me fine" sits beside
       three ticked issues, so a link must not be able to produce one: it would show four
       ticks, print a contradiction in the record, and send both to Evelin. An exclusive
       answer in the link wins and the rest are dropped. */
    if (q.get('top')) {
      var wanted = q.get('top').split('|');
      var known = {}, picked = [], excl = '';
      Q.top.opts.forEach(function (o) { known[o[0]] = 1; });
      wanted.forEach(function (v) {
        if (!known[v] || picked.indexOf(v) > -1) return;
        if (isExclusive(v)) { if (!excl) excl = v; return; }
        picked.push(v);
      });
      state.top = excl ? [excl] : picked;
    }
    /* Which of the three fabric slots the link named and we cannot honor. Read by the
       resume ladder below and by the picker screens, which say which one needs replacing. */
    var gone = {};
    /* ⚠️ RESTORE THE CLOTH ANSWERS BEFORE ANYTHING READS THEM. */
    prefsFromUrl(prefsParam(q));
    state.seed = fabricSeedFor(q);
    /* ⚠️ THE ID COMES FROM THE LINK WHEN THE LINK HAS ONE, AND IS MINTED WHEN IT DOES NOT.
       That is what makes a same-browser resume restore the RIGHT notes: reload, Back,
       Forward and a bookmarked link all carry `did`, so they all read the same row. A
       link from somebody else carries a `did` this browser has never stored, so it reads
       an empty list -- which is the correct outcome and the one Codex asked to have
       verified.
       ⚠️ BOUNDED AND CHARACTER-CHECKED, because it is an unauthenticated URL parameter
       that becomes a storage KEY. Anything outside the minted alphabet is discarded
       rather than trusted. */
    var did = (q.get('did') || '').slice(0, 16);
    state.designId = /^[a-z0-9]+$/i.test(did) ? did : '';
    designId();
    notesLoad();
    var nowVer = fabricVersion();
    var hadVer = q.get('cver') || '';
    /* ⚠️ AN OLD LINK CARRIES NO PREFERENCES, AND WE DO NOT PRETEND IT DID. The fabric she
       chose is preserved; the questions are simply unanswered, so the picker offers the
       whole drop rather than six fabrics under a heading claiming they match answers that
       were never restored. */
    if (hadVer && hadVer !== nowVer) {
      /* The catalogue moved. Keep her fabric, recompute the suggestions deliberately,
         reset paging so "Show more" cannot page past the end of a list that changed, and
         let the screen say so. */
      state.prefsRefreshed = true;
      state.prefsPage = 0;
    } else {
      var cp = parseInt(q.get('cpage'), 10);
      state.prefsPage = (isFinite(cp) && cp > 0 && cp < 200) ? cp : 0;
    }
    state.prefsVer = nowVer;
    /* ⚠️ A FABRIC THAT IS NO LONGER AVAILABLE IS EXPLAINED, NEVER SWAPPED -- and all three
       slots are checked, not just the main one. Removed from the drop and sold out are the
       same thing to a customer: she cannot have it. */
    ['fabric', 'fabricBottom', 'fabricInside', 'fabricCrop'].forEach(function (k) {
      if (state[k] && !fabricAvailable(state[k])) {
        gone[k] = state[k];
        state[k] = '';
      }
    });
    /* ⚠️ COUNTED AFTER THE AVAILABILITY SWEEP, NOT BEFORE. A legacy link naming three
       fabrics of which one has sold out is a TWO-fabric design plus an empty slot, and
       asking her to choose between three cloths when one of them is gone would be a
       question about a fabric she cannot have. The sweep above has already emptied those
       slots, so what is counted here is what we could actually cut.
       ⚠️ AND IT IS COUNTED FROM THE *FIELDS*, NOT FROM bottomFabricCode(). That accessor
       reports the SPECIFICATION, which by construction never exceeds two -- reading it
       here would mean the migration could never fire. This is the one place that looks at
       the raw slots on purpose. */
    if (state.variation === 'reversible' && isSplit() &&
        state.fabric && state.fabricBottom && state.fabricInside) {
      var distinct = {};
      [state.fabric, state.fabricBottom, state.fabricInside].forEach(function (c) {
        distinct[c] = 1;
      });
      if (Object.keys(distinct).length === 3) {
        state.fabricExcess = { top: state.fabric, bottom: state.fabricBottom,
                               inside: state.fabricInside };
      }
    }
    state.fabricGone = gone.fabric || '';
    /* Carried into state so the PARTNER screens can say which slot needs replacing too --
       `gone` itself is local to this restore. */
    state.fabricGoneSlots = gone;
    /* ⚠️ RETIRED SIZE LABELS ARRIVE ON OLD LINKS. The flow used to offer SM and MED,
       which are not in SIZES any more, so an old link would leave no chip selected while
       the record still printed the dead label. Only values NOT in the current list are
       remapped, so a link written since the rename is untouched. A pre-rename `S` meant
       what is now `XS` and will read as `S`: acceptable, because a link pre-fills a
       design and nothing is ever cut without Evelin replying by hand. */
    var OLD_SIZES = { 'SM': 'S', 'MED': 'M', 'X-SM': 'XS' };
    ['topSize','bottomSize'].forEach(function (k) {
      if (SIZES.indexOf(state[k]) > -1) return;
      state[k] = OLD_SIZES[state[k]] || 'M';
    });
    normalizeVariation();
    /* ⚠️ RESUME AT THE FIRST DECISION THE LINK IS MISSING, AND IT IS ONE WALK NOW.
       Until 2026-09-15 this was a hand-written LADDER: a gate on style, then a literal
       `Q_STEPS + 2`, then `+ 3` if the link named a size, then `FABRIC_START + ck`, then
       `FABRIC_PICK`, then `VARIATION`, then `VARIATION + 1 + i`. Seven rungs, each an
       offset, each carrying its own reason, and every one of them had to be re-derived
       whenever a question moved -- which is exactly what Codex's brief does to nineteen
       of them.

       The rule the ladder was reaching for is one sentence: resume at the FIRST APPLICABLE
       screen this link has not answered, and keep everything it answered after that. So
       that is what this does, over the route, using the same predicates Continue uses.

       ⚠️ EVERYTHING THE LADDER LEARNED IS PRESERVED, AND NOT BY ACCIDENT:
        - A link with no `style` stops on the kind screen, because `style` is screen one
          and nothing after it is applicable without one.
        - A one piece and a monokini resume correctly, because coverage and shapes declare
          their own applicability and this loop asks them. That defect -- every non-split
          suit dropped back to question one, including the `return_to` link after a
          submit -- is what the `wantsCoverage`/`wantsShapes` gate existed for.
        - A fabric that has sold out or left the drop was already cleared above, so the
          fabric screen reads as unanswered and she lands on the one that explains it. The
          ladder had to test `gone.fabric` by hand for the same result.
        - An unanswered `variation` keeps her on the variation screen rather than showing
          her a finished design priced from an answer she never gave.
        - "TEST THE LINK, NOT THE STATE" is no longer needed for sizes and is still
          honoured for shapes. `topSize`/`bottomSize` default to M, which is why a
          state test used to pass for free -- but the sizes screen is answered by the BUST
          answer, not by those chips, so `screenAnswered` is already link-independent
          there. Shapes DO carry a default, so `screenResumed` tests `topShape` by hand;
          see the note on it.
       ⚠️ LATER ANSWERS ARE NOT DISCARDED. She is placed at the gap; everything the
       link carried past it is still in `state` and still on screen when she arrives. */
    step = (function () {
      for (var i = 0; i < ROUTE.length; i++) {
        if (skipsStep(i)) continue;
        if (!screenResumed(i)) return i;
      }
      return lastStep();
    })();
    /* ⚠️ BELT AND BRACES ON THE POST-SUBMIT RETURN. return_to should preserve the
       design, but a redirect that drops it would strand someone on an empty flow
       immediately after telling them their design was sent. */
    /* ⚠️ VALIDATED STATE HERE TOO. `q.get('fabric')` was true for a link naming a fabric we
       can no longer cut, so the post-submit rescue never fired on exactly the design most
       likely to need it. */
    if (!state.fabric && !gone.fabric && q.get('contact_posted')) {
      try {
        var last = sessionStorage.getItem('mky-last');
        if (last) { location.replace(last + '&contact_posted=true'); return; }
      } catch (e) { /* private mode: fall through to the empty flow */ }
    }
    maxStep = step;
  })();
  /* ⚠️ WIRED BEFORE THE FIRST RENDER, AND EXACTLY ONCE. Everything it binds lives outside
     `#mky-step`, so no render can destroy a handler and nothing needs rebinding -- which
     is also what lets the panel survive the flow moving underneath it. */
  wireHelp();
  /* ⚠️ BEFORE THE FIRST RENDER. `revealPosted()` may open the inquiry section, and the
     render that follows paints the flow around it. It is also what stops BOTH forms
     claiming success at once -- see the note on that function. */
  var postedAs = revealPosted();
  /* ⚠️ ONLY WHEN NOTHING POSTED. Coming back from a FAILED post is the case this exists
     for; coming back from a successful one has already cleared the copy. */
  if (!postedAs) askTextRestore();
  /* ⚠️ AN ERROR BANNER MUST OPEN THE SECTION, OR SHE CANNOT READ IT. Shopify renders
     `form.errors` INSIDE the inquiry form, and that form sits in a section that is
     collapsed by default -- so a rejected post would have come back looking exactly like
     an untouched page. Success and errors are mutually exclusive in the Liquid (one is
     `if posted`, the other `unless posted`), so this needs no branch on `postedAs`. */
  /* ⚠️ AN ERROR BANNER WITH NOTHING IN IT IS NOT AN ERROR, AND SAYING SO FIXES TWO
     THINGS. `mkreal.py` strips Liquid conditionals, so the harness renders the
     `form.errors` block on EVERY page -- with an empty `<li>`, because the loop has
     nothing to iterate. Treating its presence as a failure opened the inquiry section on
     every harness screen, pushed the question down by up to 176px and put the deck pager
     behind the sticky bar; `cards` and `deck` caught it on geometry, 26 assertions and 2.
     Requiring a message is also just correct: an empty banner should never be on screen,
     on any render, and it is removed rather than merely ignored. */
  Array.prototype.forEach.call(
    document.querySelectorAll('.mky__formerr'),
    function (box) {
      var msg = Array.prototype.filter.call(box.querySelectorAll('li'), function (li) {
        return li.textContent.trim();
      });
      if (!msg.length && box.parentNode) box.parentNode.removeChild(box);
    }
  );
  if (document.querySelector('#mky-ask .mky__formerr')) {
    var askBox = document.getElementById('mky-ask');
    if (askBox) askBox.classList.add('mky__ask--on');
    askTextRestore();
  }
  /* ⚠️ DISMISS IS THE ONLY THING THAT DISCARDS THE RETAINED QUESTION, apart from Start
     again. Codex: "Do not automatically delete the recoverable question on a correlated
     return... Clear it on explicit dismissal or Start again." */
  var dismiss = document.getElementById('mky-ask-dismiss');
  if (dismiss) dismiss.addEventListener('click', function () {
    askTryClear();
    askDraftSave('');
    var host = document.getElementById('mky-ask-posted');
    if (host) host.hidden = true;
    closeAsk();
    /* ⚠️ THE SAME RULE AS closeHelp(), NOT A SECOND HARDCODED FALLBACK. This focused
       `#mky-help` unconditionally, which on a phone is inside a closed disclosure. */
    focusAfterClose(null);
  });
  var resume = document.getElementById('mky-ask-resume');
  if (resume) resume.addEventListener('click', function () {
    closeAsk();
    focusAfterClose(null);
  });
  render();
})();
